/**
 * Single source of truth for all tunable simulation + netcode constants.
 * Shared by server (authority), client (prediction/render) and load bots so
 * the same numbers are never duplicated or allowed to drift.
 *
 * COORDINATE CONVENTION (read this once, never be confused again):
 *   The simulation is a 2D top-down plane with axes (x, y).
 *     x = east/west, y = north/south.
 *   The RENDERER maps sim (x, y) -> three.js world (x, height, y), i.e.
 *   sim-y becomes world-Z (north) and the ocean height function supplies
 *   world-Y (up). All gameplay/physics/netcode stays in the 2D (x, y) plane.
 *
 * Tuned for a slow-moving NAVAL game (ships, not FPS): a 30 Hz authoritative
 * sim with snapshots at 15 Hz is plenty smooth once the client interpolates
 * remote entities and predicts the local ship.
 */

/** Authoritative simulation rate (server steps per second). */
export const TICK_HZ = 30;
/** Fixed simulation timestep in seconds. */
export const TICK_DT = 1 / TICK_HZ;
/** Network snapshot broadcast rate (snapshots per second per client). */
export const SNAPSHOT_HZ = 15;
/** How often the client sends input to the server. */
export const INPUT_HZ = 30;
/** Remote-entity interpolation buffer (ms) — ~3 snapshots at 15 Hz. */
export const INTERP_DELAY_MS = 100;
/** Max server-side rewind for lag-compensated cannon hits (ms). */
export const LAG_COMP_MS = 200;

/** Soft cap per shared-sea region. Giant PvP — no lobby cap (see V1_RAID_DESIGN §3).
 * 160 verified at ~5 ms tick with the spatial grid; shard into more regions beyond. */
export const MAX_PLAYERS_PER_ROOM = 160;
/** Fill target before considering a new region; AOI keeps each client light regardless. */
export const TARGET_PLAYERS_PER_ROOM = 80;

/** Hard global concurrent-user ceiling for one server process. Beyond this the handshake refuses new
 *  captains with a graceful "the sea is full" reject (rather than degrading the tick for everyone). Sized
 *  above a comfortably-loaded single droplet; shard into more processes/regions to grow past it. The
 *  `/stats` endpoint exposes both the live `ccu` and this `maxCcu`. */
export const MAX_CCU = 160;

/** Square sea size in world units (meters). World spans [0, WORLD_SIZE).
 * 8 km ("The World", wave 2a): a real ocean to sail — long trade routes between the
 * three outposts, ~60 treasure islands, and open sea for the Cursed-Treasure convergence.
 * AOI + the spatial grid keep per-client cost flat regardless of the map's size; the client
 * distance-culls the extra islands so draw calls stay in budget. Position quantization (u16
 * per axis) scales with this automatically (~0.125 m precision @ 8192 — imperceptible at
 * naval speeds). NOTE: growing this changes what a persisted base's (x,y) MEANS, so the
 * JsonWorldStore runs a one-time coordinate migration keyed on the stored `worldSize`. */
export const WORLD_SIZE = 8192;

/**
 * ---- World edge: the automatic "coming about" turnaround (shared sim) ----
 * Players used to sail silently into the invisible wall at [0, WORLD_SIZE] and grind there for hours
 * (the old hard clamp just pinned them). Instead, `enforceWorldEdge` (packages/sim) — called by BOTH the
 * server and the client's local prediction right after `stepShipBody` — detects when a hull enters the
 * `MARGIN` band heading outward and forces a deterministic turn back toward open water.
 *
 * Onda Fix 3 (owner feedback: "kill this aggressive override — the sea must be CLEAN and playable all the
 * way to the physical limit; only right AT the border does it turn you back; do NOT enlarge the reserved
 * strip"): the v37 band was a deep 220 m — that huge no-sail zone is exactly what the owner wanted gone (a
 * player whose base sat near it, or who simply wanted to sail the map's outer waters, kept getting shoved by
 * the come-about; a base spawned in the 250 m grid-edge row dropped the ship AT ~204 m — inside the 220 band,
 * bow pointing out — so it was stuck in a spin loop the moment it appeared). The band is now MINIMAL: sized
 * only by the geometry needed to come about without touching the hard clamp. At MAX_SPEED with the rudder
 * held hard OUTWARD the hull penetrates ≈ MAX_SPEED/TURN_RATE ≈ 8 m past where the override engages (measured
 * 4 m at v=12, 8 m at a 2× speed buff), so a `MARGIN` of ~60 m leaves ~50 m of clamp headroom with a wide
 * safety factor while reserving just ~0.7 % of the sea per edge. Inside that thin band the turn still
 * DOMINATES the helm (overwrites angVel — the owner's complaint was the SIZE of the zone, not the strength).
 * `MIN_SPEED` gates it so a ship that is ANCHORED/berthed/becalmed at the shore (speed ≈ 0) is never shaken —
 * belt-and-suspenders for a dock that sits close to the border. A corner blends both walls to a diagonal; a
 * glancing approach reflects naturally. Pure + deterministic (no wall-clock/rng, a pure function of body + dt
 * — the forced angVel is reconstructed identically by the client's reconcile replay from the snapshot), so
 * prediction stays == authority; the helper returns `true` on the ticks it's turning so the LOCAL client can
 * flash the "flat earth" banner. The old hard clamp lives on inside `stepShipBody` purely as an out-of-bounds
 * backstop — the arc completes long before a hull could reach it. */
export const WORLD_EDGE = {
  /** Metres from any wall where the come-about engages — the ONLY reserved strip. Kept minimal: worst-case
   *  inward penetration at flank (even with a 2× speed buff, rudder fighting outward) is ≈ 8 m « this, so the
   *  hull turns around with wide headroom and never reaches the hard clamp, yet the sea stays fully navigable
   *  to within this many metres of the physical edge. The client's edge bruma is tied to this exact distance,
   *  so the fog band shrinks with it ("the fog turns you"). */
  MARGIN: 60,
  /** Forced yaw rate (rad/s) while coming about. π/2 ⇒ a head-on 180° arc takes ~2 s — firmly automatic and
   *  clearly stronger than the player's own MAX_TURN_RATE (0.55), but a graceful sweep, never a teleport-snap. */
  TURN_RATE: Math.PI / 2,
  /** Per-second speed retained through the turn (gentle scrub — keeps the way so the hull arcs back out and
   *  never pins/sticks at the wall; ramming the edge at flank still isn't quite free). */
  SPEED_KEEP: 0.9,
  /** Keep steering until the bow points this-much inward (dot of heading·inwardNormal): 0.5 ⇒ the come-about
   *  completes to within 60° of straight-back-to-sea before releasing, so the hull leaves the band aimed at
   *  open water instead of skimming parallel along the border. */
  ALIGN: 0.5,
  /** Below this hull speed (m/s) the override is SUPPRESSED: an anchored / berthed / becalmed ship sitting
   *  in the band is left alone (a stationary hull can't cross the hard clamp anyway, and jerking a docked
   *  ship around reads as a bug). The moment it makes way outward again the come-about re-engages. */
  MIN_SPEED: 0.5,
} as const;

/**
 * ---- Outposts (trading posts: sell chests, the LOCAL market venue) ----
 * Three of them in a wide triangle across the sea (wave 2a; was 2 on a diagonal). Positions are
 * FRACTIONS of WORLD_SIZE so the Room spawns them and the deterministic TRADE-WIND lanes read the
 * EXACT SAME numbers (zero drift between server + client prediction). One is the CAPITAL — identity
 * only for now (a bigger name on the chart + a golden berth); the legendary auction lands in a future
 * wave. Kept clear of the central spawn heart (0.5,0.5) and the map edges.
 */
export interface OutpostDef { name: string; fx: number; fy: number; capital: boolean; }
export const OUTPOSTS: readonly OutpostDef[] = [
  { name: 'Port Royal',     fx: 0.5, fy: 0.80, capital: true },
  { name: 'Tortuga Bank',   fx: 0.2, fy: 0.25, capital: false },
  { name: "Serpent's Rest", fx: 0.8, fy: 0.30, capital: false },
];
export const OUTPOST_RADIUS = 90;

/**
 * ---- World Editor override seam (Creative Mode phase 2) ----
 * The sea (treasure islands + their chests, outposts, fishing shoals, static wrecks) is generated
 * procedurally per room seed by `generateWorld()` in `@piratas/sim`. This is the seam that lets the owner
 * REPLACE that procedural layout with a hand-arranged one exported by the World Editor.
 *
 * DEFAULT = null → every section is generated procedurally EXACTLY as before (a golden test in
 * `packages/sim/src/worldgen.test.ts` pins that this is a byte-for-byte no-op). A future wave pastes an
 * editor export here; each PROVIDED section replaces its procedural counterpart (skipping its rng draws),
 * so the whole sea becomes the curated layout for every room. Positions are sim-plane (x, y) in
 * [0, WORLD_SIZE). Chests/radius are optional (radius defaults to the historical 45 m island size).
 */
export interface WorldOverrideChest { x: number; y: number; }
export interface WorldOverrideIsland { x: number; y: number; radius?: number; chests?: WorldOverrideChest[]; }
export interface WorldOverrideOutpost { name: string; x: number; y: number; radius?: number; capital?: boolean; }
export interface WorldOverrideShoal { x: number; y: number; }
export interface WorldOverrideWreck { x: number; y: number; rich?: boolean; }
export interface WorldOverride {
  islands?: WorldOverrideIsland[];
  outposts?: WorldOverrideOutpost[];
  shoals?: WorldOverrideShoal[];
  wrecks?: WorldOverrideWreck[];
}
export const WORLD_OVERRIDE: WorldOverride | null = null;

/**
 * ---- Island archetypes (Onda Ilhas-Tipo) ---- The owner edits a handful of island TYPES and every treasure
 * islet follows its assigned type. An archetype is the island's DRESSING: which hero model it wears
 * (a leaning-coconut isle, a bare sandbar, or the procedural fallback), extra decoration pieces (each placed
 * as a FRACTION of the island radius so it scales to any island size), and optional per-type apron tuning.
 *
 * AUTHORITY: dressing is RENDER-ONLY (no wire, no sim) — `render.ts setWorld` reads the archetype and dresses
 * each island. Chest offsets are GAMEPLAY, so they are NOT applied by render or worldgen here: the live sim
 * keeps the worldgen-scattered chest positions (and the client seats the visual on that exact spot, so pickup
 * never desyncs — see render.ts frente 1). The `chests` offsets travel as DATA for the authoritative path
 * (Export TS → commit; a future wave wires them into worldgen) — mirroring the Landmark/City "render-preview
 * vs committed-authority" split documented in docs/EDITOR.md.
 *
 * DEFAULT = null → `archetypeForIsland` falls back to `DEFAULT_ISLAND_ARCHETYPES`, which reproduces the
 * shipped 1/3-sandbar / 2/3-coconut dressing EXACTLY (the retired hard-coded id hash). Worldgen is untouched,
 * so the golden test stays a byte-for-byte no-op. A committed non-null set re-dresses every island.
 */
export type IslandBaseModel = 'coconut' | 'sand' | 'procedural';
export type IslandDecoKind = 'coconut' | 'sand' | 'shipwreck' | 'rock' | 'palm';
/** A decoration placed relative to the island: horizontal offset is `fx,fz × island radius`, so one archetype
 *  dresses islands of any size. `rot` is yaw (rad); `scale` multiplies the kind's default decoration size. */
export interface IslandDecoration { kind: IslandDecoKind; fx: number; fz: number; rot: number; scale: number; }
/** A chest position as a fraction of the island radius (authoritative-via-commit; see the AUTHORITY note). */
export interface IslandChestOffset { fx: number; fz: number; }
export interface IslandArchetype {
  name: string;
  base: IslandBaseModel;
  /** relative share of islands assigned to this archetype (default 1) — a deterministic id hash distributes them. */
  weight?: number;
  decorations?: IslandDecoration[];
  chests?: IslandChestOffset[];
  /** per-archetype sand-apron tuning; omit to use the render defaults (ISLE.BEACH_* in render.ts). */
  beachMult?: number; beachRise?: number; beachSink?: number;
}
export interface IslandArchetypeSet { archetypes: IslandArchetype[]; }
export const ISLAND_ARCHETYPES: IslandArchetypeSet | null = null;

/** The shipped default set — reproduces the current dressing EXACTLY. Sandbar is FIRST with weight 1 so the
 *  cumulative-weight hash below assigns it precisely when `imul(id, 2654435761) % 3 === 0` (identical to the
 *  retired hard-coded `isBar` test); coconut takes the other 2/3. */
export const DEFAULT_ISLAND_ARCHETYPES: IslandArchetypeSet = {
  archetypes: [
    { name: 'Sandbar', base: 'sand', weight: 1 },
    { name: 'Coconut Isle', base: 'coconut', weight: 2 },
  ],
};

/** Deterministic island id → archetype (pure integer hash; no Math.random, so client render == any future
 *  server use). Distributes ids across the set by each archetype's `weight`. With the default set this is the
 *  exact 1/3-sandbar split the render used before this wave. */
export function archetypeForIsland(id: number, set: IslandArchetypeSet | null = ISLAND_ARCHETYPES): IslandArchetype {
  const arches = (set ?? DEFAULT_ISLAND_ARCHETYPES).archetypes;
  if (arches.length === 0) return DEFAULT_ISLAND_ARCHETYPES.archetypes[1]!; // safety net: a coconut isle
  let total = 0; for (const a of arches) total += Math.max(1, a.weight ?? 1);
  let h = (Math.imul(id, 2654435761) >>> 0) % total;
  for (const a of arches) { const w = Math.max(1, a.weight ?? 1); if (h < w) return a; h -= w; }
  return arches[arches.length - 1]!;
}

/**
 * ---- Trade winds (wave 2a) ---- A circulating current runs the triangle of outposts
 * (OUTPOSTS[i] → OUTPOSTS[i+1], wrapping): sail WITH the flow inside a lane and gain up to BOOST extra
 * forward speed, PROPORTIONAL to how well your heading aligns with the lane (against the flow = nothing).
 * This makes the long hauls between posts viable AND turns the routes into visible, ambushable arteries.
 * Evaluated by the PURE `tradeWindMul` in @piratas/sim (reads these + OUTPOSTS), folded into stepShipBody
 * on BOTH server and client prediction identically — so it never desyncs. Lanes run one way round the
 * loop by design (the spec's "contra o fluxo = sem boost"); a round trip in the loop direction is boosted.
 */
export const TRADE_WIND = {
  /** Max extra forward speed (fraction) sailing dead-aligned with the current inside a lane. */
  BOOST: 0.35,
  /** Lane half-width: max perpendicular distance (m) from the centerline to still catch the current. */
  LANE_WIDTH: 150,
} as const;

/** Treasure islands scattered across the sea (wave 2a "The World" — was 16 on the 4 km map).
 *  Rejection-sampled clear of base slots + each other; the client distance-culls them so the bigger
 *  count never blows the draw-call budget. */
export const TREASURE_ISLAND_COUNT = 60;

/** Treasure-island HULL COLLISION footprint, as a fraction of the island's gameplay radius (WorldIsland.radius,
 *  default 45 m). Ships (players + NPCs) are pushed off this solid CORE exactly like a base island — the soft,
 *  never-trapping shove of `collideCircles` (packages/sim), applied identically on the server and the client's
 *  local prediction so prediction stays == authority (the v43 bump forces stale clients to reload the new sim
 *  wiring). Sized to the VISIBLE sand islet (render.ts ISLE.FOOT_MULT ⇒ footprint radius ≈ isl.radius × 0.575),
 *  which sits well INSIDE the 20..50 m chest scatter (worldgen) + the 22 m hoist ring (CHEST.PICKUP_R): every
 *  chest stays reachable from a hull beached at the shore (proven over all 8 canonical rooms — see
 *  islandCollision.test.ts, incl. the galleon r=14 worst case). */
export const TREASURE_ISLAND_COLLIDE_FRAC = 0.58;

/**
 * ---- Smart auto-sail (tap-to-sail quartermaster) ----
 * The chart tap used to synthesise a STRAIGHT-line rudder only — a course through an island pinned the hull
 * on the shore forever, and on arrival the ship sailed straight past the mark (the owner's report). The
 * autopilot is now a full quartermaster (apps/client/src/autosail.ts + packages/sim/src/nav.ts): it plans an
 * A*-over-coarse-grid route around the SAME solid circles the sim collides with, string-pulls it into a few
 * straight sailor legs, re-plans when wind/drift invalidates a leg, trims sail down on approach and STOPS at
 * the mark (sails furled + anchor down). Pure client-side INPUT synthesis on the same C_INPUT path a thumb
 * uses — zero wire, the server stays fully authoritative; these are game-feel tuning constants only.
 */
export const AUTOSAIL = {
  /** Within this (m) of the (water-projected) mark → arrived: sails furled, anchor down, course cleared. */
  ARRIVE_R: 30,
  /** Planning margin (m) ADDED to the hull radius around every solid obstacle — routes keep hull + this off
   *  every shore, so drift between re-plans never kisses the sand. */
  CLEARANCE: 22,
  /** Reactive margin (m, + hull radius) for live leg re-validation: only re-plan when the CURRENT leg truly
   *  compromises this (smaller than CLEARANCE so normal drift inside the planning margin never thrashes). */
  HARD_CLEARANCE: 6,
  /** A* grid cell (m). 8192/32 ⇒ 256×256 cells; conservative square-vs-inflated-circle rasterisation, so a
   *  route of free cells is PROVABLY sailable and channels narrower than ~a cell route around instead. */
  CELL: 32,
  /** Begin shortening sail within this (m) of the final mark (arrive slow, like a real harbour approach). */
  SLOW_R: 140,
  /** Never trim below this while under way — keeps steerage (rudder authority needs speed). */
  MIN_SAIL: 0.2,
  /** Advance to the next waypoint within this (m) even before line-of-sight to the one after opens up. */
  WP_REACH_R: 48,
  /** Min seconds between route recomputes (drift/leg-blocked/stuck triggers are all throttled by this). */
  REPLAN_COOLDOWN_S: 0.7,
  /** Below this hull speed (m/s) with sails commanded up counts toward "stuck" (beached / pinned). */
  STUCK_SPEED: 0.6,
  /** Stuck this long (s) → force a re-plan from wherever we're pinned. */
  STUCK_S: 3,
  /** Accumulated stuck this long (s) → give up honestly (strike the course + tell the captain). */
  STUCK_GIVEUP_S: 12,
  /** A between-ticks jump bigger than this (m) = teleport (respawn / port exit) → re-plan from the new seat. */
  TELEPORT_JUMP: 120,
  /** Keep any auto-sail destination at least this far (m) inside the world border — clear of the WORLD_EDGE
   *  come-about band (60 m), so the hold at the mark never fights the edge turnaround. */
  EDGE_INSET: 80,
} as const;

/**
 * Area-of-interest radius (meters). A client only receives entities within
 * this radius of its ship. Keeps snapshot size bounded regardless of room
 * population and is the backbone of both scaling and anti-cheat (you cannot
 * "see" — or shoot — what was never sent to you).
 */
export const AOI_RADIUS = 600;
/** Spatial hash cell size for broad-phase queries (meters). Sized so the AOI
 * query (radius AOI_RADIUS) only sweeps a handful of cells. */
export const GRID_CELL = 256;

/** ---- Ship tuning (deterministic physics) ---- */
export const SHIP = {
  /** Hull length (meters) — bow-to-stern, used for OBB hit tests + control points. */
  LENGTH: 20,
  /** Hull beam (meters) — port-to-starboard. */
  BEAM: 7,
  /** Broad-phase collision radius (meters). */
  RADIUS: 11,
  /** Top forward speed at full sail with a perfect point of sail (m/s). */
  MAX_SPEED: 12,
  /** Forward acceleration responsiveness toward target speed (1/s). */
  ACCEL: 3.2,
  /** Passive drag when coasting / overspeed (1/s). */
  DRAG: 0.8,
  /** Max turn rate (rad/s) at cruising speed. */
  MAX_TURN_RATE: 0.55,
  /** Rudder responsiveness (how fast angular velocity approaches target, 1/s). */
  TURN_ACCEL: 2.2,
  /** Speed at which the rudder reaches full authority (m/s). */
  MIN_STEER_SPEED: 2.5,
  /** Seconds for sail trim to move fully furled<->full. */
  SAIL_TRIM_RATE: 0.5,
  /** Anchor drop deceleration responsiveness (1/s). */
  ANCHOR_DRAG: 3.5,
  /** Speed retained after hitting a world boundary (fraction). */
  WALL_BOUNCE: 0.25,
  /** Exponential decay rate (1/s) of a ram knockback impulse — ~0.6s shove (see RAM.KNOCK_*). */
  KNOCK_DECAY: 3.4,
} as const;

/** ---- Flooding / damage (the asset's model: water level == health) ---- */
export const FLOOD = {
  /** waterLevel units added per second by ONE open leak. */
  RATE_PER_LEAK: 0.045,
  /** waterLevel removed per second while bailing with a bucket. */
  BAIL_RATE: 0.1,
  /** waterLevel >= this => the ship sinks. */
  SINK_THRESHOLD: 1.0,
  /** Maximum simultaneous leaks a hull tracks. */
  MAX_LEAKS: 6,
  /** Seconds of "plank" action to patch the nearest leak. */
  PLANK_TIME: 1.2,
  /** Passive self-bail when no leaks (slow drain so near-misses recover). */
  PASSIVE_DRAIN: 0.01,
} as const;

/** ---- Contextual survival hints (CLIENT-ONLY UX; NO wire, NO sim, NO PROTOCOL_VERSION bump) ----
 * Two impossible-to-ignore-but-non-intrusive nudges born from watching real captains DIE without ever
 * touching the repair control, and freeze — thinking the game hung — when they try to sail while anchored.
 * The client reads its OWN already-known state (integrity, anchor toggle, sail/rudder intent, speed) and
 * pulses the REAL control; the server is never consulted. Gate logic lives in `apps/client/src/uxhints.ts`,
 * proven by `uxhints.test.ts`. Tunable here so the timing/decay can be dialled without touching code. */
export const UX_HINTS = {
  /** 🪣 "Taking water! HOLD 🪣 to bail" — fires when the hull is flooding and the captain is NOT bailing.
   *  RECALIBRATED FOR NOVICES (owner report 2026-07-15: "muito player novo morrendo por isso"). The old band
   *  (onset 0.15 + 4.5 s patience) meant the danger window only closed at onset+4.5 s ≈ 5.6 s — LONGER than a
   *  newbie's time-to-sink under turret/PvP fire (~4.4 s, proven in repro), so the alarm reliably outlived the
   *  ship. Now it fires EARLY (first hits, not only heavy damage) with a short patience that still lets a
   *  competent captain react-and-never-see-it, and NO teaching layer mutes it (life-or-death beats lessons). */
  REPAIR: {
    /** waterLevel (= 1 − integrity) at/above which the ship counts as "taking on water" (first real leaks). */
    ONSET_WATER: 0.08,
    /** ms of taking-on-water WITHOUT bailing before the emergency hint fires. Short enough to beat the newbie
     *  time-to-sink under fire, long enough that a captain who bails on her own within the beat never sees it
     *  (the patience IS the veteran filter). */
    DELAY_MS: 1600,
    /** once she's this flooded the hint fires even for a captain who has ALREADY bailed this session (a dire,
     *  half-sunk hull is life-or-death — re-alarm regardless of "she knows the bucket"). */
    REPEAT_WATER: 0.45,
    /** minimum gap between two repair hints (measured from when the last one hid) — anti-spam re-nudge. */
    COOLDOWN_MS: 7000,
    /** the bail alarm is NOT muted while a teaching layer is up (the Helm Coach / the Primer manual sheet):
     *  a flooding hull can't wait for a lesson to end. Anchor/cargo hints DO hold while one is up. */
    TEACHING_SUPPRESSED: false,
  },
  /** ⚓ "Weigh anchor to set sail!" — fires when a moored captain applies clear movement intent but can't budge. */
  ANCHOR: {
    /** ms of "trying to move while anchored" before the hint fires (short — the frustration is immediate). */
    DELAY_MS: 1400,
    /** minimum gap between two anchor hints — anti-spam. */
    COOLDOWN_MS: 10000,
    /** sailTarget at/above this reads as intent to get under way. */
    MIN_SAIL: 0.25,
    /** |rudder| at/above this reads as intent to steer. */
    MIN_RUDDER: 0.5,
    /** sim m/s at/below which she's genuinely "not moving" (an anchor hold), so the hint is warranted. */
    MAX_SPEED: 1.0,
  },
  /** 🏭 Home-warehouse FILL chip (Cargo Runs V1). The passive Farm grows trade cargo into the base warehouse;
   *  a captain who lost the old ⓓ mint went BLIND to that progress (the chip only lit at 100%). It now surfaces
   *  the partial fill from SHOW_FRAC up (a compact "🏭 NN%" + progress bar), flipping to the strong "HAUL READY"
   *  at FULL_FRAC. Below SHOW_FRAC it hides so the topbar never clutters. Pure client HUD read — zero wire. */
  WAREHOUSE: {
    /** warehouse-fill fraction (0..1) AT/ABOVE which the partial chip appears (below it: hidden, no clutter). */
    SHOW_FRAC: 0.25,
    /** fill fraction at/above which the chip flips to the strong "HAUL READY" (a full run is loadable). */
    FULL_FRAC: 0.999,
  },
  /** ⚖ FIRST-CARGO delivery hint (Cargo Runs V1). Real captains asked "do I deliver at the base?" — so the very
   *  first time a cargo run auto-loads at the home berth (⚖ chip appears), teach WHERE to cash it in. Fires on
   *  the rising edge of a laden hold, shows for DISPLAY_MS, and retires FOREVER once a delivery is observed
   *  (persisted per device). Muted while the FTUE guides. Pure client — zero wire. */
  FIRST_CARGO: {
    /** cargoUnits at/above which the hold counts as "a run just loaded" (ignore sub-unit dust). */
    MIN_UNITS: 1,
    /** how long the hint + ⚖ pulse stay up after a fresh load before quieting (kept short — "não poluir"). */
    DISPLAY_MS: 7000,
  },
} as const;

/** ---- Wind ---- */
export const WIND = {
  /** Base wind strength multiplier on sailing speed (0..1+). */
  STRENGTH: 1.0,
  /** Radians/sec the global wind direction drifts. */
  DRIFT_RATE: 0.015,
  /**
   * Minimum speed factor when sailing straight into the wind (in irons).
   * 1.0 disables wind influence; lower = more tactical sailing.
   */
  MIN_FACTOR: 0.4,
} as const;

/**
 * ---- Dynamic storm / weather ----
 * A single continuous intensity (0 calm … 1 full storm) derived deterministically from the WALL-CLOCK
 * EPOCH (`stormIntensityAt` in @piratas/sim). Storm is ATMOSPHERE/tension, never punishment — the
 * gameplay knobs below are deliberately small so the player always keeps control. Everything is
 * tunable here (the single source of truth); the wave-amplitude/visual knobs are client-render-only
 * (the authoritative 2D sim never reads ocean height), the wind/turn knobs are server-authoritative.
 *
 * ZERO-WIRE SYNCHRONISATION. `stormIntensityAt` is a PURE function of epoch seconds, so every client
 * and every server process derives the identical weather from a clock they already share — no extra
 * byte, no schedule broadcast, no join-in-progress handshake, and (unlike the old sim-clock anchor)
 * it survives a server restart instead of resetting the world's weather to calm. The server still
 * evaluates it and puts the result in the existing snapshot-header byte, which stays the authority
 * for the gameplay knobs; dropping that byte would be a WIRE change (PROTOCOL_VERSION bump) for no
 * gain, so it is deliberately kept.
 */
export const STORM = {
  /**
   * ---- schedule (v53: rare, long, IRREGULAR) ----
   *
   * The old schedule was one fixed 780 s sine-ish cycle: a storm every 13 minutes, ~2.5 min long, on a
   * metronome you could set your watch by. Two problems. It was too FREQUENT to ever feel like an event,
   * and it was too REGULAR — weather that arrives on a timer is scenery, weather that arrives when it
   * feels like it is a memory.
   *
   * Now: the epoch is cut into `SLOT_S` slots, and each slot k holds EXACTLY ONE storm whose start
   * offset, duration, ramp length and peak fury are all drawn from a deterministic hash of k. The jitter
   * is bounded so consecutive starts land 45–70 min apart (`SLOT_S ± 2·SLOT_JITTER_S`), each storm runs
   * 6–10 min end to end, and the peak varies 0.68…1.0 so roughly one storm in eight is only a squall
   * (below EYE.MIN_STORM — no eye opens) instead of a full hurricane.
   *
   * Duty cycle: mean 480 s of storm per mean 3450 s ⇒ ~13.9% weather, ~86% clear sunny sea — a touch
   * CALMER than the v37 schedule the owner audited into place, with the storm time concentrated into
   * events four times rarer and three times longer.
   */
  /** Mean seconds between storm onsets. Slot length: exactly one storm is scheduled per slot. */
  SLOT_S: 3450,
  /** Bound (s) on how far a storm's start slides inside its slot ⇒ onset gaps of 45–70 min. */
  SLOT_JITTER_S: 375,
  /** Total storm length (s) including both ramps — 6 to 10 minutes. */
  SPAN_MIN_S: 360,
  SPAN_MAX_S: 600,
  /** Ramp-in / ramp-out length (s). Also capped at 40% of the span so short storms stay smooth. */
  RAMP_MIN_S: 60,
  RAMP_MAX_S: 90,
  /** Peak intensity is drawn uniformly from [PEAK_MIN, MAX_INTENSITY] — not every gale is a hurricane. */
  PEAK_MIN: 0.68,
  /** Peak intensity ceiling (0..1). Keep ≤1; lower it to cap how wild storms ever get. */
  MAX_INTENSITY: 1.0,

  /* --- gameplay (server-authoritative; kept gentle so controls never feel broken) --- */
  /** Wind strength multiplier (×WIND.STRENGTH) at calm and at full storm. */
  WIND_CALM_MUL: 1.0,
  WIND_STORM_MUL: 1.22,
  /** Brief gust ripple at full storm: ±this fraction of wind strength, oscillating at GUST_RATE. */
  GUST_AMP: 0.14,
  GUST_RATE: 0.6,
  /** Wind-direction drift multiplier (×WIND.DRIFT_RATE) at full storm — the sea turns a bit faster. */
  DRIFT_MUL: 2.0,
  /** Minor steering resistance at full storm: turn rate ×(1 − storm·this). SMALL on purpose. */
  TURN_RESIST: 0.16,
  /** Hard ceiling for the encoded wind strength (wire packs 0..2.55) so it never clamps/desyncs. */
  WIND_STRENGTH_MAX: 2.5,

  /* --- visuals (client-render-only) --- */
  /**
   * Ocean SWELL SCALE at full storm (GPU vertices + CPU bob/wake, kept in lockstep).
   *
   * NOT a flat multiplier on the sea: each wave component takes the share of it declared by its `swell`
   * weight in @piratas/sim's WAVE_DEFS, so the 42/26 m vaga rears up nearly the full 1.95× while the
   * centimetre chop (already near saturation in any breeze) grows ~20%. That is what makes a peak storm
   * read as a GIANT ROLLING SWELL with texture on it rather than a calm sea viewed through a zoom lens.
   *
   * Budget check (the hard limit is Σ Q·k·A < 1, past which the Gerstner sum self-intersects):
   * calm 0.290 → storm 0.485, i.e. a 2.06× safety margin. `ocean.test.ts` asserts it AT THIS VALUE, so
   * raising this constant will fail the suite rather than silently shredding the sea.
   */
  WAVE_SCALE: 1.95,
  /**
   * Coast shoaling envelope. A 2 m open-ocean vaga has no idea an island is there and would render as a
   * wall of water standing ON the beach. The storm EXCESS (never the approved calm sea) is faded out over
   * a band around each nearby island: fully damped inside `COAST_INNER_MUL` × the island radius, fully
   * open sea past `COAST_OUTER_MUL` × radius + `COAST_OUTER_ADD` metres. Shared function, so the hull's
   * CPU bob and the rendered surface shoal identically (@piratas/sim `oceanCoastFactor`).
   */
  COAST_INNER_MUL: 1.15,
  COAST_OUTER_MUL: 2.4,
  COAST_OUTER_ADD: 90,
  /** In a storm the ship RIDES the swell: a gentle bias toward the highest hull corner (0..1) +
   *  a tiny extra freeboard (m), both scaled by intensity, so big crests don't wash over the deck —
   *  kept small so the hull still sits IN the water (no floating-above-the-sea gap). */
  SHIP_RIDE: 0.28,
  SHIP_LIFT: 0.12,
  /** Ocean shader storm tint: how grey/dark the water goes + extra whitecaps, at full storm. */
  OCEAN_DARKEN: 0.42,
  WHITECAP_BOOST: 0.55,
  /** Scene/ocean fog at full storm (calm ocean fog ≈ 0.0016, calm scene-fog-near ≈ 250). */
  FOG_DENSITY_STORM: 0.0052,
  FOG_NEAR_STORM: 110,
  /** Global light desaturation + cool blue-grey shift at full storm (0..1). */
  DESATURATE: 0.45,
  /** Rain: max particle count (LOD-scaled by intensity), box half-size around camera, fall speed. */
  RAIN_MAX: 1700,
  RAIN_AREA: 120,
  RAIN_FALL: 95,
  /** Storm intensity at which rain begins / reaches full. */
  RAIN_START: 0.22,
  RAIN_FULL: 0.7,
  /** Lightning: min seconds between strikes at full storm, flash duration, intensity to begin. */
  LIGHTNING_MIN_GAP: 7,
  LIGHTNING_FLASH_S: 0.22,
  LIGHTNING_START: 0.45,
  /** Camera sway amplitude (world units / radians) scaled by storm — subtle, never nauseating. */
  CAM_SWAY_POS: 0.6,
  CAM_SWAY_ROT: 0.012,
  /** How fast the client eases its local intensity toward the server value (per second). */
  EASE_RATE: 0.6,
} as const;

/**
 * ---- Ambient sea state (client-render-only, zero wire) ----
 *
 * The storm above is an EVENT — rare by design, ~14% of the clock. This is what the other 86% does.
 *
 * A single fixed "calm" is the thing that makes an ocean read as a texture instead of a place: sail for
 * an hour and you have seen the same water for fifty minutes. So the calm sea breathes, slowly, between
 * GLASSY and BREEZY, on the same kind of deterministic pure-function-of-the-clock that the storm uses
 * (`seaStateAt` / `chopGainAt` in @piratas/sim) — no wire, no schedule, no state to resync, and every
 * client on the same second sees the same sea.
 *
 * IT MOVES THE SKIN, NOT THE SHAPE. Only the short CHOP band's amplitude rides this (and, in the
 * renderer, the cat's-paw contrast and specular glitter that live on the chop's slope). The long swell —
 * the silhouette the owner approved and the thing the hull visibly rides — is untouched at every value.
 * That is the difference between enriching the approved sea and replacing it.
 */
export const SEA_STATE = {
  /** Three mutually incommensurate periods (s) — ~19, ~31 and ~47 min. Their sum has no learnable cycle. */
  PERIOD_A_S: 1153,
  PERIOD_B_S: 1861,
  PERIOD_C_S: 2837,
  /** Period (s) of the slow hashed walk layered on top, so even the sine sum never quite repeats. */
  DRIFT_S: 900,
  /** Chop-band gain at the glassiest / breeziest ambient state. 1.0 is the approved water-v2 calm sea,
   *  and it sits inside this range — so the signed-off screenshot is a moment this sea still passes
   *  through, rather than a look that has been replaced. */
  CHOP_MIN: 0.55,
  CHOP_MAX: 1.35,
  /** Extra chop gain a full gale forces on top of whatever the breeze was doing. */
  CHOP_STORM: 0.45,
  /** Hard ceiling on the summed gain. The Gerstner steepness budget (Σ Q·k·A < 1) is asserted in
   *  ocean.test.ts AT this ceiling AND at STORM.WAVE_SCALE simultaneously — raise it and the suite fails
   *  rather than the sea silently folding through itself. */
  CHOP_CEIL: 1.6,
} as const;

/**
 * ---- AURORA BOREALIS (schedule = zero wire; look = client-render-only) ----
 *
 * THE NIGHT SKY'S RARE EVENT. The storm above is the day's event; this is the night's. Same machinery,
 * same law: `auroraIntensityAt(epochSeconds)` in @piratas/sim is a PURE function of a clock every
 * participant already has, so every captain on the sea is under the same curtains on the same second
 * with not one byte crossing the wire, no schedule broadcast, no join-in-progress handshake, and
 * nothing to resync after a reconnect. It survives a deploy for the same reason the storm does: the
 * anchor is the wall-clock epoch, never sim time.
 *
 * THE SLOT IS A NIGHT, NOT AN HOUR. A storm can happen at any time, so its schedule cuts the epoch into
 * arbitrary slots. An aurora can only happen at NIGHT, and the day/night cycle already cuts the epoch
 * into nights: `dayNightPhase` puts midnight at phase 0, i.e. at every integer multiple of
 * `DAY_NIGHT_CYCLE_S`. So night `k` IS the epoch second `k * DAY_NIGHT_CYCLE_S`, and the aurora slot is
 * that night — one hashed draw per night decides whether it happens at all, and the display is centred
 * on that night's midnight. The client additionally gates on its own `nightFactor`, so the ramp can
 * never bleed into a lit sky even if the constants below are retuned past the night's width.
 *
 * RARE ON PURPOSE. `NIGHT_CHANCE` 0.28 ⇒ roughly one night in three and a half. A player who sails
 * every evening sees it now and then; a player who sees it every night has seen wallpaper. Deep night
 * (`nightFactor` > 0.3) is ~25% of the 30-minute cycle ≈ 7.6 min, and the display runs 6–8 min inside
 * it, so an aurora night is an aurora night from end to end and the fade is the dawn taking it.
 *
 * THE BREAKUP. A real auroral substorm is not a steady glow: a quiet arc hangs for a while, then breaks
 * up — the rays speed up, the curtain folds, the whole sky moves — for a minute or two, then settles.
 * `auroraBreakupAt` returns that beat as its own 0..1 pulse inside the display, and the renderer spends
 * it on ray speed and fold rate, never on brightness (a brightness spike is a bloom accident).
 *
 * IT DIES IN A GALE. A storm closes a cloud deck over the whole sky; an aurora painted on top of that
 * is a light leak. The pure function folds `stormIntensityAt` in itself (fully gone by `STORM_KILL`),
 * so the two events can never be on screen together on any device.
 */
export const AURORA = {
  /** Fraction of nights that get a display — a hashed draw per night. 0.28 ⇒ ~1 night in 3.6. */
  NIGHT_CHANCE: 0.28,
  /** Bound (s) on how far the display's centre slides off that night's midnight. */
  CENTER_JITTER_S: 80,
  /** Total display length (s), both ramps included — 6 to 8 minutes. */
  SPAN_MIN_S: 360,
  SPAN_MAX_S: 480,
  /** Ramp-in / ramp-out (s). It GROWS for minutes; an aurora that switches on is a shader, not weather.
   *  Also capped at 40% of the span so the shortest display still reaches its peak. */
  RAMP_MIN_S: 90,
  RAMP_MAX_S: 150,
  /** Peak intensity is drawn uniformly from [PEAK_MIN, MAX_INTENSITY] — not every night is a Kp 7. */
  PEAK_MIN: 0.72,
  MAX_INTENSITY: 1.0,
  /** The substorm BREAKUP: its length (s) and where in the display it lands (fraction of the span). */
  BREAKUP_MIN_S: 65,
  BREAKUP_MAX_S: 125,
  BREAKUP_AT_MIN: 0.40,
  BREAKUP_AT_MAX: 0.66,
  /** Storm intensity at which the display is fully gone (linear ramp from 0). */
  STORM_KILL: 0.45,

  /* --- client-render-only from here down (the look; never gameplay, never the wire) --- */

  /** `nightFactor` where the curtains begin / are fully present. Below NIGHT_IN the sky is too lit. */
  NIGHT_IN: 0.30,
  NIGHT_FULL: 0.74,
  /**
   * THE MAGNETIC ZENITH: elevation (deg) and bearing (deg clockwise from world north, +Z) of the point
   * every ray converges on. This is the whole anatomy in two numbers — rays are lines of constant
   * bearing AROUND this point, so they fan out from it exactly the way real field lines do, and because
   * it is a WORLD direction (not a camera one) the display is a navigation cue: the aurora is north.
   */
  ZENITH_ELEV_DEG: 76,
  ZENITH_BEARING_DEG: 9,
  /**
   * Where each curtain's bright lower border hangs, as an angle (deg) from the magnetic zenith, plus how
   * far that border wanders, how tall its diffuse tail is, how hard the border itself is, the scale of
   * the arc's undulation and its own drift rate. All NON-HARMONIC (no value is a multiple of another) so
   * the three never beat into a repeating pattern.
   *
   * THE NUMBERS ARE MEASURED, NOT CHOSEN FOR LOOKS. The sea camera sits above the ship and looks DOWN at
   * it: probed in the running game at a normal close zoom, the frame holds sky from 0° to 26° of
   * elevation and nothing above that. So the whole display — three borders and all three tails — is
   * authored to live inside 5°…31°. A postcard-correct arc at 45° is an arc nobody in this game would
   * ever see; the moon learned this first, and was moved to 5° for the same reason.
   *
   * With the magnetic zenith at ZENITH_ELEV_DEG, a border listed here as `d` hangs at elevation
   * (ZENITH_ELEV_DEG − d) on the display's own bearing — 71 ⇒ 5°, 63 ⇒ 13°, 54 ⇒ 22°.
   */
  CURTAIN: [
    /* border° , wander° , tail° , edge° , arc scale , drift rad/s */
    [71, 4.5, 10.0, 1.5, 1.7, 0.0130],
    [63, 6.5, 14.0, 2.6, 2.9, -0.0089],
    [54, 3.5, 9.0, 2.2, 4.4, 0.0203],
  ] as const,
  /** Solana read as PHYSICS. 557 nm oxygen green is where the beam stops (the sharp lower border);
   *  the nitrogen bands are the blue-teal middle; the forbidden 630 nm line only lights the thin air at
   *  the top. Bottom → top. They happen to be the brand's own three colours. */
  COL_LOW: 0x14f195,
  COL_MID: 0x22b8e0,
  COL_HIGH: 0x9945ff,
  /** Additive radiance ceiling of the curtain in the sky (pre-tonemap, sRGB-ish working space). The
   *  additive stack of three curtains is rolled through x·A/(A+x) so it ASYMPTOTES here instead of
   *  running off the end of the bloom pass's blur kernel — see the sun-disc note in render.ts. */
  SKY_PEAK: 0.92,
  /** Overall gain on the sky curtain and on its reflection in the water (the road). */
  SKY_GAIN: 0.70,
  SEA_GAIN: 0.60,
  /** How far the hemisphere ambient leans to the aurora's own colour at a full display — the mint/violet
   *  rim on decks and canvas that says the light is IN the world rather than painted on the backdrop. */
  RIM_GAIN: 0.24,
  RIM_COL: 0x35c8c0,
} as const;

/**
 * ---- CAT'S PAWS (client-render-only) ----
 *
 * The thing that makes a real sea look ALIVE in a light breeze: the wind does not arrive evenly. It
 * comes in wide, soft patches that darken the water where they land (chop scatters the sky instead of
 * mirroring it) and slide downwind, leaving glassy lanes between them. Sailors call them cat's paws.
 *
 * Implementation is deliberately NOT a drawn thing — no streak meshes, no line textures, nothing with
 * an edge that could repeat. It is one large, slow noise field whose DOMAIN DRIFTS WITH THE WIND, and
 * it modulates two quantities that already exist per pixel: how much of the short chop's slope reaches
 * the normal, and how bright the specular glitter is. Where the field is high you get a nervous, matte
 * patch; where it is low, a smooth lane with a hard sun path on it. Cost is one noise lookup.
 */
export const CATSPAW = {
  /** Patch scale: 1/metres. ~0.006 ⇒ patches ~170 m across, which is the size they read at from a deck. */
  SCALE: 0.0062,
  /** How fast the whole field slides downwind (m/s). Slower than the wind itself, as gust fronts are. */
  DRIFT: 5.5,
  /** Chop-slope multiplier at the calmest / most nervous point of the field. */
  CHOP_MIN: 0.30,
  CHOP_MAX: 1.55,
  /** Specular glitter multiplier over the same range — a glassy lane mirrors, a paw scatters. */
  SPEC_MIN: 0.55,
  SPEC_MAX: 1.30,
} as const;

/**
 * ---- Cinematic ship sinking (client-render-only) ----
 * A readable 3-phase wreck played when a ship sinks (EV_SINK): DAMAGE (fire/smoke/list) →
 * BREAK (hull splits into a bow + stern chunk, splash/debris/shockwave) → SINK (chunks roll and
 * plunge under, bubbles/foam). Pre-broken via per-chunk clipping planes on the existing model (no
 * physics, no new meshes). Purely cosmetic — the server already owns the sink/respawn. All tunable.
 */
export const SINK_FX = {
  DAMAGE_S: 1.3,      // pre-break: the stricken ship lists + burns + smokes
  SINK_S: 3.3,        // the two chunks plunge under after the break
  LIST_ANGLE: 0.28,   // radians the hull rolls onto its side during DAMAGE
  BOW_DIP: 0.14,      // radians the bow dips during DAMAGE
  BREAK_ROLL: 0.5,    // radians each chunk rolls as it sinks
  BREAK_PITCH: 0.95,  // radians each chunk pitches (broken end drops, outer end lifts — the jackknife)
  SEPARATE: 7,        // metres the two chunks drift apart along the hull (clear silhouette split)
  DEPTH: 19,          // metres a chunk descends below the surface before it's gone
  SEAM_OVERLAP: 0.4,  // clip overlap at the break so there's no visible gap at the seam
  FIRE_N: 5,          // deck fire sprites
  SMOKE_HZ: 14,       // dark smoke puffs per second during DAMAGE
  BUBBLE_HZ: 16,      // rising bubbles per second during SINK
  DEBRIS_N: 14,       // planks thrown at the break
  SPLASH_N: 18,       // white splash puffs at the break
  SHAKE: 1.3,         // camera shake added at the break
  SLOWMO_S: 0.45,     // brief hit-stop at the break (uses the existing hitstop slow-mo)
} as const;

/** ---- Cannons ---- */
export const CANNON = {
  /** Cannonball muzzle speed (m/s). */
  SPEED: 60,
  /** Seconds a cannonball lives before despawning (range ~= SPEED*LIFETIME). */
  LIFETIME: 4.0,
  /** Max horizontal travel before despawn (meters). */
  RANGE: 240,
  /** Seconds between broadside volleys, per side. */
  RELOAD: 4.0,
  /** Cannonball collision radius (meters) — gameplay hit-forgiveness, NOT the visual size. */
  RADIUS: 2.5,
  /** VISUAL cannonball radius (meters) — kept small + proportional to the ship (the hitbox above is
   *  intentionally more generous). Render-only. */
  BALL_VISUAL_RADIUS: 0.5,
  /** Render-only: stretch the ball along its travel direction for a fast-projectile motion streak. */
  BALL_STRETCH: 2.1,
  /** Number of cannons per broadside (visual + spread). */
  PER_SIDE: 3,
  /** Lateral spread of a broadside (meters across the muzzles). */
  SPREAD: 8,
  /** floodRate of the leak a direct hit punches in the enemy hull. */
  LEAK_RATE: 0.045,
  /** Raking fire: a ball travelling within this |dot| of the target's keel axis (≈ entered bow/stern,
   *  sweeping the deck lengthwise) is a RAKE — 1 = dead-on the keel, 0 = perpendicular beam shot. */
  RAKE_ALIGN: 0.86,
  /** Damage multiplier on the leak a raking hit punches (rewards crossing the enemy's T). */
  RAKE_BONUS: 1.9,
  /** Vertical launch component for the visible ballistic arc (m/s). */
  ARC_VY: 9,
  /** ---- Cannon elevation (0..1) drives the REAL firing range (W/S while aiming) ---- */
  /** Elevation 0 = flat short-range direct fire; 1 = high long-range lob. Maps to ground range (m). */
  ELEV_RANGE_MIN: 70,
  ELEV_RANGE_MAX: 240,
  /** Arc peak height (m) at elevation 0..1 — drives BOTH the height-gated hit and the visual arc. */
  ELEV_PEAK_MIN: 2.2,
  ELEV_PEAK_MAX: 15,
  /** A ball strikes a hull only while below this height (m); above it the lob flies OVER near ships. */
  DECK_GATE: 4.5,
  /** Cosmetic: max barrel angle (rad) the indicator shows at elevation 1 (~33°). */
  ELEV_MAX_ANGLE: 0.58,
  /** Starting elevation + how fast W/S (or the slider) moves it, per second. */
  ELEV_DEFAULT: 0.4,
  ELEV_STEP: 0.7,
} as const;

/** ---- Aiming assist (CLIENT-ONLY visual; never auto-leads, never reveals the exact impact) ----
 * Holding a broadside shows a soft bearing guide: a translucent firing arc along the true
 * broadside heading out to effective range, fading at the tip. The player still manually leads
 * the target, times the travel, and compensates for their own ship's velocity. */
export const AIM = {
  /** Base half-angle (rad) of the bearing cone — a stylized aiming margin, NOT the real spread. */
  CONE_HALF_ANGLE: 0.11,           // ~6.3°
  /** Extra half-angle scaled by (ammo.spreadMul - 1), so wide-spread shot (grape) reads wider. */
  CONE_SPREAD_GAIN: 0.09,
  /** Fraction of effective range the guide reaches (the far end fades — a hint, not a hard marker). */
  RANGE_FRAC: 1.0,
  /** Opacity of the crisp bearing line/edges; the cone fill is a fraction of this (kept subtle). */
  ALPHA: 0.5,
  /** Camera zoom-in (0..1) + look-at shift toward the firing side while aiming. */
  CAMERA_ZOOM: 0.2,
  /** Seconds to ramp the guide + camera in/out (no pop). */
  RAMP: 0.16,
} as const;

/** ---- Ship classes (combat depth) ----
 * A class is an ARCHETYPE expressed purely as multipliers + hull dimensions —
 * it never adds new progression stats (the 4 universal upgrade levels stay).
 * The class multipliers COMPOSE with the upgrade multipliers in `shipStats`.
 * Hull length/beam drive the OBB hit box (sim) and the render scale (client),
 * so a galleon is a genuinely bigger target than a sloop. */
export const ShipClassId = { SLOOP: 0, BRIGANTINE: 1, GALLEON: 2, FRIGATE: 3, CUTTER: 4, FLUYT: 5, EAST_INDIAMAN: 6 } as const;
export type ShipClassId = (typeof ShipClassId)[keyof typeof ShipClassId];

export interface ShipClassDef {
  /** Multiplier on SHIP.MAX_SPEED (composes with the upgrade speedMul). */
  speedMul: number;
  /** Multiplier on SHIP.MAX_TURN_RATE. */
  turnMul: number;
  /** Hull length/beam (meters) — drives OBB hit box + client render scale. */
  length: number;
  beam: number;
  /** Broad-phase collision radius (meters). */
  radius: number;
  /** Cannons per broadside. */
  cannonsPerSide: number;
  /** Cargo capacity multiplier on RAID.CARGO_*. */
  cargoMul: number;
  /** Extra flood resistance: incoming leak rate is further multiplied by (1 - this). */
  floodResistBonus: number;
  /** Reload-time multiplier (>1 = slower broadsides). */
  reloadMul: number;
  /** Ramming "mass": heavier hulls deal more and take less on hull-vs-hull contact. */
  ramMass: number;
}

export const SHIP_CLASS: Record<ShipClassId, ShipClassDef> = {
  [ShipClassId.SLOOP]:      { speedMul: 1.18, turnMul: 1.28, length: 14, beam: 5, radius: 8,  cannonsPerSide: 2, cargoMul: 0.6, floodResistBonus: 0,    reloadMul: 0.85, ramMass: 0.7 },
  [ShipClassId.BRIGANTINE]: { speedMul: 1.0,  turnMul: 1.0,  length: 20, beam: 7, radius: 11, cannonsPerSide: 3, cargoMul: 1.0, floodResistBonus: 0.05, reloadMul: 1.0,  ramMass: 1.0 },
  [ShipClassId.GALLEON]:    { speedMul: 0.85, turnMul: 0.78, length: 26, beam: 9, radius: 14, cannonsPerSide: 4, cargoMul: 1.5, floodResistBonus: 0.15, reloadMul: 1.2,  ramMass: 1.6 },
  // Frigate — the dedicated warship: galleon firepower (4/side) + the fastest reload + faster/agiler than the
  // galleon, but a thin-ish hull and a poor cargo hold (great at fighting, weak at hauling loot from a raid).
  [ShipClassId.FRIGATE]:    { speedMul: 1.08, turnMul: 1.0,  length: 22, beam: 7, radius: 11, cannonsPerSide: 4, cargoMul: 0.55, floodResistBonus: 0.05, reloadMul: 0.9, ramMass: 1.1 },
  // ---- Onda Frota (v47): three new hulls ----
  // Cutter — the glass skirmisher: fastest & nimblest afloat, a single gun and a tiny hold. Run, don't fight.
  [ShipClassId.CUTTER]:        { speedMul: 1.25, turnMul: 1.40, length: 10, beam: 4,  radius: 6,  cannonsPerSide: 1,  cargoMul: 0.4, floodResistBonus: 0,    reloadMul: 0.8,  ramMass: 0.5 },
  // Fluyt — the trader's mule: a cavernous hold + solid hull, feeble guns and slow to come about.
  [ShipClassId.FLUYT]:         { speedMul: 0.90, turnMul: 0.85, length: 24, beam: 10, radius: 13, cannonsPerSide: 2,  cargoMul: 2.2, floodResistBonus: 0.12, reloadMul: 1.3,  ramMass: 1.4 },
  // East Indiaman — the OWNER-EXCLUSIVE flagship (entitlement-gated, see room.ts CLASS handler): the biggest
  // hold, the thickest hull and a wall of ten guns per side. Ponderous, but a floating fortress.
  [ShipClassId.EAST_INDIAMAN]: { speedMul: 0.82, turnMul: 0.72, length: 30, beam: 11, radius: 16, cannonsPerSide: 10, cargoMul: 2.5, floodResistBonus: 0.20, reloadMul: 1.25, ramMass: 1.8 },
};
export const SHIP_CLASS_COUNT = 7;
/** New captains start in the humble SLOOP and grind up; legacy profiles (no class) migrate to galleon. */
export const DEFAULT_SHIP_CLASS: ShipClassId = ShipClassId.SLOOP;
export const LEGACY_SHIP_CLASS: ShipClassId = ShipClassId.GALLEON;

/** Treasure-chest HOLD capacity by hull: how many treasure chests a ship may carry at once. Derived from the
 *  cargo multiplier in ONE place (no new tuning field, so it can never drift from the fleet table) — a hull
 *  built to haul (cargoMul ≥ 2) carries a SECOND chest; every fighter/skirmisher carries one. The resulting
 *  table:  Cutter 0.4→1 · Frigate 0.55→1 · Sloop 0.6→1 · Brigantine 1.0→1 · Galleon 1.5→1 · Fluyt 2.2→2 ·
 *  East Indiaman 2.5→2. The Fluyt (the trader's mule) is the design driver: two chests reinforce its cargo
 *  identity and pair with Cargo Runs. Both server (pickup cap) and client (HUD "slot full" cue) read this. */
export function chestCapFor(cls: ShipClassId): number {
  return Math.max(1, Math.floor((SHIP_CLASS[cls] ?? SHIP_CLASS[LEGACY_SHIP_CLASS]).cargoMul));
}

/** Doubloons to UNLOCK a bigger hull at the shipyard (the SLOOP is free/owned from the start). Once unlocked
 *  it's yours — swapping between owned hulls is free. An escalating ladder so the fleet is a real grind. */
export const SHIP_UNLOCK_COST: Record<ShipClassId, number> = {
  [ShipClassId.SLOOP]: 0,
  // The first hull evolution is a day-1..3 retention beat: 3000 was too far to reach before a new captain
  // churned, so the first upgrade sat unbought. Halved to 1500 (≈ a handful of chests / a marauder streak).
  [ShipClassId.BRIGANTINE]: 1500,
  [ShipClassId.GALLEON]: 10000,
  [ShipClassId.FRIGATE]: 22000,
  // Onda Frota (v47): the Cutter is the cheap day-1 speed toy; the Fluyt is a mid-game trade hauler.
  [ShipClassId.CUTTER]: 1000,
  [ShipClassId.FLUYT]: 6000,
  // East Indiaman is NOT for sale — an owner-exclusive flagship gated by the 'east_indiaman' entitlement.
  // The Infinity sentinel is defence-in-depth: even if the equip handler's entitlement guard were bypassed,
  // no vault can ever afford it (the grant path adds it to unlockedClasses so the owner equips it for free).
  [ShipClassId.EAST_INDIAMAN]: Infinity,
};

/** ---- Per-level upgrade steps (the 4 universal ship upgrades) ----
 * These COMPOSE with the class multipliers above. They are the single source of the numbers the
 * client's legibility helpers (`shipstats.ts`) surface as before→after readouts, and they mirror the
 * literals in the server's authoritative `shipStats()` (apps/server/src/persistence/types.ts) —
 * keep the two in sync (both currently hand-hold these constants; the server should import these). */
export const SHIP_UPGRADE = {
  /** Top-speed gain per Speed level (+6% / level, composes with class speedMul). */
  SPEED_PER_LVL: 0.06,
  /** Turn-rate gain per Speed level (+3% / level). */
  TURN_PER_LVL: 0.03,
  /** Cannon-damage gain per Cannons level (+12% / level). */
  DAMAGE_PER_LVL: 0.12,
  /** Flooding-rate reduction per Hull level (−5% / level). */
  HULL_FLOOD_PER_LVL: 0.05,
  /** Cap on the Hull flood reduction (−50% at Hull Lv11+; the shipyard caps buys at Lv10 = −45%). */
  HULL_FLOOD_MAX: 0.5,
  /** Highest buyable level for every ship upgrade (matches the shipyard/server cap). */
  MAX_LVL: 10,
} as const;

/** ---- Ship ROLE identity (client legibility) ----
 * A one-glance archetype badge + one-line pitch per hull, so a captain SEES that a class is a TRADEOFF,
 * not a linear "pricier = stronger" ladder. Pure presentation — no wire, no sim. The bars beside these
 * (Speed/Firepower/Durability/Cargo/Turn) are derived from the real SHIP_CLASS numbers in `shipstats.ts`. */
export const SHIP_ROLE: Record<ShipClassId, { badge: string; role: string; tagline: string }> = {
  [ShipClassId.SLOOP]:      { badge: '🏹', role: 'Skirmisher', tagline: 'Hit-and-run scout — fastest & nimblest afloat, but just 2 guns and a paper hull.' },
  [ShipClassId.BRIGANTINE]: { badge: '⚖️', role: 'Balanced',   tagline: 'The honest all-rounder — 3 guns, a solid hull and a fair hold. Learn the sea here.' },
  [ShipClassId.GALLEON]:    { badge: '🛡️', role: 'Tank',       tagline: 'Floating fortress — 4 guns, the biggest hold, shrugs off floods. Slow to come about.' },
  [ShipClassId.FRIGATE]:    { badge: '⚔️', role: 'Attacker',   tagline: 'Glass cannon — hits hard & fast, sinks fast. The master aggressor’s ship.' },
  [ShipClassId.CUTTER]:        { badge: '🏹', role: 'Skirmisher', tagline: 'Fastest hull afloat — glass, one gun, run.' },
  [ShipClassId.FLUYT]:         { badge: '📦', role: 'Hauler',     tagline: "Cavernous hold, feeble guns — the trader's mule." },
  [ShipClassId.EAST_INDIAMAN]: { badge: '👑', role: 'Flagship',   tagline: "Owner's pride — biggest hold, thickest hull, a wall of cannon." },
};

/** ---- Hard grants / entitlements carried as canonical STRINGS on `Profile.entitlements.grants` ----
 * Distinct from the object monetization entitlements (VIP/vouchers): these are durable, owner-authored
 * access + cosmetic flags. Server-authoritative in every read (equip gate, badge byte) — the client is
 * never trusted. Write these exact strings into a profile to grant them. */
export const GRANT = {
  /** Unlocks + permits equipping the owner-exclusive East Indiaman flagship. */
  EAST_INDIAMAN: 'east_indiaman',
  /** Renders a "DEV" tag beside the captain's name. */
  DEV_BADGE: 'dev_badge',
  /** Renders the captain's name in gold. */
  GOLD_NAME: 'gold_name',
} as const;

/** Name-badge bit flags packed into the u8 that rides EV_SPAWN / PortEventType.SPAWN, so OTHER players see
 *  a captain's DEV tag + gold name. Derived server-side from the profile grants; the client only reads bits. */
export const NAME_BADGE = { DEV: 1 << 0, GOLD: 1 << 1 } as const;

/** ---- Ammo types (combat depth; unlimited tactical selection, no inventory) ----
 * Round = balanced hull damage. Chain = anti-mobility (tangles rigging, slows
 * the target). Grape = short-range "shotgun" (double pellets, wide spread). */
export const AmmoType = { ROUND: 0, CHAIN: 1, GRAPE: 2 } as const;
export type AmmoType = (typeof AmmoType)[keyof typeof AmmoType];

export interface AmmoDef {
  /** Per-pellet leak rate multiplier on CANNON.LEAK_RATE. */
  leakMul: number;
  /** Muzzle speed multiplier on CANNON.SPEED. */
  ballSpeedMul: number;
  /** Range/lifetime multiplier on CANNON.RANGE / CANNON.LIFETIME. */
  rangeMul: number;
  /** Reload-time multiplier on CANNON.RELOAD. */
  reloadMul: number;
  /** Lateral spread multiplier on CANNON.SPREAD. */
  spreadMul: number;
  /** Pellets per cannon (1 = one ball/cannon; 2 = grape double-shot). */
  pelletMul: number;
  /** Rigging damage added to the target on hit (0..1) — chain only. */
  riggingDamage: number;
  /** Doubloons to buy one batch (AMMO_BUY_BATCH broadsides) at your base or any port. Round is the cheap
   *  workhorse; chain/grape are the premium tactical shot. */
  buyCost: number;
  /** Raster icon asset path (leading '/'; rendered via the client's shared itemIcon() helper). */
  icon: string;
}

export const AMMO: Record<AmmoType, AmmoDef> = {
  [AmmoType.ROUND]: { leakMul: 1.0,  ballSpeedMul: 1.0,  rangeMul: 1.0, reloadMul: 1.0,  spreadMul: 1.0, pelletMul: 1, riggingDamage: 0,    buyCost: 60,  icon: '/icons/ammo/round_shot.webp' },
  [AmmoType.CHAIN]: { leakMul: 0.5,  ballSpeedMul: 0.9,  rangeMul: 1.1, reloadMul: 1.1,  spreadMul: 1.0, pelletMul: 1, riggingDamage: 0.35, buyCost: 200, icon: '/icons/ammo/chain_shot.webp' },
  [AmmoType.GRAPE]: { leakMul: 0.55, ballSpeedMul: 1.05, rangeMul: 0.5, reloadMul: 0.9,  spreadMul: 2.2, pelletMul: 2, riggingDamage: 0,    buyCost: 160, icon: '/icons/ammo/grape_shot.webp' },
};
export const AMMO_COUNT = 3;
/** Special ammo (chain/grape) is a consumable economy SINK: bought in batches at your base or any port,
 * carried as a per-type stock, consumed one per broadside fired; firing with an empty special stock falls
 * back to ROUND shot (which is itself finite now — see below). */
export const AMMO_STOCK_MAX = 20;
export const AMMO_BUY_BATCH = 10;
/** ---- Ammo economy: ROUND shot is FINITE too (owner's law: "munição infinita é na cadeia, aqui não") ----
 * Every profile is born with a powder magazine of AMMO_ROUND_START broadsides (legacy accounts are
 * grandfathered the same amount on their first load — nobody ever wakes up dry), carried up to
 * AMMO_ROUND_MAX. Restock: buy AMMO_BUY_BATCH for AMMO[ROUND].buyCost ⓓ at any port / the home berth, or
 * forge 1 iron → AMMO_BUY_BATCH at a foundry (CraftId.ROUND_AMMO). 0 round + 0 selected special = the
 * broadside is DENIED server-side (the RAM still works — the desperation weapon). This is the game's first
 * CONTINUOUS, universal ⓓ/iron sink: ~6ⓓ per broadside vs GOLD_PER_KILL=50 (~8 broadsides to sink a base
 * hull), so a clean kill roughly refunds its own powder and the profit rides the plunder.
 * NPCs (Marauder/ghost) and base turrets fire OUTSIDE this economy (free round) — they are player drains,
 * not economy participants. Death does NOT tax the magazine (V1 — death-spiral veto by design). */
export const AMMO_ROUND_START = 40;
export const AMMO_ROUND_MAX = 60;
// export const AMMO_ROUND_DEATH_LOSS = 0; // RESERVED (V1 vetoed): broadsides lost on sink, if the owner ever wants a death tax.

/** ---- Rigging damage (chain shot) — a temporary speed/turn debuff on the target ---- */
export const RIGGING = {
  /** Cap on rigging damage (0..1). At MAX the ship limps at (1-MAX) of its mobility. */
  MAX: 0.7,
  /** Rigging repaired per second passively. */
  DECAY_PER_S: 0.06,
  /** Extra rigging repaired per second while the repair/bail action is held. */
  REPAIR_PER_S: 0.15,
  /** Remote ships above this rigging level show the tattered-sail cue (ShipFlag.RIGGING). */
  CUE_THRESHOLD: 0.12,
} as const;

/** ---- Ramming (hull-vs-hull collision) — damage is flooding, never knockback ---- */
export const RAM = {
  /** Minimum closing speed (m/s) along the contact for a ram to register. */
  MIN_CLOSING_SPEED: 2.5,
  /** waterLevel burst per (m/s) of closing speed (before mass + bow scaling). */
  DMG_PER_SPEED: 0.012,
  /** Hard cap on a single ram's waterLevel burst (anti one-shot). */
  MAX_DMG: 0.3,
  /** Bow-strike multiplier: rammer hitting the target's broadside bow-first. */
  BOW_BONUS: 1.6,
  /** The aggressor (bow-striker) only takes this fraction of the base burst back. */
  AGGRESSOR_SELF_FRACTION: 0.4,
  /** Lingering leak (flood/sec) a ram punches in each hull, on top of the burst. */
  LEAK_RATE: 0.045,
  /** Per-pair cooldown (s) before the same two hulls can ram-damage again. */
  COOLDOWN_S: 1.2,
  /** Knockback: m/s of shove (along the contact normal) per m/s of closing speed, before the mass split. */
  KNOCK_PER_SPEED: 0.7,
  /** Hard cap on a single hull's knock impulse (m/s) — the lighter hull can be launched, never teleported. */
  KNOCK_MAX: 8,
} as const;

/** ---- Grapple-plunder (boarding reimagined) — steal carried cargo on low-speed contact ----
 * Two enemy hulls in sustained LOW-speed contact: the attacker siphons the victim's CARRIED
 * cargo (unbanked loot) zero-sum. No damage (that's ramming's job). A modest mutual drag makes
 * the grab "stick" so a plunder lands, but it's escapable by sailing hard until the hulls part. */
export const GRAPPLE = {
  /** Broad-phase query margin (m) beyond own radius to find grapple-distance hulls. */
  RANGE: 24,
  /** Max relative speed (m/s) for contact to engage — tolerates parked/parallel, excludes ramming (>=2.5). */
  MAX_SPEED: 1.6,
  /** Seconds of sustained contact before the grapple grabs + starts siphoning. */
  GRAB_TIME: 0.6,
  /** Doubloons siphoned from the victim's carried cargo per second while held. */
  SIPHON_RATE: 60,
  /** Speed/turn drag (0..1) applied to BOTH hulls while grappled (the "stickiness"). */
  DRAG: 0.45,
  /** Hulls separated beyond this centre distance (m) break the grapple (hysteresis vs engage). */
  BREAK_DIST: 30,
  /** Struggle (0..1) added per victim mash (one rising edge of the STRUGGLE button). */
  STRUGGLE_PER_MASH: 0.1,
  /** Struggle bleed-off per second when not mashing — escape needs sustained effort. */
  STRUGGLE_DECAY: 0.4,
} as const;

/** Why a grapple ended (EV_GRAPPLE_END.reason) — drives the victim/attacker HUD toast. */
export const GrappleEnd = { SEPARATED: 0, ESCAPED: 1, VICTIM_EMPTY: 2, SUNK: 3 } as const;
export type GrappleEnd = (typeof GrappleEnd)[keyof typeof GrappleEnd];

/** Seconds a sunk ship stays as flotsam before the player respawns. */
export const RESPAWN_DELAY = 5;
/** Seconds of spawn protection after (re)spawning. */
export const SPAWN_PROTECT = 10;

/**
 * ---- Combat-logging guard (anti rage-quit) ----
 * Closing the tab mid-fight used to instantly remove your ship — dodging the sinking (and the kill/gold
 * it owed your attacker). Now, if a captain drops the socket within `WINDOW_MS` of taking OR dealing a
 * cannon/ram hit, their ship stays at sea as a PILOTLESS HUSK for `LINGER_S`: sails still set (she keeps
 * her way), the helm dead, flooding running its course, still hittable — so she can be finished off with
 * the kill + gold credited to the attacker exactly as if the captain had stayed. Reconnecting with the
 * same identity inside the window resumes the husk (position/battle-state intact) instead of respawning.
 * Ride out the linger without sinking and she's removed as a normal quit. No recent combat → an ordinary
 * disconnect removes on the spot (unchanged).
 */
export const COMBAT_LOG = {
  /** Recent-combat window (ms): a disconnect within this of the last hit dealt/taken leaves a husk. */
  WINDOW_MS: 15_000,
  /** Seconds the pilotless husk drifts at sea before it's despawned (if it hasn't already sunk). */
  LINGER_S: 10,
} as const;

/**
 * ---- Rejoin keeps your position (anti "lost voyage") ----
 * A captain who fishes/dives far out and drops the socket used to respawn at their home dock — the whole
 * trip thrown away. Now a genuine SEA disconnect stamps the ship's last spot on the profile (server-side,
 * never on the wire); the next plain sea rejoin within `WINDOW_MS` resumes at that spot instead of the dock.
 * It is CONSUMED once (like pendingHold), so a second relog can't re-teleport there. Guard-rails (all
 * server-authoritative) keep it from becoming a tactical escape or a death-dodge:
 *  - `COMBAT_LOCK_MS`: skip if a cannon/ram hit landed OR was dealt within this of the drop (a superset of
 *    PvP — turret/NPC combat counts too, the conservative choice) so disconnecting mid-fight can never
 *    teleport you back to the exact firefight. (The combat-log husk already owns the in-fight window.)
 *  - `MAX_WATER`: skip if the hull was flooding past this (about to sink → a fresh dry spawn at the dock is
 *    the merciful, non-exploitable outcome).
 *  - a saved spot now inside an island / off the edge (post world-change) falls back to the dock (validated).
 * Death/respawn is untouched; the transient hold still rides `pendingHold` (independent of this).
 */
export const REJOIN = {
  /** Preserve the disconnect spot only within this window (ms) — a stale voyage expires to the home dock. */
  WINDOW_MS: 10 * 60_000, // 10 min
  /** Don't preserve if a hit was dealt/taken within this of the drop (anti combat-log teleport). */
  COMBAT_LOCK_MS: 30_000,
  /** Don't preserve a hull flooding at/over this water level (0 dry … 1 sunk) — spawn fresh at the dock. */
  MAX_WATER: 0.6,
} as const;

/**
 * ---- Auto-anchor on AFK (anti "my ship sailed away") ----
 * A backgrounded/throttled tab stops sending C_INPUT, but the server keeps the LAST input — sails set,
 * the ship sailing itself over the horizon; the captain returns far from their spot. Fix (server-side,
 * input-layer only — never inside the sim): after `IDLE_S` seconds without a fresh C_INPUT, and only OUT
 * of combat (the combat-log window — drifting while in a fight is the intentional anti rage-quit), the
 * server furls the sails and drops anchor on the player's behalf. Any fresh C_INPUT overwrites the input
 * again (weighs anchor normally) and shows a one-shot "⚓ Anchor dropped — captain away" toast. A REJOIN
 * resume (the ≤10 min keep-your-spot reconnect) is also born anchored — a background auto-reconnect can
 * never re-launch the voyage by itself. Prediction==server holds: the client sends no inputs while away,
 * so there is nothing to replay; the snapshot self-state simply carries the anchored truth.
 */
export const AFK_ANCHOR = {
  /** Seconds without a fresh C_INPUT before the server furls sails + drops anchor (out of combat only). */
  IDLE_S: 12,
} as const;

/** Full day→night→day cycle length (seconds). Cosmetic + CLIENT-ONLY: the renderer derives the
 *  time-of-day phase from WALL-CLOCK epoch (render-only), so every player sees the same sky with
 *  zero extra wire data and it survives server restarts. 30 min per full day: ~24 min of light,
 *  ~5–6 min of night (night is ~19% of the cycle — see the client's SKY_KEYS spacing). */
export const DAY_NIGHT_CYCLE_S = 1800;

/** Day/night phase 0..1 from the WALL-CLOCK epoch (matches the client sky; 0=midnight, 0.5=noon, 0.75=dusk).
 *  Shared so the server's night-only content (the Ghost Fleet, 3.13) lines up with the client's visible night.
 *  NOT part of the deterministic ship sim — used only for cosmetic/NPC scheduling (never for prediction). */
export function dayNightPhase(epochMs: number): number { return ((epochMs / 1000 / DAY_NIGHT_CYCLE_S) % 1 + 1) % 1; }
/** Is it night (dark enough for the Ghost Fleet)? Matches the client's twilight→dawn dark window (~15%). */
export function isNight(epochMs: number = Date.now()): boolean { const p = dayNightPhase(epochMs); return p >= 0.85 || p <= 0.10; }

/**
 * ---- The Kraken (deep-water spectacle, v1 — ZERO WIRE) ----
 *
 * A GIGANTIC rigged leviathan that EMERGES in deep water, looms in place for a few minutes, then SINKS.
 * It is pure PRESENCE: no collision, no combat, no interaction, no server entity, no snapshot bytes. The
 * whole show is a DETERMINISTIC function of the shared WALL-CLOCK epoch (the exact same base the day/night
 * sky uses — {@link dayNightPhase}), so every client independently computes the identical state and all
 * players see the same monster rise from the same spot at the same instant, with nothing on the wire and
 * zero cost while it is submerged (the client culls it entirely between windows).
 *
 * Schedule: the wall-clock is chopped into {@link KRAKEN.PERIOD_S}-second windows. In the first
 * `EMERGE+IDLE+SINK` seconds of each window the Kraken is present at ONE of {@link KRAKEN.SPOTS} — a
 * curated ring of deep-water zones far from the world centre (the capital / risk waters), far from the
 * edge bruma, and away from spawns/FTUE lanes. Which spot (and its facing) is a stable hash of the window
 * index, so a given window always resolves to the same place for everyone; the rest of the window it is
 * gone. Everything here is DATA — retune freely without touching the wire.
 *
 * v2 SEAM (NOT this wave): promote the Kraken to a real server-authoritative boss (its own entity in
 * room.ts, marker on a throttled event like EV_WHALE, damage/aggro, felled → loot + on-chain provenance
 * as the owner's mint trigger). When that lands, this client-only schedule becomes the *fallback* for
 * regions with no live boss, or is retired. Keep the pure `krakenStateAt` as the presentation contract.
 */
export const KRAKEN = {
  /** Master switch for the sea-monster spectacle. OFF until the final model lands (owner rejected the
   *  first kraken GLB — water membrane baked between the tentacles; a sea-serpent replacement is in
   *  the pipeline and will re-use this whole schedule/render system). */
  ENABLED: false,
  /** Target tentacle-span footprint (m) the client normalizes the GLB to. ~50 m dwarfs a ~20 m galleon
   *  ~2.5× and lifts the mantle apex ~26 m over the waterline — unmistakably the biggest thing at sea. */
  SPAN_M: 50,
  /** Wall-clock window length (s): one appearance per this many seconds (3600 = hourly). DATA — tune freely. */
  PERIOD_S: 3600,
  /** Seconds to rise from fully submerged to fully emerged (body Y is code-driven; tentacles unfurl). */
  EMERGE_S: 6,
  /** Seconds it looms in place, tentacles undulating (the menacing "still at sea" beat). */
  IDLE_S: 180,
  /** Seconds to submerge — tentacles gather UP + inward so the arm tips are the last thing under. */
  SINK_S: 6,
  /** Client render/animate ONLY within this range (m) of the camera; beyond it (and while submerged) the
   *  mesh is never built → the spectacle costs literally nothing except in the deep-water zone it inhabits. */
  CULL_D: 1500,
  /** Metres the body sinks below the local ocean surface at rise=0 (fully submerged, out of sight). */
  DEEP_M: 60,
  /** Curated deep-water appearance zones (world coords, hexagon at ~0.32·WORLD_SIZE from centre). Far from
   *  the capital/risk centre and ≥1.4 km inside the edge bruma. A window's spot is a hash of its index. */
  SPOTS: [
    { x: 6348, y: 5396 },
    { x: 4096, y: 6696 },
    { x: 1844, y: 5396 },
    { x: 1844, y: 2796 },
    { x: 4096, y: 1496 },
    { x: 6348, y: 2796 },
  ],
} as const;

/** Kraken presentation phase (drives the client mesh + clip blend; SUBMERGED = culled, no render). */
export const KrakenPhase = { SUBMERGED: 0, EMERGING: 1, IDLE: 2, SINKING: 3 } as const;
export type KrakenPhase = (typeof KrakenPhase)[keyof typeof KrakenPhase];

/** Fully-resolved Kraken spectacle state for one instant — a pure function of the wall clock (below). */
export interface KrakenState {
  phase: KrakenPhase;
  /** true only during EMERGING/IDLE/SINKING — false = submerged/absent (the client renders nothing). */
  active: boolean;
  /** Index into {@link KRAKEN.SPOTS} (and the wall-clock window index) — identical for every client. */
  spotIndex: number;
  windowIndex: number;
  /** World position (one of KRAKEN.SPOTS). */
  x: number;
  y: number;
  /** Deterministic facing (rad, −π..π), stable within a window. */
  yaw: number;
  /** 0 = fully submerged … 1 = fully emerged. Drives body Y (rise·−DEEP_M) + the emerge/sink clip blend. */
  rise: number;
}

/** Deterministic 32-bit integer hash (Wang/Murmur-finalizer style) — pure, no state, no wall-clock read. */
function krakenHashU32(n: number): number {
  let x = n >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}

/**
 * Resolve the Kraken spectacle at a wall-clock epoch (ms). PURE + deterministic: same `epochMs` → same
 * state on every client (the whole point — no wire, shared clock, shared world). Unit-testable without
 * any renderer. `active=false` means "nothing to draw" (submerged between windows), the common case.
 */
export function krakenStateAt(epochMs: number): KrakenState {
  const s = epochMs / 1000;
  const period = KRAKEN.PERIOD_S;
  const windowIndex = Math.floor(s / period);
  const into = s - windowIndex * period;            // 0 .. PERIOD_S seconds into this window
  const E = KRAKEN.EMERGE_S, I = KRAKEN.IDLE_S, K = KRAKEN.SINK_S;
  const spotIndex = krakenHashU32(windowIndex) % KRAKEN.SPOTS.length;
  const spot = KRAKEN.SPOTS[spotIndex]!;
  const yaw = (krakenHashU32(windowIndex ^ 0x9e3779b9) / 0xffffffff) * (2 * Math.PI) - Math.PI;

  let phase: KrakenPhase = KrakenPhase.SUBMERGED;
  let rise = 0;
  if (into < E) { phase = KrakenPhase.EMERGING; rise = E > 0 ? into / E : 1; }
  else if (into < E + I) { phase = KrakenPhase.IDLE; rise = 1; }
  else if (into < E + I + K) { phase = KrakenPhase.SINKING; rise = K > 0 ? 1 - (into - E - I) / K : 0; }

  const active = phase !== KrakenPhase.SUBMERGED;
  return { phase, active, spotIndex, windowIndex, x: spot.x, y: spot.y, yaw, rise };
}

/** ---- Ghost Fleet (3.13) ---- At night, spectral ships rise in the risk waters (the contested centre):
 *  hostile, hard to fell, and ramming any hull that strays near. Sink one for spectral essence (a rare
 *  alchemy ingredient) + a chance at a relic. They melt away at dawn. NPC hulls (shared stepShipBody) with
 *  a GHOST flag → the client renders a translucent, emissive spectral skin. No new physics. */
export const GHOST_FLEET = {
  SPAWN_EVERY_S: 60,          // attempt a spawn this often while it's night
  MAX_ACTIVE: 4,
  SPEED_MUL: 0.92,
  HUNT_RANGE: 520,            // detect + chase players within this
  RAM_RANGE: 32,              // spectral ram when this close to a hull
  RAM_COOLDOWN_S: 3,
  RAM_KNOCK: 7,               // m/s shove (reuses the ram knock impulse)
  RAM_LEAK: 0.09,             // flood/sec leak the spectral ram punches
  RAM_BURST: 0.14,            // instant waterLevel burst on a spectral ram
  RISK_CENTER_FRAC: 0.42,     // spawn in the risk-waters band (dist/half below this)
  SPECTRAL_MIN: 1, SPECTRAL_MAX: 3, // spectral essence dropped when felled
  RELIC_CHANCE: 0.4,
  GHOST_TROPHY_KILLS: 1,      // sinking this many ghosts earns the Spectral Lantern figurehead
} as const;

/**
 * ---- Marauders (P0 launch: NPC pirate hunters) ----
 * An empty sea at low concurrency murders the free-for-all fantasy: a lone captain sails a dead ocean.
 * Marauders are hostile NPC pirates that give chase, scaled INVERSELY to the live player population — a
 * lonely sea swarms with them, a busy sea has none. They navigate + fight with the SHARED, deterministic
 * `stepShipBody` + `fireBroadside` (the exact convoy/ghost-fleet pattern), so client interpolation never
 * disagrees with the server and there is ZERO client prediction of them. All AI is server-side + seeded
 * (room `mulberry32`, iteration sorted, no wall-clock/Math.random). They obey the world edge, respect
 * spawn protection + safe harbours/bases, spawn from over the horizon, and drop a modest, anti-inflation
 * bounty when sunk. Every number here is tunable.
 */
export const MARAUDER = {
  /** Population-scaled target count: `target = clamp(round(BASE − players·RATIO), 0, MAX_ACTIVE)`.
   *  With BASE 4 / RATIO 0.5: 0-1 players → 4 marauders, tapering to 0 by ~8 players. A busy region (and
   *  the 160-CCU worst case) therefore pays for ZERO marauder ticks — the scaling itself guarantees the
   *  server's peak-tick budget can never regress from this feature. Only attempted while ≥1 player is present
   *  (nobody to menace in an empty room). */
  MAX_ACTIVE: 4,
  BASE: 4,
  RATIO: 0.5,
  /** First spawn attempt this many seconds after the room boots (a lonely captain isn't alone for long),
   *  then re-evaluated every SPAWN_EVERY_S (tracks the population target up by a spawn / down by a cull). */
  FIRST_S: 20,
  SPAWN_EVERY_S: 40,
  /** Marauder hull: a BRIGANTINE archetype at these upgrade levels — a credible threat, never a galleon.
   *  SPEED_MUL trims the shared class speed so a fresh sloop can still out-run one. */
  SHIP_CLASS: ShipClassId.BRIGANTINE,
  HULL_LVL: 2, SPEED_LVL: 1, DAMAGE_LVL: 1, CARGO_LVL: 1,
  SPEED_MUL: 0.95,
  /** Detect + begin hunting an eligible player within this (m); keep the chase until they break DISENGAGE_RANGE
   *  (hysteresis so a target on the edge of range isn't dropped/re-acquired every tick). */
  AGRO_RANGE: 250,
  DISENGAGE_RANGE: 420,
  /** Broadside standoff band (m): close to STANDOFF_MAX, hold off inside STANDOFF_MIN, present the beam between
   *  — the marauder circles to bring a broadside to bear rather than bow-charging. */
  STANDOFF_MIN: 70,
  STANDOFF_MAX: 170,
  /** Loose a broadside when the target is within FIRE_RANGE (m) and roughly abeam (within FIRE_ARC rad of the
   *  90°-off-the-bow beam), gated by the normal per-side reload. */
  FIRE_RANGE: 200,
  FIRE_ARC: 0.5,
  /** Anti-frustration keep-out: a marauder NEVER targets/pursues a player within this margin (m) beyond an
   *  outpost ring or inside the player's own base water — you're safe in port / at home. (SPAWN_PROTECT +
   *  docked-home immunity are honoured too.) Mirrors the BASE_PLACEMENT clearances in spirit. */
  SAFE_MARGIN: 60,
  /** Patrol: pick a fresh waypoint in open water; on arriving within this (m) roll the next one. */
  PATROL_REACH: 70,
  /** Spawn siting: at least this far off any world edge (well clear of the WORLD_EDGE come-about band) and,
   *  preferably, outside every player's AOI so a marauder rises over the horizon instead of popping into view;
   *  SPAWN_CLEAR is the min clearance beyond an island/outpost/base radius. */
  SPAWN_EDGE_CLEAR: 320,
  SPAWN_CLEAR: 90,
  SPAWN_TRIES: 28,
  /** Reward for sinking a marauder — routed through the daily soft-cap + idempotent ledger like any award,
   *  and deliberately well below GOLD_PER_KILL so a low-pop marauder grind can't inflate the economy. */
  GOLD_PER_MARAUDER: 30,
  /** Chance a sunk marauder leaves a divable wreck at the site (the existing sunk-ship loot drop — no new wire). */
  WRECK_CHANCE: 0.5,
} as const;

/**
 * ---- Raider Invasion event (admin-driven stress event; NO wire) ----
 * A one-shot, owner-controlled invasion: N hostile raider hulls conjured on top of the live sea to (a) run a
 * capacity stress test of the room step + AOI/snapshot fan-out under many active combat NPCs and (b) stage a
 * "black sails on the horizon" community event. Raiders REUSE the deterministic Marauder AI verbatim — they
 * hunt/fire on PLAYERS ONLY (the target scan iterates `this.players`; NPC-fired cannonballs are barred from
 * hitting other NPC hulls in `stepBalls`), so raiders never attack each other. They live ENTIRELY OUTSIDE the
 * population-scaled marauder scheduler (a distinct `raider` flag), so turning the event OFF (target 0) removes
 * every raider instantly and returns the sea to normal — the kill-switch. Zero wire: raiders ride the existing
 * ships array + EV_SPAWN exactly like a marauder (ShipFlag.NPC), so no PROTOCOL_VERSION bump. Every number
 * here is a safety rail, not a gameplay tuning — the count is chosen live by the admin call.
 */
export const RAIDER_EVENT = {
  /** Hard safety ceiling on concurrent event raiders PER ROOM — a fat-fingered admin call can never conjure an
   *  unbounded swarm. The stress target (120) sits comfortably under this. */
  MAX_ACTIVE: 200,
  /** Default target when the admin call omits `count` (the announced 120-raider invasion). */
  DEFAULT_COUNT: 120,
  /** Anti-instakill siting: each raider rises in a ring this far (m) from a randomly chosen online player, so it
   *  SAILS IN and engages within seconds rather than materialising on top of anyone. Falls back to open-water
   *  siting when the sea is empty. */
  SPAWN_RING_MIN: 260,
  SPAWN_RING_MAX: 620,
  /** Min clearance (m) beyond any island/outpost/base radius when siting a raider, and the keep-off from the
   *  world edge (well clear of the come-about band). */
  SPAWN_CLEAR: 90,
  SPAWN_EDGE_CLEAR: 320,
  /** Rejection-sampling attempts to find a clear spot before giving up on this raider's spawn. */
  SPAWN_TRIES: 24,
} as const;

/**
 * ---- NPC out-of-combat recovery (merchants, ghosts, marauders) ----
 * NPC hulls used to carry every leak forever: one glancing broadside doomed a merchant three minutes later,
 * and a wounded marauder patrolled at 90% water until it quietly sank alone. Now a crew left FULLY alone for
 * IDLE_S turns to repairs — a slow bail plus fothering the worst leak first, a deliberately weaker mirror of
 * the player's own bail/plank (`stepFlooding`). The design line ("arrume mas não deixe apelão"): hit-and-run
 * still bleeds NPCs — a heavily-holed hull keeps FLOODING at the unchanged leak physics during the whole idle
 * window and founders before the crew ever reaches the pumps — but a lightly-bled NPC left alone patches up,
 * so FINISHING a target requires commitment. ANY combat event (a hit taken, a hit landed, a broadside fired,
 * a spectral ram) re-arms the idle timer. Zero wire: recovery is server-only state; the client only sees the
 * existing ShipFlag.REPAIRING cosmetic bit while the crew works.
 */
export const NPC_RECOVERY = {
  /** Seconds fully OUT of combat (no hit taken, no hit landed, no broadside fired, no ram) before the crew turns to repairs. */
  IDLE_S: 12,
  /** × FLOOD.BAIL_RATE while recovering — an NPC crew pumps far slower than a focused captain (slow, committable). */
  BAIL_RATE_MUL: 0.35,
  /** × FLOOD.PLANK_TIME per leak fothered while recovering (worst leak first, mirroring the player plank). */
  PLANK_TIME_MUL: 3,
} as const;

/**
 * ---- THE TOKEN TICKER (display) ----
 *
 * The ONE place the player-facing ticker is spelled. Owner decision 2026-08-23: the game is
 * "Sea of Solana", so the token is **$SOS**. Every string a player or visitor reads — HUD, the
 * Premium storefront, the Royal Bank, server refusal notes, Solana Pay plates, the landing site —
 * must render this constant (or the literal it defines), never a hard-coded ticker.
 *
 * ⚠️ INTERNAL IDENTIFIERS DELIBERATELY KEEP THE `SOR` SPELLING and are NOT display: the `SOR_*`
 * env vars, the `Sor*`/`sor*` symbols and filenames, `MsgType.S_SOR`/`C_SOR`, `SorOp`, the
 * µ$SOR wire unit names (`priceUSorPerDbl`, `amountSorX100`, …) and the recovery `CODE_PREFIX`
 * ('SOR-', so codes already in players' hands keep validating). Renaming those would be a wire +
 * ops break for zero player benefit. Only what is RENDERED changes.
 */
export const TOKEN = {
  /** Bare ticker, no sigil — for places that add their own ("SOS/USD", a pump.fun form field). */
  TICKER: 'SOS',
  /** The player-facing spelling: what every UI string interpolates. */
  DISPLAY: '$SOS',
} as const;

/** Shorthand for the player-facing ticker — `${TICKER}` reads better than `${TOKEN.DISPLAY}` inline. */
export const TICKER = TOKEN.DISPLAY;

/** ---- Economy (off-chain gold; web3 settlement layered on later) ---- */
export const ECONOMY = {
  /** Gold for sinking another player's ship. */
  GOLD_PER_KILL: 50,
  /** Gold from selling a looted treasure chest at an outpost. */
  GOLD_PER_CHEST: 120,
  /** Full-rate daily earn window per account. The FIRST DAILY_EARN_CAP of gross combat/chest/contract
   *  earnings each day mints at 100% AND is the ONLY portion that is $SOR-claim-eligible (the token's
   *  hard firewall — unchanged). Past it, earnings taper via SOFT_CAP but NEVER zero (see softCapAward). */
  DAILY_EARN_CAP: 5000,
  /**
   * Soft-cap tiers replacing the old hard stop: past DAILY_EARN_CAP the marginal mint rate drops but
   * the faucet never shuts off (a grinder still progresses, just slower). Tiers are on GROSS gold
   * earned that day (Profile.dailyEarned tracks gross): 100% up to DAILY_EARN_CAP, then TIER1_RATE up
   * to TIER1_UNTIL, then TIER2_RATE forever. Eligibility ($SOR) is capped to the 100% window only.
   */
  SOFT_CAP: {
    /** Gross-earned ceiling of the reduced-but-decent middle tier (DAILY_EARN_CAP..TIER1_UNTIL). */
    TIER1_UNTIL: 10000,
    /** Marginal mint rate for gross earnings in (DAILY_EARN_CAP, TIER1_UNTIL]. */
    TIER1_RATE: 0.40,
    /** Marginal mint rate for gross earnings above TIER1_UNTIL (deep diminishing returns, never 0). */
    TIER2_RATE: 0.15,
  },
  /** RETIRED 2026-07-22 (owner: "there's no money-printing machine — you either play for it, or nothing").
   *  The recurring passive login/come-back gold is DEAD: a captain earns doubloons only by playing (kill / chest /
   *  cargo / dig / contract / marauder, all through the active `award` soft-cap). Kept as a tunable
   *  0 so the login/streak lane still exists as a code path (streak counting drives WINBACK + telemetry) — set
   *  > 0 only to deliberately re-arm the passive faucet. See RETENTION + STREAK.LOGIN_REWARDS (also retired). */
  DAILY_LOGIN: 0,
  /**
   * ---- Starter vault: the doubloons a BRAND-NEW base is born with (faucet F11) ---- **ZERO since 2026-07-26.**
   *
   * It used to be 1.200 ⓓ. The pre-$SOR red-team measured what that meant with the token ARMED: account
   * creation has no friction at all (`profileId = 'u_' + sha256(deviceToken)`, the token comes FROM the client),
   * and the harness opened **480 accounts/min from one IP, 114 ms each, zero rejections** — i.e. ~576.000 ⓓ/min
   * of minted-from-nothing seed money, linear in N and bounded only by network bandwidth
   * (docs/REDTEAM-BOTS-2026-07-26.md §4.6, docs/FAUCET-AUDIT-2026-07-26.md F11).
   *
   * The owner's call: *"he already comes with a ship — what else does a new player need?"* And the code agrees.
   * Everything a first session actually requires is handed over IN KIND or is free:
   *   • the hull — SLOOP, `DEFAULT_SHIP_CLASS`, free, `unlockedClasses` at creation;
   *   • powder — `AMMO_ROUND_START` (40) broadsides in the magazine, ~8 per sinking, and death does NOT tax it;
   *   • repairs — bailing and hand-planking cost nothing (`REPAIR_PLANK` is a 3,2× SPEEDUP, never a gate);
   *   • the money loop — hoisting a chest, selling it in an outpost ring, fishing, diving, digging and the
   *     base farm are all free; there is no docking fee, no respawn fee, no upkeep, no hunger.
   * The ONLY continuous ⓓ sink a captain meets is restocking ammo (`AMMO[ROUND].buyCost` 60 ⓓ / 10 broadsides),
   * which cannot bind before the first chest (EV ~419 ⓓ) and has a doubloon-free alternative anyway
   * (`CraftId.ROUND_AMMO`: 1 iron → a batch, iron comes from free wreck dives).
   *
   * So the starter grant bought the new captain nothing he needed — it only bought the sybil farmer his fleet.
   * If a real wall is ever introduced, the fix is to hand the ESSENTIAL over in kind (like the powder above):
   * an item is neither withdrawable nor launderable through the market, and a doubloon is both.
   *
   * NOT retroactive: `createDefaultBase` only runs when a profile has NO stored base, so the ~757 existing
   * estates keep every doubloon they hold. Nothing on the boot path recomputes or claws back a stored vault.
   */
  STARTER_VAULT: 0,
} as const;

/**
 * ---- Retention lane — the come-back hook, engagement-GATED (economy-close 2026-07-15; faucet RETIRED 2026-07-22) ----
 * The passive login/come-back GOLD is DEAD (owner: "no money-printing machine — you play for it or nothing"):
 * ECONOMY.DAILY_LOGIN and STREAK.LOGIN_REWARDS are now 0, so appearing/sailing pays NO doubloons. What remains is
 * the engagement HOOK itself: on the captain's first real sea action of the day (alive, un-berthed, ≥ENGAGE_MIN_SPEED)
 * the streak counter advances (drives WINBACK + telemetry) and a coinless welcome-back toast fires. An idle alt that
 * logs in and closes never engages, so it never advances its streak. If the constants above are ever set > 0 again,
 * this same gate pays them out (the code path is preserved). WEEKLY_CAP_DBL is a dormant fallback knob, unused.
 */
export const RETENTION = {
  /** The come-back hook advances the streak on first real engagement, not on the bare login (faucet is retired). */
  ENGAGEMENT_GATED: true,
  /** Ship speed (m/s) at sea, alive & un-berthed, that counts as "actively sailing" = engaged for the day. */
  ENGAGE_MIN_SPEED: 1.5,
  /** FALLBACK-only (unused while ENGAGEMENT_GATED): a generous per-week retention ceiling a legit player never
   *  reaches, if the engagement hook is ever swapped for a cap. Documented so the site has one knob. */
  WEEKLY_CAP_DBL: 20000,
} as const;

/**
 * ---- Growth / Recruiters (the viral loop) ----
 * A player who shares their `?crew=<name>` invite link is a RECRUITER. When a captain born from that link
 * (`Profile.recruitedBy` stamped once, immutably, at creation — never at signup) reaches a real ENGAGEMENT
 * milestone — their FIRST kill OR first treasure-chest sale — the recruiter is paid `REWARD` doubloons, once,
 * idempotently (ledger key `recruit:<recruiterId>:<recruitedId>`).
 *
 * Anti-sybil (critical pre-$SOR): the reward is MILESTONE-gated (never at signup, so a fleet of dead alts pays
 * nothing), ONE recruiter per profile (immutable), NO self-recruit (same device token → same profileId → blocked),
 * and exactly-once. The bounty is a growth MINT credited OUTSIDE the daily earn cap (owner order: growth-first) —
 * the ledger reason `'recruit'` is deliberately NOT an EARN_REASON, and it is NEVER $SOR-claim-eligible.
 */
export const RECRUIT = {
  /** Doubloons paid to the recruiter when their recruit hits the engagement milestone (first real chest/cargo SALE). */
  REWARD: 500,
  /** ANTI-SYBIL CAP (2026-07-22): max recruit bounties a single recruiter may MINT per UTC day. A fleet of scripted
   *  alts can no longer print unbounded ⓓ into one main — past this many landed milestones in a day, further recruit
   *  bounties pay nothing (the milestone still fires once per recruit, it just doesn't mint over the cap). A genuine
   *  recruiter rarely lands this many REAL first-sales in a day; picked deliberately low. Pairs with the milestone
   *  itself being raised from "first blood" to a real chest/cargo SALE (harder to fake than sinking one marauder). */
  DAILY_CAP: 4,
  /** SEAM (future): pay the recruit bounty in $SOR instead of (or alongside) doubloons. 0 = disabled — doubloons
   *  only. Wired later once the token economy is live; recorded here so the payout site has a single knob. */
  REWARD_SOR: 0,
} as const;

/**
 * Apply the daily earn SOFT CAP to a raw award. Pure + deterministic (no clock, no RNG).
 * `grossBefore` = the account's gross gold earned so far today (Profile.dailyEarned). Returns the
 * post-cap `credited` doubloons AND the `eligible` slice (the part that fell inside the first
 * DAILY_EARN_CAP, minted at 100%) — only that slice ever touches the $SOR claim ledger.
 */
export function softCapAward(grossBefore: number, amount: number): { credited: number; eligible: number; grossAfter: number } {
  if (!(amount > 0)) return { credited: 0, eligible: 0, grossAfter: grossBefore };
  const CAP = ECONOMY.DAILY_EARN_CAP, T1 = ECONOMY.SOFT_CAP.TIER1_UNTIL;
  const tiers: [number, number, boolean][] = [
    [CAP, 1.0, true],                        // [grossEnd, rate, eligible]
    [T1, ECONOMY.SOFT_CAP.TIER1_RATE, false],
    [Infinity, ECONOMY.SOFT_CAP.TIER2_RATE, false],
  ];
  let g = Math.max(0, grossBefore), remaining = amount, credited = 0, eligible = 0;
  for (const [end, rate, elig] of tiers) {
    if (remaining <= 0) break;
    if (g >= end) continue;
    const take = Math.min(remaining, end - g); // raw gold consumed inside this tier
    credited += take * rate;
    if (elig) eligible += take;               // full-rate window == eligible window (rate is 1.0 here)
    g += take;
    remaining -= take;
  }
  return { credited, eligible, grossAfter: g };
}

/**
 * P2P cosmetic marketplace (v1: off-chain, Doubloon-settled — the on-chain POE/NFT version reuses
 * this exact escrow/fee engine later). Server-authoritative; trades are gated to being docked home.
 * See docs/ECONOMY.md §4. The DBL fee is a sink (partly burned), so it never breaks the token firewall.
 */
export const MARKET = {
  /** Anti-abuse: minimum interval (ms) between accepted C_MARKET ops per connection (mirrors the casino's
   *  CASINO.RATE_LIMIT_MS). Covers EVERY market op — cosmetic, gold, goods and furniture LIST/BUY/CANCEL —
   *  so a script can't hammer the escrow/ledger. Over-rate ops are refused with a standard market note. */
  RATE_LIMIT_MS: 500,
  FEE_PCT: 0.06,        // 6% take-rate on every sale (docs/ECONOMY.md §4)
  BURN_SHARE: 0.60,     // of that fee: 60% burned (deflation), remainder to treasury
  MAX_LISTINGS: 8,      // active listings per seller
  MIN_PRICE: 10,
  MAX_PRICE: 5_000_000,
  FLIP_COOLDOWN_S: 300, // anti-wash: a cosmetic just bought can't be re-listed for 5 min

  /** ---- P2P GOLD market (feature 6): sell a package of doubloons for $SOR (dormant until the TGE) ----
   *  The gold is escrowed OUT of the seller's base on listing (returned on cancel). Buying settles in
   *  $SOR through the P2pSettlement seam, which is DISABLED until the token is live. Prices ride the wire
   *  as $SOR ×100 (2 decimals). Premium doubloon PACKS are deliberately priced ~30% above this P2P floor
   *  (a convenience tax) so the player market is the primary source of bought gold. */
  GOLD_MIN_PACK: 1000,            // min doubloons in a single gold listing
  GOLD_MAX_PACK: 1_000_000,       // max doubloons in a single gold listing
  GOLD_MIN_PRICE_SOR_X100: 1,     // min ask: 0.01 $SOR
  GOLD_MAX_PRICE_SOR_X100: 100_000_000, // max ask: 1,000,000.00 $SOR (fits u32)
  MAX_GOLD_LISTINGS: 4,           // active gold listings per seller

  /** ---- Physical GOODS market (2b.8): sell materials/consumables for DOUBLOONS (immediate, game-economy
   *  settlement — NOT $SOR). Same FEE_PCT/BURN_SHARE sink. List with the goods IN YOUR HOLD at an outpost;
   *  buying moves them into the buyer's hold (piratable home). "Money metaphysical, goods physical." */
  GOODS_MIN_QTY: 1, GOODS_MAX_QTY: 999,
  GOODS_MIN_PRICE: 1, GOODS_MAX_PRICE: 100_000, // doubloons per unit
  MAX_GOODS_LISTINGS: 12,          // active goods listings per seller
} as const;

/**
 * ================================ ECONOMY WATCHDOG (real-time invariants) ================================
 *
 * The thresholds of `apps/server/src/economyWatchdog.ts` — the module that watches the money supply LIVE and
 * can pull a subsystem kill-switch by itself. Owner's order (2026-07-26): *"analyse the economy in real time
 * and pull triggers, not find out afterwards that it was drained like other projects."*
 *
 * **Every number here is calibrated against a MEASURED value**, not a guess. The measurements come from the
 * four red-team fronts of 2026-07-26 (`docs/FAUCET-AUDIT-2026-07-26.md`, `docs/REDTEAM-BOTS-2026-07-26.md`,
 * `docs/REDTEAM-WITHDRAW-2026-07-26.md`) and the rationale for each threshold + its headroom is tabulated in
 * `docs/ECONOMY-WATCHDOG.md`. A watchdog that cries on a normal day gets switched off in week one, so the
 * bias is deliberate: **wide headroom over normal play, tight against every strategy anyone has measured.**
 *
 * Design constraints that shaped these numbers (see the doc's anti-DoS section):
 *  · Two levels. ALERT only notifies. ACTION pauses the ONE implicated subsystem — never the whole game, and
 *    never in a way that traps funds (repo law: a pause settles an open blackjack hand and leaves
 *    CANCEL/COLLECT open).
 *  · An automatic trigger IS an attack surface. Anyone who learns a threshold could trip it on purpose to deny
 *    the game to everyone else, so ACTION additionally requires: the breach SUSTAINED over
 *    `ACTION_SUSTAIN_SAMPLES` evaluations, a CORROBORATING signal from a different invariant family, a
 *    remaining auto-pause budget for the UTC day, and no re-arm inside `REARM_COOLDOWN_MS`.
 *  · Nothing auto-RESUMES. Only the owner reopens a subsystem (`POST /admin/pause`) — an automatic resume
 *    would hand an attacker an oscillator.
 *  · Scale-free where possible. Absolute ⓓ/day thresholds rot as the population grows, so the supply signals
 *    are normalised per ACTIVE EARNER and the composition signals are ratios against their own history.
 */
export const ECONOMY_WATCHDOG = {
  /** How often the watchdog sweeps (ms). OUTSIDE the 30 Hz tick loop — a plain `setInterval` in index.ts. The
   *  sweep is one O(profiles) pass + a few map scans; the tick budget (p99 < 25 ms, today avg 0.45 ms) is
   *  never touched. Measured sweep cost is reported by `GET /admin/watchdog` as `lastSweepMs`. */
  EVAL_INTERVAL_MS: 60_000,
  /** Log LOUDLY if one sweep exceeds this (ms) — the watchdog policing its own cost. Not an alert, a smell. */
  SWEEP_BUDGET_MS: 50,
  /** Consecutive sweeps a metric must stay over its ALERT threshold before the alert is raised. 3 × 60 s = a
   *  3-minute floor on detection, which kills single-sample noise (one gilded chest, one big fill). */
  SUSTAIN_SAMPLES: 3,
  /** …and over its ACTION threshold before a subsystem is paused. 5 × 60 s = 5 minutes. Deliberately longer
   *  than SUSTAIN_SAMPLES: a pause is expensive, so it must be a trend and not a spike. */
  ACTION_SUSTAIN_SAMPLES: 5,
  /** A breached metric only CLEARS when it falls to `CLEAR_FRACTION` × its threshold (hysteresis). Without
   *  this, a metric parked on the threshold flaps alert/recovered/alert forever and trains the owner to
   *  ignore the channel. */
  CLEAR_FRACTION: 0.8,
  /** Minimum gap between repeat notifications of the SAME invariant (ms). Economy drifts far slower than a
   *  health incident, so this is longer than the incident watchdog's 10 min. */
  NOTIFY_COOLDOWN_MS: 30 * 60_000,
  /** Max AUTOMATIC pauses per subsystem per UTC day. Past this the ACTION downgrades to a loud ALERT saying
   *  the budget is spent — the anti-DoS ceiling: an attacker who learns a threshold cannot toggle a
   *  subsystem all day, at worst he spends this budget and then only makes noise. */
  MAX_AUTO_PAUSES_PER_SUBSYSTEM_PER_DAY: 2,
  /** …and across ALL subsystems per UTC day (a second ceiling, so N invariants can't pause N subsystems). */
  MAX_AUTO_PAUSES_PER_DAY: 3,
  /** After an automatic pause of a subsystem, no further automatic pause of it for this long (ms), even if the
   *  owner resumes it in the meantime. Gives a human time to look before the machine acts twice. */
  REARM_COOLDOWN_MS: 6 * 3_600_000,
  /** Trigger records kept in memory (and served by `GET /admin/watchdog`) — the "history of firings". */
  HISTORY_MAX: 200,

  /* ------------------------- 1. supply: is the money supply running away? ------------------------- */
  /** mint ÷ burn for the UTC day. Scale-free (a ratio), so it survives population growth. NOT tightly
   *  calibrated: the audits measured the NET (~+4.000 ⓓ/day on a full day, +8.602 while the retention faucet
   *  was alive) but never published the mint/burn split, so 10:1 is a deliberately loose first threshold. The
   *  honest instruction in `docs/ECONOMY-WATCHDOG.md`: read `mintBurnRatio` off `GET /admin/watchdog` for a
   *  week of real play, then tighten to ~2× the observed p95. */
  MINT_BURN_RATIO_ALERT: 10,
  /** …and ignore the ratio entirely until the day has minted at least this much. Without a floor, the first
   *  minutes of every UTC day (mint 300, burn 0) would read as infinite inflation. */
  MINT_BURN_MIN_MINT_DBL: 20_000,
  /** NET inflation (mint − burn) per ACTIVE EARNER for the UTC day. An "active earner" is a non-guest profile
   *  with `dailyEarned > 0` today. Calibration: the red-team measured a dedicated bot at 5.692 gross ⓓ/h,
   *  which the soft cap credits as ~14.038 ⓓ over a 10 h grind. So this threshold means *the AVERAGE earner
   *  is netting what the best measured bot nets on a full-day grind* — implausible for a real population, and
   *  the whole point is that it does not move when the population does. */
  NET_INFLATION_PER_EARNER_ALERT: 12_000,
  /** Ignore the per-earner figure until at least this many earners exist (a 1-earner day is meaningless). */
  MIN_EARNERS_FOR_SUPPLY: 5,

  /* ---------------------- 2. composition: is one faucet suddenly the economy? --------------------- */
  /** Share of the day's mint held by its single largest reason. **This is the signal that WAS the problem**:
   *  when `chest` measured 86% of mint, that number *was* the alarm nobody had. Thin headroom is admitted —
   *  chest is the biggest faucet BY DESIGN (F1), so a quiet day can legitimately sit near 0.8. Treated as an
   *  awareness signal (ALERT only, never ACTION); the sharp instrument is REASON_SPIKE below. */
  REASON_DOMINANCE_ALERT: 0.85,
  /** …with the same "the day must have real volume" floor as the ratio above. */
  REASON_DOMINANCE_MIN_MINT_DBL: 20_000,
  /** A single reason's mint in the last COMPLETE UTC hour ÷ that reason's mean over the previous complete
   *  hours. This is the fast lane (≈1 hour resolution vs. the daily signals) and the one that catches a NEW
   *  faucet or a re-armed old one, because it compares a reason against ITSELF rather than against a fixed
   *  number — immune to population growth. */
  REASON_SPIKE_ALERT: 5,
  /** …but a traffic spike lifts EVERY reason at once, which is not a composition change. So a spike only
   *  counts if the reason's SHARE of mint also grew by this factor. Without it, a Discord raid that triples
   *  CCU would fire every reason simultaneously (the auto-poster's false-spike incident, in economy form). */
  REASON_SPIKE_SHARE_GROWTH: 1.5,
  /** Hours of history required before the spike ratio is trusted, and the ⓓ floor the spiking hour must clear
   *  (so a reason going 10 ⓓ → 100 ⓓ is arithmetic, not an incident). */
  REASON_SPIKE_MIN_HOURS: 6,
  REASON_SPIKE_MIN_HOUR_DBL: 5_000,

  /* --------------------- 3. actors: is the tail detaching from the population? -------------------- */
  /** p99 ÷ p95 of today's GROSS `dailyEarned` across active earners. Deliberately NOT p99/p50: with a gross,
   *  uncapped meter a casual 300 ⓓ session vs. a grinder's 30.000 is a legitimate 100×, so p99/p50 screams on
   *  day one. p99/p95 asks a sharper question — "are the top 1% a different species from the top 5%?" — which
   *  is exactly the shape a farm makes. Baseline expectation 1.2–2×; needs one week of prod reads to tighten
   *  (the raw p50/p95/p99 are always in the endpoint payload for precisely that). */
  EARNER_TAIL_ALERT: 4,
  /** Minimum active earners before percentiles mean anything, and a floor on p99 so the ratio cannot fire on
   *  tiny numbers (p95 = 5 ⓓ, p99 = 40 ⓓ is an 8× that means nothing). */
  EARNER_TAIL_MIN_EARNERS: 50,
  EARNER_TAIL_MIN_P99_DBL: 20_000,
  /** Sustained per-account GROSS earn rate (ⓓ/h) — the faucet-ceiling alarm. Measured envelope: best island a
   *  bot actually farmed = **6.084 ⓓ/h**; the planner's best absolute island = 11.023 ⓓ/h; the theoretical
   *  ceiling with a 2-chest hauler (Fluyt/East Indiaman) = **20.781 ⓓ/h**. So ALERT sits just under the
   *  theoretical best-known strategy (it should fire on someone playing perfectly — that is worth a look) and
   *  gives 2.96× headroom over the best rate anyone has actually achieved. */
  FARM_RATE_ALERT_DBL_PER_H: 18_000,
  /** …and ACTION at 1.44× the best theoretical strategy: nothing anyone has modelled reaches here, so this is
   *  a faucet nobody knows about. Note the honest gap documented in `docs/ECONOMY-WATCHDOG.md`: there is NO
   *  kill-switch for the chest/cargo faucet, so this ACTION can only pause `pvp_rewards`, and only when the
   *  day's dominant mint reason is `kill`. Otherwise it stays a (loud) ALERT. */
  FARM_RATE_ACTION_DBL_PER_H: 30_000,
  /** The per-account rate is measured over a rolling window of at most this long, requiring at least
   *  FARM_RATE_MIN_ELAPSED_MS of data. The window is LONG on purpose: a single gilded chest at maximum
   *  multipliers is 12.375 ⓓ in one hoist, which over a 15-minute window would read 49.500 ⓓ/h — a guaranteed
   *  false positive. Over the 45-minute minimum it reads 16.500 ⓓ/h, safely under the 18.000 ALERT. */
  FARM_RATE_WINDOW_MS: 90 * 60_000,
  FARM_RATE_MIN_ELAPSED_MS: 45 * 60_000,
  /** Cap on tracked earners (memory bound; MAX_CCU is 160/process so this is ~30× headroom). */
  MAX_TRACKED_EARNERS: 5_000,
  /** New NON-GUEST profiles created inside `ACCOUNT_BURST_WINDOW_MS`. Measured attack: **480 accounts/min,
   *  114 ms each, zero rejections, one IP** — i.e. 4.800 in this window. A generous organic peak (the owner's
   *  Discord/Philippines bursts) is tens per hour, so 60 leaves ~2× over any plausible organic rate and sits
   *  80× BELOW the measured attack. ⚠️ Raise this before any real acquisition campaign. ALERT-only: there is
   *  no "signups" subsystem to pause, so its job is to alert and to CORROBORATE another invariant's ACTION —
   *  which is exactly right, because a sybil fleet is the shared prefix of nearly every attack in the audits. */
  ACCOUNT_BURST_ALERT: 60,
  ACCOUNT_BURST_WINDOW_MS: 10 * 60_000,

  /* ------------------- 4. flow: is value pooling from many accounts into one? --------------------- */
  /** ⓓ moved between ONE ordered (from → to) pair of profiles in a rolling 24 h. Alarming per PAIR rather than
   *  per account is the point: the alt→main launderer looks unremarkable account-by-account and obvious as a
   *  pair. 25.000 ⓓ is 5× the daily earn cap and 5 days of one account's ⓓ→$SOR cash-out quota. Channels fed:
   *  the goods/cosmetic/furniture markets (6% fee) and the raid/revenge steal channel (**0% fee, chosen
   *  target** — finding U-7). ACTION pauses `marketplace`, which by repo law never traps an escrow. */
  PAIR_FLOW_ALERT_DBL: 25_000,
  PAIR_FLOW_ACTION_DBL: 100_000,
  PAIR_FLOW_WINDOW_MS: 24 * 3_600_000,
  /** Cap on tracked pairs (oldest-touched evicted first) so the tally can never grow unbounded. */
  MAX_TRACKED_PAIRS: 20_000,

  /* --------------- 5. price: is the ⓓ↔$SOR reference being walked? (the big one) ------------------ */
  /** Ratio between the live `referencePrice()` and the anchor sampled ~24 h ago, in EITHER direction. **The
   *  most valuable alarm in the set**: the withdraw red-team walked the reference **1.850 → 16.175 µ$SOR/ⓓ =
   *  8.74× inside a single UTC day with 61 accounts**, and at that reference the store→book arbitrage pays
   *  +850% per round trip. One maximum-size fill at the top of the ±25% band moves the EWMA by up to +12.5%,
   *  so 1.35×/day means the reference has been WALKED, not traded. ACTION at 2.0× pauses `exchange` — and a
   *  paused exchange still lets a captain CANCEL an ask (funds are never trapped). */
  REF_DRIFT_ALERT: 1.35,
  REF_DRIFT_ACTION: 2.0,
  /** How far back the drift anchor reaches (ms) — hourly samples of the reference are kept for this long. */
  REF_DRIFT_WINDOW_MS: 24 * 3_600_000,
  /** Ignore the drift while the reference is below this (µ$SOR/ⓓ): a virgin/bootstrap book at 0 has no price
   *  to drift, and dividing by it would read as an infinite move. */
  REF_DRIFT_MIN_PRICE_USOR: 100,

  /* ---------------- 6. cash-out: how much $SOR left the vault in a sliding 24 h? ------------------ */
  /** ⚠️ **THE OWNER MUST SET THESE AT ARM TIME.** They are the sliding-24 h outflow ceilings in µ$SOR, and the
   *  defaults are placeholders (2.000 / 5.000 $SOR) picked to be *smaller* than any plausible vault so the
   *  alarm errs loud rather than silent. Ground them in the vault's real day-1 liquidity — the
   *  "OPEN QUESTION — day-1 liquidity" of `contracts/INTEGRATION.md`. Context for the sizing: a VETERAN tier
   *  caps at 500 $SOR/day/account, and the withdraw red-team showed every calendar-day cap pays TWICE across
   *  a UTC midnight (W-3/W-12) — which is exactly why this watchdog measures a SLIDING window instead.
   *  ACTION pauses `exchange`. While the vault is disarmed the observed outflow is 0, so this never fires. */
  VAULT_OUTFLOW_24H_ALERT_USOR: 2_000_000_000,
  VAULT_OUTFLOW_24H_ACTION_USOR: 5_000_000_000,

  /* ------------------------------- 7. solvency: reconciliation ------------------------------------ */
  /** A reconciliation divergence (the ledger believing it owes more $SOR than the vault holds) latches this
   *  invariant for this long (ms) so one transient divergence cannot be missed between two sweeps. ALERT-only
   *  BY DECISION: today `onReconcileDivergence` merely writes a log line and calls the state HEALTHY (finding
   *  W-5/W-7a), and that fix belongs to another workstream — the watchdog's job here is to stop the silence,
   *  not to change the handler's behaviour. Promoting it to ACTION is one line, spelled out in the doc. */
  RECONCILE_LATCH_MS: 6 * 3_600_000,
} as const;

/**
 * ---- P2P ⓓ↔$SOR EXCHANGE (marketplace anti-bot spec `docs/design/MARKETPLACE-ANTIBOT.md`) ----
 * The order-book cash-out: farmers list game-earned doubloons (ⓓ) for $SOR; buyers execute against the book.
 * ALL of this is OFF-CHAIN authoritative (matching, escrow of ⓓ, fills, fees, internal $SOR balances) — only
 * the SorVault contract touches the chain (deposit + voucher withdraw). BUILT-BUT-DISARMED: the engine is inert
 * until the server env arms it (`SOR_VAULT_*`), and no gameplay value is minted here — it only MOVES already-
 * earned, firewall-eligible ⓓ into $SOR. Every value is a knob; tune at launch, not in code.
 *
 * Units: `sorBalance`/fees are µ$SOR INTEGERS (1 $SOR = 1e6 µ$SOR); ask price is µ$SOR per ⓓ (integer, tick 10).
 */
export const EXCHANGE = {
  /** µ$SOR per 1 $SOR (the custodial ledger's integer unit — avoids float drift on balances/fees). */
  USOR_PER_SOR: 1_000_000,
  /** Take-rate on every fill, charged on the BUYER's $SOR leg (seller receives 1−FEE_PCT). Spec D8. */
  FEE_PCT: 0.06,
  /** Split of the fee: treasury/burn/owner. LOCKED in ECONOMY-DECISIONS §Resolvidos = 40/40/20 (burn raised
   *  30→40 for extra deflation vs. the farmer's structural sell pressure). Must sum to 1. */
  FEE_SPLIT: { treasury: 0.40, burn: 0.40, owner: 0.20 },
  /** Minimum doubloons in a single ask, and the minimum partial-fill quantity (anti-dust). */
  LOT_MIN: 100,
  /** Ask time-to-live (hours): an expired ask returns the escrowed ⓓ to the seller's bank (parking is costly). */
  ORDER_TTL_H: 48,
  /** Max simultaneous OPEN asks per captain. */
  MAX_OPEN_ORDERS: 3,
  /** Max doubloons a captain may have parked across all open asks (~5 days of DAILY_EARN_CAP). */
  MAX_OPEN_DBL: 25_000,
  /** Price band: an ask must sit within ±PRICE_BAND of the EWMA reference. Launch TIGHT (0.25, spec §7.1) — a
   *  raw book moves the EWMA easily; loosen with data. Tunable hot. */
  PRICE_BAND: 0.25,
  /** Ask-price granularity, µ$SOR per ⓓ (prices snap to this tick). */
  TICK_USOR: 10,
  /** EWMA half-life (hours) of the volume-weighted reference price. */
  REF_HALFLIFE_H: 6,
  /** Below this many (non-tainted) fills/day the reference stays pinned to BOOTSTRAP_REF_PRICE (bootstrap). */
  REF_MIN_FILLS_PER_DAY: 20,
  /**
   * HYSTERESIS release threshold: once the EWMA has taken over, it stays in charge until the trailing-24 h count of
   * ref-moving fills falls BELOW this (lower) number — it does not hand back at `REF_MIN_FILLS_PER_DAY`.
   *
   * Round 2 turned `fills24h` into a genuine sliding count (good: it killed the last calendar bucket), but with a
   * single threshold the public price then FLIP-FLOPPED between the owner's anchor and the EWMA every time the 20th
   * vote aged in or out of the window. MEASURED over 14 days with 20 clean sellers + 8 clean buyers:
   * `1,15× → 1,00× → 1,25× → 1,00×` — jumps of ±25% several times a day, which is worse for a captain reading the
   * band than the once-a-day discontinuity it replaced. A Schmitt trigger costs one constant and removes the
   * oscillation without weakening the W-19 shields (the drift clamp and the per-account influence cap are untouched).
   */
  REF_RELEASE_FILLS_PER_DAY: 12,
  /**
   * ---- WASH-TRADE SHIELD ON THE REFERENCE PRICE (W-19) ------------------------------------------------------
   * The ref price is the LASTRO: every ⓓ a farmer cashes out is paid at a multiple of it, out of real buyers'
   * deposits. Measured before these two knobs: 60 sybil sellers + 1 funded buyer walked the EWMA from 1 850 to
   * 16 175 µ$SOR/ⓓ (8.74×) inside ONE UTC day, because each 5 000-ⓓ fill at the top of the ±25% band lifts the
   * EWMA ~5% and the band then moves with it — a ratchet with no ceiling.
   *
   * `REF_MAX_DRIFT_24H` — the hard ceiling on how far the EWMA may travel from a SLIDING 24 h anchor (not a
   * calendar bucket: a calendar anchor would allow 2× the drift across midnight, the W-3 mistake). Honest price
   * discovery compounds day over day; a ratchet dies inside one day.
   *
   * `REF_MAX_FILLS_PER_ACCOUNT_24H` — how many fills ONE account (as maker OR taker) may contribute to the
   * reference per 24 h. The wash-trade shape is always the same: many alt sellers, ONE funded buyer recycling the
   * same pot. Capping per-account influence means the reference needs BREADTH (≥ ~7 distinct accounts to reach
   * REF_MIN_FILLS_PER_DAY) before it takes over from the owner's bootstrap anchor. Fills above the cap still
   * EXECUTE — they just don't get to set the price the whole market is paid at.
   */
  REF_MAX_DRIFT_24H: 0.15,
  REF_MAX_FILLS_PER_ACCOUNT_24H: 3,
  /** Admin anchor reference price (µ$SOR per ⓓ) for a virgin book. 0 = no reference yet → listing is refused
   *  until the owner anchors it (no band → no safe price). Set to $SOR-DEX-price ÷ target-ⓓ-value on arm. */
  BOOTSTRAP_REF_PRICE: 0,
  /** Minimum withdrawal in $SOR (anti-dust vs. gas). */
  WITHDRAW_MIN_SOR: 10,
  /** Global circuit breaker: pause withdrawals when the trailing-24h outflow exceeds this fraction of the vault
   *  balance (mirrors the on-chain SorVault dailyOutflowCap). */
  OUTFLOW_BREAKER_PCT: 0.2,
  /**
   * ABSOLUTE global 24 h outflow ceiling in $SOR (0 = disabled) — ⚠️ OWNER DECISION, set this at arm time.
   *
   * W-6: every other ceiling in this file is PER ACCOUNT (tier cap 100/250/500 $SOR/day, cash-out allowance
   * 5 000 ⓓ/day), so the house's total daily exposure was bounded only by how many accounts an attacker could
   * create — measured at 480/min in the faucet audit. This is the one ceiling that does not depend on counting
   * accounts. It uses the same SLIDING 24 h window as the breaker, not a calendar bucket, so it cannot be doubled
   * across UTC midnight. Default matches the `SOR_VAULT_MAX_SIGN_PER_DAY_SOR` default (5 000) so the three
   * ceilings — process signing cap, this one, and the on-chain `dailyOutflowCap` — start out coherent.
   */
  OUTFLOW_24H_MAX_SOR: 5000,
  /** Floor ($SOR) under the fraction-of-collateral breaker when the LIVE vault balance is unknown and the ledger
   *  falls back to its own internal total. Without it, 20% of a book holding 50 $SOR is 10 $SOR — one
   *  `WITHDRAW_MIN_SOR` — so a young or lightly funded exchange could not settle a single honest cash-out. The
   *  absolute `OUTFLOW_24H_MAX_SOR` ceiling still applies on top, so this only ever relaxes the *fraction*. */
  OUTFLOW_BREAKER_FLOOR_SOR: 250,
  /** Reconciliation (I1) tolerance, µ$SOR. The invariant halts the cash-out ONLY on a real OVER-emission —
   *  `internalTotal − onChainVaultBalance > RECONCILE_EPS_USOR` (the ledger believes it owes MORE $SOR than the
   *  vault actually holds = insolvency / double-credit). The reverse (`onChain ≥ internal`) is HEALTHY and never
   *  halts: the ledger FLOORS each deposit to µ$SOR (weiToUsor) while the vault keeps the full wei, so
   *  Σ⌊depositᵢ⌋ ≤ ⌊Σ⌋ → on-chain sits ≥ internal by sub-µ$SOR dust in honest operation, and unlinked/parked
   *  deposits leave the vault legitimately over-collateralized. Dust only ever pushes the diff NEGATIVE, so 0 is
   *  the correct positive tolerance (any positive divergence is a genuine bug); the knob exists for ops headroom. */
  RECONCILE_EPS_USOR: 0,
  /**
   * The tolerance in the OTHER direction (µ$SOR): how far the on-chain vault may sit ABOVE the ledger's internal
   * total before that is treated as a signal rather than dust. W-5: `onChain ≥ internal` used to be declared
   * unconditionally HEALTHY, which is exactly how a WIPED ledger (every custodial balance → 0 against a full
   * vault) reported `ok: true`. Legitimate causes are floor dust, PARKED deposits (passed in explicitly) and
   * liquidity the owner seeded — set this to the seeded amount at arm time. 1 $SOR of default headroom.
   */
  RECONCILE_OVER_EPS_USOR: 1_000_000,
  /** Delayed-settlement tiers (spec §2.3): hold hours before a withdrawal may be signed, per-day $SOR cap, and
   *  the $SOR amount above which even this tier forces a manual review. */
  TIERS: {
    NEW: { holdH: 72, dailyCapSor: 100, reviewOverSor: 50 },
    STANDARD: { holdH: 24, dailyCapSor: 250, reviewOverSor: Number.POSITIVE_INFINITY },
    VETERAN: { holdH: 1, dailyCapSor: 500, reviewOverSor: Number.POSITIVE_INFINITY },
  },
  /** Tier qualification. NEW = account younger than NEW_MAX_AGE_DAYS, OR first withdrawal, OR score ≥ NEW_SCORE_MIN.
   *  VETERAN = age ≥ VETERAN_MIN_AGE_DAYS AND ≥ VETERAN_MIN_CLEAN clean withdrawals AND score < VETERAN_SCORE_MAX.
   *  ⚠️ A profile with NO birth stamp (`Profile.createdMs` absent — the legacy population from before that field
   *  existed) is an OLD account, not a newborn: its age resolves to `CASHOUT_MATURITY.LEGACY_ACCOUNT_AGE_DAYS`
   *  (see `antibot.accountAgeDaysOf`), never 0. */
  TIER_RULES: {
    NEW_MAX_AGE_DAYS: 14, NEW_SCORE_MIN: 60,
    VETERAN_MIN_AGE_DAYS: 45, VETERAN_MIN_CLEAN: 5, VETERAN_SCORE_MAX: 30,
  },
  /** Anti account-takeover (spec §7.2): a first-seen or changed withdrawal address waits this long before it
   *  may be used, and a withdrawal above WITHDRAW_2FA_SOR wants reinforced (2FA) confirmation. */
  WITHDRAW_ALLOWLIST_DELAY_H: 24,
  WITHDRAW_2FA_SOR: 250,
  /**
   * ⚰️ DEAD SINCE THE SOLANA PIVOT (2026-08-22) — kept only so an old `market-ledger.json` / WAL still reads.
   * There is no voucher and no `SorVault.sol`: the server signs and broadcasts the transfer itself, and the
   * exposure window is now the ~60-90 s life of a Solana blockhash, not 168 h of unrevokable bearer instrument.
   * NOTHING reads this. Do not reintroduce a code path that does.
   */
  VOUCHER_TTL_H: 168,
  /**
   * GRACE (minutes) the poller waits before declaring a broadcast transfer dead and refunding the held $SOR.
   *
   * ⚓ SOLANA: the primary proof is now a PROTOCOL FACT — a transaction whose blockhash is behind
   * `lastValidBlockHeight` can never be included. This grace is the second condition on top of it, because the
   * other half of the test ("`getSignatureStatuses` has no record") is an RPC's word: a node lagging the real
   * chain head, or one that has not indexed the signature yet, would otherwise get a landed transfer refunded —
   * a double payout. Belt: `MarketLedger.withdrawalsAwaitingChain` keeps re-checking a refunded withdrawal for
   * hours, and `markConfirmed` REVERSES the refund if a status turns up (the chain is the truth).
   *
   * W-2 (`docs/REDTEAM-WITHDRAW-2026-07-26.md`) is where this constant comes from; on EVM it was the ONLY guard
   * against a late `Withdrawn` event, which is why it was sized at 60 min rather than seconds.
   */
  VOUCHER_REAP_GRACE_MIN: 60,
  /** Risk-score bands 0–100 (spec §2.2). Effects are GRADUATED and only ever regulate the cash-out tap
   *  (list/withdraw) — playing, raiding and buying are NEVER blocked by score. */
  RISK: {
    ELEVATED: 30, // +24h hold, daily cap halved
    HIGH: 60,     // mandatory review on every withdrawal, listing throttled, fills tainted (out of the EWMA)
    FROZEN: 85,   // market frozen for the account (list + withdraw); the GAME stays 100% normal
    /** Edge weights feeding the score (0–100 soft-summed, capped). Strong edges dominate; ip-subnet is weak and
     *  never sufficient alone (family/lan-house rule — needs ≥2 independent strong edges to reach HIGH). CGNAT /
     *  shared mobile public IP is noise → W_WEAK is tiny (a lone weak edge can't even reach ELEVATED=30). */
    W_STRONG: 35, W_MEDIUM: 15, W_WEAK: 8,
  },
  /**
   * ---- CASH-OUT ALLOWANCE (player-first revision 2026-07-15 — see docs/design/ECONOMY-DECISIONS §Revisão) ----
   * The owner ABOLISHED "gold classes": every doubloon is equal and sellable. Protection moved OFF a balance
   * class (eligible vs. inelegible) ONTO daily THROUGHPUT + the risk GRAPH. `DAILY_ALLOWANCE_DBL` is the one
   * legible number ("Cash-out allowance: X ⓓ/day") — the daily ceiling on ⓓ a captain can SELL (convert to
   * $SOR) in the market, consumed at FILL and reset per UTC day like the other caps. Tiers modulate ONLY the
   * throughput/ceiling/hold — NEVER "which gold is worth what". The `market.sub.gold` firewall — gameplay mints
   * ⓓ, NEVER $SOR — is a DIFFERENT thing and is UNCHANGED (cash-out is P2P: the $SOR that leaves is what a
   * buyer deposited → solvency by construction).
   */
  CASHOUT: {
    /** Daily ⓓ→$SOR sell throughput per account (mirrors the DAILY_EARN_CAP soft-cap of 5000). Caps EVERYTHING
     *  in aggregate — better than the old eligibility, which only capped classified sources. Consumed at fill. */
    DAILY_ALLOWANCE_DBL: 5000,
    /** Tiers modulate ONLY the throughput (NOT which gold is sellable): the allowance = DAILY_ALLOWANCE_DBL ×
     *  this multiplier. VETERAN (clean, ≥45d) gets more headroom; NEW is the safe default. */
    TIER_ALLOWANCE_MULT: { NEW: 1, STANDARD: 1, VETERAN: 2 },
    /** Fast-lane of the FIRST cash-out: a NEW-tier account in the NORMAL band whose FIRST EVER withdrawal is
     *  ≤ this many $SOR skips the hold entirely (converts the newbie's trust — the invisible friction the honest
     *  never feel; Kintara lesson). Above it, if flagged, or on ANY later withdrawal, the tier hold applies.
     *  ⚠️ W-4: `resolveWithdrawalTier` had no "first" term at all, so this was a PERMANENT zero-hold lane for
     *  every account under 14 days old (4 × 25 = the whole NEW daily cap with zero clawback window). Below
     *  WITHDRAW_2FA_SOR so it never trips 2FA. */
    FASTLANE_SOR: 25,
    /** Goods-market laundering signal (feeds the graph, NEVER blocks): a material sold at ≥ this × the item's
     *  running median unit-price draws a `traded-with` edge between the counterparties. Needs OVERPRICE_MIN_SAMPLES
     *  recent sales of that material to have a median at all. */
    OVERPRICE_MULT: 5,
    OVERPRICE_MIN_SAMPLES: 5,
    /** Trade-concentration signal: a counterparty that is ≥ TRADE_CONCENTRATION_PCT of an account's fills (over at
     *  least TRADE_MIN_FILLS fills) draws a `traded-with` edge (a dedicated buyer of one farmer = wash/funnel). */
    TRADE_CONCENTRATION_PCT: 0.8,
    TRADE_MIN_FILLS: 5,
    /** Victim-concentration signal (follow-up feeder — the pure scorer ships now): a killer whose victim HHI is ≥
     *  VICTIM_HHI over ≥ VICTIM_MIN_KILLS tracked kills (always sinks the same accounts = self-farming) draws a
     *  `victim-concentration` edge. */
    VICTIM_HHI: 0.5,
    VICTIM_MIN_KILLS: 8,
  },
  /**
   * ---- CASH-OUT MATURITY GATE (anti-sybil, 2026-07-26) ---- ⚙️ OWNER-TUNABLE (every number below)
   *
   * Onboarding stays 100% FREE — the expensive door is the EXIT. Creating an account costs nothing (measured:
   * ~480 accounts/min), so the one thing a sybil farm cannot mass-produce is a captain who has actually PLAYED
   * and has BUILT something. The gate therefore refuses only the ⓓ→$SOR cash-out (the withdrawal that moves
   * value off the server) until every ENABLED criterion is true — it never touches gameplay, never confiscates
   * and never freezes: the doubloons and the custodial $SOR stay exactly where they are, and the moment the
   * account matures the same cash-out goes through untouched.
   *
   * ⚠️ 2026-07-27 (owner): the ACCOUNT-AGE axis is OFF (`MIN_ACCOUNT_AGE_DAYS: 0`). Wall-clock age ripens in
   * parallel for free, so it punished honest newcomers without costing a farm anything; PLAYTIME is the axis
   * that holds the anti-sybil floor. Each criterion is independently disable-able by setting it to 0.
   *
   * Composes with (never replaces) the existing layers: the one-wallet-per-profile binding (R-3), the risk graph
   * bands, and the delayed-settlement TIERS above. This is the FLOOR — passing it only gets you to the tier ladder.
   */
  CASHOUT_MATURITY: {
    /** Master switch. false = the gate is inert (the tier ladder + risk bands still apply). */
    ENABLED: true,
    /** Criterion 1 — ACCOUNT AGE (days since `Profile.createdMs`). ⛔ **DISABLED (owner decision 2026-07-27):
     *  `0` = the axis NEVER gates** — every account age is `>= 0`, so `age < 0` can never be true and the axis
     *  drops out of both `missing` and the refusal note (see `antibot.resolveCashoutMaturity`).
     *  Why: wall-clock age costs a farm nothing but patience (register 480 accounts, wait a week, they ripen in
     *  PARALLEL), while it taxes the honest captain who found the game today and wants to feel the tap work. The
     *  axis that actually makes a sybil's cost LINEAR is MIN_PLAYED_HOURS — one live session per identity, which
     *  cannot be parallelised for free. Playtime is the anti-sybil defence now; age is not.
     *  The knob stays wired (nothing else changed) so the owner can re-arm it with a single number if a farm ever
     *  shows up that is willing to pay the playtime. NOTE: `TIER_RULES` (NEW/STANDARD/VETERAN) still keys off
     *  account age — that is the withdrawal LADDER, a different mechanism, deliberately untouched. */
    MIN_ACCOUNT_AGE_DAYS: 0,
    /** Criterion 2 — ACCUMULATED PLAYTIME (hours, `Profile.playedMs`, server-side only, never on the wire).
     *  Age alone is beatable by a farm that registers accounts and lets them ripen; a bot must additionally hold
     *  a live session per account for this long, which is where the sybil's cost actually becomes linear. */
    MIN_PLAYED_HOURS: 5,
    /** Criterion 3 — BASE PROGRESS: the captain's home base HQ level (starter base = 1, `BASE.HQ_MAX_LVL` = 10).
     *  Doubloons SPENT on the base are doubloons NOT cashed out, so a farm that qualifies has already burned a
     *  meaningful slice of what it came to extract. */
    MIN_HQ_LVL: 3,
    /** LEGACY FALLBACK — account age used when `Profile.createdMs` is ABSENT (profiles saved before that field
     *  existed — ~38% of the base). Those captains are OLD, so the missing stamp must read as veteran, never as
     *  "born a second ago". 10 years: unreachable by any real account, so it can never be confused with data. */
    LEGACY_ACCOUNT_AGE_DAYS: 3650,
    /** LEGACY FALLBACK — playtime credited to a profile saved before `playedMs` existed (grandfathered ONCE on
     *  its next login, mirroring the `roundStock` grandfather). Same rule: an existing captain must never be
     *  demoted to "0 hours played" by the arrival of the counter that measures it. */
    LEGACY_PLAYED_HOURS: 100,
  },
} as const;

/** Canonical doubloon-origin labels. ⚠️ REVISED 2026-07-15 (player-first): these are NO LONGER a market
 *  sellability class — every doubloon is sellable, capped only by CASHOUT.DAILY_ALLOWANCE_DBL. Provenance is
 *  now an INTERNAL signal of the risk graph ONLY (funding / trade-concentration / overpricing edges +
 *  telemetry) — never a visible class, never a solo block. `sorEconomy.tagOrigin` keeps recording every origin
 *  (metrics.originMinted) so the graph can read it; nothing here makes a balance "un-sellable". (The legacy
 *  claim-pool eligibility in sorEconomy is a SEPARATE, disarmed mechanism and is unaffected.) */
export const DBL_ORIGIN = {
  SELLABLE: 'sellable', PURCHASE: 'purchase', MARKET_BOUGHT: 'market_bought',
  INSURANCE: 'insurance', REFERRAL: 'referral', PLUNDER: 'plunder', CASINO: 'casino', STARTER: 'starter',
} as const;

/** The shared rarity ladder. `epic` (v50, gacha "A Draga") sits between rare and legendary — three tiers
 *  cannot sustain gacha pacing or an honest near-miss. Ordered low→high; the INDEX is what rides the wire (u8). */
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';
export const RARITY_ORDER: readonly Rarity[] = ['common', 'rare', 'epic', 'legendary'];
export function rarityIndex(r: Rarity): number { return RARITY_ORDER.indexOf(r); }
/** The ONE signature colour per rarity (hex), shared by the shop UI, the marketplace card and the Draga
 *  ritual's underwater glow: white foam → blue bioluminescence → kraken-ink purple → the-sea-turns-gold. */
export const RARITY_COLOR: Record<Rarity, number> = {
  common: 0xf0ead6, rare: 0x3aa0ff, epic: 0x9a4bd6, legendary: 0xf5c542,
};

/** A ship cosmetic slot. `hull`/`sail`/`flag` render today; `wake`/`cannonfx`/`figurehead`/`sigil` are the
 *  gacha EXPANSION kinds — typed + catalog-ready as a v50 SEAM, their bespoke render lands incrementally
 *  (a cosmetic of an unrendered kind is still ownable/tradeable, it just shows no new mesh yet). */
export type CosmeticKind = 'hull' | 'sail' | 'flag' | 'wake' | 'cannonfx' | 'figurehead' | 'sigil';
export interface Cosmetic {
  readonly id: number;
  readonly name: string;
  readonly kind: CosmeticKind;
  readonly color: number;   // hex tint applied to the hull / sails
  readonly rarity: Rarity;
  /** DBL price at the primary shop (unlimited supply → a sink). null = not shop-buyable (earn/trade only). */
  readonly shopPrice: number | null;
  readonly desc: string;    // flavor shown in the marketplace UI
  /** Icon path (generated art) under the client's public/; the swatch color is the render fallback. */
  readonly icon: string;
}

/** The cosmetic catalog (shared: server validates, client renders). id 0 per kind = default/none. */
export const COSMETICS: readonly Cosmetic[] = [
  { id: 1, name: 'Crimson Hull', kind: 'hull', color: 0x9c2b2b, rarity: 'common', shopPrice: 600, icon: '/cosmetics/1.webp', desc: 'Timbers stained the red of a thousand raids — let them see you coming.' },
  { id: 2, name: 'Emerald Hull', kind: 'hull', color: 0x1f7a4d, rarity: 'common', shopPrice: 600, icon: '/cosmetics/2.webp', desc: 'Deep-sea green lacquer, favored by smugglers who hug the shadowed shallows.' },
  { id: 3, name: 'Obsidian Hull', kind: 'hull', color: 0x23262e, rarity: 'common', shopPrice: 900, icon: '/cosmetics/3.webp', desc: 'Pitch-black boards that swallow the moonlight — a ghost on the night tide.' },
  { id: 4, name: 'Royal Azure Hull', kind: 'hull', color: 0x274a9c, rarity: 'rare', shopPrice: 2500, icon: '/cosmetics/4.webp', desc: "Naval-blue enamel with a captain's polish — noble lines on an outlaw's ship." },
  { id: 5, name: 'Golden Corsair Hull', kind: 'hull', color: 0xd9a441, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/5.webp', desc: 'Gilded planks fit for a pirate king. Only the boldest plunder enough to fly it.' },
  { id: 6, name: 'Sun-Bleached Hull', kind: 'hull', color: 0xc9b78f, rarity: 'common', shopPrice: 600, icon: '/cosmetics/6.webp', desc: 'Driftwood-pale planks scoured by a hundred crossings under a merciless sun.' },
  { id: 7, name: 'Verdigris Hull', kind: 'hull', color: 0x3e8f80, rarity: 'rare', shopPrice: 2500, icon: '/cosmetics/7.webp', desc: 'Copper sheathing gone sea-green — an old warship dragged back from the deep.' },
  { id: 8, name: 'Ember Hull', kind: 'hull', color: 0x2a1410, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/8.webp', desc: 'Charred black timbers still glowing at the seams. Forged in a burning port.' },
  { id: 11, name: 'Bone-White Sails', kind: 'sail', color: 0xf0ead6, rarity: 'common', shopPrice: 400, icon: '/cosmetics/11.webp', desc: 'Bleached canvas the color of a picked-clean skeleton. Cold, and unmistakable.' },
  { id: 12, name: 'Blood Sails', kind: 'sail', color: 0xb02a2a, rarity: 'common', shopPrice: 600, icon: '/cosmetics/12.webp', desc: 'Dyed deep red so the powder smoke never shows — the last thing a merchant sees.' },
  { id: 13, name: 'Storm-Grey Sails', kind: 'sail', color: 0x5a6472, rarity: 'common', shopPrice: 600, icon: '/cosmetics/13.webp', desc: "Slate canvas that vanishes into a squall — you're aboard before they know it." },
  { id: 14, name: 'Sherwood Green Sails', kind: 'sail', color: 0x00c805, rarity: 'rare', shopPrice: 2500, icon: '/cosmetics/14.webp', desc: 'Sherwood green on the open sea — steal from the rich, run with the wind.' },
  { id: 15, name: 'Kraken-Ink Sails', kind: 'sail', color: 0x2a1f4a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/15.webp', desc: 'Canvas stained with abyssal ink, said to whisper when the deep stirs.' },
  { id: 16, name: 'Sunset-Gold Sails', kind: 'sail', color: 0xd9a441, rarity: 'common', shopPrice: 600, icon: '/cosmetics/16.webp', desc: 'Warm gold canvas that catches the dying light — a beautiful thing to be robbed by.' },
  { id: 17, name: 'Amethyst Sails', kind: 'sail', color: 0x6a4a9c, rarity: 'common', shopPrice: 600, icon: '/cosmetics/17.webp', desc: 'Deep royal purple, dyed with a fortune in stolen sea-snails. Vanity, and pride.' },
  { id: 18, name: 'Ghost Sails', kind: 'sail', color: 0xcfd8dc, rarity: 'rare', shopPrice: 2500, icon: '/cosmetics/18.webp', desc: 'Threadbare, half-translucent canvas. They say the ship it came from never made port.' },
  // ---- flags / emblems (a rendered emblem on the masthead, not just a colour) ----
  { id: 20, name: 'Jolly Roger', kind: 'flag', color: 0x14161a, rarity: 'common', shopPrice: 500, icon: '/cosmetics/20.webp', desc: 'The classic white skull and crossed bones on black. No colours to strike — no quarter given.' },
  { id: 21, name: 'Crossed Sabres', kind: 'flag', color: 0x14161a, rarity: 'common', shopPrice: 500, icon: '/cosmetics/21.webp', desc: 'Two bared cutlasses over black. A promise, not a warning.' },
  { id: 22, name: 'Kraken Sigil', kind: 'flag', color: 0x14161a, rarity: 'rare', shopPrice: 2500, icon: '/cosmetics/22.webp', desc: 'A coiled kraken clutching a shattered hull. Flown by those who court the deep.' },
  { id: 23, name: 'Compass Rose', kind: 'flag', color: 0x0e1c33, rarity: 'common', shopPrice: 700, icon: '/cosmetics/23.webp', desc: 'A gilded compass rose on midnight blue — for the captain who is never truly lost.' },
  { id: 24, name: 'Blood Cross', kind: 'flag', color: 0x14161a, rarity: 'common', shopPrice: 500, icon: '/cosmetics/24.webp', desc: 'A dripping crimson cross. Some crews pray to it; most just fear it.' },
  { id: 25, name: 'Crowned Skull', kind: 'flag', color: 0x14161a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/25.webp', desc: 'A skull crowned in gold. Worn only by those who have ruled a stretch of sea.' },
  // ---- fishing jackpot (2b): the 1/500 "legendary figurehead", pulled from the deep. Tradeable on the marketplace. ----
  { id: 27, name: "Angler's Sigil", kind: 'flag', color: 0x123a3a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/27.webp', desc: 'A drowned figurehead hauled up on a fishing line — one in five hundred casts, they say. The deep gives up its own.' },
  // ---- Founders' Fleet NFT livery: granted by holding the on-chain NFT, never sold, never tradeable in-game ----
  { id: 9, name: "Founder's Ebony", kind: 'hull', color: 0x141210, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/9.webp', desc: 'Ebony timbers trimmed in true gold. The shipyard built exactly three hundred and five — then burned the plans.' },
  { id: 19, name: "Founder's Black Sails", kind: 'sail', color: 0x17151a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/19.webp', desc: 'Black canvas woven with a golden crest. The founding fleet answers to no flag but its own.' },
  { id: 26, name: "Founders' Standard", kind: 'flag', color: 0x14161a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/26.webp', desc: 'The gold-on-black standard of the founding fleet. Held, not bought — it follows the charter on the chain.' },
  // ---- Gacha "A Draga" exclusives (v50) — epic (kraken-ink purple) + legendary (the sea reacts) sets.
  //      Rendered via the existing hull/sail/flag tint; earned from the Smuggler's Chest or the Shard Shop. ----
  { id: 30, name: 'Kraken-Ink Hull',      kind: 'hull', color: 0x4a2f6e, rarity: 'epic',      shopPrice: null, icon: '/cosmetics/30.webp', desc: 'Timbers steeped in abyssal ink — the purple the deep bleeds when a kraken turns.' },
  { id: 31, name: 'Voidscale Hull',       kind: 'hull', color: 0x2c3f52, rarity: 'epic',      shopPrice: null, icon: '/cosmetics/31.webp', desc: 'Overlapping leviathan scales, slate and violet, that drink the moonlight whole.' },
  { id: 32, name: 'Wraith Sails',         kind: 'sail', color: 0x6fae9a, rarity: 'epic',      shopPrice: null, icon: '/cosmetics/32.webp', desc: 'Spectral canvas half-out of this world — a green glow that shouldn\'t be there.' },
  { id: 33, name: 'Kraken-Eye Standard',  kind: 'flag', color: 0x3a2258, rarity: 'epic',      shopPrice: null, icon: '/cosmetics/34.webp', desc: 'A single lidless eye of the deep, inked in purple. It watches the sea back.' },
  { id: 35, name: 'Leviathan Hull',       kind: 'hull', color: 0x1a2b3a, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/35.webp', desc: 'Forged from the ribs of the thing under the trench. The sea itself pays it respect.' },
  { id: 36, name: 'Solar Corsair Sails',  kind: 'sail', color: 0xf2c14e, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/36.webp', desc: 'Sailcloth spun from captured dawn — you are the sunrise the merchant fleet dreads.' },
  { id: 37, name: 'Ghost Fleet Standard', kind: 'flag', color: 0xbfe6d8, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/37.webp', desc: 'The colours of a fleet that never made port. Fly it and the drowned salute you.' },
  // ---- Sinks P1: the Black Pearl sink. A 0-stat, permanent PRESTIGE hull, CRAFTED from black pearls at the
  //      shipwright (never dropped, never shop-bought) — the same "the deep gives up its own" precedent as the
  //      1/500 Angler's Sigil. Rendered via the existing hull tint (nacre pearl-white); excluded from the gacha
  //      pool (CRAFT_ONLY_COSMETIC_IDS) so it stays a craft-exclusive brag. ----
  { id: 40, name: 'Pearl-Inlaid Hull', kind: 'hull', color: 0xe8e2d0, rarity: 'legendary', shopPrice: null, icon: '/cosmetics/40.webp', desc: 'Timbers inlaid with a fortune in black pearls, polished to a nacreous sheen. Hauled from the deep, one pearl at a time.' },
] as const;

/** Cosmetic id of the Pearl-Inlaid Hull, granted by the black-pearl craft (Sinks P1). */
export const PEARL_INLAID_COSMETIC = 40;
/** Cosmetics that are CRAFT-EXCLUSIVE (never enter the gacha pool nor the shop) — the crafted prestige pieces. */
export const CRAFT_ONLY_COSMETIC_IDS: ReadonlySet<number> = new Set([PEARL_INLAID_COSMETIC]);

/** Cosmetic ids granted by the Founders' Fleet NFTs (soulbound in-game: the NFT itself is the tradeable). */
export const NFT_COSMETICS = { HULL: 9, SAIL: 19, FLAG: 26 } as const;
export const NFT_COSMETIC_IDS: ReadonlySet<number> = new Set([9, 19, 26]);

export function cosmeticById(id: number): Cosmetic | undefined {
  return COSMETICS.find((c) => c.id === id);
}

/** ---- Bases ("casas") — persistent player islands ---- */
export const BASE = {
  /** N x N build grid. */
  GRID: 8,
  /** Meters per grid cell (for visual layout + building footprints). */
  CELL_SIZE: 6,
  /** Water radius around the island that counts as "at this base" (raid/bank proximity). */
  WATER_RADIUS: 75,
  /** Doubloon storage capacity at storage level 1, +per level. */
  STORAGE_BASE_CAP: 2000,
  STORAGE_CAP_PER_LVL: 2500,
  /** Protection window (seconds) granted to a base after it is successfully raided. */
  SHIELD_AFTER_RAID_S: 1800,
  /** Damaged-building HP regen per second (TURRETS only — HP is a turret-only concept, see buildingMaxHp). */
  BUILDING_REGEN_PER_S: 0.6,
  /** HQ level gates everything else. */
  HQ_MAX_LVL: 10,
  /** farms-demand-first: how far above the HQ's REAL level any other building may be UPGRADED —
   *  building target level ≤ min(HQ_MAX_LVL, hqLvl + this). 1 = "one level ahead of the HQ", which makes
   *  the long-standing HQ tooltip promise true: the HQ is the spine every other building climbs along.
   *  Existing over-gate buildings are grandfathered (never demoted) — only NEW upgrades respect the gate. */
  BUILDING_LVL_OVER_HQ: 1,
  /** Dock building level at which the open slip becomes an ENCLOSED DRY-DOCK that hides the
   *  owner's parked ship from every other player's snapshot (info / anti-ambush). lvl 1 = open jetty. */
  DRYDOCK_LVL: 2,
  /** Seconds the ship must stay continuously docked before the dry-dock "closes" and the ship
   *  vanishes from others — prevents an instant blink-out while an attacker is right on top of you. */
  DRYDOCK_CLOSE_S: 2,
  /** Max hull speed (m/s) that still counts as "berthed" — you must actually stop to dock, so a
   *  full-speed pass through your own ring never grants instant safe-harbor immunity. */
  DOCK_MAX_SPEED: 1.2,
} as const;

/**
 * ---- Base placement rules (Onda Fix 3) ----
 * Where a NEW captain's home island may be planted, and what makes an EXISTING persisted base valid.
 * Motivation (owner-reported bug): a player's base was spawned right at the map limit and the ship "bugs out
 * against the edge of the world and can't even move" — the base landed in the 250 m grid-edge row, and
 * `spawnNearBase` pushes the ship ~46 m FURTHER toward the border, dropping it at ~204 m: inside the old
 * 220 m come-about band, bow pointing out → an instant spin loop. With the band now minimal (WORLD_EDGE),
 * `EDGE_CLEARANCE` guarantees the anchor — and therefore the seaward spawn point — sits clear of it with
 * maneuvering room, WITHOUT reserving a large strip (owner: "do not enlarge the reserved area"). The other
 * clearances stop a base being planted inside/atop an island, an outpost, or another base (the historical
 * slot grid checked outposts but NOT treasure islands). Distances are centre-to-centre in metres. The
 * selection search is fully deterministic (a fixed grid of slots, no rng) so the same world always lays out
 * the same bases; an unsatisfiable search falls back to the first free slot, never an infinite loop. */
export const BASE_PLACEMENT = {
  /** Min metres from any world edge to a base ANCHOR. Must clear the WORLD_EDGE come-about band PLUS the
   *  ~46 m seaward spawn offset PLUS maneuvering slack, so a ship spawning or mooring at the dock is never
   *  inside the override band — but kept modest (≈ MARGIN + ~100 m) so the reserved strip stays small. */
  EDGE_CLEARANCE: 160,
  /** Min centre-to-centre metres from a treasure island (island radius 45 + base footprint ~30 + slack), so
   *  a base never overlaps island collision or wedges a hull between two shores. */
  MIN_ISLAND_DIST: 130,
  /** Min centre-to-centre metres from an outpost centre (matches the slot grid's own outpost keep-out:
   *  OUTPOST_RADIUS 90 + 120). */
  MIN_OUTPOST_DIST: 210,
  /** Min centre-to-centre metres between two player bases (< the 260 m slot pitch, so adjacent grid slots are
   *  always valid, but a relocated/off-grid base still keeps clear water from its neighbours). */
  MIN_BASE_DIST: 200,
} as const;

/** Auto-defense turret (server-side; reuses the cannonball sim). */
export const TURRET = {
  RANGE: 220,
  RELOAD: 3.0,
  /** Flood/leak damage dealt to an attacking ship per hit (stiffened so an absent-owner base still bites). */
  LEAK_RATE: 0.06,
  HP_BASE: 120,
  HP_PER_LVL: 80,
  BALL_SPEED: 65,
  /** Seconds a turret stays offline after its HP is depleted (then it regens). */
  DISABLE_S: 12,
  /** RETALIATION-ONLY defense: a base's turrets fire *only* at a ship that has attacked THIS base —
   *  hit one of its buildings, or fired on the owner in home waters — within this many seconds. A ship
   *  merely sailing past is NEVER engaged. (At 249 CCU bases nest close together; proactive auto-attack
   *  was sinking innocent traffic → churn. Turrets now defend, they don't hunt.) */
  RETALIATE_WINDOW_S: 18,
} as const;

/** Idle resource generation. */
export const FARM = {
  /** Doubloons/sec generated into storage at farm level 1, +per level. */
  PER_S_BASE: 0.6,
  PER_S_PER_LVL: 0.5,
  /** Offline accrual clamp (seconds). A base that sat unattended pays out AT MOST this many seconds of
   *  idle farm on the next login — an absence of days can never dump a retroactive fortune (the "anchor
   *  and disappear" inflation vector; the warehouse cap makes long absences moot anyway — it fills and
   *  stops). 8h = one overnight/workday session, generous but bounded.
   *
   *  Historical note (farms-demand-first, v54): the old passive-farm doubloon soft cap
   *  (FARM.DAILY_CAP / FARM.SOFT_CAP / farmSoftCapAward / farmTierOf / Profile.farmEarned) was REMOVED —
   *  it had been orphaned since Cargo Runs v48 turned the farm into a CARGO producer (delivery is the
   *  sole mint; the meter never incremented, the snapshot tier was always 0). git remembers. */
  OFFLINE_MAX_S: 8 * 3600,
} as const;

/** Live raid economics (server-authoritative; see V1_RAID_DESIGN §6/§7). */
export const RAID = {
  /** Fraction of CURRENT stealable storage taken per second while adjacent to + damaging the storage. */
  STEAL_TICK_PCT: 0.05,
  /** Storage fraction that can NEVER be stolen (base is never wiped / protects the poor). */
  PROTECTED_FRACTION: 0.25,
  /** Absolute floor that can never be stolen — it protects the poor: a captain below it is simply not worth
   *  raiding, so a fresh/broke estate is never farmed for its last coins. It used to be justified as "equals
   *  the starter grant, so Sybil victim farms can never harvest fresh-account seed money"; with
   *  `ECONOMY.STARTER_VAULT` at 0 there IS no seed money to harvest, and the floor now stands purely on the
   *  protect-the-poor rationale (kept at 1.200 on purpose — raiding is redistribution, not a faucet, so this
   *  number never mints anything). */
  MIN_PROTECTED_FLOOR: 1200,
  /** Attacker cargo hold at cargo level 1, +per level. */
  CARGO_BASE: 600,
  CARGO_PER_LVL: 500,
  /** Per-account/day cap on stolen doubloons (anti-farm). */
  DAILY_STEAL_CAP: 8000,
  /** Doubloons/sec drained from the hold into your base while banking (must be berthed). A galleon hold
   *  (~1600) takes ~4s — long enough that a chaser can catch and sink you mid-deposit. */
  BANK_RATE: 400,
  /** How long the storehouse stays "cracked" (stealable) after a cannon hit — you must bombard to loot. */
  STORAGE_CRACK_S: 6,
  /** Live raid pays the FULL RAID_SUCCESS trophy; this per-base cooldown gates the reward + victim warn. */
  RAID_REWARD_COOLDOWN_MS: 60_000,
  /** Fraction of a sunk laden ship's cargo the KILLER salvages into their own hold (predation pays). */
  SALVAGE_FRACTION: 0.5,
  /** Cargo (as a fraction of the hold cap) above which a ship flies the LADEN cue — a juicy target. */
  LADEN_FRACTION: 0.5,
  /** Async (offline) raids — an instant, server-resolved fallback vs a base's STATIC defenses
   * (the "fallback when the live sea is quiet"). Same steal math, but closed-form (no sailing). */
  ASYNC_MAX_SECONDS: 14,     // cap on the simulated time-on-target
  ASYNC_EFFICIENCY: 0.45,    // async loot = 45% of the closed-form steal (no sailing risk -> lower reward)
  ASYNC_DAILY_FRACTION: 0.4, // async can only supply this fraction of the daily steal cap — live raiding is the point
  ASYNC_COOLDOWN_MS: 45_000, // minimum gap between one raider's async raids
  ASYNC_RETRY_COOLDOWN_MS: 5_000, // shorter throttle after a wasted scan (no target / clan-mate / cap) — anti-spam but lets legit retries
  ASYNC_TURRET_DPS: 0.045,   // flood/sec each ACTIVE turret lands on the async raider (count-based defense)
  ASYNC_REPEL_SECONDS: 4,    // time-on-target below this = the raid is repelled (negligible loot)
} as const;

/**
 * ---- Cargo Runs (economy V1 — the logistics loop) ----
 * "Wealth only exists once it reaches port — and it may never arrive." The passive Farm no longer mints
 * doubloons; it produces CARGO UNITS into the base warehouse (bounded by storageCap, STOPS when full). The
 * captain LOADS the warehouse into the ship hold (bounded by the real cargo cap, `shipStats().cargo`), sails
 * a laden (slower) ship, and DELIVERS at one of the three outposts — the ONLY doubloon mint. Longer base→port
 * hauls pay more (they risk more). Sinking laden loses the whole load; a share is salvaged by the killer.
 *
 * ALL knobs live here (single source of truth). The weight→speed CURVE is consumed by `cargoSpeedMul()` in
 * @piratas/sim (folded into `stepShipBody`, so server + client prediction agree byte-for-byte); the payout
 * math is `cargoDistanceMult()` / `cargoDeliveryValue()` below (pure, shared by the server's delivery and the
 * client's port payment preview — one source, zero drift).
 */
export const CARGO_RUN = {
  /** Doubloons paid per delivered cargo unit — the MASTER calibration knob (target ~2–4k ⓓ/day median). */
  VALOR_UNIT: 0.12,
  /** Base→delivery-port distance zone boundaries (metres). <NEAR = green, NEAR..FAR = yellow, >FAR = red. */
  DIST_ZONES: { NEAR: 2500, FAR: 5000 },
  /** Payout multiplier per zone. Locked by the risk math: with ~30% chance of losing the load in the red, the
   *  far haul must pay > 1.43× the near just to break even on expected value — 2.0× covers the loss + a premium,
   *  0.9× at a port glued to your base resolves the base-colada edge for free. */
  MULTS: { NEAR: 0.9, MID: 1.5, FAR: 2.0 },
  /** +5% for delivering a FULL hold (>= FULL_LOAD_FRAC of the cargo cap) — pushes the "fill up and go" optimum. */
  FULL_LOAD_BONUS: 1.05,
  FULL_LOAD_FRAC: 0.98,
  /**
   * Non-linear top-speed penalty by cargo-load fraction (0..1). Piecewise-linear + continuous, steeper at the
   * top ("o topo dói"): 0→LIGHT_UNTIL ramps 0→LIGHT_PENALTY, then →MID_UNTIL ramps to MID_PENALTY, then →1.0
   * ramps to HEAVY_PENALTY. Endpoints match the spec bands: 0–50% ⇒ −5%, 50–80% ⇒ −15%, 80–100% ⇒ −40%.
   */
  WEIGHT_SPEED_CURVE: {
    LIGHT_UNTIL: 0.5, LIGHT_PENALTY: 0.05,
    MID_UNTIL: 0.8,   MID_PENALTY: 0.15,
    HEAVY_PENALTY: 0.40,
  },
  /** Sink loss: fraction of a lost load's UNITS salvaged to the last attacker. V1 FALLBACK for the blueprint's
   *  "disputable physical loot" (a true contested sea-drop is a V2 upgrade): the killer must sail the salvage to
   *  a port and DELIVER it, so it still mints only through the normal gate. The remainder is destroyed
   *  (deflationary). Tunable 0.30–0.50 in playtest. */
  LOOT_PCT: 0.40,
  /**
   * ---- V1.5 SEAM: pre-departure cargo insurance (paid in $SOR, lights up with the token) ----
   * V1 ships with NO payment and NO refund — this is the documented HOOK only. When $SOR activates: cover one
   * tier per voyage (25/50/75%, never 100%), bought only at port, consumed on sink → the covered fraction of the
   * lost load's value is refunded in doubloons. The armer + the sink refund branch are stubbed in room.ts
   * (`armCargoRunInsurance`) behind `ENABLED`, so flipping this on is the whole activation. */
  INSURANCE: { ENABLED: false, TIERS: [0.25, 0.5, 0.75] as readonly number[] },
} as const;

/** Payout multiplier for a delivery whose BASE→PORT great-circle distance is `dist` metres (pure; shared by the
 *  server's delivery + the client's preview so they can never disagree). */
export function cargoDistanceMult(dist: number): number {
  if (dist < CARGO_RUN.DIST_ZONES.NEAR) return CARGO_RUN.MULTS.NEAR;
  if (dist < CARGO_RUN.DIST_ZONES.FAR) return CARGO_RUN.MULTS.MID;
  return CARGO_RUN.MULTS.FAR;
}

/** Doubloons paid for delivering `units` cargo (loaded from a hull of capacity `cargoCap`) at a port `dist`
 *  metres from the captain's base. The single source of the delivery payout — the server credits exactly this,
 *  the client previews exactly this. Full-hold bonus applies when the delivered load ~fills the cap. */
export function cargoDeliveryValue(units: number, dist: number, cargoCap: number): number {
  if (units <= 0) return 0;
  const full = cargoCap > 0 && units >= cargoCap * CARGO_RUN.FULL_LOAD_FRAC;
  return Math.round(units * CARGO_RUN.VALOR_UNIT * cargoDistanceMult(dist) * (full ? CARGO_RUN.FULL_LOAD_BONUS : 1));
}

/** Territory control — the sea is split into a GRID×GRID of regions; clans accrue influence there by
 *  killing + raiding, and the top-influence clan "controls" the region (flies its tag + a home-turf steal bonus). */
export const TERRITORY = {
  GRID: 2,                    // 2×2 = 4 regions
  INFLUENCE_PER_KILL: 5,
  INFLUENCE_PER_RAID: 12,
  DECAY_PER_TICK: 0.97,       // influence bleeds each LB tick (~12s) so control must be maintained (~5min half-life)
  MIN_TO_CONTROL: 30,         // below this a region is uncontested (no tag flown)
  CONTROL_STEAL_BONUS: 1.25,  // raiding in a region YOUR clan controls steals 25% faster (home-turf edge)
} as const;

/** Display names for the 4 territory regions (index 0..3 = cy*GRID+cx), shown in the Capital's clan hall. */
export const REGION_NAMES: readonly string[] = ['The Western Shallows', 'The Eastern Reach', 'The Southern Deep', 'The Northern Straits'];

/** Which region (0..GRID²-1) a world point falls in. */
export function regionOf(x: number, y: number): number {
  const g = TERRITORY.GRID, cell = WORLD_SIZE / g;
  const cx = Math.max(0, Math.min(g - 1, Math.floor(x / cell)));
  const cy = Math.max(0, Math.min(g - 1, Math.floor(y / cell)));
  return cy * g + cx;
}

/** ---- Daily contracts: the casual player's guided path to gold ("how do I earn?") ----
 * PER_DAY templates are drawn deterministically per profile per day; progress is tracked server-side
 * and the reward auto-claims (gold, still routed through the daily mint cap). */
export const ContractType = { SINK: 0, STEAL: 1, CHEST: 2, BANK: 3 } as const;
export type ContractType = (typeof ContractType)[keyof typeof ContractType];
export interface ContractTemplate { t: ContractType; goal: number; reward: number; }
export const CONTRACTS = {
  PER_DAY: 3,
  /** Contract types a lone newcomer can finish WITHOUT any other player — a pure PvE loop that is always
   *  available regardless of server population (dig/sell treasure + bank carried loot). ensureContracts
   *  guarantees at least one of these is in every day's draw, so the board is never an all-PvP dead end.
   *  (SINK also counts NPC marauders now, but marauders only spawn at low population, so it is not a
   *  population-independent guarantee — CHEST/BANK are.) */
  SOLO_PVE_TYPES: [2, 3] as readonly ContractType[], // CHEST, BANK
  TEMPLATES: [
    { t: 0, goal: 2, reward: 400 },    // sink 2 ships (players OR NPC marauders — PvE-completable when marauders sail)
    { t: 1, goal: 800, reward: 350 },  // steal 800 doubloons in live raids (PvP: needs another captain's base)
    { t: 2, goal: 2, reward: 300 },    // sell 2 treasure chests (solo PvE)
    { t: 3, goal: 1000, reward: 300 }, // bank 1000 of carried loot at your dock (solo PvE)
  ] as readonly ContractTemplate[],
} as const;

/** Treasure-island chests — a randomized GOLD haul (personal currency), banked instantly at an outpost.
 *  Scales the PvE loop instead of a flat trinket. Kept in the GOLD economy, separate from raid doubloons. */
/** ---- Premium storefront (docs/business/SOR_UTILITY.md) ---- NO fiat. Two tiers:
 *  • Doubloon tier (LIVE): in-game convenience bought with banked doubloons (`dbl`). The server debits
 *    base.doubloons and applies the effect immediately — same debit path as base upgrades / clan create.
 *    Anchor: an active captain banks ~1–5k doubloons/day, so these are an evening's plunder, not a wall.
 *  • $SOR tier (opens at the TGE): prestige / packs priced in $SOR (`sor`), gated behind the (OFF) $SOR
 *    gate. Every $SOR payment splits BURN_SHARE burned / the rest to the treasury; the on-chain charge
 *    verifier plugs in at the token launch. The round $SOR values here are PLACEHOLDERS — tune freely. */
export const PREMIUM = {
  BURN_SHARE: 0.5,
  TOURNAMENT_RAKE: 0.10,
  WAGER_RAKE: 0.07,
  /** Fraction of a sunk INSURED hold refunded straight to the owner's bank. */
  INSURANCE_RECOVERY: 0.5,
  ACTIONS: {
    // --- Doubloon tier (LIVE — bought with in-game doubloons) ---
    FARM_BOOST_24H: { dbl: 2500, mul: 2, hours: 24 },
    CARGO_INSURANCE: { dbl: 1500 },
    CONTRACT_REROLL: { dbl: 800 },          // 1 free reroll/day, then this many doubloons per extra reroll
    CONTRACT_EXTRA_SLOT: { dbl: 1500 },
    NAME_CHANGE: { dbl: 2500 },
    BASE_RELOCATE: { dbl: 3500 },
    // --- $SOR tier (opens at the TGE, gated OFF; values are round placeholders) ---
    // Packs stay the instant, no-haggle path priced ABOVE the P2P doubloon market (the player marketplace
    // remains the PRIMARY source of bought doubloons); they settle in $SOR so real value flows to the token.
    // ⚠️ PLACEHOLDERS ONLY — nothing reads these. The LIVE paid catalog is `STORE.PRODUCTS` below, and
    // VIP / HIGH_STAKES / WAR were DELISTED from it in 2026-07-26 (U-1) because no effect existed. Never
    // wire a checkout to these numbers: add the effect first, then list it in STORE.PRODUCTS.
    DOUBLOON_PACK_S: { sor: 5, doubloons: 6000 },
    DOUBLOON_PACK_L: { sor: 20, doubloons: 30000 },
    VIP_MONTHLY: { sor: 10, farmBonus: 0.15 },
    HIGH_STAKES_ENTRY: { sor: 5, lootMul: 2 },
    WAR_DECLARATION: { sor: 25 },
  },
} as const;

/** ---- SOR Store (Phase 1 $SOR monetization) ---- The catalog + payment economics of the paid store.
 *  This is the SINGLE source of truth for the store's products, shared by the client UI and the server.
 *  Prices are in WHOLE $SOR; `id` is the internal `productId` stamped on the order. QUANTITIES and PRICES
 *  are overridable per-product by server env (`SOR_STORE_<KEY>_SOR` / `_DBL` / `_DAYS` / `_HOURS` /
 *  `_VOUCHERS`). `kind` selects the delivery effect the server applies (idempotently, outside the daily
 *  cap, origin PURCHASE). The store is DISARMED until `SOR_STORE_ENABLED=1` + the recipient wallet are set.
 *
 *  ⚓ SOLANA (2026-08-22): the token is an SPL mint created on pump.fun — there is NO contract of ours on
 *  chain, so there is no Purchase event and no burn/treasury split enforced by code. Checkout is a Solana
 *  Pay TRANSFER REQUEST: the server mints an order carrying a unique `reference` pubkey, the wallet sends a
 *  plain SPL transfer to the recipient tagged with that reference, and the backend finds it with
 *  `getSignaturesForAddress(reference)`. Treasury/burn policy is an OFF-CHAIN owner action now. */
export const STORE = {
  /** Share of store revenue the owner burns by hand after the fact (the token has no burn hook of ours).
   *  Display/economics only — nothing on chain enforces it. */
  BURN_SHARE: 0.5,
  /** Commitment the backend requires before delivering a purchase — env `SOR_CONFIRMATIONS` overrides with
   *  the literal `confirmed` or `finalized`. (v57: was a block-confirmation COUNT on an EVM chain; Solana
   *  expresses reorg safety as a commitment level, so the knob changed type, not purpose.) */
  DEFAULT_COMMITMENT: 'confirmed',
  /** Housekeeping only: a pending (unpaid) order is marked expired after this window. Payment idempotency
   *  is keyed on `orderId` + an on-chain-order stamp, NEVER on time — an order paid late still delivers
   *  (U-2: `ingest` revives an expired order the moment its Purchase shows up). */
  ORDER_TTL_MS: 30 * 60_000,
  /** U-4: how many orders one profile may hold OPEN (not delivered). The 6th mint recycles that profile's
   *  oldest STALE order; if all of them are still fresh (< ORDER_TTL_MS) the mint is refused instead — so a
   *  loop on `/store/order` can never grow `sor-store.json` without bound, and a fresh order (the one a real
   *  buyer is about to pay) is never dropped from under him. */
  MAX_OPEN_ORDERS_PER_PROFILE: 6,
  /** U-4: how long a never-delivered order is kept for reconciliation before housekeeping deletes it. Long
   *  on purpose — deleting an unpaid order makes a later payment unrecoverable ('unknown-order'), so this is
   *  the outer bound of the U-2 promise, not a business rule. DELIVERED orders are kept (audit trail). */
  ORDER_RETENTION_MS: 30 * 86_400_000,
  /** How many OPEN orders the poller checks against the RPC in one pass. Each check is one
   *  `getSignaturesForAddress(reference)` (+ a `getTransaction` when something is found), so this is the
   *  RPC-rate brake: the remaining orders are picked up on the next poll (the cursor is per-order now —
   *  there is no global chain cursor to fall behind, so lagging costs latency, never a lost payment). */
  MAX_ORDER_CHECKS_PER_POLL: 40,
  /** How long a PAID purchase whose delivery keeps failing (e.g. the buyer has no base yet) is retried from
   *  the order ledger. After this it is flagged for manual delivery in `/admin/store` (`stuck`) — the
   *  payment is never silently forgotten, and the poller never wedges. */
  DELIVERY_RETRY_WINDOW_MS: 7 * 86_400_000,
  /** ⛔ U-8 — THE ANTI-ARBITRAGE MARGIN, and the owner's one calibration knob for it.
   *  The store MINTS ⓓ into the bank; the exchange lets ANY banked ⓓ out for $SOR (no origin class since the
   *  single-doubloon change). So a pack that costs LESS per ⓓ than the book pays is free money, linear in
   *  sybil accounts. This is the convenience tax the pack must carry OVER what a ⓓ fetches on the book at the
   *  top of the price band, net of the 6% fee — the "~30% above P2P" of docs/business/SOR_UTILITY.md, now a
   *  number the server refuses on instead of prose. `checkPackArb()` (storearb.ts) is the arithmetic;
   *  `SorStore.createOrder` refuses a violating pack and `/admin/store` reports it.
   *  Raise it for a fatter cushion; NEVER set it below 0 (that authorises the round-trip). */
  MIN_MARKUP_OVER_BOOK: 0.30,
  /** ⚓ THE CAPTAIN LICENSE IS AN ORDER TOO (2026-08-22). It is deliberately NOT in `PRODUCTS` below — it is
   *  not a vitrine row, it mints no ⓓ, and it can never be resold on the book, so the U-8 arbitrage guard
   *  has nothing to say about it (`checkPackArb` only ever looks at `kind === 'doubloons'`). What it DOES
   *  share is the whole payment rail: the server mints it as a normal `StoreOrder` with its own Solana Pay
   *  `reference`, the same poller finds the transfer by that reference, the same used-signature ledger makes
   *  the payment spendable exactly once, and delivery hands off to `SorEconomy.grantLicense`. The price is
   *  NOT here: it is `CAPTAIN_LICENSE_PRICE_SOR` (server env), the same number the S_SOR panel publishes.
   *  These two constants exist only so the client and the server cannot disagree on how to NAME the order. */
  LICENSE_KEY: 'LICENSE',
  /** Internal productId of the license order. 1/2 are the packs; 3/4/5 are BURNED (delisted U-1 products
   *  whose ids may never be reused); 100+ is the cosmetics seam. 10 is the non-catalog lane. */
  LICENSE_PRODUCT_ID: 10,
  /** ⛔ LAW (U-1, red-team 2026-07-26): a product may only be LISTED here if `storeDelivery.ts` grants an
   *  effect that some OTHER line of the server/protocol/client actually READS. Selling a product whose
   *  delivery writes a field nobody consumes is selling nothing — the money is burned on-chain and the game
   *  state does not change. `storeCatalog.test.ts` enforces both halves of this (every listed `kind` has a
   *  delivery branch; every entitlement the delivery writes has a reader). Do not re-list on a promise. */
  PRODUCTS: {
    // Doubloon packs settle in $SOR and credit the base bank OUTSIDE the daily cap (origin PURCHASE).
    // PACK_L carries a +25% doubloons/$SOR bonus over PACK_S (625 vs 500 ⓓ per $SOR).
    // Raster icons (GPT-authored, alpha-cut, /icons/store/) — rendered via itemIcon() in the client
    // vitrine; purely cosmetic (labels/blurbs/icons never ride on-chain and the store stays env-gated).
    PACK_S:      { id: 1, kind: 'doubloons',   sor: 5,  doubloons: 2500,  icon: '/icons/store/pack_s.webp', label: 'Doubloon Pouch',  blurb: '2,500 doubloons — skip the grind' },
    PACK_L:      { id: 2, kind: 'doubloons',   sor: 20, doubloons: 12500, icon: '/icons/store/pack_l.webp', label: 'Doubloon Chest',  blurb: '12,500 doubloons — +25% bonus' },
    // ---- DELISTED 2026-07-26 (U-1) — three products (40 of the 65 $SOR on the menu) were VAPOR ----
    // They were sold, burned 50% of the $SOR on-chain, wrote an entitlement field, and NOTHING in the game
    // read it. Product ids 3/4/5 are BURNED (never reuse them: an old on-chain Purchase carries the id).
    // Re-listing requires the effect to exist FIRST — precisely:
    //   • VIP_90D      (id 3, 10 $SOR, kind 'vip')         needs `entitlements.vipUntilMs` read by the farm
    //     accrual (`room.ts` accrueFarm → the +15% yield), by the daily-contract roll (an extra CONTRACT),
    //     and by the NAME_BADGE byte on EV_SPAWN (the badge other captains see = a wire bump).
    //   • HIGH_STAKES  (id 4, 5 $SOR, kind 'high_stakes')  needs `entitlements.highStakesUntilMs` read by
    //     `resolveAsyncRaid`/`RAID.STEAL_TICK_PCT` (the 2× loot) AND the stated downside (lose the stake on
    //     sinking) implemented — plus a raid sub-cap review, since 2× loot doubles a real faucet.
    //   • CLAN_WAR     (id 5, 25 $SOR, kind 'war')         needs a war OPCODE (`ClanOp.DECLARE_WAR` = wire
    //     bump), a war state machine on the clan record, and `entitlements.warVouchers` spent by it. The
    //     only "Clan War" that exists today is the free Sunday calendar event (`WorldEventId.CLAN_WAR`).
    // SKINS SEAM (Phase 1.x): add e.g. SKIN_KRAKEN: { id: 100, kind: 'cosmetic', sor: 8, cosmeticId: <COSMETICS id>, ... }
  },
} as const;
export type StoreProductKey = keyof typeof STORE.PRODUCTS;

/* =========================================================================================
 *  GACHA — "A Draga" (Baús do Naufrágio). Spec: docs/design/GACHA.md. THE single source of the
 *  odds/pity/prices/catalog. V1 = the ⓓ trail (Baú do Contrabandista); the $SOR trail (Baú Afundado)
 *  is a documented SEAM (GACHA.SOR_CHEST, DISARMED). Rolls are 100% server-side; the client only
 *  dramatises the result. Balance is skill-based LAW: cosmetics 0-stat forever, amulets are a ±5%
 *  TRADEOFF only (never dano/flood/reload/HP), ships are stat-identical variants + ⓓ-ladder deeds.
 * ========================================================================================= */

/** ---- Amuletos de Bordo — the ONE functional slot the gacha may touch (the "Rocket Boots", domado). ----
 *  RULES (docs/design/GACHA.md §3, LOCKED): one slot, no stacking, combat effects are a ±AMULET_MAX_DELTA
 *  tradeoff PAIR only, and every amulet is ALSO buyable in ⓓ (gacha is a shortcut, never a wall). The
 *  forbidden zone — damage / flood / reload / HP — has NO entry here, by design. `plus`/`minus` are
 *  fractions (0 = pure QoL, no downside). Combat amulets (`speed_turn`/`turn_speed`) feed the ship sim via
 *  shipStats(); QoL effects (cargo/fishing/repair/minimap) are owned+equippable with their application a
 *  documented follow-on seam (see AMULET_EFFECT_WIRED). */
export type AmuletEffect = 'speed_turn' | 'turn_speed' | 'cargo' | 'fishing' | 'repair' | 'minimap';
export interface Amulet {
  readonly id: number;
  readonly name: string;
  readonly rarity: Rarity;
  readonly effect: AmuletEffect;
  readonly plus: number;   // primary benefit (fraction)
  readonly minus: number;  // paired downside (fraction; 0 for pure QoL)
  /** DBL price at the shop — gacha is the shortcut to the SAME item, never the only door. */
  readonly shopPriceDbl: number;
  readonly desc: string;
  /** Raster charm icon (leading-'/' asset path, GPT-authored & alpha-cut like the fish/elixir icons);
   *  rendered via the client's shared itemIcon() helper on every charm surface (Dredge, shop, reveal). */
  readonly icon: string;
}
export const AMULETS: readonly Amulet[] = [
  { id: 1, name: 'Trade-Wind Sail',    rarity: 'epic',   effect: 'speed_turn', plus: 0.05, minus: 0.05, shopPriceDbl: 14000, icon: '/icons/charms/trade_wind_sail.webp', desc: '+5% speed, −5% turn. The corsair who runs in a straight line and prays never to come about.' },
  { id: 2, name: 'Lead Keel',          rarity: 'epic',   effect: 'turn_speed', plus: 0.05, minus: 0.05, shopPriceDbl: 14000, icon: '/icons/charms/lead_keel.webp', desc: '+5% turn, −5% speed. Comes about on a pin — but pays for it on the straightaway.' },
  { id: 3, name: "Stevedore's Hook",   rarity: 'rare',   effect: 'cargo',   plus: 0.10, minus: 0, shopPriceDbl: 7000, icon: '/icons/charms/stevedores_hook.webp', desc: '+10% hold space. Pure economy QoL — nothing changes in a fight.' },
  { id: 4, name: 'Amber Lure',         rarity: 'rare',   effect: 'fishing', plus: 0.20, minus: 0, shopPriceDbl: 7000, icon: '/icons/charms/amber_lure.webp', desc: '−20% fishing time. Out of combat, full stop.' },
  { id: 5, name: "Caulker's Seal",     rarity: 'rare',   effect: 'repair',  plus: 0.10, minus: 0, shopPriceDbl: 7000, icon: '/icons/charms/caulkers_seal.webp', desc: '+10% faster bailing & repairs. Ride out a flooding and limp home.' },
  { id: 6, name: "Lookout's Spyglass", rarity: 'common', effect: 'minimap', plus: 0.10, minus: 0, shopPriceDbl: 3500, icon: '/icons/charms/lookouts_spyglass.webp', desc: '+10% minimap range (render only — NEVER the network AOI). Information, not power.' },
] as const;
export function amuletById(id: number): Amulet | undefined { return AMULETS.find((a) => a.id === id); }
/** Which amulet EFFECTS are wired into the game. ALL SIX are live now (Sinks P3): the combat tradeoff pair
 *  rides shipStats()→speedMul/turnMul; cargo scales the resolved hold cap (shipStats + ProfileMsg.cargoCap so
 *  prediction stays exact); fishing shortens the bite wait; repair speeds bailing; minimap widens the client
 *  sight ring (render only, NEVER the network AOI). Kept as a set so any future effect defaults to "not wired". */
export const AMULET_EFFECT_WIRED: ReadonlySet<AmuletEffect> = new Set<AmuletEffect>(['speed_turn', 'turn_speed', 'cargo', 'fishing', 'repair', 'minimap']);
/** The resolved (speedMul, turnMul) multipliers an equipped amulet contributes — 1,1 when none/QoL. Applied
 *  in shipStats() on BOTH server and client-prediction (it rides the resolved speedMul/turnMul on ProfileMsg,
 *  so no extra wire field). Clamped to ±AMULET_MAX_DELTA so no config typo can breach the skill-based line. */
export function amuletCombatMul(amuletId: number | undefined): { speed: number; turn: number } {
  const a = amuletId ? amuletById(amuletId) : undefined;
  if (!a) return { speed: 1, turn: 1 };
  const cap = GACHA.AMULET_MAX_DELTA;
  const plus = Math.min(cap, a.plus), minus = Math.min(cap, a.minus);
  if (a.effect === 'speed_turn') return { speed: 1 + plus, turn: 1 - minus };
  if (a.effect === 'turn_speed') return { speed: 1 - minus, turn: 1 + plus };
  return { speed: 1, turn: 1 };
}
/** QoL amulet effect → its resolved multiplier (1 = none / not the matching amulet). Each reads the amulet's
 *  own `plus` (the LOCKED magnitudes: cargo +10% hold, fishing −20% bite wait, repair +25% bail, minimap +10%
 *  sight). These are the NON-combat QoL effects, so they are NOT clamped to AMULET_MAX_DELTA (that cap guards
 *  only the combat speed/turn pair). Every one is ALSO buyable in ⓓ — the gacha is a shortcut, never a wall. */
export function amuletCargoMul(amuletId: number | undefined): number {
  const a = amuletId ? amuletById(amuletId) : undefined;
  return a?.effect === 'cargo' ? 1 + a.plus : 1;       // Stevedore's Hook: +hold space (scales the resolved cargo cap)
}
export function amuletFishingMul(amuletId: number | undefined): number {
  const a = amuletId ? amuletById(amuletId) : undefined;
  return a?.effect === 'fishing' ? 1 - a.plus : 1;     // Amber Lure: a <1 multiplier on the fishing bite wait
}
export function amuletRepairMul(amuletId: number | undefined): number {
  const a = amuletId ? amuletById(amuletId) : undefined;
  return a?.effect === 'repair' ? 1 + a.plus : 1;      // Caulker's Seal: +bail/repair rate
}
export function amuletMinimapMul(amuletId: number | undefined): number {
  const a = amuletId ? amuletById(amuletId) : undefined;
  return a?.effect === 'minimap' ? 1 + a.plus : 1;     // Lookout's Spyglass: +minimap sight ring (render only)
}

/** Gacha item classes (u8 on the wire). SHARDS = a duplicate auto-converted to Relic Shards (never "nothing"). */
export const GachaItemKind = { COSMETIC: 0, AMULET: 1, DEED: 2, SHARDS: 3 } as const;
export type GachaItemKind = (typeof GachaItemKind)[keyof typeof GachaItemKind];
export interface GachaEntry { kind: GachaItemKind; ref: number; rarity: Rarity; }

export const GACHA = {
  /** The ⓓ trail is LIVE (needs no token). The $SOR trail waits on the TGE (see SOR_CHEST). */
  ENABLED: true,
  /** ---- 🪙 Baú do Contrabandista (the ⓓ chest — V1) ---- */
  /** Owner-set 2026-07-14 (1500 → 5000): a MEANINGFUL sink (~1 day of Cargo Runs at 2–4k ⓓ/day). */
  SMUGGLER_COST: 5000,
  /** 100% of the ⓓ paid is DESTROYED (pure sink) — never minted, never routed to the treasury. */
  SMUGGLER_BURN_ALL: true,
  /** ---- Published base odds per single pull (MUST sum to 1). Shown in-UI + on a public /odds page. ---- */
  ODDS: { common: 0.782, rare: 0.17, epic: 0.04, legendary: 0.008 } as Record<Rarity, number>,
  /** ---- Pity — the "Medidor de Braças". Persists across sessions (Profile.gacha), diegetic (the winch chain). ---- */
  PITY: {
    /** Hard pity: legendary GUARANTEED on the Nth pull without a legendary. */
    HARD: 40,
    /** Soft pity: from this pull on, legendary chance ramps +SOFT_STEP each pull (real avg ≈ 1 per 28–33). */
    SOFT_START: 30,
    SOFT_STEP: 0.03,
    /** Mini-pity: epic-or-better guaranteed every this-many pulls without an epic. */
    EPIC_EVERY: 30,
  },
  /** ---- Duplicate → Estilhaços de Relíquia (never a "you got nothing" screen). ---- */
  SHARDS_FROM_DUP: { common: 15, rare: 75, epic: 300, legendary: 1500 } as Record<Rarity, number>,
  /** ---- Shard Shop: buy ANY catalog item deterministically (a known ceiling for the whale + kills "impossible to get"). ---- */
  SHARD_PRICE: { common: 120, rare: 600, epic: 2400, legendary: 9000 } as Record<Rarity, number>,
  /** One FREE Smuggler's Chest per ISO week (V1: a simple weekly grant; the "Mapa de Salvamento" quest is a seam). */
  FREE_WEEKLY: true,
  /** Ship Deeds in the pool: epic = the mid ⓓ-ladder hulls, legendary = the top ones. A deed UNLOCKS the same
   *  class as the shipyard ⓓ ladder (SHIP_UNLOCK_COST) — a shortcut on the ladder, never a hull outside it.
   *  East Indiaman is owner-exclusive and NEVER enters the pool. */
  DEEDS: { epic: [ShipClassId.CUTTER, ShipClassId.FLUYT], legendary: [ShipClassId.FRIGATE, ShipClassId.GALLEON] } as Record<'epic' | 'legendary', readonly ShipClassId[]>,
  /** The skill-based ceiling: no combat amulet may ever exceed this magnitude, and always as a tradeoff pair. */
  AMULET_MAX_DELTA: 0.05,
  /** ---- ⚓ Baú Afundado ($SOR trail) — DISARMED SEAM. Opens at the TGE beside the SOR Store rail. ---- */
  SOR_CHEST: {
    ENABLED: false,          // gated OFF until the token launches (mirrors STORE DISARMED discipline)
    SOR_PER_PULL: 1,         // 1 $SOR / pull; 10-pack for 9 (placeholder, tune freely — never on-chain)
    TEN_PACK_SOR: 9,
    BURN_SHARE: 0.5,         // 50% burn / 50% treasury — the exact SOR Store rail
  },
  /** ---- Resale SEAM: gacha items are P2P-tradeable in ⓓ with a 7-day soul-bind post-drop. Depends on the
   *  P2P market anti-bot project (not in V1). Shards + consumables are NEVER tradeable. ---- */
  RESALE: { TRADEABLE: true, SOULBIND_DAYS: 7, ENABLED_V1: false },
} as const;

/** All pool candidates of a given rarity: non-NFT / non-fishing cosmetics + amulets + ship deeds. The server
 *  rolls a rarity (odds+pity) then picks one of these uniformly; a result already owned → Estilhaços. */
export function gachaCandidates(rarity: Rarity): GachaEntry[] {
  const out: GachaEntry[] = [];
  for (const c of COSMETICS) {
    if (c.rarity !== rarity) continue;
    if (NFT_COSMETIC_IDS.has(c.id)) continue; // Founders' livery is NFT-bound — never dropped
    if (c.id === 27) continue;                // Angler's Sigil is fishing-exclusive
    if (CRAFT_ONLY_COSMETIC_IDS.has(c.id)) continue; // Pearl-Inlaid Hull is craft-exclusive (Sinks P1)
    out.push({ kind: GachaItemKind.COSMETIC, ref: c.id, rarity });
  }
  for (const a of AMULETS) if (a.rarity === rarity) out.push({ kind: GachaItemKind.AMULET, ref: a.id, rarity });
  if (rarity === 'epic' || rarity === 'legendary') for (const cls of GACHA.DEEDS[rarity]) out.push({ kind: GachaItemKind.DEED, ref: cls, rarity });
  return out;
}
/** Estilhaços granted when a drop duplicates an item you already own. */
export function dupShards(rarity: Rarity): number { return GACHA.SHARDS_FROM_DUP[rarity]; }
/** Estilhaços cost to buy a given rarity outright in the Shard Shop. */
export function shardPrice(rarity: Rarity): number { return GACHA.SHARD_PRICE[rarity]; }

/** Anti kill-trading: repeated kills of the same victim (or fresh/farmed accounts) mint nothing. */
export const KILL_GUARD = {
  /** The same attacker↔victim pair pays gold/trophies/contract credit at most once per this window. */
  PAIR_COOLDOWN_MS: 600_000,
  /** Victims below this trophy count (fresh/bot accounts) pay no kill reward at all. */
  MIN_VICTIM_TROPHIES: 1,
} as const;

export const CHEST = {
  GOLD_MIN: 150,
  GOLD_MAX: 500,
  /** Respawn is JITTERED (uniform min..max) so bots can't run a fixed farm route on a 25s clock. */
  RESPAWN_MIN_S: 20,
  RESPAWN_MAX_S: 45,
  /** ---- Base exclusion (anti "front-door farm") ---- A chest does NOT spawn or respawn within this radius
   *  (metres) of an ACTIVE player base. Kills the exploit where a captain logs in to find treasure islands
   *  sitting on their doorstep and farms them for free without ever leaving home water (the worldgen quirk that
   *  lets a treasure island land next to a base slot — its `nearBase` guard is a documented no-op). Enforced
   *  SERVER-SIDE only (room.ts), never in the shared/deterministic worldgen: chests inside the radius are held
   *  suppressed (hidden via the existing CHEST_TAKEN event) while a base is active there, and return to the
   *  normal respawn cycle once the base goes inactive. NOT a wire change — do not bump PROTOCOL_VERSION.
   *  Sized to EXACTLY AOI_RADIUS (600 m): a chest inside this radius never even ENTERS the base owner's
   *  area-of-interest, so it is physically un-seeable from home water — the "farm from the doorstep" exploit
   *  is closed for good, not just discouraged. REVISIT when population scales: the zone is only active while a
   *  base's OWNER is online, so at N large online bases sizeable fractions of the map can go chest-free — a
   *  per-base cap or a shrink-under-load rule may be needed then. */
  BASE_EXCLUSION_R: AOI_RADIUS,
  /** Treasure HOISTING — the chest is not auto-grabbed. Park within PICKUP_R and hold under
   *  HOIST_MAX_SPEED for HOIST_TIME seconds while a crane winches it up. Standing still = exposed:
   *  this is the risk/reward beat. Moving fast or leaving the ring bleeds the progress back. */
  PICKUP_R: 22,
  HOIST_TIME: 4.5,
  HOIST_MAX_SPEED: 2.5,
  HOIST_DECAY_MUL: 1.5,
  GILDED_CHANCE: 0.08,   // rare "gilded" chest
  GILDED_GOLD: 1500,

  /** ---- Risk waters (feature 4): a chest is worth more the deeper into danger it sits ----
   *  Multiplier by distance to the map centre (WORLD_SIZE/2), normalized by the half-size:
   *  outer band (dist/half > RISK_OUTER_FRAC) ×RISK_OUTER_MUL (<1 — safe water is now a PENALTY, so a
   *  risk-free farm route is the WORST gold/hour), middle ×RISK_MID_MUL, hot centre (< RISK_CENTER_FRAC)
   *  ×RISK_CENTER_MUL. A chest hoisted while the global storm is ACTIVE takes an extra ×RISK_STORM_MUL.
   *  Area split of the circular bands over the square map (fraction of map area = π·frac²/4 for frac ≤ 1):
   *  centre = π·0.29²/4 ≈ 6.6% ×3.0 · mid ≈ 23.6% ×1.5 · outer ≈ 69.8% ×0.65. Area-weighted average
   *  multiplier stays ≈1.0 (gold on the map is roughly conserved) but is CONCENTRATED where captains fight. */
  RISK_CENTER_FRAC: 0.29,  // dist/half below this = the contested centre  (circle area ≈ 6.6% of the map)
  RISK_OUTER_FRAC: 0.62,   // dist/half above this = safe outer waters      (mid band ≈ 23.6%, outer ≈ 69.8%)
  RISK_OUTER_MUL: 0.65,    // safe outer waters PAY LESS — bots/farmers in zero-risk water take the haircut
  RISK_MID_MUL: 1.5,
  RISK_CENTER_MUL: 3.0,
  /** Storm premium on top of the band multiplier — kept as a knob, currently NEUTRAL (×1.0). The storm
   *  today is cosmetic (wind/rain/camera sway) with no real hazard to a hoisting captain, so it earns no
   *  gold premium; a risk-free bonus is an unearned one. Raise this back above 1 once the storm carries
   *  actual danger (e.g. stronger currents, lightning strikes, capsizing risk) worth paying a captain for. */
  RISK_STORM_MUL: 1.0,
  RISK_STORM_THRESHOLD: 0.5, // global storm intensity at/above which the storm bonus (currently neutral) would apply

  /** ---- Cursed Treasure (feature 3): a map-wide convergence event ---- One high-value chest spawns
   *  in OPEN water every CURSED_EVERY_MIN..MAX minutes (announced to everyone), with a LONG hoist so it
   *  becomes a fight, not a grab. It stays until hoisted; claiming it starts the next timer. */
  CURSED_MIN: 3500,
  CURSED_MAX: 5500,
  CURSED_HOIST_TIME: 10,      // seconds (vs the normal 4.5) — a long, exposed winch
  CURSED_PICKUP_R: 26,        // hoist ring (matches the widened normal radius)
  CURSED_EVERY_MIN_S: 480,    // 8 min
  CURSED_EVERY_MAX_S: 720,    // 12 min
  CURSED_CLEAR_DIST: 150,     // min distance (m) from any base slot / island / outpost (open sea only)
} as const;

/**
 * Risk-waters multiplier on a chest's gold value by WHERE it is hoisted (feature 4). Pure + shared:
 * the server bakes it into the payout (rollChest) and the client shows the "🌊 Rich waters ×N" toast
 * from the SAME function — so they can never disagree. `stormIntensity` is the global 0..1 storm level.
 */
export function chestRiskMultiplier(x: number, y: number, stormIntensity = 0): number {
  const half = WORLD_SIZE / 2;
  const dist = Math.hypot(x - half, y - half) / half; // 0 dead-centre .. ~1.41 at a corner
  let mul = dist < CHEST.RISK_CENTER_FRAC ? CHEST.RISK_CENTER_MUL
    : dist < CHEST.RISK_OUTER_FRAC ? CHEST.RISK_MID_MUL
      : CHEST.RISK_OUTER_MUL;
  if (stormIntensity >= CHEST.RISK_STORM_THRESHOLD) mul *= CHEST.RISK_STORM_MUL;
  return mul;
}

/* ============================================================================
 * ---- Wave 2b: Professions & Production ----
 * Materials are the spine: everything gathered AT SEA (fish, wreck salvage, storm
 * essence) rides in the ship's HOLD (carriedGoods) and must be carried home + DEPOSITED
 * (docked at your own port) to become BASE INVENTORY (Base.materials), which feeds crafting.
 * Sink at sea → the hold is lost (the killer salvages ~50%, same tension as gold/salvage).
 * ========================================================================== */

/** Material ids (u8 on the wire). Sea-gathered (fish/oil/iron/wood/relic/storm_essence),
 *  farmed at a Crop Plot (herb/sugar), or crafted (grog/oils/powder). black_pearl is the
 *  rare fishing jackpot ingredient. */
export const MaterialId = {
  FISH: 0, FISH_OIL: 1, IRON: 2, WOOD: 3, RELIC: 4, STORM_ESSENCE: 5,
  HERB: 6, SUGAR: 7, GROG: 8, REPAIR_OIL: 9, SPYGLASS_TONIC: 10, LUCK_ELIXIR: 11,
  FINE_POWDER: 12, BLACK_PEARL: 13,
  // ---- wave 3 ----
  AMBERGRIS: 14,      // the White Whale's prize — a legendary tradeable alchemy ingredient
  MAP_FRAGMENT: 15,   // a torn piece of a treasure map (cartographer)
  TREASURE_MAP: 16,   // MAP_PIECES fragments combined — tradeable; follow it to the X and dig
  REPAIR_PLANK: 17,   // carpenter's consumable — patches a hull leak at sea
  SPECTRAL_ESSENCE: 18, // dropped by the night's ghost fleet — a rare alchemy ingredient
  // ---- Fish species (the PvP tactical SINK) — each is EATEN at sea for a SHORT, cooldown-gated combat
  //      buff (see BUFFS). Reeled by tier: the common catch nets a mackerel, a PERFECT reel the rarer
  //      three. NEW ids only — the generic FISH(0)/FISH_OIL(1) economy + refunds are untouched. ----
  HARBOR_MACKEREL: 19,   // common — a bail/repair burst (never a heal; HP is water level)
  REEF_SNAPPER: 20,      // uncommon — a tighter helm (+turn)
  IRONSCALE_GROUPER: 21, // rare — a tougher hull (−leak per hit taken)
  LEVIATHAN_ROE: 22,     // legendary — a short speed surge
  // ---- Sinks P1: premium alchemy CONSUMABLES that finally give the orphan rares a recipe (crafted, never
  //      gathered — new ids only, so prod material profiles are untouched). Both are QoL/utility, skill-first. ----
  AMBERGRIS_ELIXIR: 23,  // the alchemist's masterwork from Ambergris — a long-burning fortune tincture (oil slot)
  WRAITHLIGHT_DRAUGHT: 24, // distilled from Spectral Essence — a short draught of ghost-sight in the dark (oil slot)
  // ---- Chum (the generic-FISH sink) — grind the FISH(0) hoard IN VOLUME at the Tavern into a bucket of chum:
  //      a fishing BAIT consumable (fish slot) that draws the shoals in (faster bites + richer waters) while it
  //      lasts. Its craft cost in FISH strictly exceeds the FISH it can return during the buff, so it can never
  //      pay for itself — a guaranteed NET FISH sink (see CHUM below + the sink-math test). ----
  CHUM_BUCKET: 25,
  // ---- economy-close (2026-07-15): the RELIC sink — an UNCAPPED repeatable alchemy consumable so the
  //      workhorse rare (4 faucets) finally has a destination furniture (capped) can't provide. Oil-slot
  //      fortune draught in the SAME luck band as Luck Elixir (no power creep) — the relic-fuelled twin. ----
  RELIQUARY_DRAUGHT: 26,
} as const;
export type MaterialId = (typeof MaterialId)[keyof typeof MaterialId];
export const MATERIAL_COUNT = 27;

export interface MaterialDef {
  /** Display name (EN — all game text is English). */
  name: string;
  /** Emoji/icon glyph for the parchment inventory + market (drawn, not a texture request). */
  icon: string;
  /** Reference sell value in doubloons at the physical goods market (the default quote/floor). */
  sell: number;
  /** Max stack in one hold/inventory slot (kept generous — inventory is a flat count map). */
  stack: number;
}

/** The material catalog — the single source of truth (server settles, client renders labels). */
export const MATERIALS: Record<MaterialId, MaterialDef> = {
  // The raw materials carry raster icons (GPT-authored, alpha-cut, /icons/materials/) exactly like the
  // fish species below — itemIcon() renders the path as <img>, itemIconText() falls back to 📦.
  [MaterialId.FISH]:          { name: 'Fish',           icon: '/icons/materials/fish.webp',           sell: 32,  stack: 999 },
  [MaterialId.FISH_OIL]:      { name: 'Fish Oil',       icon: '/icons/materials/fish_oil.webp',       sell: 95,  stack: 999 },
  [MaterialId.IRON]:          { name: 'Iron',           icon: '/icons/materials/iron.webp',           sell: 48,  stack: 999 },
  [MaterialId.WOOD]:          { name: 'Salvaged Wood',  icon: '/icons/materials/salvaged_wood.webp',  sell: 26,  stack: 999 },
  [MaterialId.RELIC]:         { name: 'Relic',          icon: '/icons/materials/relic.webp',          sell: 230, stack: 999 },
  [MaterialId.STORM_ESSENCE]: { name: 'Storm Essence',  icon: '/icons/materials/storm_essence.webp',  sell: 165, stack: 999 },
  [MaterialId.HERB]:          { name: 'Herb',           icon: '/icons/materials/herb.webp',           sell: 40,  stack: 999 },
  [MaterialId.SUGAR]:         { name: 'Sugar',          icon: '/icons/materials/sugar.webp',          sell: 34,  stack: 999 },
  [MaterialId.GROG]:          { name: 'Grog',           icon: '/icons/elixirs/grog.webp', sell: 125, stack: 999 },
  [MaterialId.REPAIR_OIL]:    { name: 'Repair Oil',     icon: '/icons/elixirs/repair_oil.webp', sell: 150, stack: 999 },
  [MaterialId.SPYGLASS_TONIC]:{ name: 'Spyglass Tonic', icon: '/icons/elixirs/spyglass_tonic.webp', sell: 185, stack: 999 },
  [MaterialId.LUCK_ELIXIR]:   { name: 'Luck Elixir',    icon: '/icons/elixirs/luck_elixir.webp', sell: 270, stack: 999 },
  [MaterialId.FINE_POWDER]:   { name: 'Fine Powder',    icon: '/icons/elixirs/fine_powder.webp', sell: 205, stack: 999 },
  [MaterialId.BLACK_PEARL]:   { name: 'Black Pearl',    icon: '/icons/materials/black_pearl.webp',   sell: 900, stack: 999 },
  [MaterialId.AMBERGRIS]:     { name: 'Ambergris',      icon: '/icons/materials/ambergris.webp',     sell: 1400, stack: 999 },
  [MaterialId.MAP_FRAGMENT]:  { name: 'Map Fragment',   icon: '/icons/materials/map_fragment.webp',  sell: 90,  stack: 999 },
  [MaterialId.TREASURE_MAP]:  { name: 'Treasure Map',   icon: '/icons/materials/treasure_map.webp',  sell: 480, stack: 999 },
  [MaterialId.REPAIR_PLANK]:  { name: 'Repair Plank',   icon: '/icons/materials/repair_plank.webp',  sell: 70,  stack: 999 },
  [MaterialId.SPECTRAL_ESSENCE]: { name: 'Spectral Essence', icon: '/icons/materials/spectral_essence.webp', sell: 320, stack: 999 },
  // The four fish SPECIES carry a raster icon (a leading-'/' asset path, not an emoji) — the client's shared
  // itemIcon() helper renders these as <img> in every inventory/market/codex surface, and as the big art in the
  // catch-reveal card. Text-only surfaces (a <option>, a textContent toast) fall back to a neutral fish glyph.
  [MaterialId.HARBOR_MACKEREL]:   { name: 'Harbor Mackerel',   icon: '/icons/fish/harbor_mackerel.webp',   sell: 44,  stack: 999 },
  [MaterialId.REEF_SNAPPER]:      { name: 'Reef Snapper',      icon: '/icons/fish/reef_snapper.webp',      sell: 96,  stack: 999 },
  [MaterialId.IRONSCALE_GROUPER]: { name: 'Ironscale Grouper', icon: '/icons/fish/ironscale_grouper.webp', sell: 185, stack: 999 },
  [MaterialId.LEVIATHAN_ROE]:     { name: "Leviathan's Roe",   icon: '/icons/fish/leviathans_roe.webp',    sell: 430, stack: 999 },
  // The premium alchemy consumables carry raster icons like the fish species do (GPT-authored, alpha-cut,
  // /icons/elixirs/) — same itemIcon() contract: <img> on HTML surfaces, category-glyph fallback on text-only.
  [MaterialId.AMBERGRIS_ELIXIR]:    { name: 'Ambergris Elixir',    icon: '/icons/elixirs/ambergris_elixir.webp', sell: 1600, stack: 999 },
  [MaterialId.WRAITHLIGHT_DRAUGHT]: { name: 'Wraithlight Draught', icon: '/icons/elixirs/wraithlight_draught.webp', sell: 380,  stack: 999 },
  // Chum: crafted from FISH IN VOLUME (see CHUM.FISH_COST). A pure consumable — the sell floor is kept LOW
  // (well under the raw FISH it costs) so there is never a craft→sell arbitrage; you burn fish to fish better.
  [MaterialId.CHUM_BUCKET]:         { name: 'Chum Bucket',         icon: '/icons/elixirs/chum_bucket.webp', sell: 60,   stack: 999 },
  // economy-close: the RELIC sink. Sell floor kept modest — the point is to BURN relics into a fortune, not to
  // arbitrage; the value is the buff, not a resale.
  [MaterialId.RELIQUARY_DRAUGHT]:   { name: 'Reliquary Draught',   icon: '/icons/elixirs/reliquary_draught.webp', sell: 240,  stack: 999 },
} as const;

export function materialById(id: number): MaterialDef | undefined {
  return (MATERIALS as Record<number, MaterialDef>)[id];
}

/** The profession a material's trade belongs to — used to award a small XP to the seller on a goods sale
 *  (2b.8). null = no clear trade (raw farm/rare drops earn no sale XP). */
export function materialProfession(id: number): string | null {
  switch (id) {
    case MaterialId.FISH: case MaterialId.FISH_OIL:
    case MaterialId.HARBOR_MACKEREL: case MaterialId.REEF_SNAPPER:
    case MaterialId.IRONSCALE_GROUPER: case MaterialId.LEVIATHAN_ROE:
    case MaterialId.CHUM_BUCKET: return 'fishing';
    case MaterialId.IRON: case MaterialId.WOOD: case MaterialId.RELIC: return 'diver';
    case MaterialId.GROG: return 'tavern_keeper';
    case MaterialId.REPAIR_OIL: case MaterialId.SPYGLASS_TONIC: case MaterialId.LUCK_ELIXIR: case MaterialId.FINE_POWDER: case MaterialId.SPECTRAL_ESSENCE:
    case MaterialId.AMBERGRIS_ELIXIR: case MaterialId.WRAITHLIGHT_DRAUGHT: case MaterialId.RELIQUARY_DRAUGHT: return 'alchemist';
    case MaterialId.REPAIR_PLANK: return 'smith';
    default: return null;
  }
}

/** Hold ("porão") rules. */
export const HOLD = {
  /** Doubloon-agnostic: materials/sec moved from the hold into base inventory while berthed at your
   *  own dock (like banking gold — you must linger in home water, exposed until fully anchored). */
  DEPOSIT_RATE: 12,
  /** Fraction of a sunk laden hold the KILLER salvages into their own hold (matches RAID.SALVAGE_FRACTION). */
  SALVAGE_FRACTION: 0.5,
} as const;

/**
 * ---- Fishing (2b.2) ---- Shoals scattered across the sea (gulls circling over a disturbed patch).
 * Park slow inside the ring → CAST → a jittered wait → a BITE (react within the window) → the tap
 * TIMING sets the tier: perfect = rare, ok = common, late = it escaped. Richer waters (the risk bands)
 * multiply the rare/jackpot odds. Server-authoritative (room PRNG, never Math.random). Catch → the HOLD.
 */
export const FISHING = {
  // Fishing density audit (v43): on the 8 km sea, 24 shoals put the median sail-to-nearest-shoal at ~885 m
  // (~74 s @ flank 12 m/s, ~98 s @ a 9 m/s cruise; p90 ~1.8 km). Kept at 24 ON PURPOSE: the shoals share the
  // room's single rng stream with every runtime event (Eye of the Storm, Cursed Treasure, marauder/bottle
  // spawns), so changing the count reshuffles all of them across every live room — too broad a ripple for a
  // density tweak. The real gap was DISCOVERY (shoals were invisible on the chart); that is now fixed by drawing
  // them (hud.ts). Revisit as a deliberate standalone bump if sail-times still feel long once spots are visible.
  SHOAL_COUNT: 24,
  /** Ring radius you must be inside to cast (m). */
  RING_R: 28,
  /** Max hull speed to cast/keep a line out (like the treasure hoist). */
  CAST_MAX_SPEED: 2.5,
  /** Jittered wait before a fish bites (s). */
  WAIT_MIN_S: 2.0, WAIT_MAX_S: 6.0,
  /** After a bite, reel within this window or it escapes (s). Generous to stay latency-fair. */
  REEL_WINDOW_S: 1.3,
  /** Reel reaction (server-measured, bite→reel) under this = a PERFECT catch (rare tier). */
  PERFECT_S: 0.45,
  /** On a PERFECT reel: base chance of a RARE haul (premium fish + fish oil); else common fish. */
  RARE_CHANCE: 0.35,
  /** Common catch: 1..MAX fish. */
  COMMON_FISH_MIN: 1, COMMON_FISH_MAX: 2,
  /** Jackpots (rolled on a perfect reel, before the rare/common split), ×rarity multiplier: */
  FIGUREHEAD_CHANCE: 1 / 500,   // a legendary figurehead cosmetic (tradeable)
  PEARL_CHANCE: 1 / 1000,       // a Black Pearl (the alchemist's rare ingredient)
  /** Fisher XP per successful catch. */
  XP_PER_CATCH: 12,
} as const;
/** Cosmetic id granted by the 1/500 fishing jackpot (the "legendary figurehead" — a tradeable flag). */
export const FISHING_JACKPOT_COSMETIC = 27;
/** Fishing tiers (EV_FISH_CATCH.tier). */
export const FishTier = { ESCAPED: 0, COMMON: 1, RARE: 2, JACKPOT: 3 } as const;
export type FishTier = (typeof FishTier)[keyof typeof FishTier];

/**
 * ---- Fish-species bycatch (the PvP tactical SINK) ---- Every catch also nets ONE tier-mapped species
 * fish that is EATEN for a short combat buff (see BUFFS). Mapping (natural, skill-gated, all farmable):
 *  • a COMMON reel → 1 Harbor Mackerel (the everyday catch).
 *  • a PERFECT (RARE-tier) reel that isn't a pearl/figurehead/fragment → a Reef Snapper by default, with
 *    these chances (× risk-water rarity × luck — the SAME multipliers the pearl rolls use) to upgrade it:
 *    ROE_CHANCE → Leviathan's Roe (legendary), else GROUPER_CHANCE → Ironscale Grouper (rare).
 * The generic FISH/FISH_OIL/pearl/fragment loot is UNCHANGED — species ride on top as the buff currency
 * that closes the fisher→PvPer market loop. Skill-first: better timing/richer waters → a better species.
 */
export const FISH_SPECIES = { ROE_CHANCE: 0.05, GROUPER_CHANCE: 0.18 } as const;
/** The 4 fish-species consumable ids, common→legendary (tier→species mapping + the sip menu/tests). */
export const FISH_SPECIES_IDS = [
  MaterialId.HARBOR_MACKEREL, MaterialId.REEF_SNAPPER, MaterialId.IRONSCALE_GROUPER, MaterialId.LEVIATHAN_ROE,
] as const;

/** The species' own 4-rung rarity flavor (mackerel→roe). Drives the CATCH-REVEAL card's tier treatment: the
 *  label reads in fish flavor, while the border/glow reuses the shared RARITY_COLOR rungs (see FISH_RARITY_COLOR)
 *  so the reveal matches the gacha/codex colour language. Client-side only (no wire) — the species id rides the
 *  existing EV_FISH_CATCH.item. */
export type FishRarity = 'common' | 'uncommon' | 'rare' | 'legendary';
export const FISH_SPECIES_RARITY: Record<number, FishRarity> = {
  [MaterialId.HARBOR_MACKEREL]:   'common',
  [MaterialId.REEF_SNAPPER]:      'uncommon',
  [MaterialId.IRONSCALE_GROUPER]: 'rare',
  [MaterialId.LEVIATHAN_ROE]:     'legendary',
};
/** Fish flavor rarity → the signature colour, mapped onto the shared RARITY_COLOR ladder in escalating order
 *  (foam → blue → kraken-purple → gold). Reuses the exact hexes the shop/marketplace/Draga ritual already use. */
export const FISH_RARITY_COLOR: Record<FishRarity, number> = {
  common: RARITY_COLOR.common, uncommon: RARITY_COLOR.rare, rare: RARITY_COLOR.epic, legendary: RARITY_COLOR.legendary,
};
/** Is `id` one of the four buffed fish species? Returns its flavor rarity, else null (a pearl/fragment/generic
 *  catch keeps the plain toast). The catch reveal keys off this. */
export function fishSpeciesRarity(id: number): FishRarity | null { return FISH_SPECIES_RARITY[id] ?? null; }

/**
 * ---- Chum (the generic-FISH sink) ---- The generic FISH(0) had no consumption sink (hoards of thousands
 * sat idle). Chum closes that: grind FISH IN VOLUME at the Tavern into a Chum Bucket — a fishing BAIT
 * consumable (occupies the SHORT, cooldown-gated 'fish' buff slot, so it's non-stack and rides the existing
 * fishBuff HUD badge — ZERO wire). While active it draws the shoals in: −(1−WAIT_MUL) off the pre-bite wait
 * and ×LUCK_MUL on the rare/species/jackpot rolls. The reel TIMING (perfect vs. common) stays 100% player
 * skill — chum never auto-perfects a catch.
 *
 * BALANCE — a guaranteed NET FISH sink (the whole point): FISH_COST must EXCEED the FISH a captain can reel
 * during one buff window, so a chum can never pay for itself (no perpetual-motion fish fountain). The math:
 *   mean pre-bite wait (chum)  = (WAIT_MIN_S+WAIT_MAX_S)/2 × WAIT_MUL = 4.0 × 0.80 = 3.2 s
 *   cycle (wait + reel react + recast) ≈ 3.2 + 0.6 + 0.6 = 4.4 s   → ~68 catches in DURATION_S (300 s)
 *   generic FISH per (common) catch ≈ (COMMON_FISH_MIN+MAX)/2 = 1.5 → E[FISH during buff] ≈ 68 × 1.5 ≈ 102
 *   even the ceiling (every common reel rolls COMMON_FISH_MAX) ≈ 68 × 2 ≈ 136, and an all-bonus grinder
 *   (Amber Lure stacked, frame-perfect) tops out ≈ 181 — ALL below FISH_COST=200. The buff also only shifts
 *   species/pearl/oil odds (not the generic-FISH count), so LUCK_MUL never feeds the sink. → strict net drain.
 */
export const CHUM = {
  /** FISH consumed per Chum Bucket crafted (the sink). Kept ABOVE the FISH one buff can return (see math). */
  FISH_COST: 200,
  /** Active buff duration (s) — the 'fish' slot cooldown is set equal to it (≥ duration ⇒ non-stack). */
  DURATION_S: 300,
  /** Pre-bite wait multiplier while chummed (<1 = faster bites). Shortens ONLY the jittered wait, like Amber Lure. */
  WAIT_MUL: 0.80,
  /** Fishing-luck multiplier while chummed (>1 = better tier/species/jackpot odds). Fishing-only, not global luck. */
  LUCK_MUL: 1.15,
} as const;

/**
 * ---- Wreck diving (2b.3) ---- Static half-submerged wrecks dot the sea; a PLAYER sinking also spawns a
 * divable wreck (the battle's "body") for a few minutes. Park over one → DIVE (a hoist-style 6s bar, and
 * you're exposed) → salvage iron/wood (always) + a relic sometimes + a rare pearl at the rich wrecks.
 * Each wreck holds a few dives before it's picked clean (static ones respawn; dynamic ones expire).
 */
export const WRECK = {
  STATIC_COUNT: 14,
  /** Ring radius to dive a wreck (m) + the max speed to keep the dive going. */
  DIVE_R: 20,
  DIVE_MAX_SPEED: 2.5,
  /** Seconds of the dive bar (you're vulnerable the whole time). */
  DIVE_TIME: 6,
  /** Dives a wreck yields before it's picked clean. */
  CHARGES_STATIC: 3,
  CHARGES_DYNAMIC: 2,
  /** A picked-clean STATIC wreck refills after a jittered cooldown (dynamic ones just expire). */
  STATIC_RESPAWN_MIN_S: 90, STATIC_RESPAWN_MAX_S: 180,
  /** A dynamic (player-sunk) wreck lasts this long, and the room caps how many are active (evict oldest). */
  DYNAMIC_TTL_S: 240,
  DYNAMIC_MAX: 48,
  /** Fraction of static wrecks that are "rich" (can yield a Black Pearl). */
  RICH_FRACTION: 0.28,
  /** Salvage per dive. */
  IRON_MIN: 1, IRON_MAX: 3,
  WOOD_MIN: 1, WOOD_MAX: 3,
  RELIC_CHANCE: 0.35,
  PEARL_CHANCE: 0.05,       // rich static wrecks only
  /** Player-sunk wrecks (the fresh battle wreckage) are richer than the old static ones. */
  DYNAMIC_YIELD_MUL: 1.6,
  DYNAMIC_RELIC_BONUS: 1.4,
  /** Wreck Diver XP per completed dive. */
  XP_PER_DIVE: 14,
} as const;
/** First id used for DYNAMIC (player-sunk) wrecks — kept clear of the small static ids. */
export const DYNAMIC_WRECK_ID_BASE = 40000;

/**
 * ---- Storm essence (2b.4) — a RISK gather ---- The game's storm is a GLOBAL intensity (not positional),
 * so essence is harvested by SAILING HARD through a storm: while the storm is intense enough and you sail
 * fast, you accrue progress; every SECONDS_PER of storm-running yields 1 storm_essence into the hold.
 * Deterministic (a per-player second counter — no RNG). storm_essence is a key alchemy ingredient.
 */
export const STORM_ESSENCE = {
  MIN_INTENSITY: 0.5,  // the storm must be at least this fierce
  MIN_SPEED: 5,        // and you must be sailing faster than this (m/s)
  SECONDS_PER: 40,     // seconds of storm-running per 1 essence
} as const;

/**
 * ---- Production & crafting (2b.5) ---- Three new base buildings turn gathered materials into goods:
 *  CROP PLOT grows herb + sugar over time (like the Farm, but materials), TAVERN brews grog + doubles as
 *  the alchemy bench (repair oil / spyglass tonic / luck elixir), FOUNDRY forges special ammo cheaply
 *  (iron, not doubloons) + fine powder. Building types 6/7/8 extend the base BuildingType (server side).
 *  Crafting is server-authoritative + gated to being docked at your home port.
 */
export const ProdBuilding = { TAVERN: 6, FOUNDRY: 7, CROP: 8 } as const;
export const PRODUCTION = {
  /** Crop Plot idle yield (materials/sec) at level 1, +per level, capped so offline it can't grow forever. */
  CROP: { HERB_PER_S_BASE: 0.013, HERB_PER_LVL: 0.008, SUGAR_PER_S_BASE: 0.013, SUGAR_PER_LVL: 0.008, CAP: 250 },
  /**
   * ---- farms-demand-first (owner: "o cara NÃO pode ter a opção de construir em casa, somente qd tiver em
   * level top tier, o cara precisa ir nos portos/cidades") — the craft-station matrix. ----
   * Crafting happens at STATIONS: every outpost ring offers public tavern+forge stations of level
   * PORT_STATION_LVL; the CAPITAL is the master forge at CAPITAL_STATION_LVL; the player's OWN base
   * building only works once it reaches HOME_CRAFT_MIN_LVL (the aspirational capstone — full autonomy),
   * and then its level is the station level. A recipe crafts where stationLvl ≥ its minLvl. Port crafting
   * consumes the ship HOLD (you haul materials through PvP water — demand + traffic); home crafting keeps
   * consuming the base stockpile.
   */
  PORT_STATION_LVL: 2,     // any outpost's public station: covers every common (minLvl ≤ 2) recipe
  CAPITAL_STATION_LVL: 10, // Port Royal's master stations: cover everything, rare recipes included
  HOME_CRAFT_MIN_LVL: 8,   // own Tavern/Foundry level at which crafting AT HOME unlocks (top tier)
  /** Extra-output chance per OWN-building level above 1 when crafting AT HOME (public stations grant none —
   *  rented tools; your own master workshop is finer). Mints MATERIALS only, never doubloons. */
  YIELD_PER_LVL: 0.02,
  /** Hard clamp on the composed extra-output chance (perk + building) — the ceiling stays skill-first. */
  YIELD_MAX: 0.35,
} as const;

/** Composed chance (0..PRODUCTION.YIELD_MAX) of one EXTRA output unit on a craft: the profession perk factor
 *  (professionPerkFactor, 1..1.10) plus the OWN building's level bonus when crafting at home (`buildingLvl`
 *  1 at a public station → no bonus). Pure — the single formula the server rolls and any UI may quote. */
export function craftYieldBonusChance(perkFactor: number, buildingLvl: number): number {
  const perk = Math.max(0, perkFactor - 1);
  const bld = Math.max(0, buildingLvl - 1) * PRODUCTION.YIELD_PER_LVL;
  return Math.min(PRODUCTION.YIELD_MAX, perk + bld);
}

/** Craft recipes. farms-demand-first: a recipe crafts at any STATION whose level ≥ its `minLvl` (default 1)
 *  — public port stations (lvl PRODUCTION.PORT_STATION_LVL, consume the ship HOLD), the Capital's master
 *  stations (lvl CAPITAL_STATION_LVL), or the player's OWN building once it reaches HOME_CRAFT_MIN_LVL
 *  (consumes the base stockpile + earns the per-level yield bonus). */
export const CraftId = {
  GROG: 0, CHAIN_AMMO: 1, GRAPE_AMMO: 2, REPAIR_OIL: 3, SPYGLASS_TONIC: 4, LUCK_ELIXIR: 5, FINE_POWDER: 6,
  // wave 3
  TREASURE_MAP: 7, // cartographer: assemble MAP_PIECES fragments into a tradeable treasure map (3.9)
  REPAIR_PLANK: 8, // carpenter: forge repair planks that patch a leak at sea (3.10)
  // ---- Sinks P1: recipes for the 3 orphan rares (Ambergris / Spectral Essence / Black Pearl) ----
  AMBERGRIS_ELIXIR: 9,  // alchemist: the White Whale's ambergris → a premium fortune elixir
  WRAITHLIGHT_DRAUGHT: 10, // alchemist: the ghost fleet's essence → a short night-sight draught
  PEARL_INLAID_HULL: 11, // shipwright: N black pearls → a 0-stat "Pearl-Inlaid" hull cosmetic (permanent, craft-only)
  CHUM: 12,             // angler: grind FISH IN VOLUME → a Chum Bucket bait (the generic-FISH sink)
  // economy-close: the RELIC sink — melt a batch of relics into a fortune draught (repeatable, uncapped).
  RELIQUARY_DRAUGHT: 13, // alchemist: RELIC ×6 (+herb) → a Reliquary Draught (oil-slot luck), the relic surplus sink
  // ammo-economy: round shot is finite now — the smith casts the workhorse batch from iron (the iron demand loop).
  ROUND_AMMO: 14,        // smith: IRON ×1 → a batch of AMMO_BUY_BATCH round shot (capped at AMMO_ROUND_MAX)
} as const;
export type CraftId = (typeof CraftId)[keyof typeof CraftId];
export const CRAFT_COUNT = 15;
export interface CraftInput { mat: number; qty: number; }
export interface CraftRecipe {
  name: string;
  /** Required built building (ProdBuilding). */
  building: number;
  inputs: CraftInput[];
  /** Output: a material into the stockpile, 'ammo' into the carried broadside stock (AmmoType id), or
   *  'cosmetic' — a 0-stat cosmetic granted to the profile (id = Cosmetic id; unique, crafted once). */
  out: { kind: 'material' | 'ammo' | 'cosmetic'; id: number; qty: number };
  /** Profession track earning XP for this craft (wired in 2b.7). */
  prof: string;
  /** Flavor for the parchment craft menu. */
  desc: string;
  /** Minimum STATION level (see the craft-station matrix in PRODUCTION). Absent = 1 (any port station).
   *  3 = the rare tier: the Capital's master stations or a top-tier (lvl ≥ HOME_CRAFT_MIN_LVL) home building. */
  minLvl?: number;
}
export const CRAFT: Record<CraftId, CraftRecipe> = {
  [CraftId.GROG]:           { name: 'Grog',           building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.SUGAR, qty: 2 }, { mat: MaterialId.HERB, qty: 2 }],                                     out: { kind: 'material', id: MaterialId.GROG, qty: 1 },           prof: 'tavern_keeper', desc: 'A crew-steadying brew — a sail buff in a bottle.' },
  [CraftId.CHAIN_AMMO]:     { name: 'Chain Shot',     building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 3 }],                                                                           out: { kind: 'ammo', id: AmmoType.CHAIN, qty: 1 },               prof: 'smith',         desc: 'Forge a batch of rigging-tangling chain — iron, not doubloons.' },
  [CraftId.GRAPE_AMMO]:     { name: 'Grape Shot',     building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 3 }],                                                                           out: { kind: 'ammo', id: AmmoType.GRAPE, qty: 1 },               prof: 'smith',         desc: 'A batch of close-range grape — cheap firepower from your own iron.' },
  [CraftId.REPAIR_OIL]:     { name: 'Repair Oil',     building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.FISH_OIL, qty: 2 }, { mat: MaterialId.HERB, qty: 1 }],                                    out: { kind: 'material', id: MaterialId.REPAIR_OIL, qty: 1 },     prof: 'alchemist',     desc: 'Alchemy: faster repairs + bailing while active.' },
  [CraftId.SPYGLASS_TONIC]: { name: 'Spyglass Tonic', building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.FISH_OIL, qty: 2 }, { mat: MaterialId.STORM_ESSENCE, qty: 1 }],                            out: { kind: 'material', id: MaterialId.SPYGLASS_TONIC, qty: 1 }, prof: 'alchemist',     desc: 'Alchemy: a wider view of the chart while active.' },
  [CraftId.LUCK_ELIXIR]:    { name: 'Luck Elixir',    building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.FISH_OIL, qty: 2 }, { mat: MaterialId.HERB, qty: 2 }, { mat: MaterialId.SUGAR, qty: 2 }], out: { kind: 'material', id: MaterialId.LUCK_ELIXIR, qty: 1 },    prof: 'alchemist',     desc: 'Alchemy: fortune favors your casts + hauls while active.' },
  [CraftId.FINE_POWDER]:    { name: 'Fine Powder',    building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.STORM_ESSENCE, qty: 1 }, { mat: MaterialId.IRON, qty: 2 }],                                out: { kind: 'material', id: MaterialId.FINE_POWDER, qty: 1 },    prof: 'alchemist', minLvl: 3,     desc: "The alchemist's recipe, forged at the foundry: a faster reload while active." },
  [CraftId.TREASURE_MAP]:   { name: 'Treasure Map',   building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.MAP_FRAGMENT, qty: 4 }],                                                                   out: { kind: 'material', id: MaterialId.TREASURE_MAP, qty: 1 },   prof: 'alchemist',     desc: 'Piece four fragments into a chart at any port’s Ship’s Stores — then sail to the red X and dig.' },
  [CraftId.REPAIR_PLANK]:   { name: 'Repair Planks',  building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 2 }, { mat: MaterialId.IRON, qty: 1 }],                                          out: { kind: 'material', id: MaterialId.REPAIR_PLANK, qty: 3 },   prof: 'smith',         desc: 'The carpenter’s planks: carry them and patch a hull leak at sea with REPAIR.' },
  // ---- Sinks P1: the promised alchemy recipes for the orphan rares (burn the rare → a QoL consumable/cosmetic) ----
  [CraftId.AMBERGRIS_ELIXIR]:    { name: 'Ambergris Elixir',    building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.AMBERGRIS, qty: 1 }, { mat: MaterialId.HERB, qty: 2 }],           out: { kind: 'material', id: MaterialId.AMBERGRIS_ELIXIR, qty: 1 },    prof: 'alchemist', minLvl: 3, desc: 'The alchemist’s masterwork: the White Whale’s ambergris rendered into a long-burning fortune tincture.' },
  [CraftId.WRAITHLIGHT_DRAUGHT]: { name: 'Wraithlight Draught', building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.SPECTRAL_ESSENCE, qty: 1 }, { mat: MaterialId.SUGAR, qty: 1 }], out: { kind: 'material', id: MaterialId.WRAITHLIGHT_DRAUGHT, qty: 1 }, prof: 'alchemist', minLvl: 3, desc: 'Distil the ghost fleet’s essence into a draught of cold sight — see far through the dark, but not for long.' },
  [CraftId.PEARL_INLAID_HULL]:   { name: 'Pearl-Inlaid Hull',   building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.BLACK_PEARL, qty: 2 }],                                             out: { kind: 'cosmetic', id: PEARL_INLAID_COSMETIC, qty: 1 },          prof: 'smith', minLvl: 3,     desc: 'Inlay black pearls into your timbers — a nacre-white brag that costs nothing in a fight. Crafted once.' },
  // The FISH sink: grind a whole hoard of the generic catch into one bucket of chum (CHUM.FISH_COST fish → 1).
  [CraftId.CHUM]:                { name: 'Chum Bucket',         building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.FISH, qty: CHUM.FISH_COST }],                                     out: { kind: 'material', id: MaterialId.CHUM_BUCKET, qty: 1 },         prof: 'fishing',   desc: 'Grind a hoard of fish into a bucket of chum — toss it at a shoal to draw the fish in. Bites come faster and the waters run richer while it lasts.' },
  // economy-close: the RELIC sink — an uncapped, repeatable destination for the workhorse rare. Burns 6 relics
  // into one fortune draught (oil-slot luck, same band as Luck Elixir). Craft it forever; each one is 6 relics gone.
  [CraftId.RELIQUARY_DRAUGHT]:   { name: 'Reliquary Draught',   building: ProdBuilding.TAVERN,  inputs: [{ mat: MaterialId.RELIC, qty: 6 }, { mat: MaterialId.HERB, qty: 1 }],               out: { kind: 'material', id: MaterialId.RELIQUARY_DRAUGHT, qty: 1 },   prof: 'alchemist', minLvl: 3, desc: 'Melt a reliquary of salvaged relics into a tincture of old fortune — the same charm as the Luck Elixir, distilled from your relic hoard instead.' },
  // ammo-economy: the workhorse shot, cast from the gunner's own iron (cheapest per-broadside path — 1 iron/batch vs 3 for chain/grape).
  [CraftId.ROUND_AMMO]:          { name: 'Round Shot',          building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 1 }],                                                   out: { kind: 'ammo', id: AmmoType.ROUND, qty: 1 },                     prof: 'smith',                desc: 'Cast a batch of plain round shot — the workhorse of every broadside, from your own iron.' },
};
export function craftById(id: number): CraftRecipe | undefined { return (CRAFT as Record<number, CraftRecipe>)[id]; }

/**
 * ---- Consumable buffs (2b.6) ---- At most ONE drink + ONE oil/potion active at a time, +~8% for 10 min
 * (spyglass is the client-only exception at +25% sight). The SIM-touching effects (sail, reload) ride the
 * SelfState so client PREDICTION and the server apply them identically, exactly like riggingDamage. An
 * item is spent from the BASE inventory (not the hold), usable AT SEA (a quick sip from the HUD).
 */
export const BUFF = { DURATION_S: 600 } as const;
export interface BuffDef {
  /** Which mutually-exclusive slot the buff occupies. 'drink'/'oil' are the 10-min sips; 'fish' is the
   *  SHORT, cooldown-gated tactical slot (Wave — Peixe = Sink de PvP) that STACKS with a drink + an oil. */
  slot: 'drink' | 'oil' | 'fish';
  /** Sail multiplier (grog / Leviathan's Roe) — folded into SelfState.speedBuffMul, applied in prediction + server. */
  sailMul?: number;
  /** Reload-time multiplier (<1 = faster; fine powder) — server-side reload timers. */
  reloadMul?: number;
  /** Bail/repair-rate multiplier (repair oil / Harbor Mackerel) — server-side flooding. */
  repairMul?: number;
  /** Luck multiplier on rare/jackpot rolls (luck elixir) — server-side. */
  luckMul?: number;
  /** Minimap sight multiplier (spyglass tonic) — CLIENT-ONLY, non-sim (ok to be generous). */
  viewMul?: number;
  /** Turn-rate multiplier (Reef Snapper) — folded into SelfState.turnBuffMul, applied in prediction + server. */
  turnMul?: number;
  /** Incoming-leak multiplier on a hull hit (<1 = tougher; Ironscale Grouper) — server-side flooding. */
  leakResistMul?: number;
  /** Pre-bite fishing-wait multiplier (<1 = faster bites; Chum Bucket) — server-side fishing only, folds into
   *  the same wait factor as the Amber Lure charm. Never touches the reel TIMING (skill stays skill). */
  baitWaitMul?: number;
  /** Fishing-luck multiplier (>1 = better tier/species/jackpot odds; Chum Bucket) — server-side, applied only
   *  in the catch resolve (NOT the global luckMul that also drives wreck/ghost drops). */
  baitLuckMul?: number;
  /** Active duration (s). Absent → BUFF.DURATION_S (the 10-min drink/oil default). Fish buffs are SHORT. */
  durationS?: number;
  /** Re-sip cooldown (s) from activation. Absent → 0 (drink/oil never gate). Fish buffs enforce a real cooldown. */
  cooldownS?: number;
  label: string;
  icon: string;
}
export const BUFFS: Record<number, BuffDef> = {
  [MaterialId.GROG]:           { slot: 'drink', sailMul: 1.08,     label: '+8% sail',   icon: '🍺' },
  [MaterialId.REPAIR_OIL]:     { slot: 'oil',   repairMul: 1.08,   label: '+8% repair', icon: '🧴' },
  [MaterialId.SPYGLASS_TONIC]: { slot: 'oil',   viewMul: 1.25,     label: '+25% sight', icon: '🔭' },
  [MaterialId.LUCK_ELIXIR]:    { slot: 'oil',   luckMul: 1.08,     label: '+8% luck',   icon: '🍀' },
  [MaterialId.FINE_POWDER]:    { slot: 'oil',   reloadMul: 1 / 1.08, label: '+8% reload', icon: '✨' },
  // ---- Fish species — SHORT, cooldown-gated tactical edges (the PvP sink). Own 'fish' slot: stacks with a
  //      drink + an oil, but only ONE fish buff at a time and the cooldown (≥ duration) blocks re-sipping.
  //      Magnitudes modest + durations short + real cooldowns → skill-first, never breaks the ceiling. ----
  [MaterialId.HARBOR_MACKEREL]:   { slot: 'fish', repairMul: 1.15,     durationS: 15, cooldownS: 45, label: '+15% repair',  icon: '🐠' },
  [MaterialId.REEF_SNAPPER]:      { slot: 'fish', turnMul: 1.12,       durationS: 15, cooldownS: 50, label: '+12% helm',    icon: '🐡' },
  [MaterialId.IRONSCALE_GROUPER]: { slot: 'fish', leakResistMul: 0.87, durationS: 18, cooldownS: 60, label: '-13% leaks',   icon: '🦈' },
  [MaterialId.LEVIATHAN_ROE]:     { slot: 'fish', sailMul: 1.15,       durationS: 10, cooldownS: 90, label: '+15% surge',   icon: '🥚' },
  // ---- Sinks P1: premium alchemy oils (own the single OIL slot; NO combat power — pure economy/info QoL). ----
  //      Ambergris Elixir = the top of the line: a bit MORE luck than the common Luck Elixir AND 1.5× the
  //      duration, so the rarest ingredient buys a longer, richer fortune. Wraithlight = a SHORT, strong
  //      night-sight (render only) — the ghost fleet's own glow bottled. Longer/shorter than the 10-min default
  //      via durationS (handleConsume reads buffDurationS for the oil slot too).
  [MaterialId.AMBERGRIS_ELIXIR]:    { slot: 'oil', luckMul: 1.10, durationS: 900, label: '+10% luck',  icon: '🪔' },
  [MaterialId.WRAITHLIGHT_DRAUGHT]: { slot: 'oil', viewMul: 1.35, durationS: 300, label: '+35% sight', icon: '🔮' },
  // ---- Chum Bucket (the FISH sink) — a fishing BAIT in the 'fish' slot: NO combat power, only faster/richer
  //      bites while it lasts. Cooldown = duration (≥ duration ⇒ non-stack). Combat-neutral: the combat getters
  //      key off the specific SPECIES ids, so chum in the fish slot grants no turn/sail/repair/leak edge. ----
  [MaterialId.CHUM_BUCKET]:         { slot: 'fish', baitWaitMul: CHUM.WAIT_MUL, baitLuckMul: CHUM.LUCK_MUL, durationS: CHUM.DURATION_S, cooldownS: CHUM.DURATION_S, label: '−20% bite wait', icon: '🪣' },
  // economy-close: the RELIC sink's payoff — a fortune draught in the SAME luck band as the common Luck Elixir
  // (no power creep: only one oil is ever active, and this equals — never exceeds — the existing +8% luck oil).
  // Default 10-min duration (omits durationS). It exists to DRAIN relic surplus, not to raise the ceiling.
  [MaterialId.RELIQUARY_DRAUGHT]:   { slot: 'oil', luckMul: 1.08, label: '+8% luck', icon: '🏵' },
};
export function buffOf(materialId: number): BuffDef | undefined { return (BUFFS as Record<number, BuffDef>)[materialId]; }
/** A buff's active duration (fish buffs are short; drink/oil fall back to the 10-min default). */
export function buffDurationS(materialId: number): number { return buffOf(materialId)?.durationS ?? BUFF.DURATION_S; }
/** A buff's re-sip cooldown from activation (0 = ungated; only the fish slot enforces one). */
export function buffCooldownS(materialId: number): number { return buffOf(materialId)?.cooldownS ?? 0; }
/** Consumable materials that can be activated (for the HUD quick-menu) — drink + oil + the 4 fish species. */
export const CONSUMABLE_IDS: readonly number[] = Object.keys(BUFFS).map(Number);

/**
 * ---- Wraithlight ghost-sight (farms-demand-first, v54) ---- The Wraithlight Draught used to be a
 * render-only +sight oil (no real effect). It now has TEETH, server-decided: while the oil is active, the
 * viewer's self-snapshot carries up to MAX_PINGS `spectral` indicators for what the cold sight senses
 * within RANGE (4× the 600 m AOI): RICH static wrecks that still hold treasure (the `rich` flag was
 * server-secret — pearl hunters finally have a reason to buy the draught) and Ghost Fleet ships beyond the
 * horizon (essence hunters find their prey at night). Anti-cheat: NEVER a player — only NPC ghosts and
 * static wrecks; ghosts ride as coarse bearing+distance indicators, never exact tracks. A viewer without
 * the buff receives nothing (server-side omission, unforgeable — same principle as AOI).
 */
export const WRAITHLIGHT = {
  /** Sensing radius (m) of the active draught. */
  RANGE: 2400,
  /** Max indicators per snapshot (byte budget: 1 + n×5 bytes). Nearest first. */
  MAX_PINGS: 4,
  /** Metres per wire unit of a ping's quantized distance (u8 → saturates at ~4 km). */
  DIST_QUANT: 16,
} as const;
/** What a spectral ping points at. RICH_WRECK carries the wreck id (statics are world-known — the reveal is
 *  WHICH one is rich); GHOST_SHIP is a pure bearing+distance indicator (id 0). */
export const SpectralKind = { RICH_WRECK: 0, GHOST_SHIP: 1 } as const;
export type SpectralKind = (typeof SpectralKind)[keyof typeof SpectralKind];

/**
 * ---- Professions (2b.7) ---- Nine trades leveled by DOING (lv 1–50). Each domain grants a small passive
 * PERK, capped at +10% at lv50 (linear) — skill-first: a perk never breaks the competitive ceiling, and the
 * sim-touching ones (sailing→sail, gunnery→reload) COMPOSE with the consumable buffs, both riding the same
 * SelfState factors so client prediction stays exact. XP + levels ride S_PROFESSIONS (owner-only, throttled).
 */
export const PROF_MAX_LEVEL = 50;
export const PROF_PERK_MAX = 0.10; // +10% at lv50
export const PROFESSION_KEYS = ['sailing', 'gunnery', 'plunder', 'fishing', 'tavern_keeper', 'smith', 'alchemist', 'diver', 'privateer'] as const;
export type ProfessionKey = (typeof PROFESSION_KEYS)[number];
export interface ProfMeta { name: string; icon: string; perk: string; }
export const PROFESSIONS: Record<string, ProfMeta> = {
  // Rope-ring crest medallions (GPT-authored, alpha-cut, /icons/professions/) — same itemIcon()
  // contract as MATERIALS: <img> on HTML surfaces, ⚓ fallback on text-only.
  sailing:       { name: 'Sailing',   icon: '/icons/professions/sailing.webp',   perk: 'up to +10% sail speed' },
  gunnery:       { name: 'Gunnery',   icon: '/icons/professions/gunnery.webp',   perk: 'up to +10% faster reload' },
  plunder:       { name: 'Plunder',   icon: '/icons/professions/plunder.webp',   perk: 'up to +10% steal rate' },
  fishing:       { name: 'Fisher',    icon: '/icons/professions/fishing.webp',   perk: 'up to +10% catch luck' },
  tavern_keeper: { name: 'Taverner',  icon: '/icons/professions/tavern_keeper.webp', perk: 'up to +10% brew yield' },
  smith:         { name: 'Smith',     icon: '/icons/professions/smith.webp',     perk: 'up to +10% forge yield' },
  alchemist:     { name: 'Alchemist', icon: '/icons/professions/alchemist.webp', perk: 'up to +10% potion yield' },
  diver:         { name: 'Wreck Diver', icon: '/icons/professions/diver.webp',   perk: 'up to +10% faster dives' },
  privateer:     { name: 'Privateer',  icon: '/icons/professions/privateer.webp', perk: 'up to +10% bounty reward' },
};
/** XP awards per action (fishing/diving/craft use their own feature constants). */
export const PROF_XP = { GUNNERY_HIT: 5, SAIL_PER_50M: 1, PLUNDER_PER_DBL: 0.02, CRAFT: 8, MARKET_SALE: 4 } as const;

/** Cumulative XP to REACH level L (L=1 → 0). A rising curve so lv50 is a real long grind. */
export function xpForLevel(level: number): number {
  return level <= 1 ? 0 : Math.round(50 * Math.pow(level - 1, 1.6));
}
/** The level (1..PROF_MAX_LEVEL) for a given XP total. */
export function professionLevel(xp: number): number {
  let lvl = 1;
  for (let L = 2; L <= PROF_MAX_LEVEL; L++) { if (xp >= xpForLevel(L)) lvl = L; else break; }
  return lvl;
}
/** Progress toward the next level, 0..1 (1 at the cap). */
export function professionLevelFraction(xp: number): number {
  const lvl = professionLevel(xp);
  if (lvl >= PROF_MAX_LEVEL) return 1;
  const cur = xpForLevel(lvl), next = xpForLevel(lvl + 1);
  return next > cur ? Math.max(0, Math.min(1, (xp - cur) / (next - cur))) : 1;
}
/** The passive perk factor for a track's XP: 1.0 at lv1 → 1+PROF_PERK_MAX at lv50 (linear). */
export function professionPerkFactor(xp: number): number {
  return 1 + PROF_PERK_MAX * (professionLevel(xp) - 1) / (PROF_MAX_LEVEL - 1);
}

/* ============================================================================
 * ---- Wave 3: Consequence & content ----
 * The sea grows a memory and a cast of characters: drifting bottles with a stranger's last words,
 * a Sea Journal of the day's deeds, a hunted White Whale, WANTED corsairs, merchant convoys, the calm
 * Eye of a storm, buried-treasure cartography, ship's carpentry, P2P freight, a Capital auction house,
 * and a spectral night fleet. All server-authoritative, all deterministic (room PRNG only).
 * ========================================================================== */

/** ---- Message in a Bottle (3.1) ---- Bottles drift rarely across the open sea; sail over one to fish
 *  it up and read a stranger's note — flavor lore, a treasure/wreck hint, a few coins, or a map fragment.
 *  Server-owned + deterministic (room PRNG); collected on proximity (a light touch, no minigame). */
export const BOTTLE = {
  /** Most bottles adrift at once (the sea is vast — they stay a rare, lucky find). */
  MAX_ACTIVE: 6,
  /** Jittered seconds between spawn attempts. */
  EVERY_MIN_S: 90, EVERY_MAX_S: 210,
  /** Sail within this (m) of a bottle to fish it up. */
  PICKUP_R: 26,
  /** Min distance (m) from any base/island/outpost when spawning (open water only). */
  CLEAR_DIST: 120,
  /** A collected bottle sometimes pays out: chance of a small coin purse, and (rarer) a map fragment. */
  COIN_CHANCE: 0.34, COIN_MIN: 60, COIN_MAX: 260,
  FRAGMENT_CHANCE: 0.12,
} as const;
/** What a fished-up bottle turned out to hold (EV_BOTTLE_MSG.kind) — drives the client toast styling. */
export const BottleKind = { LORE: 0, HINT: 1, COIN: 2, FRAGMENT: 3 } as const;
export type BottleKind = (typeof BottleKind)[keyof typeof BottleKind];
/** The note pools (EN). LORE = flavor; HINT = a rumor toward wrecks/treasure. Coin/fragment reuse a HINT-ish
 *  line + the payout. Picked by the room PRNG so every bottle reads unique but stays deterministic. */
export const BOTTLE_LORE: readonly string[] = [
  'To whoever finds this: the gold was never the point. It was the sea. — a drowned captain',
  'They took my ship but not my name. Remember the Gull of Nassau.',
  'Tell my daughter the horizon is not the end. It only looks that way.',
  'I buried nothing. I spent it all, and I regret not a doubloon.',
  'If the water is calm, be twice as afraid. Signed, a wiser corpse.',
  'Three storms I outran. The fourth I did not. Sail lighter than I did.',
  'The sea keeps every promise you make to it. Choose them well.',
];
/** ---- Sea Journal (3.2) ---- A living feed of the sea's recent deeds (sinkings, records, big sales,
 *  the Whale, WANTED corsairs), auto-generated server-side and shown in a parchment panel. `kind` drives
 *  the client icon/tint. Kept to the last JOURNAL_MAX lines (a rolling ledger of the day). */
export const JOURNAL_MAX = 14;
export const JournalKind = { SINK: 0, TREASURE: 1, RECORD: 2, SALE: 3, WHALE: 4, BOUNTY: 5, GHOST: 6 } as const;
export type JournalKind = (typeof JournalKind)[keyof typeof JournalKind];
/** A big goods sale is journal-worthy at/above this doubloon total (keeps the feed to notable trades). */
export const JOURNAL_SALE_MIN = 2000;

/** ---- Figurehead Trophies (3.3) ---- Achievements become a carved bow ornament, earned + mounted
 *  server-side (never bought or traded — a badge of honor). The equipped trophy id rides EV_SPAWN so
 *  every viewer sees it on your prow; the client renders a distinct figurehead per id (the White Whale
 *  trophy re-uses the whale GLB, tiny, on the bowsprit). id 0 = none. */
export const TrophyId = { WHALE: 1, CURSED: 2, PRIVATEER: 3, GHOST: 4 } as const;
export type TrophyId = (typeof TrophyId)[keyof typeof TrophyId];
export interface TrophyDef { name: string; desc: string; }
export const TROPHIES: Record<number, TrophyDef> = {
  [TrophyId.WHALE]:     { name: 'White Whale Figurehead', desc: 'Carved from the leviathan that nearly took you under. Land the killing blow to earn it.' },
  [TrophyId.CURSED]:    { name: 'Cursed Crown',           desc: 'Prised from the map-wide hoard — it hums against the wind.' },
  [TrophyId.PRIVATEER]: { name: "Corsair's Skull",        desc: 'A hunter of the hunted. Bring enough WANTED captains to justice.' },
  [TrophyId.GHOST]:     { name: 'Spectral Lantern',       desc: 'Torn from a ship that should not sail. It never quite goes dark.' },
};
export function trophyById(id: number): TrophyDef | undefined { return (TROPHIES as Record<number, TrophyDef>)[id]; }

/** ---- Bounty Board / WANTED (3.6) ---- Sinking ships and plundering raise a bounty on YOUR head. Cross
 *  WANTED_THRESHOLD and you fly WANTED — red on every captain's chart, a target for all. Whoever sinks a
 *  WANTED captain collects a share of the bounty (routed through the daily mint cap) + Privateer XP; the
 *  dead captain's slate is wiped. Gated by the same anti kill-trading guard as gold, so it can't be farmed. */
export const BOUNTY = {
  PER_KILL: 250,          // bounty added to YOUR head per ship you sink
  PER_PLUNDER_PCT: 0.05,  // + this fraction of doubloons you plunder in a live raid
  WANTED_THRESHOLD: 800,  // at/above this bounty you fly WANTED (red on the chart, ShipFlag.WANTED)
  MAX: 60000,
  PAYOUT_FRACTION: 0.6,   // fraction of a WANTED victim's bounty paid to their killer (the rest is the crown's cut)
  PRIVATEER_XP_BASE: 40,  // Corsair XP for felling a WANTED captain (+ a touch scaled by their bounty)
  PRIVATEER_XP_PER_1K: 8, // + this much XP per 1000 bounty on the victim's head
  PRIVATEER_TROPHY_LEVEL: 10, // Privateer level that earns the Corsair's Skull figurehead
} as const;

/** ---- The White Whale (3.5) ---- A scheduled hunt (1–2×/day) announced map-wide. The leviathan MIGRATES
 *  the sea, DIVES (submerged + invulnerable) and RESURFACES (a vulnerability window), and HEADBUTTS hunters
 *  who crowd it (reuses the ram knockback + a leak). High HP shared across every hunter — the captain with
 *  the MOST accumulated damage lands the prize: ambergris (a legendary tradeable) + the Whale figurehead.
 *  Fully server-authoritative + deterministic (room PRNG for waypoints). Its transform rides a throttled
 *  map-wide EV_WHALE so everyone sees the marker + spout and can converge. */
export const WHALE = {
  HP: 4200,                  // total damage to fell it (many hunters chip in)
  SPEED: 6.5,                // migration cruise speed (m/s)
  TURN_RATE: 0.4,            // rad/s toward the current waypoint
  RADIUS: 15,                // cannonball hit radius (it is huge)
  SURFACE_S: 20,             // stays up (vulnerable) this long, then sounds
  DIVE_S: 8,                 // submerged (invulnerable, no damage taken) this long
  HEADBUTT_R: 30,            // retaliates against hunters within this while surfaced
  HEADBUTT_COOLDOWN_S: 3.5,  // per-hunter cooldown on a headbutt
  HEADBUTT_KNOCK: 7.5,       // m/s shove (reuses the ram knock impulse)
  HEADBUTT_LEAK: 0.10,       // flood/sec leak the headbutt punches in the hull
  HEADBUTT_BURST: 0.16,      // instant waterLevel burst on a headbutt
  WAYPOINT_R: 140,           // within this of the target waypoint → pick the next
  FIRST_S: 720,              // first appearance ~12 min after a fresh server boot (so it's reachable)
  EVERY_MIN_S: 43200,        // then 1–2×/day
  EVERY_MAX_S: 86400,
  LIFETIME_S: 480,           // if not felled, it sounds and leaves after ~8 min
  AMBERGRIS_MIN: 2,          // prize (ambergris) to the top-damage hunter
  AMBERGRIS_MAX: 4,
  RUNNERUP_AMBERGRIS: 1,     // a consolation lump to the 2nd-best hunter (if they did real damage)
  MARK_EVERY_TICKS: 6,       // broadcast the whale marker every N ticks (~5 Hz at 30 Hz tick)
  MIN_DAMAGE_TO_QUALIFY: 100,// a hunter must have dealt at least this to place for a prize
  /** Damage a single cannonball deals to the whale = HIT_POINTS × (ball leak / base leak), so damage
   *  upgrades + heavier ammo scale it. A base broadside (3–4 balls) ≈ 90–120; HP/that ≈ a multi-minute hunt. */
  HIT_POINTS: 30,
} as const;
/** EV_WHALE.state — the whale marker's phase (drives the client mesh + announces + SFX). */
export const WhaleState = { SURFACE: 0, DIVE: 1, SPAWN: 2, DEAD: 3, GONE: 4 } as const;
export type WhaleState = (typeof WhaleState)[keyof typeof WhaleState];

/** ---- Merchant convoy (3.7) ---- NPC merchant ships cross between outposts along the trade winds, laden
 *  with goods. A pirate can ATTACK them (sink a merchant → loot its cargo + gold into your hold) or accept
 *  an ESCORT CONTRACT (shadow the convoy; a paycheck for every merchant that reaches port alive). The NPCs
 *  navigate with the SHARED stepShipBody (deterministic), so client + server never disagree on where they are. */
export const CONVOY = {
  FIRST_S: 210,               // first convoy ~3.5 min after boot
  EVERY_MIN_S: 300, EVERY_MAX_S: 600, // then every 5–10 min
  MAX_ACTIVE: 2,
  SHIPS_MIN: 2, SHIPS_MAX: 3, // merchants per convoy
  SPEED_MUL: 0.8,             // laden + cautious
  SPACING: 36,                // line-astern formation spacing (m)
  ARRIVE_MARGIN: 30,          // reached the destination outpost within (OUTPOST_RADIUS + this)
  GOLD_MIN: 200, GOLD_MAX: 600, // gold each merchant drops when sunk
  ESCORT_RANGE: 240,          // accept escort within this of a convoy; must be within it at delivery to be paid
  ESCORT_PAYOUT: 900,         // doubloons to the escort per surviving merchant delivered UNDER ESCORT
  /** Both halves of ESCORT_RANGE are ENFORCED server-side (room.ts escortPresentAt, faucet audit 2026-07-26):
   *  the payout counts only merchants that reach port with the escort alive and within range of the arriving
   *  hull — hire-and-go-AFK pays nothing. A captain also holds ONE active contract at a time; accepting a new
   *  convoy releases the previous one back to the board (it used to allow stamping both live convoys). */
  DESPAWN_MARGIN_S: 30,       // grace after arrival before the merchant mesh is removed
  /* ⛔ U-10 (red-team 2026-07-26) — PRESENCE OVER THE VOYAGE, not just at the pier.
   * The arrival check above is necessary but NOT sufficient: an escort ANCHORED at the destination port
   * satisfies it trivially. MEASURED in runtime: 1.800 ⓓ (two merchants, the FULL payout) to a hull anchored
   * 38 m from Tortuga Bank with 0 m of drift, and a 3-tab multi-box ceiling of ~18.000 ⓓ/h. So presence is now
   * SAMPLED along the route and the payout is scaled by the fraction of the voyage actually shadowed. */
  /** Sim-seconds between presence samples of a live convoy (0.5 Hz — the sweep is O(convoys × merchants)). */
  ESCORT_SAMPLE_S: 2,
  /** Below this fraction of samples in range the contract pays NOTHING (the port camper lands at ~0.05–0.10:
   *  240 m of approach out of a 300–600 s voyage). Above it the payout is PRORATED by the fraction, so a
   *  captain who joins a convoy halfway and shadows it home is paid for exactly the half they escorted. */
  ESCORT_MIN_PRESENCE: 0.15,
  /** A convoy resolved with fewer samples than this is paid on the arrival check alone — too short a voyage to
   *  judge a fraction (also what keeps a single-tick test/teleport honest instead of accidentally unpaid). */
  ESCORT_MIN_SAMPLES: 4,
  /** Sim-seconds of OFFLINE escort that still count as present — a dropped connection / a crashed tab is not
   *  a camper, and losing a whole contract to 20 s of bad mobile signal would be the cure killing the patient. */
  ESCORT_OFFLINE_GRACE_S: 45,
} as const;
/** Materials a merchant carries in its hold (looted by whoever sinks it). Weighted toward common trade goods. */
export const CONVOY_LOOT: readonly number[] = [0, 2, 3, 6, 7, 2, 3, 4]; // FISH, IRON, WOOD, HERB, SUGAR, IRON, WOOD, RELIC (MaterialId values)

/** ---- Cartographer (3.9) ---- Rare map FRAGMENTS drop from fishing / diving / drifting bottles; assemble
 *  MAP_PIECES of them into a tradeable TREASURE MAP at the scholar's table (a craft). Carry a map and the
 *  server marks an X on a far island; sail there and DIG (a short exposed bar) to unearth the hoard.
 *  (economy-close 2026-07-15: convoy is deliberately NOT a fragment source — CONVOY_LOOT carries only trade
 *  goods. A fragment feeds treasure-map → dig → a GOLD MINT, so widening that faucet runs against the
 *  economy-closure pass; the three sources above stay the only ones.) */
export const CARTOGRAPHER = {
  MAP_PIECES: 4,               // fragments to assemble one treasure map (matches the craft recipe)
  DIG_R: 34,                   // dig within this (m) of the island X
  DIG_MAX_SPEED: 2.5,          // and moving no faster than this (park to dig)
  DIG_TIME: 5,                 // seconds of the exposed dig bar
  GOLD_MIN: 900, GOLD_MAX: 2800,
  RELIC_CHANCE: 0.55,          // the dig often turns up a relic too
  AMBERGRIS_CHANCE: 0.05,      // and, rarely, a lump of ambergris
  FRAGMENT_FISH_CHANCE: 0.05,  // a rare-tier catch sometimes hides a fragment
  FRAGMENT_DIVE_CHANCE: 0.07,  // a wreck dive sometimes turns up a fragment
} as const;

/** ---- Capital auction house (3.12) ---- At the Capital (Port Royal) only: seal-bid auctions for rare
 *  goods (ambergris, treasure maps, black pearls, relics). A seller escrows the goods + sets a minimum bid
 *  and a deadline; bidders escrow doubloons (an outbid bidder is refunded at once). At the deadline the
 *  high bidder wins the goods, the seller banks the gold (minus a house cut) — delivered to each estate. */
export const AUCTION = {
  DURATION_S: 600,        // 10-min auctions (settle within a session)
  MIN_BID: 100,
  MAX_BID: 5_000_000,
  MIN_INCREMENT: 50,      // a new bid must beat the standing high by at least this
  MAX_ACTIVE: 6,          // active auctions per seller
  FEE_PCT: 0.06,          // house cut on the winning bid (a sink, burned/treasury)
  MIN_QTY: 1, MAX_QTY: 999,
} as const;
/** Rare materials that may be auctioned at the Capital. (Sinks P1: Spectral Essence joins so the night ghost
 *  fleet's drop finally has an in-game destination — a P2P sale, never an NPC mint.) */
export const AUCTION_ITEMS: readonly number[] = [MaterialId.AMBERGRIS, MaterialId.TREASURE_MAP, MaterialId.BLACK_PEARL, MaterialId.RELIC, MaterialId.SPECTRAL_ESSENCE];

/** ---- Sinks P2: the Salvage Appraiser (a Dredge station) ---- Converts the four orphan RARES into Relic
 *  Shards (the gacha dup-currency, which already sinks in the Shard Shop). NEVER mints ⓓ. Rates are pro-relic:
 *  the common/most-farmed rare (relic) yields the FEWEST shards per unit, the rarest (ambergris) the MOST.
 *  Calibrated to stay FAR poorer than pulling: even ambergris is ~280 ⓓ-of-sell-value per shard, and a rare
 *  Shard-Shop item costs 600 shards → farming rares can never undercut the gacha (docs/design/GACHA.md). It is
 *  a "melt your surplus into a trickle of gacha progress" sink, not a shard farm. `per` units → `shards`. */
export const APPRAISE_RATES: Record<number, { per: number; shards: number }> = {
  [MaterialId.RELIC]:            { per: 3, shards: 1 }, // the cheap, most-farmed rare — a poor rate on purpose
  [MaterialId.BLACK_PEARL]:      { per: 2, shards: 3 },
  [MaterialId.SPECTRAL_ESSENCE]: { per: 2, shards: 2 },
  [MaterialId.AMBERGRIS]:        { per: 1, shards: 5 }, // the rarest — the best rate, still far below a pull
};
export function appraiseRate(materialId: number): { per: number; shards: number } | undefined {
  return APPRAISE_RATES[materialId];
}
/** The materials the Salvage Appraiser accepts (stable order for the UI). */
export const APPRAISE_ITEMS: readonly number[] = [MaterialId.RELIC, MaterialId.BLACK_PEARL, MaterialId.SPECTRAL_ESSENCE, MaterialId.AMBERGRIS];

/** ---- Sinks P4: an HONEST inventory one-liner ---- There is NO NPC that buys goods — the `sell` field is
 *  only a reference quote and the real market is P2P — so the old "~X 🪙 each at market" over-promised
 *  liquidity that doesn't exist. This says what the item is FOR, derived from the catalog (a sipped buff, a
 *  craft/alchemy ingredient, a rare you can appraise/auction), so a captain knows why to keep it. */
export function materialUse(id: number): string {
  const b = buffOf(id);
  if (b) return `Sip at sea · ${b.label}`;
  if (id === MaterialId.REPAIR_PLANK) return 'Carry it — patch a leak at sea';
  if (id === MaterialId.TREASURE_MAP) return 'Chart to a buried hoard — or auction it';
  if (id === MaterialId.MAP_FRAGMENT) return 'Piece four into a treasure map';
  const craftInput = Object.values(CRAFT).some((r) => r.inputs.some((i) => i.mat === id));
  const rare = APPRAISE_ITEMS.includes(id) || AUCTION_ITEMS.includes(id);
  if (craftInput && rare) return 'Craft ingredient · appraise or auction the rest';
  if (craftInput) return 'Crafting & alchemy ingredient';
  if (rare) return 'Salvage — appraise for shards or auction';
  return 'Trade with other captains at the market';
}

/** ---- "Where do I find this?" one-liner (materialUse's twin) ---- One diegetic EN line per material
 *  saying WHERE it comes from, so a captain staring at a recipe or the Appraiser knows what to go DO.
 *  Sources are the audited faucets in the server room (fishing/diving/dig/ghost fleet/whale/convoy) and
 *  the base production/craft chains — keep this table in lockstep when a faucet moves. CLIENT-ONLY text,
 *  zero wire. */
const MATERIAL_SOURCE: Record<number, string> = {
  [MaterialId.FISH]: 'Cast a line anywhere — every honest reel lands some',
  [MaterialId.FISH_OIL]: 'Pressed from a lucky catch — keep casting',
  [MaterialId.IRON]: 'Salvaged from sunken wrecks — or plundered off a merchant convoy',
  [MaterialId.WOOD]: 'Pried from sunken wrecks — or plundered off a merchant convoy',
  [MaterialId.RELIC]: 'Salvaged from wrecks and dug from the sand',
  [MaterialId.STORM_ESSENCE]: 'Harvested sailing a fierce storm — or lingering in its eye',
  [MaterialId.HERB]: 'Grown on the Crop Plot back at your island',
  [MaterialId.SUGAR]: 'Grown on the Crop Plot back at your island',
  [MaterialId.GROG]: 'Brewed at a Tavern from sugar and herbs',
  [MaterialId.REPAIR_OIL]: 'Tavern alchemy — fish oil and a pinch of herbs',
  [MaterialId.SPYGLASS_TONIC]: 'Tavern alchemy — fish oil cut with storm essence',
  [MaterialId.LUCK_ELIXIR]: 'Tavern alchemy — fish oil, herbs and sugar',
  [MaterialId.FINE_POWDER]: "A master's Foundry recipe — storm essence and iron",
  [MaterialId.BLACK_PEARL]: "A jackpot reel — or a rich wreck's oyster bed",
  [MaterialId.AMBERGRIS]: "The White Whale's bounty — or a lucky dig",
  [MaterialId.MAP_FRAGMENT]: 'Snagged fishing, diving wrecks, or sealed in a drifting bottle',
  [MaterialId.TREASURE_MAP]: 'Pieced from four fragments at a Tavern — or won at auction',
  [MaterialId.REPAIR_PLANK]: 'Cut at a Foundry from wood and iron',
  [MaterialId.SPECTRAL_ESSENCE]: 'Drops from Ghost Fleet ships — hunt them after dark',
  [MaterialId.HARBOR_MACKEREL]: 'The everyday catch — any honest reel',
  [MaterialId.REEF_SNAPPER]: 'A perfect reel — the skilled angler’s usual prize',
  [MaterialId.IRONSCALE_GROUPER]: 'A rare perfect reel — richer in dangerous waters',
  [MaterialId.LEVIATHAN_ROE]: 'A legendary perfect reel — fortune and fierce waters help',
  [MaterialId.AMBERGRIS_ELIXIR]: "A master alchemist's Tavern craft — rendered from ambergris",
  [MaterialId.WRAITHLIGHT_DRAUGHT]: "A master alchemist's Tavern craft — distilled spectral essence",
  [MaterialId.CHUM_BUCKET]: 'Ground at a Tavern from a hoard of fish',
  [MaterialId.RELIQUARY_DRAUGHT]: "A master alchemist's Tavern craft — melted from six relics",
};
/** WHERE a material comes from — one diegetic EN line per MaterialId (see MATERIAL_SOURCE). */
export function materialSource(id: number): string {
  return MATERIAL_SOURCE[id] ?? 'Ask around the docks — the sea provides';
}

/** ---- P2P freight (3.11) ---- A player posts a haul at an outpost: goods (from their hold) + a fee
 *  (escrowed from their bank), bound for another outpost. A courier accepts (the goods ride THEIR hold —
 *  piratable), sails it there, and delivering pays them the fee (the poster's goods land in their base
 *  depot). Sink the courier and the goods are lootable; the poster's fee is refunded. Escrow is server-side. */
export const FREIGHT = {
  MIN_QTY: 1, MAX_QTY: 999,
  MIN_FEE: 50, MAX_FEE: 500_000,
  MAX_POSTED: 4,            // active posts per player
  // ...and the courier side of the same ceiling: contracts one captain may have IN TRANSIT at once. Symmetric
  // with MAX_POSTED on purpose — a hauler juggling four manifests is already generous for one hull, and the cap
  // bounds the blast radius of any future cargo hand-off bug to four contracts instead of every open contract.
  MAX_ACCEPTED: 4,
  FEE_PCT: 0.05,           // a small cut of the fee is a sink (burned/treasury), the rest to the courier
  /** ⛔ U-9 (red-team 2026-07-26) — CONTRACTS MUST DIE. Before these, a freight contract had NO expiry of any
   *  kind: `CANCEL` was refused outside OPEN and the only return path (`failFreightForCourier`) ran solely
   *  inside `sink()`. A courier who accepted and closed the tab kept up to 999 units of ANY material and
   *  froze the poster's fee (up to MAX_FEE = 500.000 ⓓ) plus one of the 4 MAX_POSTED slots FOREVER.
   *  An OPEN post nobody hauls parked exactly the same value with nobody at fault at all. */
  /** An OPEN post with no courier expires after this and the fee + crates go back to the poster's depot. */
  POST_TTL_MS: 48 * 3_600_000,
  /** An IN_TRANSIT manifest expires this long after the crates went aboard (`handedMs`): the fee returns to
   *  the poster and the contracted crates are clawed back (courier hold first, then their depot) and
   *  delivered to the poster. Generous vs. the ~11 min a full-world crossing takes, so a session break does
   *  not cost an honest hauler the job. NOTE: a visible countdown on the client needs a wire field (RETIDO). */
  TRANSIT_TTL_MS: 6 * 3_600_000,
} as const;

/** ---- Ship's carpenter (3.10) ---- Repair planks (crafted at the foundry) are a carried consumable: while
 *  you hold REPAIR with planks aboard and a leak open, a plank seals the worst leak FAR faster than bare
 *  hands and bails a burst of water — one plank spent per patch. Out of planks, repair falls back to the
 *  slow bare-handed patch. Integrates with the existing flooding/plank loop. */
export const CARPENTER = {
  PLANK_SPEEDUP: 3.2,       // a repair plank patches a leak this many times faster than bare hands
  PLANK_DRAIN_BURST: 0.07,  // + this waterLevel bailed out instantly when a plank seals a leak
} as const;

/** ---- Eye of the Storm (3.8) ---- The storm is a GLOBAL intensity with no centre; so, at the height of a
 *  fierce storm, a positioned EYE opens — a CALM, RICH pocket. Sailing into it is a gamble (you braved the
 *  storm to reach it) that pays: fishing/diving/chest RARITY multiplied inside, and lingering there quietly
 *  harvests storm essence (a calm inversion of the risky storm-run gather). Deterministic (room PRNG). */
export const EYE = {
  MIN_STORM: 0.72,        // the eye opens once the storm is at least this fierce
  END_STORM: 0.5,         // and closes when it eases below this (hysteresis vs MIN_STORM)
  RADIUS: 280,            // calm-zone radius (m)
  RARITY_MUL: 2.2,        // fishing / diving / chest rarity multiplier inside the eye
  ESSENCE_SECONDS_PER: 18,// seconds lingering (slow) in the eye per 1 storm essence harvested
  CLEAR_DIST: 220,        // spawn clear of any base/island/outpost (open water)
} as const;

export const BOTTLE_HINT: readonly string[] = [
  'A rumor rides this bottle: the richest wrecks lie where the water runs deep and mean.',
  'Whoever wrote this swears a hoard waits at the heart of the storm.',
  'Old chart-talk: fortune favors the fool who hoists in dangerous waters.',
  'The note names no place, only a warning: the Capital pays best for the rarest things.',
  'Scrawled in haste: follow a full treasure map to its X and dig — the deep remembers.',
];
export const PROGRESSION = {
  SHIP_UPGRADE_BASE: 300,
  BASE_UPGRADE_BASE: 400,
  COST_GROWTH: 1.7,
  MAX_LVL: 10,
} as const;

/** Clans (Armadas). */
export const CLAN = {
  MEMBER_CAP_BASE: 10,
  MEMBER_CAP_PER_LVL: 5,
  CREATE_COST: 1000,
  MAX_LVL: 10,
  /** Treasury cost to go from level L to L+1 = UPGRADE_COST_BASE * L (escalating). */
  UPGRADE_COST_BASE: 5000,
  /** Idle-farm income bonus for every member of a clan: farm × (1 + level × FARM_BONUS_PER_LVL). */
  FARM_BONUS_PER_LVL: 0.04,
} as const;

/** Clan chart MARKERS (v54) — shared Armada intel pins placed on the world chart (C_MARK / S_MARKS).
 *  Permanent until removed (no TTL in V1); visible ONLY to clan members (the server never fans them wider). */
export const CLAN_MARKERS = {
  /** Hard cap of live markers per clan — the 11th PLACE is refused with a note (never a silent replace). */
  MAX: 10,
  /** Min gap between marker ops per player (anti-spam; the sibling of the 2 s clan-ping guard). */
  RATE_MS: 1500,
  /** Label length cap in chars, applied AFTER sanitisation (control-strip / profanity mask). */
  LABEL_MAX: 16,
  /** Number of glyphs in the marker icon set: ☠ danger / ⚓ rally / 💰 loot / 🐟 fishing / 👁 sighting / ✕ mark.
   *  (Client draws them as inked chart stamps, never raw emoji — see MarkIcon in messages.ts.) */
  ICON_COUNT: 6,
} as const;

/** Treasury doubloons to raise a clan from `level` to `level+1`. */
export function clanUpgradeCost(level: number): number {
  return CLAN.UPGRADE_COST_BASE * level;
}

/** ⛔ U-5 (red-team 2026-07-26) — THE ONLY thing a clan treasury can ever be spent on is the upgrade ladder,
 *  so the ceiling of a deposit is what the clan can still SPEND: Σ clanUpgradeCost(L) for L = level … MAX−1.
 *  At level 1 that is 225.000 ⓓ (5.000 × 1..9); at MAX_LVL it is 0. Anything deposited past this was dead
 *  money by design — and, before the fix, dead money that DISBAND destroyed outright. `DEPOSIT` clamps to it.
 *  When a real second sink for `clan.treasury` appears (war chest, territory upkeep), widen this — it is the
 *  one place the cap is written. */
export function clanTreasuryCap(level: number): number {
  let total = 0;
  for (let L = Math.max(1, Math.floor(level)); L < CLAN.MAX_LVL; L++) total += clanUpgradeCost(L);
  return total;
}

/** Trophy deltas (ladder/matchmaking). */
export const TROPHY = {
  KILL: 10, DEATH: -6, RAID_SUCCESS: 8, GOT_RAIDED: -5,
  /** A live raid pays the full RAID_SUCCESS; the zero-risk async raid pays only this. */
  ASYNC_RAID: 3,
} as const;

/** Renown — the profile "trophies" prestige/ranking score (shown as 🏆 in the HUD, distinct from the
 *  wall-mounted figurehead TrophyId collectibles) — has a HARD FLOOR at 0: a death can zero out your gains
 *  but never puts you into debt. A negative prestige score is nonsense and actively confused live players
 *  (a captain reached −102). Apply at EVERY server mutation of profile/clan trophies (kill / death / raid) so
 *  the invariant lives in one shared, tested place — clamped at the source, never merely in the display. */
export function applyTrophyDelta(current: number, delta: number): number {
  return Math.max(0, (current ?? 0) + delta);
}

/** Doubloon cost to take a ship stat or base building from `lvl` to `lvl+1`. */
export function upgradeCost(kind: 'ship' | 'base', lvl: number): number {
  const base = kind === 'ship' ? PROGRESSION.SHIP_UPGRADE_BASE : PROGRESSION.BASE_UPGRADE_BASE;
  return Math.round(base * Math.pow(PROGRESSION.COST_GROWTH, Math.max(0, lvl - 1)));
}

/** farms-demand-first: Salvaged Wood knobs for base-building upgrades (see upgradeWoodCost). */
export const WOOD_COST = {
  /** Upgrades FROM a level below this cost NO wood (0→1, 1→2, 2→3) — onboarding never stalls on materials. */
  FREE_UNTIL_LVL: 3,
  /** Wood for the first paying step (lvl 3→4), then ×GROWTH per level (8, 12, 17, 24, 35, 51, 74). */
  BASE: 8,
  GROWTH: 1.45,
} as const;

/** Salvaged Wood (MaterialId.WOOD) consumed to take a BASE BUILDING from `lvl` to `lvl+1`, ON TOP of the
 *  doubloon `upgradeCost`. Demand-first (owner's law): every farmed material must be something another
 *  player NEEDS — divers salvage wood, builders burn it (or buy it from divers on the goods market).
 *  Levels below FREE_UNTIL_LVL cost none so a novice's first steps stay doubloon-only; from lvl 3 the
 *  curve lands at ~7–15% of the doubloon cost in market value (a real tax, never a wall). Pure material
 *  SINK — no doubloons are minted or moved by this. */
export function upgradeWoodCost(lvl: number): number {
  if (lvl < WOOD_COST.FREE_UNTIL_LVL) return 0;
  return Math.round(WOOD_COST.BASE * Math.pow(WOOD_COST.GROWTH, lvl - WOOD_COST.FREE_UNTIL_LVL));
}

/**
 * farms-demand-first (owner: "não faz sentido entrar tudo no lvl1"): the CONSTRUCTION unlock ladder.
 * BUILDING a structure (its 0→1 upgrade) requires the HQ to have reached the mapped level; the HQ itself
 * is born built (the starter template stamps it at level 1). Keys mirror the server BuildingType enum
 * (persistence/types.ts) / the client BT maps. Missing key (HQ) = no requirement. GRANDFATHER: anything
 * already built — including the starter template's farm/storage/dock/turrets — never disappears or
 * deactivates; only NEW constructions climb the ladder.
 */
export const BUILD_UNLOCK_HQ: Record<number, number> = {
  1: 1, // STORAGE — HQ 1
  4: 1, // FARM — HQ 1
  5: 1, // DOCK — HQ 1 (the berth is the umbilical of the whole loop — never gate first banking/refit)
  8: 2, // CROP — HQ 2
  6: 3, // TAVERN — HQ 3
  7: 4, // FOUNDRY — HQ 4
  2: 5, // TURRET — HQ 5 (crossing this is also when the base outgrows the King's Protection — see below)
};
/** Minimum HQ level required to CONSTRUCT (0→1) a building of `type`. 0 = unrestricted (the HQ itself). */
export function buildUnlockHqLvl(type: number): number { return BUILD_UNLOCK_HQ[type] ?? 0; }
/** The HQ level at which a base becomes RAIDABLE — deliberately the TURRET unlock rung (type 2) of the SAME
 *  ladder, so defense unlock and raid exposure can never drift apart. Below it (and with no turret already
 *  built — the legacy grandfather), the base is "Under the King's Protection": its structures take no cannon
 *  damage and its vault can be stolen by NO ONE, live or async. Owner's guard-rail: "cuidado aí com essa de
 *  deixar a base dos caras sem proteção senão é raidada direto". The anti-turtle counterweight is the HQ
 *  UPGRADE gate itself (a protected HQ-4 estate caps every building at level 5 → bounded wealth), so no
 *  extra penalty exists on purpose. Sea play is untouched — cargo runs stay huntable. */
export function raidableHqLvl(): number { return buildUnlockHqLvl(2); }

/** ---- King's Protection anti-exploit (owner: "e se o cara quiser exploitar isso p evitar ser raidado
 *  sempre… tem que por um hardcap de lvl ou de ouro nele") ---- The protection expires on WHICHEVER comes
 *  first: HQ ≥ raidableHqLvl(), or the account's MONOTONIC lifetime doubloons earned (Profile.lifetimeEarned,
 *  server-only — every real inflow counts, spending never refunds it) reaching EARN_CAP. Expiry is latched on
 *  the base (Base.raidProtectionExpired) so it never un-expires and stays checkable for offline owners. When
 *  it expires by ANY trigger the TURRET construction unlocks regardless of HQ — a raidable base is never
 *  forbidden from defending itself. */
export const RAID_PROTECT = {
  /** Lifetime-earned doubloons at which the King's Protection ends (≈ 5 days of the active daily cap). */
  EARN_CAP: 25_000,
  /** Fraction of EARN_CAP at which the one-time early warning fires ("the King's clerks are counting…"). */
  WARN_FRACTION: 0.8,
} as const;

/* ============================================================================
 * ---- Wave 5: The Addiction Machine (retention & meta) ----
 * Server-authoritative meta layered on top of the arena: a War Journal of raids
 * suffered with a one-shot REVENGE, a 90-day Season Pass, the Captain's Codex of
 * collections, and a retention pack (Dawn Report, opt-in push, login/kill streaks,
 * a weekly event calendar, weekly profession ladders, weekend Clan Wars).
 * Meta timers (season/week/day/calendar) use the REAL server clock OUTSIDE the
 * deterministic ship sim — never inside stepShipBody. One wire bump (31→32).
 * ========================================================================== */

/** ---- War Journal + Revenge (5.1) ---- Every raid you suffer is logged (who, when, how much, live vs
 *  async, repelled). Each entry grants ONE revenge within the window: raid the aggressor back (async, with a
 *  plunder bonus) if they're offline, or get their position marked if they're at sea. A revenge raid grants
 *  the target NO counter-revenge (breaks the Clash-of-Clans infinite cycle). */
export const WAR_JOURNAL = {
  MAX: 12,                       // entries kept per captain (newest first)
  REVENGE_WINDOW_MS: 48 * 3_600_000, // 48h to take revenge before an entry goes cold
  REVENGE_PLUNDER_BONUS: 1.5,    // an async revenge raid steals this much more from the aggressor's base
  MARK_EVERY_S: 2,               // re-mark an online target's position to the avenger this often
} as const;

/** ---- Season Pass (5.2) ---- A 90-day track (real clock). XP is earned by playing; the FREE track is
 *  active now, the PREMIUM track is priced in $SOR and sits behind the (OFF) $SOR gate. Rewards are
 *  entitlements (gold, farm boosters, titles, cosmetics). The season ladder resets each season. */
export const SEASON = {
  DURATION_DAYS: 90,
  /** Anchor epoch (UTC ms) that season 0 starts at — seasons are DURATION_DAYS blocks from here. */
  EPOCH_MS: Date.UTC(2026, 0, 1),
  LEVELS: 50,
  XP_PER_LEVEL: 1000,           // linear track: level = floor(xp / XP_PER_LEVEL)+1, capped at LEVELS
  PREMIUM_PRICE_SOR: 500,       // premium track unlock price in $SOR (gated OFF until the TGE)
  /** Season-pass XP per activity (kept modest so the 50-level track is a whole-season journey). */
  XP: { KILL: 40, CHEST: 25, FISH: 8, DIVE: 10, DELIVERY: 30, RAID: 35, WHALE: 140, DIG: 45, BOTTLE: 6, CONTRACT: 30, AUCTION_WIN: 20 },
} as const;

/** Season reward kinds (SeasonReward.kind). */
export const SeasonRewardKind = { GOLD: 0, BOOSTER_H: 1, TITLE: 2, COSMETIC: 3 } as const;
export interface SeasonReward { kind: number; value: number; }
/** One row of the season track: the FREE reward + the PREMIUM reward at `level`. undefined = no reward that
 *  level for that track. The client renders names from this shared table; the server validates claims. */
export interface SeasonRow { level: number; free?: SeasonReward; premium?: SeasonReward; }

/** Title ids (a lightweight entitlement — a name flown under your captain). 0 = none. */
export const TitleId = {
  NONE: 0, DECKHAND: 1, CORSAIR: 2, SEA_WOLF: 3, TIDE_BREAKER: 4, LEVIATHAN: 5,
  COLLECTOR: 6, WARLORD: 7, TREASURER: 8, LEGEND: 9,
} as const;
export type TitleId = (typeof TitleId)[keyof typeof TitleId];
export const TITLES: Record<number, string> = {
  [TitleId.DECKHAND]: 'Deckhand', [TitleId.CORSAIR]: 'Corsair of the Season', [TitleId.SEA_WOLF]: 'Sea Wolf',
  [TitleId.TIDE_BREAKER]: 'Tide-Breaker', [TitleId.LEVIATHAN]: 'Leviathan', [TitleId.COLLECTOR]: 'The Collector',
  [TitleId.WARLORD]: 'Armada Warlord', [TitleId.TREASURER]: 'Crown Treasurer', [TitleId.LEGEND]: 'Living Legend',
};
export function titleById(id: number): string { return TITLES[id] ?? ''; }

/** The 50-level season track (single source of truth: client renders, server grants). Free track = gold +
 *  the odd booster + milestone titles; premium track = richer gold + cosmetics + prestige titles. */
export const SEASON_TRACK: readonly SeasonRow[] = (() => {
  const rows: SeasonRow[] = [];
  for (let lvl = 1; lvl <= SEASON.LEVELS; lvl++) {
    const row: SeasonRow = { level: lvl };
    // FREE: gold every level (scaling), a farm booster every 10, a title at 25/50
    if (lvl % 10 === 0) row.free = { kind: SeasonRewardKind.BOOSTER_H, value: 12 };
    else row.free = { kind: SeasonRewardKind.GOLD, value: 200 + lvl * 40 };
    if (lvl === 25) row.free = { kind: SeasonRewardKind.TITLE, value: TitleId.DECKHAND };
    if (lvl === 50) row.free = { kind: SeasonRewardKind.TITLE, value: TitleId.SEA_WOLF };
    // PREMIUM: bigger gold, cosmetics at milestones, prestige titles
    row.premium = { kind: SeasonRewardKind.GOLD, value: 500 + lvl * 90 };
    if (lvl === 10) row.premium = { kind: SeasonRewardKind.COSMETIC, value: 4 };   // Royal Azure Hull
    if (lvl === 20) row.premium = { kind: SeasonRewardKind.COSMETIC, value: 14 };  // Sherwood Green Sails
    if (lvl === 30) row.premium = { kind: SeasonRewardKind.COSMETIC, value: 23 };  // Compass Rose flag
    if (lvl === 40) row.premium = { kind: SeasonRewardKind.TITLE, value: TitleId.TIDE_BREAKER };
    if (lvl === 50) row.premium = { kind: SeasonRewardKind.TITLE, value: TitleId.LEVIATHAN };
    rows.push(row);
  }
  return rows;
})();

/** Which season block a given wall-clock ms falls in + its window. Pure (real clock only, never in the sim). */
export function seasonInfo(nowMs: number): { id: number; startMs: number; endMs: number } {
  const span = SEASON.DURATION_DAYS * 86_400_000;
  const id = Math.max(0, Math.floor((nowMs - SEASON.EPOCH_MS) / span));
  const startMs = SEASON.EPOCH_MS + id * span;
  return { id, startMs, endMs: startMs + span };
}
/** Season level (1..LEVELS) for an XP total. */
export function seasonLevel(xp: number): number {
  return Math.max(1, Math.min(SEASON.LEVELS, Math.floor(Math.max(0, xp) / SEASON.XP_PER_LEVEL) + 1));
}

/** ---- Captain's Codex (5.3) ---- A collection book across five categories. Owning/discovering a slot marks
 *  it; a completed category pays a reward + a title. Empty slots show a hint → demand on the P2P market.
 *  COSMETICS + TROPHIES are derived from what you own; FISH/RELICS/DISCOVERIES are persisted flags. */
export const CodexCategory = { FISH: 0, COSMETICS: 1, TROPHIES: 2, RELICS: 3, DISCOVERIES: 4 } as const;
export type CodexCategory = (typeof CodexCategory)[keyof typeof CodexCategory];
export const CODEX_CATEGORY_COUNT = 5;
export interface CodexSlot { id: number; name: string; hint: string; }
export interface CodexCat { name: string; icon: string; reward: number; title: number; slots: readonly CodexSlot[]; }
/** Discovery slot ids (5.3 DISCOVERIES category) — set server-side when the deed is first done. */
export const DiscoveryId = {
  WHALE: 0, CURSED: 1, DIG: 2, BOTTLE: 3, ESCORT: 4, WANTED: 5, CLAN_WAR: 6, GHOST: 7,
} as const;
export const CODEX: Record<number, CodexCat> = {
  [CodexCategory.FISH]: { name: 'Fish', icon: '🐟', reward: 1500, title: 0, slots: [
    { id: MaterialId.FISH, name: 'Fish', hint: 'Cast a line at any shoal.' },
    { id: MaterialId.FISH_OIL, name: 'Fish Oil', hint: 'Reel in a rare catch.' },
    { id: MaterialId.BLACK_PEARL, name: 'Black Pearl', hint: 'A 1-in-1000 perfect reel.' },
  ] },
  [CodexCategory.COSMETICS]: { name: 'Cosmetics', icon: '🎨', reward: 3000, title: TitleId.COLLECTOR, slots: COSMETICS.map((c) => ({ id: c.id, name: c.name, hint: c.shopPrice ? 'Buy at the shop or the market.' : 'Earned or traded — hunt the market.' })) },
  [CodexCategory.TROPHIES]: { name: 'Trophies', icon: '🏆', reward: 4000, title: TitleId.WARLORD, slots: Object.entries(TROPHIES).map(([id, t]) => ({ id: Number(id), name: t.name, hint: t.desc })) },
  [CodexCategory.RELICS]: { name: 'Relics', icon: '🏺', reward: 2500, title: 0, slots: [
    { id: MaterialId.RELIC, name: 'Relic', hint: 'Salvage a shipwreck.' },
    { id: MaterialId.STORM_ESSENCE, name: 'Storm Essence', hint: 'Sail hard through a storm.' },
    { id: MaterialId.AMBERGRIS, name: 'Ambergris', hint: 'Land the killing blow on the White Whale.' },
    { id: MaterialId.TREASURE_MAP, name: 'Treasure Map', hint: 'Assemble four map fragments.' },
    { id: MaterialId.SPECTRAL_ESSENCE, name: 'Spectral Essence', hint: 'Fell a ship of the night Ghost Fleet.' },
  ] },
  [CodexCategory.DISCOVERIES]: { name: 'Discoveries', icon: '🗺', reward: 3500, title: TitleId.LEGEND, slots: [
    { id: DiscoveryId.WHALE, name: 'The White Whale', hint: 'Deal real damage to the leviathan.' },
    { id: DiscoveryId.CURSED, name: 'The Cursed Hoard', hint: 'Hoist the map-wide Cursed Treasure.' },
    { id: DiscoveryId.DIG, name: 'Buried Treasure', hint: 'Follow a treasure map and dig.' },
    { id: DiscoveryId.BOTTLE, name: 'Message in a Bottle', hint: 'Fish up a drifting bottle.' },
    { id: DiscoveryId.ESCORT, name: 'Convoy Escort', hint: 'See a merchant convoy safely to port.' },
    { id: DiscoveryId.WANTED, name: 'WANTED', hint: 'Grow notorious enough to fly the red flag.' },
    { id: DiscoveryId.CLAN_WAR, name: 'Clan War', hint: 'Score for your Armada in a weekend war.' },
    { id: DiscoveryId.GHOST, name: 'The Ghost Fleet', hint: 'Fell a spectral ship of the night.' },
  ] },
};

/** Material ids the Codex tracks (FISH + RELICS categories) — marked when first obtained. */
export const CODEX_MATERIAL_IDS: ReadonlySet<number> = new Set(
  [...CODEX[CodexCategory.FISH]!.slots, ...CODEX[CodexCategory.RELICS]!.slots].map((s) => s.id),
);

/** ---- Retention pack (5.4) ---- */
/** Login + kill streaks (5.4c). Login streak = consecutive days played (escalating base grant); kill streak
 *  = kills without dying (announced map-wide from a threshold, and a bonus on the streaker's head). */
export const STREAK = {
  /** RETIRED 2026-07-22 (owner: "no printing press — play for it or nothing"): the passive login-STREAK gold is
   *  DEAD (all zeros). The streak COUNTER itself still advances on an engaged day (drives WINBACK + engagement
   *  telemetry, never a payout). Length 7 is preserved so the index math is unchanged; set any entry > 0 only to
   *  deliberately re-arm the passive streak faucet. Active earning (kill/chest/cargo/…) is the only gold now. */
  LOGIN_REWARDS: [0, 0, 0, 0, 0, 0, 0] as readonly number[],
  KILL_ANNOUNCE_AT: 3,       // announce a kill streak map-wide once it reaches this
  KILL_STEP_ANNOUNCE: 3,     // and again every this-many kills beyond
  KILL_BONUS_PER: 30,        // extra gold to whoever sinks a streaker, per streak kill…
  KILL_BONUS_MAX: 500,       // …capped here (the bounty on a streaker's head)
} as const;

/** Win-back push (retention, P0 #6). A periodic SERVER-side sweep (off the hot tick path, wall-clock based —
 *  never inside the deterministic sim) nudges captains whose daily login streak is about to lapse. Cheapest
 *  retention lever there is: one push before the 24h streak break. */
export const WINBACK = {
  /** Only bother saving a streak worth keeping. */
  MIN_STREAK: 2,
  /** Danger zone: fire when the last session ended between this many hours ago… */
  MIN_HOURS_AGO: 20,
  /** …and this many (the ~2h window before a 24h streak lapse). */
  MAX_HOURS_AGO: 22,
  /** Sweep cadence (minutes). Runs on an unref'd timer, well off the 30 Hz loop. */
  SWEEP_MINUTES: 12,
  /** Global rate sanity — never blast more than this many pushes in a single sweep. */
  MAX_PER_SWEEP: 100,
  /** Idempotency: at most one win-back per captain per this many hours (< 24 so the next cycle can nudge). */
  DEDUP_HOURS: 20,
} as const;

/** Weekly event calendar (5.4d). The active event is derived from the REAL clock (UTC day-of-week), applied
 *  as multipliers in the room. One event per day; the HUD banner shows the day's event. */
export const WorldEventId = { NONE: 0, DOUBLE_FISH: 1, CURSED_DOUBLE: 2, CLAN_WAR: 3, BOUNTY_HUNT: 4, MARKET_FEST: 5, WHALE_WEEK: 6 } as const;
export type WorldEventId = (typeof WorldEventId)[keyof typeof WorldEventId];
export interface CalendarDay { event: number; label: string; desc: string; }
/** Index by UTC day-of-week (0 = Sunday). Sunday = Clan War; the rest cycle themed boosts. */
export const CALENDAR: readonly CalendarDay[] = [
  { event: WorldEventId.CLAN_WAR,      label: 'Clan War Sunday',   desc: 'Territory influence counts double — muster your Armada.' },
  { event: WorldEventId.MARKET_FEST,   label: 'Market Monday',     desc: 'Goods sell for more — the outposts are busy.' },
  { event: WorldEventId.DOUBLE_FISH,   label: 'Angler Tuesday',    desc: 'Double fishing yield and rare-catch luck.' },
  { event: WorldEventId.WHALE_WEEK,    label: 'Leviathan Wednesday', desc: 'The White Whale surfaces more often.' },
  { event: WorldEventId.BOUNTY_HUNT,   label: 'Bounty Thursday',   desc: 'Bounties rise faster — the hunt pays double.' },
  { event: WorldEventId.CURSED_DOUBLE, label: 'Cursed Friday',     desc: 'The Cursed Treasure is worth double.' },
  { event: WorldEventId.DOUBLE_FISH,   label: 'Deep Saturday',     desc: 'Double fishing yield — the shoals run thick.' },
];
/** The active calendar day for a wall-clock ms (real clock; never used inside the deterministic sim). */
export function calendarDay(nowMs: number): CalendarDay { return CALENDAR[new Date(nowMs).getUTCDay()] ?? CALENDAR[0]!; }
/** Multipliers applied by the active event (1 = no change). */
export const EVENT_MUL = {
  FISH_YIELD: 2, MARKET_PAYOUT: 1.25, CURSED_VALUE: 2, TERRITORY: 2, BOUNTY: 2,
} as const;

/** Weekly profession leaderboards (5.4e). Per-track top of the ISO-week, reset each week; a small prize to
 *  each track's leader when the week rolls. Weekend Clan Wars (5.4f) reuse the territory + calendar systems. */
export const WEEKLY = {
  TOP_N: 5,                  // entries shown per profession
  PROF_PRIZE: 2000,          // gold to the previous week's #1 in each profession (paid on next login)
} as const;
/** ISO-ish week key (YYYY-Www) for a wall-clock ms — the weekly-reset bucket (real clock, out of the sim). */
export function weekKey(nowMs: number): string {
  const d = new Date(nowMs);
  const day = (d.getUTCDay() + 6) % 7; // Mon=0
  const thu = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day + 3));
  const week1 = new Date(Date.UTC(thu.getUTCFullYear(), 0, 4));
  const wk = 1 + Math.round(((thu.getTime() - week1.getTime()) / 86_400_000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
  return `${thu.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
}

/** Clan War weekend (5.4f): the clan controlling a region when the Sunday war closes HOLDS it the next week,
 *  granting its members a farm bonus. Persisted as war holders in the world store. */
export const CLAN_WAR = {
  HOLD_FARM_BONUS: 0.25,     // +25% farm income for members of a clan holding any region
} as const;

/**
 * ---- Onda 6: The Living Port (walkable outpost interior) ----
 * Docking at an outpost lets a captain step ashore into a small 3D walkable plaza as an AVATAR
 * (the class-specific captain GLB). Movement is a second deterministic sim (`stepWalkerBody` in
 * @piratas/sim) with the SAME prediction==authority contract as ships. The PORT plane is a small
 * local square (origin at a corner) so it quantizes on its own axis (packPortPos) independent of
 * WORLD_SIZE. Constants here are the single source of truth for both the server PortRoom and the
 * client port scene + prediction.
 */
export const WALK = {
  /** Top walking speed (m/s) — a brisk stroll, not a sprint. */
  SPEED: 3.2,
  /** Approach rate toward the target velocity while moving (1/s) — snappy starts. */
  ACCEL: 14,
  /** Approach rate toward rest when idle (1/s) — quick, deliberate stops. */
  DRAG: 12,
  /** Max rate (rad/s) the avatar heading swings toward its move direction (never snaps). */
  TURN_RATE: 13,
  /** Avatar collision radius (m) — a person is a small circle on the plaza plane. */
  RADIUS: 0.42,
} as const;

export const PORT = {
  /** The plaza is a SIZE×SIZE square in port-local meters; positions live in [0, SIZE]. */
  SIZE: 64,
  /** Press-to-interact range (m) from a market stall's front / the exit dock. */
  INTERACT_R: 3.6,
  /** Spawn protection (s) granted when a captain returns to the sea from the port. */
  ENTER_SPAWN_PROTECT: 5,
  /** You cannot step ashore within this window (ms) of taking OR dealing a hit (no combat-logging into a hub). */
  COMBAT_LOCK_MS: 10_000,
  /** You cannot step ashore while flooding at or above this (nearly sunk). */
  MAX_WATER_LEVEL: 0.85,
  /** Within (tavern.r + this) of the tavern the client crossfades to the tavern ambiance + music. */
  TAVERN_AUDIO_R: 12,
} as const;

/** A solid circular prop on the plaza plane (structurally a WalkerObstacle in @piratas/sim). */
export interface PortObstacle { x: number; y: number; r: number }
/** A market stall: its footprint centre + facing, and the point a captain stands at to trade. */
export interface PortStall { x: number; y: number; heading: number; interactX: number; interactY: number }
/** A placed kit piece (Onda 6.5). The client instances the matching GLB by `kind`; solid kinds also
 *  seed the walker `obstacles` so collision + render never drift. Port-local coords, `heading` in rad.
 *  Onda Paisagem-em-pé — optional altitude `h` (m above the plaza floor; world-Y). Absent = 0 = on the
 *  ground, so a layout authored before this field stays byte-identical. Purely COSMETIC (render-only):
 *  collision/interact points come from x,y only and never read `h`, so a lifted rock-on-cliff still walks
 *  as if it sat on the floor. Not on the wire (PortLayout is shared config, never encoded).
 *  Onda Sombras — optional uniform render SCALE `s` (multiplies the kind's base footprint/height normalization;
 *  absent = 1 = the kind's shipped size, so an older layout stays byte-identical). Unlike `h`, `s` DOES shape
 *  collision since Onda Parede-invisível-3: obstacles are (re)derived as `r = baseR(kind) × s` (WYSIWYG — see
 *  packages/protocol/src/portcollision.ts), so the ring a walker feels always matches the mesh the owner sees. */
export interface PortProp { kind: string; x: number; y: number; heading: number; h?: number; s?: number }
/** Onda de-proc (vilas) — the village's legacy PROCEDURAL dressing (the central well, the crate stacks, the old
 *  box tavern) is now DATA: each is a `PortProp` of one of these kinds, rendered by a dedicated procedural builder
 *  (portscene `buildLegacyProps`) with geometry IDENTICAL to the old baked block — so the owner can move/delete
 *  them in the village editor instead of them being un-erasable bakes. These kinds have NO GLB and never render
 *  from the sea (they sit inside the plaza). `buildVillageWorld` treats a village whose props are ALL legacy as
 *  the untouched plaza (`hasKit` stays false → the centre ring is kept), so the shipped `PORT_LAYOUT` renders
 *  byte-identically to before. A "kit" village (VILLAGE1's tavern_skull/inn/…) carries a non-legacy prop, so it
 *  keeps the dense-city path. */
export const LEGACY_PROC_KINDS: ReadonlySet<string> = new Set(['well', 'crate_stack', 'tavern_old']);
/** ── Walkable-port placement catalog (SINGLE SOURCE OF TRUTH) ─────────────────────────────────────────────
 *  THE authoritative registry of every prop kind the 🏙 City/Layout Creative-Mode editor may place in a
 *  walkable port (village or capital). The CLIENT palette is built from this, AND the SERVER Save-Online
 *  validator (`editorApi.validatePortLayout` → `PORT_PROP_KINDS`) accepts EXACTLY these — so a kind can never
 *  appear in the palette that a save would then 400-reject, and never be saved for a kind the renderer can't
 *  place. Adding a placeable prop = add its kind HERE (+ its GLB in the client's KIT_URL or a procedural
 *  builder). The editor-persistence matrix test round-trips every kind here through the real validator, so a
 *  kind that skips the validator wiring breaks CI. Render-only DATA — no wire. `stall` doubles as a market
 *  station; `well`/`crate_stack`/`tavern_old` are the legacy procedural village dressing. */
export const WALKABLE_PROP_KINDS: readonly string[] = [
  // core buildings + harbour structures
  'gate', 'lighthouse', 'wall', 'house_a', 'house_b', 'fountain', 'crane', 'warehouse', 'tavern', 'stall',
  // Onda 7 — casino / auction / clan-hall stations
  'poker_table', 'slot_machine', 'blackjack_table', 'auction_stage', 'clan_trophy',
  // Onda Cidade Viva — street-dressing kit (bench / stool / lamp / cottage / lantern grove)
  'cobble_bench', 'stool', 'street_lamp', 'cottage_ember', 'lantern_grove',
  // Onda Cidade Verde — landscape kit (pine / tall pine / boulder / cliff)
  'pine', 'pine_tall', 'rock', 'cliff',
  // Onda Sombras — flat ground-shadow decals (no collision)
  'shadow_round', 'shadow_square',
  // Onda Skull Harbor — harbour kit (village buildings + the capital citadel)
  'tavern_skull', 'inn_salted', 'wharf_skullflag', 'treasure_tree', 'citadel_skull',
  // Onda de-proc — the shipped village's legacy procedural dressing, now movable/deletable props
  'well', 'crate_stack', 'tavern_old',
];
/** Onda 7: a casino/auction/clan-hall interaction station on the plaza. The client instances the matching
 *  GLB at (x,y,heading) and opens the `game` UI when a captain stands within CASINO.INTERACT_R of
 *  (interactX,interactY). Solid stations also seed a walker obstacle so collision + render never drift. */
export interface PortStation { game: number; id: number; x: number; y: number; heading: number; interactX: number; interactY: number }
/** Layout Editor wave: a RENDER-ONLY paved ground patch (the street/plaza flooring). Pure cosmetic — the
 *  walker sim never reads it (collision is `obstacles` + the plaza clamp), so it carries no wire cost and can
 *  be freely authored/removed in the editor. A rectangle centred at (x,y), `w`×`l` metres, yaw `rot` (rad),
 *  drawn with material `mat` (a GroundMat id). Both the client renderer and the editor read this. */
export const GroundMat = { COBBLE: 0, FLAGSTONE: 1, PLANKS: 2, DIRT: 3, GRASS: 4, SAND: 5 } as const;
export type GroundMat = (typeof GroundMat)[keyof typeof GroundMat];
/** Stable names for each GroundMat id (the editor's material picker + the TS export read these). */
export const GROUND_MAT_NAMES: readonly string[] = ['COBBLE', 'FLAGSTONE', 'PLANKS', 'DIRT', 'GRASS', 'SAND'];
export interface GroundPatch { x: number; y: number; w: number; l: number; rot: number; mat: number }
export interface PortLayout {
  size: number;
  /** Where a captain materialises on entry (just past the dock, facing into the plaza). */
  spawn: { x: number; y: number; heading: number };
  /** The exit dock (an AABB near the south edge): walking into it (or the button) sails back to sea. */
  exit: { minX: number; minY: number; maxX: number; maxY: number };
  /** The tavern footprint (also the audio-crossfade trigger centre). */
  tavern: { x: number; y: number; r: number };
  /** Market stalls (the market_stall GLB) — trade opens near `interact*`. */
  stalls: readonly PortStall[];
  /** Every solid prop (stalls + tavern + well + crates) for closed-form walker collision. */
  obstacles: readonly PortObstacle[];
  /** Onda 6.5 kit placements the client instances (walls, houses, gate, lighthouse, fountain, crane). */
  props?: readonly PortProp[];
  /** Onda 6.5: where the entry camera looks (the plaza up the main street) — frames the reveal. */
  camLook?: { x: number; y: number };
  /** Onda 7: casino tables/machines + the auction rostrum + the clan hall plaque (Capital only). */
  casino?: readonly PortStation[];
  /** Layout Editor wave: render-only paved ground patches (street/plaza flooring). Cosmetic — never affects
   *  collision, so it stays off the wire. The client renderer + the editor draw these. */
  ground?: readonly GroundPatch[];
}

/** The shared plaza layout (v1: one interior, the same for all three outposts). Both the server PortRoom
 *  and the client read THIS, so collision + interaction geometry can never drift. Port-local coords. */
export const PORT_LAYOUT: PortLayout = (() => {
  const stalls: PortStall[] = [
    { x: 12, y: 26, heading: 0,        interactX: 15.5, interactY: 26 }, // west row, faces east
    { x: 12, y: 42, heading: 0,        interactX: 15.5, interactY: 42 },
    { x: 52, y: 26, heading: Math.PI,  interactX: 48.5, interactY: 26 }, // east row, faces west
    { x: 52, y: 42, heading: Math.PI,  interactX: 48.5, interactY: 42 },
  ];
  const obstacles: PortObstacle[] = [
    ...stalls.map((s) => ({ x: s.x, y: s.y, r: 1.9 })), // stall footprints (r hugs the market_stall GLB — was 2.2)
    { x: 32, y: 55, r: 7 },   // tavern
    { x: 32, y: 32, r: 2.6 }, // central well / fountain
    { x: 20, y: 51, r: 1.3 }, // crates
    { x: 44, y: 51, r: 1.3 },
  ];
  // Onda de-proc — the shipped village dressing as DATA (see LEGACY_PROC_KINDS). Positions/headings reproduce the
  // old baked block EXACTLY (portscene buildLegacyProps renders geometry-identically): the box tavern at the plaza
  // head (also the audio-crossfade centre), the central well, and the four crates near the tavern (their heading is
  // the old `(cx+cz)*0.3` spin, stored negated because the renderer applies `-heading`). Since every kind here is a
  // LEGACY_PROC_KIND, `hasKit` stays false → the scene is byte-identical to the pre-data plaza. `obstacles` above is
  // unchanged (still the 2-of-4 crate radii the shipped map always had); the editor regenerates it from these props.
  const props: PortProp[] = [
    { kind: 'tavern_old', x: 32, y: 55, heading: 0 },
    { kind: 'well', x: 32, y: 32, heading: 0 },
    ...([[20, 51], [44, 51], [18, 49], [46, 49]] as const).map(([cx, cz]) => ({ kind: 'crate_stack', x: cx, y: cz, heading: -((cx + cz) * 0.3) })),
  ];
  return {
    size: PORT.SIZE,
    spawn: { x: 32, y: 11, heading: Math.PI / 2 },
    exit: { minX: 24, minY: 0, maxX: 40, maxY: 10 },
    tavern: { x: 32, y: 55, r: 7 },
    stalls,
    obstacles,
    props,
  };
})();

/**
 * ---- Onda Skull Harbor: the redesigned FIRST village ----
 * The owner's "construçãozinha podre do primeiro porto" — the bare shared plaza — gets a full redesign for the
 * FIRST village outpost only (`layoutForOutpost` routes just that id here; every OTHER village keeps the byte-
 * identical `PORT_LAYOUT`, and the capital is untouched). Built from the owner's Meshy harbour kit: the Skull
 * Harbor tavern crowns the plaza, the Salted Skull inn flanks the west, a treasure tree is the central praça
 * landmark, and the Skullflag wharf sits at the water's edge by the dock. Spawn/exit/tavern geometry match the
 * old plaza so entry + the sea↔port handoff are functionally intact. Same DATA contract as CAPITAL_LAYOUT: the
 * client render/collision + the server PortRoom read THIS, so nothing can drift, and adding kit-prop kinds is
 * render-only (no wire, no PROTOCOL_VERSION bump). Walkability is guarded by portReachability.test.ts.
 */
export const VILLAGE1_LAYOUT: PortLayout = (() => {
  const props: PortProp[] = [];
  const obstacles: PortObstacle[] = [];
  // Solid-kind collision radii (footprint circles). The wharf is render-only (no radius) — it sits over the
  // dock water at the plaza's south edge and seeds no obstacle, so the exit dock stays reachable.
  const R: Record<string, number> = {
    tavern_skull: 4.5, inn_salted: 4.2, treasure_tree: 2.2, cottage_ember: 2.6,
    street_lamp: 0.5, cobble_bench: 0.8, pine: 1.2,
  };
  const place = (kind: string, x: number, y: number, heading = 0): void => {
    props.push({ kind, x, y, heading });
    const r = R[kind]; if (r) obstacles.push({ x, y, r });
  };
  // the central praça landmark — the treasure tree on its little sand/water islet (main street splits round it)
  place('treasure_tree', 32, 33, 0);
  // the Skull Harbor tavern crowns the north end of the plaza (also the audio-crossfade centre)
  const tavern = { x: 32, y: 54, r: 7 };
  place('tavern_skull', tavern.x, tavern.y, 0);
  // the Salted Skull inn (west) + a lantern cottage balancing the east
  place('inn_salted', 13, 43, Math.PI / 2);
  place('cottage_ember', 51, 43, -Math.PI / 2);
  // street lanterns flanking the entrance + two benches by the tree (sittable) + corner conifers
  place('street_lamp', 22, 24, 0);
  place('street_lamp', 42, 24, 0);
  // benches sit ABOVE the tree (between it and the tavern) so the two ruelas flanking the centre tree stay wide
  place('cobble_bench', 26, 38, Math.PI / 2);
  place('cobble_bench', 38, 38, -Math.PI / 2);
  place('pine', 8, 58, 0);
  place('pine', 56, 58, 0);
  // the Skullflag wharf sits at the water's edge (south, over the dock) — RENDER-ONLY (no obstacle), so the
  // exit dock stays walkable. It shows from inside the port; a village exterior is not projected to the open
  // sea (that path is capital-only — see render.ts capitalExteriorPieces; documented, not extended here).
  props.push({ kind: 'wharf_skullflag', x: 32, y: -3, heading: 0 });

  // market stalls in the open lower plaza (gameplay POIs — trade opens near interact*)
  const stalls: PortStall[] = [
    { x: 12, y: 24, heading: 0,       interactX: 15.5, interactY: 24 }, // west row, faces east
    { x: 12, y: 30, heading: 0,       interactX: 15.5, interactY: 30 },
    { x: 52, y: 24, heading: Math.PI, interactX: 48.5, interactY: 24 }, // east row, faces west
    { x: 52, y: 30, heading: Math.PI, interactX: 48.5, interactY: 30 },
  ];
  for (const s of stalls) obstacles.push({ x: s.x, y: s.y, r: 1.9 });

  // a flagstone spine paving the main street from the dock up to the tavern (render-only; walker never reads it)
  const ground: GroundPatch[] = [
    { x: 32, y: 34, w: 22, l: 46, rot: 0, mat: GroundMat.FLAGSTONE },
    // sand bed under the treasure tree — its base is a sand/water islet; stone plaza under it read wrong
    { x: 32, y: 33, w: 7, l: 7, rot: 0, mat: GroundMat.SAND },
  ];

  return {
    size: PORT.SIZE,
    spawn: { x: 32, y: 11, heading: Math.PI / 2 }, // just past the dock, facing NORTH up the street
    exit: { minX: 24, minY: 0, maxX: 40, maxY: 10 },
    tavern,
    stalls,
    obstacles,
    props,
    ground,
    camLook: { x: 32, y: 42 },
  };
})();

/** Is a point inside a given layout's exit dock zone? (walking into it sails back out to the sea). */
export function inPortExitOf(layout: PortLayout, x: number, y: number): boolean {
  const e = layout.exit;
  return x >= e.minX && x <= e.maxX && y >= e.minY && y <= e.maxY;
}
/** Is a port-local point inside the village exit dock zone? (kept for the v1 plaza callers). */
export function inPortExit(x: number, y: number): boolean {
  return inPortExitOf(PORT_LAYOUT, x, y);
}

/**
 * ---- Onda 6.5: The Capital ----
 * ONE outpost (the one flagged `capital` in OUTPOSTS) opens into a bigger, DENSER walkable CITY built from a
 * modular kit (perimeter walls, houses lining alleys, a harbour gate, a lighthouse, a fountain, a crane, a
 * tavern). The other two outposts keep the small village plaza (`PORT_LAYOUT`). There is no "Enter Port"
 * button for the capital: entry is the FAIXA — crossing the harbour-mouth line (server-detected, reusing the
 * sea↔port handoff). The layout is DATA here so the client render/collision and the server can never drift.
 */
export const CAPITAL = {
  /** The capital plaza is a bigger SIZE×SIZE square than a village (denser > larger). */
  SIZE: 96,
  /** Harbour-mouth "faixa" distance (m) from the outpost centre toward open sea (the world-centre side). */
  MOUTH_DIST: 46,
  /** Half-width (m) of the navigable mouth — cross wider than this and you'd hit the breakwater. */
  MOUTH_HALF: 46,
  /** Preload the capital kit GLBs when the ship is within this of the outpost centre (fade hides the load). */
  PRELOAD_R: 340,
  /** Client entry fade duration (ms) — the harbour swallows the ship for ~1s while the port scene builds. */
  FADE_MS: 1000,
} as const;

/**
 * ---- Onda 7: The Tavern Casino (Capital only) ----
 * Games are bet in DOUBLOONS (the bank currency, like the market) and resolved SERVER-SIDE with a
 * provably-fair HMAC-SHA256 roll (serverSeed + clientSeed + nonce): the client only sends intent, never
 * an outcome. The house edge makes the casino a net doubloon SINK (redistribution, no minting). All
 * tuning lives here — the single source of truth. Physical layout (tables/machines) is DATA in
 * CAPITAL_LAYOUT.casino, exactly like the market stalls.
 */
export const CasinoGame = { SLOTS: 0, BLACKJACK: 1, POKER: 2, AUCTION: 3, CLAN_HALL: 4 } as const;
export type CasinoGame = (typeof CasinoGame)[keyof typeof CasinoGame];

export const CASINO = {
  /** Press-to-interact range (m) from a table/machine's front (mirrors PORT.INTERACT_R for stalls). */
  INTERACT_R: 3.8,
  /** Minimum wager (doubloons) — floors dust bets. */
  MIN_BET: 10,
  /** Maximum single wager (doubloons) — caps one lucky hand against the house + anti-whale. */
  MAX_BET: 5000,
  /** The house edge baked into every payout (2.5%). Keeps the games a slow sink, never a faucet. */
  HOUSE_EDGE: 0.025,
  /** Anti-abuse: minimum interval (ms) between resolved single-shot bets (slots/blackjack action) per player. */
  RATE_LIMIT_MS: 500,
  /** ---- Slots: 3 reels of these symbols; 3-of-a-kind pays SLOT_PAY3[i], any-two-match pays SLOT_PAY2.
   *  The engine scales the raw table so the true EV is exactly (1 - HOUSE_EDGE) — the edge always applies. */
  SLOT_SYMBOLS: ['cherry', 'coin', 'anchor', 'cutlass', 'skull', 'parrot', 'chest', 'kraken'] as const,
  SLOT_PAY3: [5, 5, 8, 8, 10, 12, 15, 40] as const, // 3-of-a-kind multiplier per symbol (pre-scale)
  SLOT_PAY2: 2,                                       // any two match (pre-scale)
  /** ---- Blackjack: single deck, dealer HITS soft 17 (H17, the more house-favorable rule), naturals pay 6:5.
   *  A single-deck game at 3:2 is roughly break-even (marginally player-favorable with DAS + no-resplit),
   *  which — together with the peek-and-bail refund exploit (now closed: an abandoned hand auto-stands and
   *  settles at true odds, never a free refund) — is why the audit flagged the table as +EV. Paying naturals
   *  6:5 instead of 3:2 removes ~1.39% of return on the ~4.83% of hands that are naturals (≈ +1.4% edge),
   *  landing the house edge at a small-but-POSITIVE ~1.35%–1.45%. H17 is kept (S17 would REDUCE the edge). */
  BJ_DEALER_HITS_SOFT_17: true,
  BJ_BLACKJACK_PAYS: 1.2,   // 6:5 (was 1.5 = 3:2) — the house-edge lever, see note above
  /** ---- Poker: one no-limit Texas Hold'em cash table in the capital tavern. */
  POKER_SEATS: 6,
  POKER_SMALL_BLIND: 5,
  POKER_BIG_BLIND: 10,
  POKER_MIN_BUYIN: 200,   // = 20 big blinds
  POKER_MAX_BUYIN: 2000,  // = 200 big blinds
  /** ---- Poker rake: a small take on every CONTESTED pot (a flop was dealt — "no flop, no drop", so a
   *  blind-steal fold is never raked). Percentage of the pot, capped in absolute doubloons. Routed to the
   *  shared Marketplace sink (60% burned / 40% treasury, MARKET.BURN_SHARE) — the same anti-inflation
   *  posture as the market. This closes the alt→main chip-consolidation channel (dumping a stack to a main
   *  account is now taxed every hand). 5% capped at 5 big blinds is standard live cash-game rake. */
  POKER_RAKE_PCT: 0.05,
  POKER_RAKE_CAP: 50,     // = 5 big blinds
  /** Seconds a seated player has to act before the table auto-checks/folds them (keeps a live table moving). */
  POKER_ACT_TIMEOUT_S: 30,
  /** Broadcast a casino BIG WIN feed line to the plaza when a single payout profit ≥ this (doubloons). */
  BIG_WIN_FEED: 2000,
} as const;

/** Is this 1-based outpost id the capital? (Single source: the OUTPOSTS `capital` flag.) */
export function isCapitalOutpost(outpostId: number): boolean {
  return OUTPOSTS[outpostId - 1]?.capital === true;
}
/** The 1-based id of the FIRST non-capital outpost — the "first village" the owner had redesigned into the
 *  Skull Harbor port (VILLAGE1_LAYOUT). Every OTHER village keeps the shared PORT_LAYOUT byte-identical. */
export const FIRST_VILLAGE_OUTPOST = OUTPOSTS.findIndex((o) => !o.capital) + 1;
/** The walkable layout for an outpost: the dense capital city, the redesigned first village, or the plaza. */
export function layoutForOutpost(outpostId: number): PortLayout {
  if (isCapitalOutpost(outpostId)) return CAPITAL_LAYOUT;
  if (outpostId === FIRST_VILLAGE_OUTPOST) return VILLAGE1_LAYOUT; // Skull Harbor
  return PORT_LAYOUT;
}

/** The harbour-mouth geometry for the capital at world (opX,opY): a line perpendicular to the seaward
 *  direction (outpost → world centre = open water), centred `MOUTH_DIST` out. `(sx,sy)` points to sea,
 *  `(px,py)` runs along the mouth. Pure. */
export function harborMouth(opX: number, opY: number): { mx: number; my: number; sx: number; sy: number; px: number; py: number } {
  const c = WORLD_SIZE / 2;
  let dx = c - opX, dy = c - opY;                 // toward open sea (the world-centre side)
  const d = Math.hypot(dx, dy) || 1; dx /= d; dy /= d;
  return { mx: opX + dx * CAPITAL.MOUTH_DIST, my: opY + dy * CAPITAL.MOUTH_DIST, sx: dx, sy: dy, px: -dy, py: dx };
}

/** Did a ship cross the capital harbour mouth INBOUND (sea → harbour) between two positions, within the
 *  navigable width? Pure + deterministic — the server samples it each tick to trigger the faixa entry. */
export function crossedHarborMouth(prevX: number, prevY: number, curX: number, curY: number, opX: number, opY: number): boolean {
  const m = harborMouth(opX, opY);
  const sdPrev = (prevX - m.mx) * m.sx + (prevY - m.my) * m.sy;
  const sdCur = (curX - m.mx) * m.sx + (curY - m.my) * m.sy;
  if (!(sdPrev > 0 && sdCur <= 0)) return false;   // must move from the sea side to the harbour side
  const lat = (curX - m.mx) * m.px + (curY - m.my) * m.py;
  return Math.abs(lat) <= CAPITAL.MOUTH_HALF;
}

/** The Capital city layout (Onda 6.5): a dense kit-built plaza. Both the server PortRoom and the client
 *  port scene + prediction read THIS, so collision + interaction + render geometry can never drift. */
export const CAPITAL_LAYOUT: PortLayout = (() => {
  const S = CAPITAL.SIZE; // 96
  const props: PortProp[] = [];
  const obstacles: PortObstacle[] = [];
  // Onda Cidade Viva props (owner Meshy kit) get their collision radius here too, so a hand-placed bench/lamp/
  // cottage/grove picks up the same footprint the editor + validator use. None are placed by DEFAULT, so the
  // shipped layout stays byte-identical (place() is never called with these kinds below).
  // Onda Cidade Verde — the landscape kit (pine/pine_tall/rock/cliff) planted from INSIDE the city carries
  // collision too: slim trunks (pine/rock) block lightly so a captain weaves between them; the cliff is a big
  // solid mass. Same byte-identity note — none are placed by DEFAULT.
  const R: Record<string, number> = { house_a: 4, house_b: 4, warehouse: 4.2, fountain: 3.6, lighthouse: 3.8, crane: 2.6, cobble_bench: 0.8, street_lamp: 0.5, cottage_ember: 2.6, lantern_grove: 1.4, pine: 1.2, pine_tall: 1, rock: 1, cliff: 3 };
  const place = (kind: string, x: number, y: number, heading = 0, h = 0): void => {
    props.push({ kind, x, y, heading, ...(h ? { h } : {}) }); // h = altitude (m, world-Y); collision stays at floor
    const r = R[kind]; if (r) obstacles.push({ x, y, r });
  };

  // main-street houses — two rows lining the street into alleys, alternating the two variants
  place('house_a', 30, 26, 0);            place('house_b', 66, 26, Math.PI);
  place('house_b', 30, 40, 0);            place('house_a', 66, 40, Math.PI);
  place('house_a', 30, 52, 0);            place('house_b', 66, 52, Math.PI);
  // quay warehouses + a dock crane by the harbour gate
  place('warehouse', 18, 18, Math.PI / 2);
  place('warehouse', 78, 18, Math.PI / 2);
  place('crane', 66, 12, Math.PI);
  // the landmark lighthouse (NW — also pokes above the wall when seen from the sea)
  place('lighthouse', 15, 82, 0);
  // central plaza fountain (with the captain statue)
  place('fountain', 48, 60, 0);

  // the harbour gate straddles the dock street: one render piece, two solid towers, arch open at x≈48
  props.push({ kind: 'gate', x: 48, y: 14, heading: Math.PI / 2 });
  obstacles.push({ x: 42, y: 14, r: 2.4 }, { x: 54, y: 14, r: 2.4 });

  // the tavern crowns the north end of the street (also the audio-crossfade centre)
  const tavern = { x: 48, y: 84, r: 7 };
  props.push({ kind: 'tavern', x: tavern.x, y: tavern.y, heading: 0 });
  obstacles.push({ x: tavern.x, y: tavern.y, r: tavern.r });

  // --- Onda 7: the CASINO WING (a gambling den in front of the tavern) + the auction rostrum + clan hall.
  // Each station renders its GLB and opens its game UI when a captain stands within CASINO.INTERACT_R of
  // `interact*`. The interact point is pushed toward the plaza centre so the captain always stands clear.
  const casino: PortStation[] = [];
  // Collision radii hug the visible tables (walkability fix): oversized rings used to snag captains in the
  // open plaza. poker 2.7→2.4 and auction 2.4→2.1 match their GLB footprints (see KIT_FOOT in portscene).
  // slot_machine 1.5→0.9 (Parede-invisível-4): the cabinet is HEIGHT-normalized to 2.4 m and renders only
  // ~1.85×1.5 m wide — r 1.5 stuck ~1 m of invisible wall past the mesh. MUST stay equal to the shared
  // KIND_RADIUS (packages/protocol/src/portcollision.ts); the layoutbuild golden test pins the two together.
  const cRadius: Record<string, number> = { poker_table: 2.4, blackjack_table: 2.5, slot_machine: 0.9, auction_stage: 2.1 };
  let stationId = 1;
  const station = (game: number, kind: string, x: number, y: number, heading = 0): void => {
    let dx = 48 - x, dy = 62 - y;                       // toward the plaza centre (open side)
    const d = Math.hypot(dx, dy) || 1; dx /= d; dy /= d;
    casino.push({ game, id: stationId++, x, y, heading, interactX: x + dx * 3.4, interactY: y + dy * 3.4 });
    props.push({ kind, x, y, heading });
    const r = cRadius[kind]; if (r) obstacles.push({ x, y, r });
  };
  // the gambling den fills the strip between the plaza stalls and the tavern (y 72–78)
  station(CasinoGame.BLACKJACK, 'blackjack_table', 32, 74, Math.PI / 2);
  // Walkability fix: the poker pit sat at y=76 where its collision (r2.4) OVERLAPPED the tavern ring (48,84
  // r7), wedging captains who walked up the street centre toward the tavern. Nudged south to y=72 so a clear
  // ~2.6 m gap opens between the table and the tavern steps — the central approach is walkable again.
  station(CasinoGame.POKER, 'poker_table', 48, 72, 0);                  // the poker pit, centre stage
  station(CasinoGame.SLOTS, 'slot_machine', 60, 73, -Math.PI / 2);      // a little bank of three bandits
  station(CasinoGame.SLOTS, 'slot_machine', 65, 76, -Math.PI / 2);
  station(CasinoGame.SLOTS, 'slot_machine', 68, 81, -Math.PI / 2);
  // the auction rostrum stands on the plaza by the market (Onda 3 auction house made physical)
  station(CasinoGame.AUCTION, 'auction_stage', 24, 58, Math.PI / 2);
  // the clan hall of champions — a plaque station flanked by trophies, tucked along the west wall
  casino.push({ game: CasinoGame.CLAN_HALL, id: stationId++, x: 12, y: 40, heading: Math.PI / 2, interactX: 16, interactY: 40 });
  for (const ty of [34, 40, 46]) { props.push({ kind: 'clan_trophy', x: 9, y: ty, heading: Math.PI / 2 }); }

  // market stalls flanking the plaza (rendered from the market_stall GLB; trade opens near interact*)
  const stalls: PortStall[] = [
    { x: 38, y: 56, heading: 0,       interactX: 41.5, interactY: 56 },
    { x: 58, y: 56, heading: Math.PI, interactX: 54.5, interactY: 56 },
    { x: 38, y: 66, heading: 0,       interactX: 41.5, interactY: 66 },
    { x: 58, y: 66, heading: Math.PI, interactX: 54.5, interactY: 66 },
  ];
  for (const s of stalls) { props.push({ kind: 'stall', x: s.x, y: s.y, heading: s.heading }); obstacles.push({ x: s.x, y: s.y, r: 1.9 }); } // r hugs the market_stall GLB (was 2.2 — snagged captains)

  // perimeter rampart wall — instanced kit segments (collision is the plaza clamp, so no obstacles here).
  // 12 m spacing keeps the total instanced triangle count in budget while still ringing the whole city.
  const step = 12;
  for (let x = 8; x <= S - 8; x += step) props.push({ kind: 'wall', x, y: S - 2, heading: 0 });               // north
  for (let y = 16; y <= S - 12; y += step) props.push({ kind: 'wall', x: 2, y, heading: Math.PI / 2 });        // west
  for (let y = 16; y <= S - 12; y += step) props.push({ kind: 'wall', x: S - 2, y, heading: Math.PI / 2 });    // east
  for (let x = 8; x <= 34; x += step) props.push({ kind: 'wall', x, y: 2, heading: 0 });                       // south, west of the gate
  for (let x = 62; x <= S - 8; x += step) props.push({ kind: 'wall', x, y: 2, heading: 0 });                   // south, east of the gate

  // Layout Editor wave: the light main-street flagstone is now DATA (a removable/editable ground patch)
  // rather than hardcoded in portscene — the owner can reshape or delete it. The old decorative centre ring
  // was dropped (the "strange centre tiles" report). Cosmetic only; the walker never reads `ground`.
  const ground: GroundPatch[] = [
    { x: 48, y: 40, w: 14, l: 52, rot: 0, mat: GroundMat.FLAGSTONE },
  ];

  return {
    size: S,
    spawn: { x: 48, y: 12, heading: Math.PI / 2 }, // just past the dock (clear of the exit AABB), facing NORTH up the street
    exit: { minX: 40, minY: 0, maxX: 56, maxY: 9 },
    tavern,
    stalls,
    obstacles,
    props,
    casino,
    ground,
    camLook: { x: 48, y: 62 },
  };
})();

/**
 * ---- Onda 8: The Captain's House (a walkable interior instance, per captain) ----
 * Docking at your OWN base (or a clanmate's, with "Open to Armada" on) lets you step into the interior of
 * your island HQ — a small walkable cabin instance (`HouseRoom` on the server, molde `PortRoom`). It is a
 * SANCTUARY: never raidable, nothing lootable lives inside — only cosmetics + achievement. The walker sim
 * is the SAME deterministic `stepWalkerBody`/`collideRoom` lib as the port. Furniture layout is PER captain
 * (persisted on the profile), validated server-side (posse/cell/footprint/flood-fill) exactly like the port.
 * The interior is a RECTANGLE (unlike the square plaza), so it uses `collideRoom(b,obst,r,w,d)`.
 */
export const HOUSE = {
  /** Walkable interior footprint (m): a 22×14 rectangle → a 22×14 grid of 1 m cells. */
  WIDTH: 22, DEPTH: 14,
  /** One decorate cell is 1 m; `cell = gy*WIDTH + gx` (gx∈[0,WIDTH), gy∈[0,DEPTH)), centre = (gx+.5, gy+.5). */
  CELL: 1,
  /** Max furniture pieces PLACED in one house (the flood-fill + inventory bound). */
  MAX_PLACED: 64,
  /** Max of any one furniture kind a captain may stock in inventory (anti-abuse). */
  MAX_STOCK_PER_KIND: 32,
  /** Cap of avatars in one house instance: the owner + 7 visitors. */
  CAP: 8,
  /** Max simultaneous HouseRoom instances server-wide — overflow answers "House is busy, try again"
   *  (a hard ceiling so the on-demand instances can never threaten the 30 Hz sea tick). */
  MAX_ROOMS: 64,
  /** Seconds an EMPTY house instance lingers before it is reaped. */
  EMPTY_TIMEOUT_S: 60,
  /** Decorate edit ops accepted per second per player (anti-abuse rate limit). */
  EDIT_RATE_PER_S: 5,
  /** Within this of the interior hearth the client loops the fireside ambiance (reused SFX). */
  HEARTH_AUDIO_R: 9,
  /** Onda Escala (wire v42): per-piece decorate SCALE, as a percent of the kind's natural size. VISUAL ONLY —
   *  the collision footprint always stays the kind's declared cells, so a scaled piece just overhangs its
   *  footprint cosmetically (never re-validates walkability). Quantized to a u8 percent on the wire; absent on a
   *  legacy placed piece reads as SCALE_DEFAULT (100 %). The Decorate UI steps the slider by SCALE_STEP. */
  SCALE_MIN: 50, SCALE_MAX: 200, SCALE_STEP: 10, SCALE_DEFAULT: 100,
} as const;

/** Clamp a raw (possibly client-claimed) house-furniture scale percent to the legal [SCALE_MIN,SCALE_MAX]
 *  band, snapped to whole percents. A non-finite / absent value collapses to SCALE_DEFAULT (100 %). Shared so
 *  the server validator, the client Decorate UI and the render all agree on exactly one clamp. */
export function clampHouseScale(pct: number | undefined): number {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return HOUSE.SCALE_DEFAULT;
  return Math.max(HOUSE.SCALE_MIN, Math.min(HOUSE.SCALE_MAX, Math.round(pct)));
}

/** C_HOUSE ENTER arg sentinel meaning "my OWN dock" (island slot 0 is a valid VISIT target, so it can't
 *  double as the sentinel). The client sends this to enter its own house; any other value = a visited base's
 *  islandId. (The server also treats a base whose ownerId matches you as your own, so a real islandId works too.) */
export const HOUSE_OWN_DOCK = 0xffff;

/** The fixed trophy-wall slots (auto-populated by the server from the profile — NOT furniture, no edit ops).
 *  Each id renders a distinct display piece the server fills with what the captain has already earned. */
export const TrophySlotKind = {
  TROPHY_SHELF: 0,    // 🏆 wave-3 figurehead trophies earned (a shelf of them)
  CODEX_RELIC: 1,     // 📖 completed Codex sets (relics on a shelf)
  WANTED_POSTER: 2,   // 🏴‍☠️ your record bounty, as a framed WANTED poster
  CLANWAR_CUP: 3,     // ⚔ Clan War cup — shown only while your Armada holds a region (vanishes if lost)
  SHIP_MODEL: 4,      // 🚢 a model of your CURRENT ship on the map table
  SEASON_PORTRAIT: 5, // 🎖 your Season level + captain portrait
} as const;
export type TrophySlotKind = (typeof TrophySlotKind)[keyof typeof TrophySlotKind];

/** A fixed trophy slot in the shell: where it stands + which slot kind it displays. */
export interface HouseSlot { kind: number; x: number; y: number; heading: number; }
/** The interior shell (v1: one flat cabin for everyone; tiers by HQ level are v2). Walkable RECTANGLE + the door
 *  back to sea + the fixed trophy slots. Shared DATA so the client render/collision and the server can never
 *  drift — exactly like PORT_LAYOUT. (The raised NE mezzanine deck + stair were removed — the walker is 2D and
 *  never climbed them; two-story house = onda futura. `obstacles`/`solidRects` stay as empty seams.) */
export interface HouseShell {
  w: number; d: number;
  spawn: { x: number; y: number; heading: number };
  /** The door AABB (south wall): walking into it (or the button) sails back to sea. */
  exit: { minX: number; minY: number; maxX: number; maxY: number };
  /** Structural solid CIRCLE props beyond the four walls — seed walker obstacles. Empty in v1 (flat cabin). */
  obstacles: readonly PortObstacle[];
  /** Solid axis-aligned no-walk RECTANGLES (walker collision). Empty in v1; the seam remains for a future storey.
   *  Shared DATA → server + client prediction agree exactly. */
  solidRects: readonly { minX: number; minY: number; maxX: number; maxY: number }[];
  /** The auto-populated trophy wall. */
  slots: readonly HouseSlot[];
  /** The interior hearth position (the fireside-ambiance centre + where the iron stove reads "home"). */
  hearth: { x: number; y: number };
}
export const HOUSE_SHELL: HouseShell = (() => {
  const w = HOUSE.WIDTH, d = HOUSE.DEPTH;
  return {
    w, d,
    spawn: { x: 11, y: 2, heading: Math.PI / 2 }, // just inside the door, facing north into the cabin
    exit: { minX: 9, minY: 0, maxX: 13, maxY: 1.2 },
    obstacles: [], // v1 flat cabin — the NE mezzanine support posts were removed with the deck
    slots: [
      { kind: TrophySlotKind.SHIP_MODEL,      x: 11,   y: 8.5,  heading: Math.PI / 2 }, // map table, centre
      { kind: TrophySlotKind.TROPHY_SHELF,    x: 1.0,  y: 5.0,  heading: 0 },            // west wall, faces +x
      { kind: TrophySlotKind.CODEX_RELIC,     x: 1.0,  y: 9.0,  heading: 0 },            // west wall
      { kind: TrophySlotKind.WANTED_POSTER,   x: 6.0,  y: 13.4, heading: -Math.PI / 2 }, // north wall, faces -y
      { kind: TrophySlotKind.SEASON_PORTRAIT, x: 15.0, y: 13.4, heading: -Math.PI / 2 }, // north wall
      { kind: TrophySlotKind.CLANWAR_CUP,     x: 20.6, y: 6.0,  heading: Math.PI },      // east wall pedestal
    ],
    // No-walk RECTANGLES — empty in v1. The raised NE mezzanine deck + stair buttress were removed: the walker
    // is 2D and never climbed the platform, so it read as a provocation that blocked but didn't ascend. The NE
    // corner is now plain, walkable floor. two-story house = onda futura (restore the deck + these rects then).
    solidRects: [],
    hearth: { x: 3.2, y: 12.4 }, // the NW hearth corner (iron stove reads here; fireside ambiance centre)
  };
})();

/** Is a house-local point inside the door/exit zone? (walking into it sails back out to the sea). */
export function inHouseExit(x: number, y: number): boolean {
  const e = HOUSE_SHELL.exit;
  return x >= e.minX && x <= e.maxX && y >= e.minY && y <= e.maxY;
}

/** Furniture kinds (v1 catalog, ~20). Craftable ones (Carpenter/Smith/Alchemist/Fisher) have a `craft`
 *  recipe; shop ones have a doubloon `price` (a sink). `wall` pieces mount flat and never block the floor;
 *  `solid` pieces seed a walker obstacle when placed. Footprint is in grid CELLS. */
export const FurnitureKind = {
  // Carpenter (craft — wood)
  TABLE: 0, CHAIR: 1, BED: 2, BOOKSHELF: 3, BENCH: 4, CHART_TABLE: 5,
  // Smith (craft — iron)
  WALL_LAMP: 6, CANDELABRA: 7, IRON_STOVE: 8, GLOBE: 9,
  // Alchemist (craft — reagents)
  AQUARIUM: 10, POTION_SHELF: 11, STRANGE_LAMP: 12,
  // Fisher (craft — fish)
  FISH_TROPHY: 13,
  // Shop (doubloons — sink)
  RUG: 14, CURTAIN: 15, DECO_BARREL: 16, CRATE: 17, PAINTING: 18, PLANT: 19,
  // Leva 2 (Onda de Arte) — appended at the next free id so existing house wire ids never shift (retro-compat).
  CARD_TABLE: 20,
  // Leva 3 (Casa do Capitão — móveis do dono) — new Smith brass instrument, appended so wire ids never shift.
  SPYGLASS: 21,
  // economy-close (2026-07-15): the repeatable BLACK-PEARL sink. A prestige nacre lamp crafted from a pearl —
  // stackable (unlike the once-only Pearl-Inlaid Hull), so the deep's pearls have an ongoing destination.
  NACRE_LANTERN: 22,
} as const;
export type FurnitureKind = (typeof FurnitureKind)[keyof typeof FurnitureKind];

export type FurnitureOrigin = 'carpenter' | 'smith' | 'alchemist' | 'fisher' | 'shop';
export interface FurnitureDef {
  name: string;
  icon: string;
  origin: FurnitureOrigin;
  /** Footprint in grid cells (w × d, at rotation 0). */
  foot: { w: number; d: number };
  /** Seeds a walker collision circle when placed (blocks walking through). */
  solid?: boolean;
  /** Wall-mounted / flat on the floor — no collision, sits against a wall or under other pieces. */
  wall?: boolean;
  /** Craftable: recipe (building + inputs + the profession that earns XP). Sent via the Stores Craft tab. */
  craft?: { building: number; inputs: CraftInput[]; prof: string };
  /** Shop: doubloon price (a sink) — bought in the Decorate catalog. */
  price?: number;
  desc: string;
}
/** `cell = gy*WIDTH + gx` for the furniture-craft recipe id space: a furniture kind K is crafted by sending
 *  C_CRAFT with recipe id (FURNITURE_CRAFT_BASE + K) — keeps it OFF the material CraftId enum (no churn),
 *  reusing the existing craft wire + Stores UI. 100 sits clear of every real CraftId (< CRAFT_COUNT). */
export const FURNITURE_CRAFT_BASE = 100;

export const FURNITURE: Record<number, FurnitureDef> = {
  // ---- Carpenter (wood; XP on the smith/crafting track, no dedicated 'carpenter' profession) ----
  [FurnitureKind.TABLE]:       { name: 'Oak Table',      icon: '🪑', origin: 'carpenter', foot: { w: 2, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 4 }, { mat: MaterialId.IRON, qty: 1 }], prof: 'smith' }, desc: 'A stout planked table for the great cabin.' },
  [FurnitureKind.CHAIR]:       { name: 'Captain’s Chair', icon: '💺', origin: 'carpenter', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 3 }], prof: 'smith' }, desc: 'Carved oak, worn smooth by long watches.' },
  [FurnitureKind.BED]:         { name: 'Captain’s Berth', icon: '🛏', origin: 'carpenter', foot: { w: 3, d: 2 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 8 }, { mat: MaterialId.IRON, qty: 2 }], prof: 'smith' }, desc: 'A skull-carved berth draped in the Jolly Roger — the heart of the cabin.' },
  [FurnitureKind.BOOKSHELF]:   { name: 'Bookshelf',      icon: '📚', origin: 'carpenter', foot: { w: 2, d: 1 }, wall: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 5 }], prof: 'smith' }, desc: 'Charts, logs and stolen novels.' },
  [FurnitureKind.BENCH]:       { name: 'Sea Chest Bench', icon: '🧰', origin: 'carpenter', foot: { w: 2, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 4 }], prof: 'smith' }, desc: 'A bench atop a banded sea chest.' },
  [FurnitureKind.CHART_TABLE]: { name: 'Chart Table',    icon: '🗺', origin: 'carpenter', foot: { w: 2, d: 2 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 6 }, { mat: MaterialId.IRON, qty: 1 }], prof: 'smith' }, desc: 'Where the next raid is plotted.' },
  [FurnitureKind.CARD_TABLE]:  { name: 'Card Table',     icon: '🃏', origin: 'carpenter', foot: { w: 2, d: 2 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.WOOD, qty: 6 }, { mat: MaterialId.IRON, qty: 2 }], prof: 'smith' }, desc: 'Green felt, brass and gold — a captain’s game of cards.' },
  // ---- Smith (iron) ----
  [FurnitureKind.WALL_LAMP]:   { name: 'Wall Lantern',   icon: '🏮', origin: 'smith', foot: { w: 1, d: 1 }, wall: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 2 }, { mat: MaterialId.FISH_OIL, qty: 1 }], prof: 'smith' }, desc: 'Warm iron light against a dark wall.' },
  [FurnitureKind.CANDELABRA]:  { name: 'Candelabra',     icon: '🕯', origin: 'smith', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 3 }], prof: 'smith' }, desc: 'A branched iron candlestand.' },
  [FurnitureKind.IRON_STOVE]:  { name: 'Iron Stove',     icon: '🔥', origin: 'smith', foot: { w: 2, d: 2 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 6 }, { mat: MaterialId.WOOD, qty: 2 }], prof: 'smith' }, desc: 'A cast-iron hearth — the cabin crackles warm.' },
  [FurnitureKind.GLOBE]:       { name: 'Framed Globe',   icon: '🌐', origin: 'smith', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 3 }, { mat: MaterialId.WOOD, qty: 2 }], prof: 'smith' }, desc: 'A brass-framed globe of the known seas.' },
  [FurnitureKind.SPYGLASS]:    { name: 'Brass Spyglass', icon: '🔭', origin: 'smith', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.IRON, qty: 3 }, { mat: MaterialId.WOOD, qty: 2 }], prof: 'smith' }, desc: 'A brass spyglass on a tripod stand — the horizon, drawn close.' },
  // economy-close: the repeatable BLACK-PEARL sink — a nacre-white prestige lamp, crafted from a pearl at the
  // foundry. Stackable (unlike the once-only Pearl-Inlaid Hull), so the deep's pearls keep a destination.
  [FurnitureKind.NACRE_LANTERN]: { name: 'Nacre Lantern',  icon: '🏮', origin: 'smith', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.FOUNDRY, inputs: [{ mat: MaterialId.BLACK_PEARL, qty: 1 }, { mat: MaterialId.IRON, qty: 2 }], prof: 'smith' }, desc: 'An iron lamp glazed with a single black pearl, ground to a moon-pale nacre glow — one pearl, hauled from the deep.' },
  // ---- Alchemist (reagents) ----
  [FurnitureKind.AQUARIUM]:    { name: 'Aquarium',       icon: '🐠', origin: 'alchemist', foot: { w: 2, d: 1 }, solid: true, craft: { building: ProdBuilding.TAVERN, inputs: [{ mat: MaterialId.FISH_OIL, qty: 3 }, { mat: MaterialId.WOOD, qty: 2 }], prof: 'alchemist' }, desc: 'Displays the fish species in your Codex.' },
  [FurnitureKind.POTION_SHELF]:{ name: 'Potion Shelf',   icon: '⚗', origin: 'alchemist', foot: { w: 2, d: 1 }, wall: true, craft: { building: ProdBuilding.TAVERN, inputs: [{ mat: MaterialId.FISH_OIL, qty: 2 }, { mat: MaterialId.HERB, qty: 2 }], prof: 'alchemist' }, desc: 'Bottles of bright, dubious brews.' },
  [FurnitureKind.STRANGE_LAMP]:{ name: 'Strange Lamp',   icon: '🔮', origin: 'alchemist', foot: { w: 1, d: 1 }, solid: true, craft: { building: ProdBuilding.TAVERN, inputs: [{ mat: MaterialId.STORM_ESSENCE, qty: 1 }, { mat: MaterialId.FISH_OIL, qty: 1 }], prof: 'alchemist' }, desc: 'It glows a colour the sea has no name for.' },
  // ---- Fisher (fish) ----
  [FurnitureKind.FISH_TROPHY]: { name: 'Mounted Catch',  icon: '🐟', origin: 'fisher', foot: { w: 1, d: 1 }, wall: true, craft: { building: ProdBuilding.TAVERN, inputs: [{ mat: MaterialId.FISH, qty: 6 }, { mat: MaterialId.WOOD, qty: 1 }], prof: 'fishing' }, desc: 'The one that did not get away.' },
  // ---- Shop (doubloons — sink) ----
  [FurnitureKind.RUG]:         { name: 'Woven Rug',      icon: '🟥', origin: 'shop', foot: { w: 3, d: 2 }, wall: true, price: 400,  desc: 'A plush plundered carpet.' },
  [FurnitureKind.CURTAIN]:     { name: 'Velvet Curtain', icon: '🪟', origin: 'shop', foot: { w: 1, d: 1 }, wall: true, price: 300,  desc: 'Deep-red drapes for the stern windows.' },
  [FurnitureKind.DECO_BARREL]: { name: 'Rum Barrel',     icon: '🛢', origin: 'shop', foot: { w: 1, d: 1 }, solid: true, price: 250, desc: 'Decorative — mostly.' },
  [FurnitureKind.CRATE]:       { name: 'Cargo Crate',    icon: '📦', origin: 'shop', foot: { w: 1, d: 1 }, solid: true, price: 200, desc: 'Stamped with a faded East-seas mark.' },
  [FurnitureKind.PAINTING]:    { name: 'Sea Painting',   icon: '🖼', origin: 'shop', foot: { w: 1, d: 1 }, wall: true, price: 500,  desc: 'A storm, framed in gilt.' },
  [FurnitureKind.PLANT]:       { name: 'Potted Palm',    icon: '🪴', origin: 'shop', foot: { w: 1, d: 1 }, solid: true, price: 220, desc: 'A little green, far from any shore.' },
};
export function furnitureById(id: number): FurnitureDef | undefined { return (FURNITURE as Record<number, FurnitureDef>)[id]; }
export const FURNITURE_KINDS: readonly number[] = Object.keys(FurnitureKind).map((k) => (FurnitureKind as Record<string, number>)[k]!);

/** One placed furniture piece in the DEFAULT house layout: `kind` at grid `cell` (gy*HOUSE.WIDTH+gx), rot 0..3.
 *  Onda Editor-em-Mundo (wire v43): three OPTIONAL fine-transform fields let the owner pose a piece freely —
 *  `yaw` (u8 0..255 → a full turn, 256 steps of visual rotation), `fx`/`fy` (u8 0..255 sub-cell offset, 128 =
 *  centred). All VISUAL only — the collision footprint always stays the kind's declared cells at the coarse
 *  `rot` (0..3), so the validator is untouched. Absent → legacy: `yaw = rot*64` (the four cardinals), `fx/fy`
 *  centred. `scale` (v42) is the per-piece size percent (visual). */
export interface HouseFurniturePlacement { kind: number; cell: number; rot: number; yaw?: number; fx?: number; fy?: number; scale?: number; }

/** Onda Editor-em-Mundo — the quantization band for the fine sub-cell OFFSET. A `fx`/`fy` u8 maps linearly to
 *  metres in [-OFFSET_MAX, +OFFSET_MAX] with 128 = 0 (centred). Half a cell, so a nudge can slide a piece up to
 *  its own edge without ever leaving the footprint cells the validator reserved. */
export const FURN_FINE = { OFFSET_MAX: 0.5 } as const;

/** Decode a piece's fine VISUAL yaw (radians) from its coarse `rot` (0..3) + optional `yaw` u8 (0..255). Absent
 *  yaw → the legacy cardinal `rot*64`, so `furnitureYaw(rot)` === `-rot*π/2` exactly (byte-identical render to
 *  before this field existed). Sign matches the shared `-rot*π/2` convention used by housescene + the editor. */
export function furnitureYaw(rot: number, yaw?: number): number {
  const y8 = (typeof yaw === 'number' && Number.isFinite(yaw)) ? (Math.round(yaw) & 0xff) : ((rot & 3) * 64);
  return -(y8 / 256) * Math.PI * 2;
}
/** Quantize a visual yaw (radians) to the u8 (0..255) the wire/data carry — the inverse of `furnitureYaw`. */
export function quantYaw(radians: number): number {
  let y = -radians / (Math.PI * 2) * 256;
  y = ((y % 256) + 256) % 256;
  return Math.round(y) & 0xff;
}
/** Decode a piece's fine sub-cell OFFSET (house-local metres) from its optional `fx`/`fy` u8 (128 = centred).
 *  Absent/centred → {0,0}, so a piece with no offset renders exactly on its cell centre as before. */
export function furnitureOffset(fx?: number, fy?: number): { ox: number; oy: number } {
  const dx = ((typeof fx === 'number' && Number.isFinite(fx) ? (Math.round(fx) & 0xff) : 128) - 128) / 128;
  const dy = ((typeof fy === 'number' && Number.isFinite(fy) ? (Math.round(fy) & 0xff) : 128) - 128) / 128;
  return { ox: dx * FURN_FINE.OFFSET_MAX, oy: dy * FURN_FINE.OFFSET_MAX };
}
/** Quantize a sub-cell offset (metres, clamped to ±OFFSET_MAX) to the u8 (0..255) the wire/data carry. */
export function quantOffset(metres: number): number {
  const clamped = Math.max(-FURN_FINE.OFFSET_MAX, Math.min(FURN_FINE.OFFSET_MAX, metres));
  return Math.max(0, Math.min(255, Math.round(128 + (clamped / FURN_FINE.OFFSET_MAX) * 128)));
}
/** The STARTER furniture a brand-new captain's house is created with — the layout the House editor (Creative
 *  Mode, `?editor=house`) authors, exactly like DEFAULT_BASE shapes new bases. SHIPS EMPTY (owner order
 *  2026-07-12: "zera tudo, faço manual depois"): the v42 8-piece hard-coded starter was RETIRED. A fresh /
 *  untouched cabin is now a bare room until the owner authors a starter template by hand in the editor and
 *  Save-Online publishes it — that server override (read via `defaultHouseFurniture()`) then seeds NEW houses
 *  AND retrofills untouched veteran ones. Existing captains who ever decorated are never touched; the player
 *  stays free to redecorate their own. NOTE: the frozen SNAPSHOT of the old v42 starter lives in
 *  `apps/server/src/persistence/types.ts` (the boot migration that strips auto-stamped pristine houses). */
export const DEFAULT_HOUSE_FURNITURE: readonly HouseFurniturePlacement[] = [];

/** The rotated footprint (in cells) for a piece: rot 0/2 keep w×d, rot 1/3 swap to d×w. */
export function furnitureFootprint(kind: number, rot: number): { w: number; d: number } {
  const f = furnitureById(kind)?.foot ?? { w: 1, d: 1 };
  return (rot & 1) ? { w: f.d, d: f.w } : { w: f.w, d: f.d };
}

/** Onda Sit-Configurador — the UNIFIED seat registry. ONE source of truth for every sittable thing: house
 *  FURNITURE kinds (numeric FurnitureKind, stringified as the map key) AND city PROPS (string kind). Replaces the
 *  old split `SEAT_HEIGHT` (house) / `CITY_SEAT` (port). `y0` lifts the seated avatar onto the seat surface (m);
 *  `reach` is how close (m) a captain must stand to sit; `faceYaw` is a facing offset (rad) added to the base
 *  facing (house: toward the room centre; city: the placed prop's heading). `ox`/`oz` are a per-kind ANCHOR — a
 *  sub-metre offset (m) of the seated avatar off the piece centre, expressed in the piece's LOCAL frame (ox along
 *  the seat's right, oz along its forward), so it rotates with the piece's base heading and lands identically-
 *  relative on EVERY placed piece of the kind, whatever its orientation. Absent (the shipped default / a pre-anchor
 *  save) = 0 = dead centre — so the pickers stay byte-identical until the owner drags the ghost. Both pure pickers
 *  (`nearestSeat` / `nearestPortSeat` in @piratas/sim) and the render lift read it through `seatConfig(kind)`, so the
 *  seated pose can never drift from the geometry. LOCAL VISUAL ONLY — never on the wire. The in-world editor's Seat
 *  panel tunes these live (the "🧍 Position avatar" gizmo drags the ghost → ox/oz/y0/faceYaw); a Save-Online `seats`
 *  slice overrides them render-only at boot, like the other overrides — AND server-side, so the server-derived SIT
 *  transform it broadcasts is already anchored (see apps/server/src/index.ts applyEditorOverrides). */
export interface SeatConfig { y0: number; reach: number; faceYaw: number; ox?: number; oz?: number; }

/** The shipped seat defaults (the Export-TS baseline + the reset target). House `reach` reproduces the old
 *  footprint rule `0.7 + max(w,d)/2` exactly (CHAIR 1×1 → 1.2, BENCH 2×1 → 1.7, BED 3×2 → 2.2), so the pickers
 *  behave byte-identically until the owner tunes a value. */
export const SEAT_DEFAULTS: Readonly<Record<string, SeatConfig>> = {
  [FurnitureKind.CHAIR]: { y0: 0.52, reach: 1.2, faceYaw: 0 },
  [FurnitureKind.BENCH]: { y0: 0.72, reach: 1.7, faceYaw: 0 },
  [FurnitureKind.BED]:   { y0: 0.66, reach: 2.2, faceYaw: 0 },
  // the plaza cobble bench — a wide seat at 0.48 m, reachable from either side.
  cobble_bench: { y0: 0.48, reach: 1.7, faceYaw: 0 },
  // the tavern stool — a small round seat at 0.45 m; a tight reach so a captain must stand right at it (they
  // cluster round a poker table, so a wide reach would grab the wrong one).
  stool: { y0: 0.45, reach: 1.0, faceYaw: 0 },
};

/** Editor + Save-Online validator clamp bands (client + server agree on exactly one clamp). `ox`/`oz` are the
 *  anchor offsets — capped at ±1.5 m so the ghost can slide anywhere on a real seat surface but never off it. */
export const SEAT_CLAMP = { y0: [0, 2] as const, reach: [0.3, 4] as const, faceYaw: [-Math.PI, Math.PI] as const, ox: [-1.5, 1.5] as const, oz: [-1.5, 1.5] as const };
/** Clamp a (possibly client-claimed / partial) seat config onto a base default. The anchor (`ox`/`oz`) is OMITTED
 *  from the result when it resolves to 0, so a seat that was never dragged off-centre round-trips byte-identically
 *  (a pre-anchor save, or any unchanged kind, keeps `{ y0, reach, faceYaw }` exactly). */
export function clampSeatConfig(c: Partial<SeatConfig> | undefined, base: SeatConfig): SeatConfig {
  const cl = (v: number | undefined, band: readonly [number, number], d: number): number =>
    (typeof v === 'number' && Number.isFinite(v)) ? Math.max(band[0], Math.min(band[1], v)) : d;
  const p = c ?? {};
  const out: SeatConfig = { y0: cl(p.y0, SEAT_CLAMP.y0, base.y0), reach: cl(p.reach, SEAT_CLAMP.reach, base.reach), faceYaw: cl(p.faceYaw, SEAT_CLAMP.faceYaw, base.faceYaw) };
  const ox = cl(p.ox, SEAT_CLAMP.ox, base.ox ?? 0), oz = cl(p.oz, SEAT_CLAMP.oz, base.oz ?? 0);
  if (ox !== 0) out.ox = ox;
  if (oz !== 0) out.oz = oz;
  return out;
}

/** The ACTIVE registry the pickers read — a mutable clone of the defaults the CLIENT may override at boot from a
 *  Save-Online `seats` slice (render-only; sitting is a local visual, never authoritative, so this never touches
 *  the deterministic sim). The server never overrides it. */
const _seatActive: Record<string, SeatConfig> = Object.fromEntries(Object.entries(SEAT_DEFAULTS).map(([k, v]) => [k, { ...v }]));
/** The live seat config for a kind (house FurnitureKind number OR city prop string), or undefined if not a seat. */
export function seatConfig(kind: string | number): SeatConfig | undefined { return _seatActive[String(kind)]; }
/** Apply (or clear, with null) a render-only seat override — per-kind partials, each clamped onto the default.
 *  Always resets every kind to its shipped default first, so a save that drops a kind restores it. */
export function applySeatOverride(map: Record<string, Partial<SeatConfig>> | null | undefined): void {
  for (const [k, def] of Object.entries(SEAT_DEFAULTS)) _seatActive[k] = { ...def };
  if (!map || typeof map !== 'object') return;
  for (const [k, partial] of Object.entries(map)) {
    const base = SEAT_DEFAULTS[k]; if (!base || !partial || typeof partial !== 'object') continue;
    _seatActive[k] = clampSeatConfig(partial as Partial<SeatConfig>, base);
  }
}
/** The current active registry as a plain map (for the editor's Export TS + Save-Online patch). */
export function seatRegistrySnapshot(): Record<string, SeatConfig> {
  return Object.fromEntries(Object.entries(_seatActive).map(([k, v]) => [k, { ...v }]));
}

/** Interior Vivo (compat): the seat-surface height (m) per house furniture kind — derived from the unified
 *  registry so legacy callers + tests keep reading it unchanged. */
export const SEAT_HEIGHT: Readonly<Record<number, number>> =
  Object.fromEntries(FURNITURE_KINDS.filter((k) => SEAT_DEFAULTS[String(k)]).map((k) => [k, SEAT_DEFAULTS[String(k)]!.y0])) as Record<number, number>;
/** Can a captain sit on this furniture kind? (chair / bench / bed). */
export function isSeatKind(kind: number): boolean { return SEAT_DEFAULTS[String(kind)] !== undefined; }

/** Onda Cidade Viva (compat): the city-prop seat configs — derived from the unified registry (the string-keyed
 *  entries). Kept as a named export so existing imports + tests resolve unchanged. */
export const CITY_SEAT: Readonly<Record<string, SeatConfig>> =
  Object.fromEntries(Object.entries(SEAT_DEFAULTS).filter(([k]) => !/^\d+$/.test(k)).map(([k, v]) => [k, { ...v }]));
/** Can a captain sit on this city prop kind? (the cobble bench / tavern stool). */
export function isCitySeatKind(kind: string): boolean { return kind in CITY_SEAT; }

/** Every seat KIND in the registry (numeric furniture kinds + string city kinds), for the editor's Seat palette. */
export const SEAT_KINDS: readonly string[] = Object.keys(SEAT_DEFAULTS);

/** Emotes (6.5): mapped to the captain GLBs' available clips (idle/walk/run/sit/wave/dance share a 24-bone
 *  skeleton across all four hulls). `EMOTE_CLIPS[i]` is the clip the client plays for emote id `i`. */
export const EMOTE_CLIPS: readonly string[] = ['wave', 'dance', 'sit'];
export const EMOTE_LABELS: readonly string[] = ['Wave', 'Dance', 'Sit'];
export const EMOTE_ICONS: readonly string[] = ['👋', '💃', '🪑'];
export const EMOTE_COUNT = EMOTE_CLIPS.length;
/** Emote lifecycle class, indexed by emote id: `true` = the clip LOOPS until cancelled (dance), `false` = a
 *  one-shot that plays once and returns to locomotion (wave). Any real movement cancels either, and re-pressing
 *  a looping emote toggles it off (both handled client-side in portscene). `sit` (id 2) is not a free social
 *  emote — it is the contextual seat action, gated to a real seat — so its flag here is unused. */
export const EMOTE_LOOP: readonly boolean[] = [false, true, false];
/** The free social emotes offered in the on-foot emote row and broadcast to everyone (wave, dance). Sit is
 *  intentionally excluded: it is contextual (needs a seat) and never travels the free-emote row. */
export const FREE_EMOTE_IDS: readonly number[] = [0, 1];

/** ---- Avatar skins (Onda Avatares) ----
 *  The cosmetic captain look worn on foot in the port + house, seen by EVERY player (it rides the walker
 *  SPAWN event, wire v39). It is DATA only: `id` is the u8 carried on the wire + persisted on the profile,
 *  `model` is the GLB the client loads (empty string = the default per-hull captain, i.e. today's behavior).
 *  All skin GLBs are drop-in on the 4-captain contract (same 24-bone skeleton + the 6 named clips
 *  idle/walk/run/sit/wave/dance) so the walker sim, emotes and foot-sync are untouched.
 *
 *  EXTENSIBILITY / premium seam: adding a look is one row here + one .opt.glb — a future `priceSor`/`unlock`
 *  field would let skins be SOLD or earned (a natural cosmetic sink, like ship COSMETICS). The two shipped
 *  here (lady, skeleton) are FREE this wave; the Premium menu is intentionally left untouched. */
export interface AvatarSkinDef { id: number; name: string; model: string }
export const AVATAR_SKINS: readonly AvatarSkinDef[] = [
  { id: 0, name: 'By Ship',        model: '' }, // default: the class captain picked by your hull (legacy behavior)
  { id: 1, name: 'The Iron Lady',  model: '/models/avatar_test/captain_lady_anim.opt.glb' },
  { id: 2, name: 'Undying Bones',  model: '/models/avatar_test/captain_skeleton_anim.opt.glb' },
];
/** The catalog entry for a skin id, or the default (id 0) for any unknown/future id — NEVER throws, so a
 *  data value from a newer server can only ever fall back to the per-hull captain (never a broken avatar). */
export function avatarSkinById(id: number): AvatarSkinDef {
  return AVATAR_SKINS.find((s) => s.id === id) ?? AVATAR_SKINS[0]!;
}
/** True when `id` is a real catalog skin — the server validates a client's SET_SKIN request against this. */
export function isValidAvatarSkin(id: number): boolean {
  return AVATAR_SKINS.some((s) => s.id === id);
}

/** Protocol/version handshake. Bump on any wire-format change.
 * v6: ship class (EV_SPAWN.cls + ProfileMsg.shipClass), ammo bits in C_INPUT,
 * SelfState.riggingDamage, FIRE event carries ammo, EV_RAM event, ShipFlag.RIGGING.
 * v7: grapple-plunder — ShipFlag.GRAPPLED, EventType.GRAPPLE_START/END, SelfState.grappleFactor.
 * v8: grapple polish — Btn.STRUGGLE (mash-to-escape) + EV_GRAPPLE_END.reason (GrappleEnd).
 * v9: cannon elevation — InputMsg.elevation (u8) drives real ball range; SnapshotBall.elev (u8) for the arc.
 * v10: async (offline) raids — C_RAID_START (raid an offline base) + S_RAID_RESULT (outcome/alert).
 * v11: HIT event carries `by` (shooter id) + `victim` (hit ship id) for hit-confirm / got-hit feedback.
 * v12: consumable special ammo — SelfState.chainStock/grapeStock (u8) + UpgradeKind.AMMO (buy at base).
 * v13: dynamic storm — Snapshot.stormIntensity (u8) in the header; windStrength widened to 0..2.55.
 * v28: economy "living world" wave — SelfState.dailyEarned (u32, soft-cap HUD counter); EventType
 *      CURSED_SPAWN/CURSED_CLAIM (map-wide cursed-treasure convergence) + PLUNDER (raid coin-stream FX);
 *      MarketOp LIST_GOLD/BUY_GOLD/CANCEL_GOLD + MarketStateMsg.goldListings (P2P gold market, dormant).
 * v29: "The World" wave (2a) — WORLD_SIZE 4096→8192 (position quantization meaning changes, so the wire
 *      version MUST bump even though the byte layout is unchanged); THREE named outposts with a `capital`
 *      flag in S_WORLD (WorldOutpost.name/capital); physical LOCAL market — MarketReq gains `outpostId`,
 *      MarketState gains `atOutpost` + per-listing `outpostId` + `ledgerSales`; trade winds are pure-sim
 *      (no wire). One bump covers the whole 2a wave.
 * v30: "Professions & Production" wave (2b) — ONE bump for the whole wave. MATERIALS + the ship HOLD
 *      (S_GOODS carries hold + base inventory on change); fishing shoals + wrecks in S_WORLD; Btn.ACTION
 *      (contextual cast/reel/dive); SelfState gains activity/activityProgress + the buff/perk sail mul +
 *      two active-consumable slots; EventType FISH_BITE/FISH_CATCH/WRECK_SPAWN/WRECK_DESPAWN; C_CRAFT +
 *      C_CONSUME; MarketReq gains a third arg `c` + MarketOp LIST/BUY/CANCEL_GOODS with MarketState.goods
 *      (physical goods book, doubloon-settled); S_PROFESSIONS (8 XP tracks).
 * v31: "Consequence & content" wave (3) — ONE bump for the whole wave. New EventTypes BOTTLE_SPAWN/
 *      DESPAWN/MSG (Message in a Bottle), WHALE (the White Whale hunt marker), BOUNTY (WANTED alert),
 *      EYE (Eye of the Storm zone), DIG (treasure-map dig payout); ShipFlag.WANTED (red on the chart) +
 *      NPC convoy/ghost ships ride the ships array via reserved EV_SPAWN.cls (10=merchant, 11=ghost);
 *      SelfState gains bounty + repairPlanks; five new MaterialIds (ambergris, map fragment, treasure map,
 *      repair plank, spectral essence) + a repair-plank CraftId; a ninth 'privateer' profession; S_JOURNAL
 *      (the Sea Journal feed), C_/S_AUCTION (Capital auction house), C_/S_FREIGHT + C_ESCORT (P2P freight
 *      + merchant-convoy escort contracts). One bump covers the whole wave.
 * v32: "The Addiction Machine" wave (5) — ONE bump for the whole wave. War Journal + Revenge (S_WARJOURNAL,
 *      C_REVENGE, EventType.REVENGE_MARK); Season Pass (S_SEASON, C_SEASON); Captain's Codex (S_CODEX,
 *      C_CODEX); Dawn Report (S_DAWN); login/kill streaks (SelfState.killStreak + EventType.KILLSTREAK);
 *      weekly event calendar (S_CALENDAR); weekly profession ladders (S_PROFLEADER). One bump covers it.
 * v33: "The Living Port" wave (6) — ONE bump for the whole wave. A walkable outpost interior: C_WALK
 *      (avatar move input, mirrors C_INPUT), C_PORT (enter/exit/emote intent), S_PORTWELCOME (enter a
 *      port instance), S_PORTSNAP (avatar transforms + spawn/despawn/emote events, port-local packPortPos),
 *      S_PORTPOP ("N captains in port" per outpost, pushed to sea players on change). A second deterministic
 *      sim (stepWalkerBody) shares the prediction==authority contract. One bump covers the whole wave.
 * v34: "The Tavern Casino" wave (7) — ONE bump for the whole wave. A provably-fair casino wing in the
 *      Capital tavern (slots / blackjack / no-limit Hold'em poker), bet in doubloons, resolved server-side:
 *      C_CASINO (game intent — spin / deal / hit-stand-double-split / sit-leave-fold-check-call-raise) +
 *      S_CASINO (game state/result as a JSON payload — not hot path). CASINO_LAYOUT gains `casino` stations
 *      (tables/machines + the physical auction rostrum + the clan hall plaque). PortStation is DATA in
 *      CAPITAL_LAYOUT. One bump covers the whole wave.
 * v35: "The Captain's House" wave (8) — ONE bump for the whole wave. A walkable per-captain interior
 *      instance (HouseRoom, molde PortRoom, never raidable): C_HOUSE (enter/toggle-open/kick intent),
 *      S_PORTWELCOME gains a `kind` byte (0=port, 1=house) so the client picks the scene, S_HOUSE_STATE
 *      (full interior state on entry + after each edit: shell, placed furniture, inventory, auto-populated
 *      trophy slots, flags), C_HOUSE_EDIT (place/move/remove/buy a furniture piece) + S_HOUSE_EDIT
 *      (ok/reject reason). The walker snapshot/welcome/emote/exit reuse the port codec verbatim
 *      (S_PORTSNAP/C_WALK/C_PORT). HOUSE_SHELL + FURNITURE are DATA in config. Furniture is a tradeable
 *      good on the EXISTING P2P market (design §3.5): MarketOp LIST/BUY/CANCEL_FURNITURE (C_MARKET, same
 *      a/b/c shape) + MarketStateMsg.furnitureListings (local outpost book, like goods) and
 *      .furnitureOwned (the viewer's un-placed house pieces) — sourced from / settled into house.inventory,
 *      instant doubloon settlement, no $SOR license. One bump covers the whole wave.
 * v36: "World edge coming-about" — NO new wire field, but the SHARED ship sim changed: `enforceWorldEdge`
 *      now forces a deterministic turnaround at the map border (see WORLD_EDGE) instead of the silent hard
 *      clamp, called after `stepShipBody` on both the server and client prediction. An old client would
 *      predict the old clamp and disagree with the server at the edge, so the handshake must reject it until
 *      it refreshes — hence the bump even though no bytes changed. (Client banner is prediction-detected,
 *      no wire.)
 * v37: "Onda Fix 2" — NO new wire field, but the SHARED ship sim + the server weather clock changed, so an
 *      old cached client would predict/agree differently and must refresh. Two authoritative changes:
 *      (1) `enforceWorldEdge` redesigned for a fluid, DOMINANT come-about — a deeper WORLD_EDGE.MARGIN, the
 *          forced turn now overwrites the ship's angVel (the helm can't fight it) and steers until the bow is
 *          clearly back inward, so the hull draws a wide arc back to sea instead of skimming/grinding the
 *          border. Pure fn of body+dt → prediction still == authority.
 *      (2) STORM cycle retuned so clear sunny sea is the PREDOMINANT weather (owner audit "sempre chuva ou
 *          noite"): measured rain-visible fraction 53.8% → 14.1%, calm 36% → 83%, combined clear-day
 *          34.6% → 64.4% (night unchanged at 25%). Server ships stormIntensity in the snapshot header as
 *          before (same byte), so it's not a wire change — the bump is for the prediction-affecting edge sim.
 *      Client-only in the same wave (no bump needed on their own): joystick strafe un-mirrored, home-dock
 *      "anchor to enter your house" hint, and a longer/heavier flat-earth edge banner.
 * v38: "Onda Fix 3" — NO new wire field, but the SHARED ship sim changed AGAIN, so a cached old client would
 *      predict the come-about differently from the server on the orla and must refresh — hence the bump:
 *      `enforceWorldEdge` was retuned to a MINIMAL reserved strip (WORLD_EDGE.MARGIN 220→60) plus a new
 *      `MIN_SPEED` gate that suppresses the override for an anchored/becalmed hull. An old client still
 *      predicting the deep 220 m band (v37) would auto-turn ~160 m sooner than the authority and diverge in
 *      the outer waters until it reloads. The rest of Onda Fix 3 is server-only (no wire): validated base
 *      placement (BASE_PLACEMENT + freeSlot/isValidBaseSpot), off-edge base relocation on boot, and the
 *      JsonWorldStore migrateBasePlacement. The Onda P1 hardening it merges with (combat-log husk, MAX_CCU,
 *      C_MARKET rate limit, client account-recovery/disclaimers) is also all server-/client-only — this
 *      single bump covers only the prediction-affecting WORLD_EDGE change.
 * v39: "Onda Avatares" — a selectable cosmetic captain SKIN, seen by all players on foot. New u8 fields:
 *      PortEvent SPAWN carries `skin` (the walker's look, cached client-side like ship cosmetics on EV_SPAWN,
 *      in BOTH the port and the house); ProfileMsg carries `avatarSkin` (the self's persisted look, driving
 *      the Captain's Look picker + the local avatar). Set channel is PortOp.SETSKIN on C_PORT (validated
 *      server-side against AVATAR_SKINS, persisted on Profile.avatarSkin, re-broadcast as a fresh SPAWN for a
 *      live re-skin in port/house). Data is retro-compatible: a profile with no skin reads 0 = default.
 * v40: "Onda Social — Chat & Voice" — a professional server-authoritative TEXT CHAT and a WebRTC P2P VOICE
 *      mesh, both signalled over our own binary wire. New client→server: C_CHAT (channel u8 + UTF-8 text,
 *      byte-capped by CHAT.MAX_BYTES) and C_RTC (opaque voice signalling: HELLO/BYE/ARMADA prefs + SDP/ICE
 *      relay to a peer voiceId). New server→client: S_CHAT (channel, sender voiceId + name + clan tag +
 *      server-sanitised text + server timestamp) and S_RTC (WELCOME your voiceId / PEERS the mesh you should
 *      connect to / SIGNAL a relayed blob from a peer). Chat channels: LOCAL (AOI at sea, whole room ashore),
 *      ARMADA (clan), GLOBAL (world, harder rate-limit). All sanitisation/profanity-mask/rate-limit/routing is
 *      server-side; the client only ever sends {channel,text} or an opaque signalling blob. NO change to the
 *      sim or any existing packet — the bump is only because the handshake pins the wire version and the four
 *      new tags must be understood on both ends. See CHAT + VOICE below.
 * v41: "Onda Marauders" — hostile NPC pirate hunters that scale inversely with the live population (see
 *      MARAUDER). NO new packet and NO byte-layout change: they ride the existing ships array + EV_SPAWN
 *      (name = a deterministic English pirate name, tag = '' so no clan bracket, cls = the brigantine hull),
 *      navigate + fire with the SHARED deterministic sim (zero client prediction, pure interpolation like the
 *      convoy/ghost fleet). The single wire touch is one new bit in the already-u16 snapshot ship `flags`:
 *      ShipFlag.NPC (1<<11), set for marauder hulls so the client paints a black masthead flag + a red name
 *      (cosmetic only). An old cached client would silently ignore the unknown bit (render a plain ship), but
 *      the handshake pins the version, so the bump forces the consistent hostile look. */
/**
 * S_REJECT reason strings the CLIENT has to recognise, shared so the two sides can never drift.
 *
 * NOT a wire change: the reason has always been a variable-length string in `S_REJECT`, so nothing about the
 * format moves and `PROTOCOL_VERSION` stays put. What this replaces is the client sniffing the text with a regex
 * (`/another device/i`) — a coupling that silently breaks the moment someone rewords the server message.
 * In-game text is English.
 */
export const REJECT = {
  /** One live session per account, across the sea and the walkable port/house instances. TERMINAL on the client:
   *  a bumped tab that reconnects would bump the winner right back, and the two tabs flap forever. */
  DUPLICATE_SESSION: 'Signed in on another device — close the other tab and tap SET SAIL',
  /** The SAME captain is mid-transfer between venues (a sub-millisecond window while their loot changes hands).
   *  One tab, nothing to close — so it must NOT read like the duplicate-session refusal. */
  IDENTITY_BUSY: 'Still finishing your last move — tap SET SAIL again',
} as const;

export const PROTOCOL_VERSION = 57; // v57 — ⚓ SOLANA PIVOT: the on-chain economy moved off Robinhood Chain (EVM) to SOLANA with an SPL token minted on pump.fun (1B supply, 6 decimals, no mint/freeze authority — no contract of ours exists). Every identity/payment string on the wire changed shape from EVM hex to base58: wallet link is now an ed25519 `signMessage` proof (C_WALLET addr cap 42→48, sig 132→96) over `Sea of Solana — link wallet <pubkey> :: <nonce>` (NO lowercasing — base58 is case-sensitive); C_SOR's `txHash` became `signature` (a Solana transaction signature, cap 90); S_SOR's `tokenAddress`/`routerAddress` became `mint`/`recipient` (48) and rpcUrl/dexUrl grew to 200. Caps are named in `WIRE_CAP` (messages.ts). Checkout is a Solana Pay transfer request keyed on a per-order `reference` pubkey (pure HTTP, no wire).
// (v56 — Kill-Cam narrative rebuild: the SINK event now carries the FATAL BALL ID (the exact cannonball that dealt the killing blow), so the client can pull the killing ball's true launch→impact trajectory from its own replay buffer instead of GUESSING which nearby ball did it (the old heuristic degenerated at close range / with many balls in flight = the owner's "bugando pra caramba"). 2 bytes on sink events only. (v55 forced a clean reload for the capital port-pos decode fix; that carries forward unchanged.)
// v54 was ONE combined bump for the waves that shipped together
// (v54 was NEVER deployed, so the "ammo-economy" wave extends its layout under the SAME number):
// ---- "economy-gaps" (QoL da madrugada, still under the SAME undeployed v54): (1) ProfileMsg gains a
// trailing `cargoAutoLoad` u8 bool (the per-player "auto-load the warehouse when berthed home" preference,
// default ON; a short read defaults true so the historical behavior never changes); (2) C_PORT gains op
// SET_CARGO_AUTOLOAD=5 (arg 1/0) on the existing op+arg shape — no codec change. Auto-anchor on AFK and the
// honest delivery toast are server-side input/notify logic: ZERO wire.
// ---- "ammo-economy" (round finito — the owner's law "munição infinita é na cadeia, aqui não"): the
// self-snapshot header gains `roundStock` u8 right before chainStock/grapeStock — the finite powder
// magazine (0..AMMO_ROUND_MAX), per-viewer self state only, never leaked to other players. Selection wire
// (2 ammo bits in C_INPUT) and every other packet are unchanged; CraftId gains ROUND_AMMO (rides the
// existing craft wire).
// ---- "farms-demand-first" (every farmable must be something another player wants to buy): (1) the
// self-snapshot header LOSES the dead `farmTier` u8 (orphaned since Cargo Runs v48 — the farm mints cargo,
// not doubloons; the tier was always 0) and GAINS a trailing `spectral` block (u8 count + n×[u8 kind,
// u16 id, u8 bearing, u8 dist/16 m]) — the Wraithlight Draught's REAL ghost-sight: server-decided indicators
// of rich wrecks + Ghost Fleet ships within 2.4 km, present ONLY while the viewer's oil buff is the draught
// (see WRAITHLIGHT). (2) S_BASES gains a per-base `protected` u8 bool ("Under the King's Protection" — a
// low-tier base that cannot be raided; see RAID_PROTECT/raidableHqLvl). (3) NEW C_LOAD_GOODS (tag 68;
// client→server: u8 materialId + u16 qty) — while berthed at home, move stockpile materials back INTO the
// hold so they can be hauled to the new public port craft stations / sold at market; the auto-deposit became
// edge-triggered to match. Byte layout of the self header changed (removal mid-header) → a stale v53 client
// would misread every field after it; the handshake pins the bump, next deploy forces a reload.
// ---- "Clan chart markers" (shared Armada intel pins on the world chart): TWO new messages — C_MARK (tag 66;
// place/remove a marker: op u8, id u16, icon u8, pos u16×2 packPos-quantized like every other position,
// optional ≤16-char label via the standard length-prefixed str) and S_MARKS (tag 67; the FULL marker list for
// YOUR clan + a one-shot EN note to toast). Markers are war intel: the server fans S_MARKS ONLY to clan
// members (full sync on join/clan-change, push on every change) — they never ride a snapshot and never reach
// a non-member. Caps/limits in CLAN_MARKERS.
// Previously v53 ("Market V1 — wiring vivo do exchange ⓓ↔$SOR", BUILT-BUT-DISARMED):
// the wire touch is the new S_EXCHANGE message (the P2P order-book view: ref price+band, custodial $SOR balance,
// cash-out allowance, book depth, my orders, in-flight withdrawals) + five new C_MARKET ops (EX_STATE/LIST/BUY/
// CANCEL/WITHDRAW) that reuse the existing op+a/b/c request shape. The whole exchange stays inert unless the
// server arms SOR_VAULT_* (the handlers reply "The Royal Bank is not yet open."), so v53 changes NO existing
// packet byte-layout — it only ADDS a message the current client never receives until the exchange is live.
// Previously v52 ("Sinks P1–P4" — recipes for the orphan rares + the 4 inert Ship
// Charm effects go live): the ONE wire touch is a new trailing `cargoCap` u16 on ProfileMsg (the resolved hold
// capacity, INCLUDING the Stevedore's Hook cargo charm) — the client's prediction load penalty divides
// cargoUnits by this exact cap, so a cargo-charm captain predicts weight identically to the server (invariant 6).
// A stale short read falls back to cargoOf(cls,cargoLvl) (no charm), so it's retro-safe, but the weight curve
// would then diverge for a charmed hull → the handshake pins the bump. Everything else is NO byte-layout change:
// new material/craft ids (Ambergris Elixir, Wraithlight Draught, Pearl-Inlaid hull) ride the existing goods/craft
// wire; the Salvage Appraiser reuses C_GACHA (new op APPRAISE, existing a/b args); fishing/repair charms are
// server-only; the minimap charm is a client-only render cue read from the already-present S_GACHA equippedAmulet.
// Previously v51 ("Peixe = Sink de PvP" — 4 fish species → 4 short tactical buffs):
// the self-snapshot header gains FOUR fields — `turnBuffMul` (the Reef Snapper +turn, folded into local
// PREDICTION exactly like `speedBuffMul`, so client == server while turning), plus `fishBuff` (active fish
// material id), `fishBuffS` (short buff seconds left) and `fishCdS` (re-sip cooldown) for the HUD badge. The
// speed-surge (Leviathan's Roe) rides the EXISTING `speedBuffMul` (no new field); the repair/leak buffs are
// server-only flooding. No byte-layout change to any other packet, but the self header grew → handshake pins
// the bump. Previously v50 ("A Draga" gacha — the ⓓ Smuggler's Chest trail): TWO new tags —
// C_GACHA (op: pull / free-weekly / shard-buy / equip-amulet / request-state; a,b args) and S_GACHA (the
// SERVER-DECIDED roll result the client only dramatises + the full gacha state: the Braças pity meter, Relic
// Shard balance, owned amulets, equipped amulet). No byte-layout change to any existing packet, but the
// rarity union gained `epic` and cosmetics/amulets/ship-deeds now flow as loot, so the handshake pins the bump.
// An equipped combat amulet (±5% speed/turn tradeoff) rides the ALREADY-present resolved speedMul/turnMul on
// ProfileMsg — client prediction stays == server truth with no extra field. Previously v49 (Cargueiro carries
// 2 chests — the Fluyt hauls a second treasure chest):
// the self-snapshot header's single `carrying` bool becomes a `chestCount` u8 (0..hull chest cap) so the client
// can show every chest aboard (2 on the Fluyt/East Indiaman, 1 elsewhere). Chest capacity is derived per hull
// from the cargo multiplier (`chestCapFor` — Fluyt/East Indiaman 2, all others 1). Same byte width as the bool it
// replaces, but the semantics change (a stale v48 client would read a count as a 0/1 flag), so the handshake bump
// is mandatory. Previously v48 (Cargo Runs V1 — the logistics economy loop): the self-snapshot header
// gains a trailing `cargoUnits` u16 (trade goods loaded aboard — drives the weight HUD + the LOCAL ship's
// prediction load penalty), `warehouseUnits` u16 (base warehouse stock) and `warehouseCap` u16 (its cap) so the
// client shows the "ready for max transport" cue + the per-port delivery payment preview. The shared ship sim's
// ShipBody gains `cargoLoad` (0..1) which `stepShipBody` reads to scale top speed by the non-linear weight curve
// (server + client prediction identical). All three header appends default to 0 on a stale short read, but the
// weight curve changes the ship kinematics, so a stale v47 client MUST refresh — the handshake pins the bump.
// Previously v47 (Onda Frota / fleet + owner flagship + name badges + farm readability):
// three new ship classes (CUTTER 4, FLUYT 5, EAST_INDIAMAN 6 — the last owner-exclusive, entitlement-gated). Wire
// touches: EV_SPAWN + PortEventType.SPAWN gain a trailing `badge` u8 (bit0 DEV tag, bit1 gold name — so OTHER
// players see a captain's identity), and the self-snapshot header gains a trailing `farmTier` u8 (0/1/2) so the
// client can SAY the daily passive-farm cap was hit ("1/tick" stops reading as a bug). All three appends default
// to 0 on a short read, but the shared ship sim now knows 7 classes, so a stale v46 client must refresh — the
// handshake pins the bump. Previously v46 (⚓ Captain's Orders / admin broadcast): a NEW server->client message
// S_ANNOUNCE (MsgType 62) carries a big on-screen BANNER — {level u8, durationMs u16, text} — fanned to every
// online client by the Bridge's POST /admin/broadcast (mode banner|both). The CHAT half of a broadcast reuses the
// existing S_CHAT (a GLOBAL system line, voiceId 0), so only the banner is new wire. A stale v45 client would not
// know the S_ANNOUNCE tag, so the handshake pins the bump. (v45 was Recruiters / growth loop): S_PROFILE appends a u16 `recruits` (the self
// captain's own "how many I recruited" placard) and S_LEADERBOARD appends a Top Recruiters ladder (name + count).
// Both are backward-tolerant appends (decoders default to 0/empty on a short read), but a stale v44 client would
// never read them, so the handshake pins the bump. (v44 was the trophy-wall toggle): S_HOUSE_STATE carries a
// `hideTrophyWall` u8 bool so
// VISITORS render the owner's choice, and C_HOUSE gains HouseOp.TOGGLE_WALL (reuses the existing op/arg layout) so
// the owner flips it from Decorate Mode. A stale v43 client would neither send the toggle nor read the new state
// byte, so the handshake pins the bump. (v43 was: (1) treasure-island HULL COLLISION via collideCircles, server +
// prediction; (2) SYNCED SIT — C_PORT PortOp.SIT + S_PORTSNAP PortEventType.SIT carry the seat transform; (3) per-
// furniture FINE transform — C_HOUSE_EDIT + S_HOUSE_STATE carry u8 yaw + u8 fx/fy.)

/**
 * ---- Onda Social (v40): text chat + WebRTC voice tuning. 100% social layer; never touches the sim. ----
 * Text chat is server-authoritative (sanitised, profanity-masked, rate-limited, channel-routed). Voice is a
 * WebRTC P2P mesh signalled over our wire — proximity/spatial at sea + in port/house, opt-in radio for the
 * Armada. Single droplet: STUN only (public), no SFU, no TURN (documented backlog: ~10-15% of symmetric NATs
 * fail P2P without a TURN relay).
 */
export const CHAT = {
  /** Hard cap on a single message's UTF-8 byte length (server truncates; client input mirrors it). */
  MAX_BYTES: 240,
  /** Client scrollback ring per channel view (older lines drop off). */
  SCROLLBACK: 100,
  /** General chat (LOCAL + ARMADA) token bucket, per connection: ~1 msg/s sustained, burst 3. */
  RATE_PER_SEC: 1,
  BURST: 3,
  /** GLOBAL is louder to the whole world, so it is throttled harder: ~1 msg / 5 s, burst 2. */
  GLOBAL_RATE_PER_SEC: 0.2,
  GLOBAL_BURST: 2,
} as const;

/** Chat channel ids (C_CHAT.channel / S_CHAT.channel). */
export const ChatChannel = {
  LOCAL: 0,   // the sea = the sender's AOI; a port/house = the whole walkable room
  ARMADA: 1,  // the sender's clan — members only, wherever they are (cross-room)
  GLOBAL: 2,  // everyone online (harder rate-limit)
} as const;
export type ChatChannel = (typeof ChatChannel)[keyof typeof ChatChannel];

/** ⚓ Captain's Orders — the admin (owner) in-game broadcast. `level` drives the banner colour (info=gold,
 *  warn=amber, alert=red) + the system chat accent. It is SERVER-authored, admin-only (POST /admin/broadcast,
 *  X-Admin-Token) — no player can ever send one. `mode` (chat / banner / both) is chosen server-side and simply
 *  selects which packets fan out: a GLOBAL system S_CHAT line, an S_ANNOUNCE banner, or both. */
export const AnnounceLevel = {
  INFO: 0,   // gold — hype an event, ask for feedback, share a link
  WARN: 1,   // amber — a heads-up (a soft warning, a nudge)
  ALERT: 2,  // red — imminent server reset / maintenance
} as const;
export type AnnounceLevel = (typeof AnnounceLevel)[keyof typeof AnnounceLevel];

/** Server-enforced sanity bounds for an admin broadcast (the Bridge mirrors them for its live preview). The
 *  banner ALWAYS auto-dismisses (PvP-first HUD): it lingers at most MAX_BANNER_MS then fades on its own. */
export const BROADCAST = {
  MAX_TEXT_CHARS: 240,       // a tweet-ish line; anything longer is truncated server-side before it fans out
  MIN_BANNER_MS: 2000,
  MAX_BANNER_MS: 30000,      // 30 s hard ceiling — a broadcast can never permanently occlude the combat HUD
  DEFAULT_BANNER_MS: 8000,
  /** The distinct, non-spoofable sender name on the system CHAT line (voiceId 0 = system). */
  SENDER: '⚓ Captain’s Orders',
} as const;

export const VOICE = {
  /** Sea proximity-voice radius (m): only captains within this of each other are peered + heard. */
  RADIUS: 150,
  /** Mesh cap per client (nearest-first at sea; whole room ashore, still capped). Keeps the P2P mesh small. */
  MAX_PEERS: 8,
  /** Spatial PannerNode ref/max distances (m) — full volume within REF, inaudible past MAX (== RADIUS feel). */
  ROLLOFF_REF: 14,
  ROLLOFF_MAX: 150,
  /** Max bytes of one signalling payload (SDP offer/answer/ICE) — bounds a malicious relay blob. */
  SIGNAL_MAX_BYTES: 8192,
  /** Opus target bitrate (kbps, mono) requested on the sender — DTX on, small + clear for speech. */
  BITRATE_KBPS: 24,
  /** Open-mic VAD: RMS threshold (0..1) above which the AnalyserNode considers you "speaking". */
  VAD_THRESHOLD: 0.045,
  /** Public STUN for NAT traversal — the always-present FALLBACK ICE server (only enough to connect when at
   *  least one peer sits behind a cone NAT). A relay TURN server, when configured on the droplet, is layered on
   *  TOP of this at runtime via the game server's `GET /voice/turn` (no wire change): CGNAT / symmetric-NAT
   *  clients (the majority of Brazilian 4G) can ONLY connect through TURN, so without it P2P silently never
   *  establishes — the "granted the mic and nothing happened" report. See apps/server/src/voiceTurn.ts +
   *  infra/docker-compose.yml (coturn). */
  STUN_URLS: ['stun:stun.l.google.com:19302'],
  /** Lifetime (seconds) of the ephemeral HMAC TURN credential the server signs (coturn use-auth-secret). The
   *  client re-fetches `/voice/turn` before a cached credential expires, so a long session never dials a peer
   *  with a stale username. Server env TURN_TTL_SEC overrides this default. */
  TURN_TTL_SEC: 3600,
  /** Max ICE-restart attempts per peer before the client stops retrying (the server's periodic mesh recompute
   *  re-pairs from scratch anyway). Each retry re-fetches ICE config + calls restartIce() on the offerer. */
  ICE_MAX_RESTARTS: 2,
} as const;

/**
 * ---- Landmark Editor (micro-wave) — the PORT EXTERIOR seen from the open sea, made editable ----
 * The far-sea landmark of a port — the guardian lighthouse, the arcing seawall panels, the anchored ship
 * masts poking over the wall — used to be computed in code (`addCapitalLandmark` in render.ts). It is now
 * DATA, keyed per outpost id, so the owner + partner can arrange each port's exterior in the editor
 * (`?editor=landmark`) — the Capital (Port Royal) AND each village individually.
 *
 * RENDER-ONLY, no wire, no PROTOCOL_VERSION bump — exactly like the city/capital `PortLayout` overrides:
 * the client fetches any Save-Online `landmark` override at boot (GET /editor/layouts) and builds the
 * landmark from it, else from `LANDMARK_LAYOUTS` below. Pieces are LOCAL to the outpost centre (sim plane;
 * y → world z). The DEFAULT is baked from the exact same math the old code ran at the shipped Capital's
 * seaward heading (phi = -PI/2), so `landmarkForOutpost(1)` reproduces the hard-coded arc pixel-for-pixel
 * (proven by `config.landmark.test.ts`). Villages ship nothing (empty), matching today.
 *
 * NOTE (gameplay): the authoritative Capital harbour-mouth "faixa" (`crossedHarborMouth`, server-side) is
 * NOT driven by this — it reads the outpost position, not the landmark. Moving the seawall's mouth visually
 * via Save Online does not move where a ship actually enters the harbour; an authoritative change is an
 * Export TS → commit (same reconcile note as the City slice).
 */
/** The exterior kit. `lighthouse`/`wall`/`mast` are the shipped breakwater pieces; `islet`/`sandbar` are
 *  Onda Fix 5 shoreline decoration — the treasure-islet GLBs reused to dress a port's approach (render-only);
 *  `pine`/`pine_tall`/`rock`/`cliff` are the Onda Entorno landscape kit that greens the bare ground around a
 *  port's ramparts (dense conifers, coastal boulders, stackable cliff chunks). Every kind is render-only DATA
 *  (no wire), instanced ONE mesh per kind per port, exactly like the seawall.
 *
 *  Onda Portos Livres — the union is OPEN (`(string & {})`): ANY city-kit GLB kind (`wharf_skullflag`,
 *  `citadel_skull`, `tavern_skull`, houses, town-dressing, …) is now a legal exterior piece. The sea renderer
 *  lazily bakes+instances any kit kind it doesn't recognise as a classic breakwater piece, normalising it with
 *  the SAME KIT_FOOT/KIT_TALL tables the walkable city uses (so scale never drifts between the two scenes). The
 *  named literals are kept for editor autocomplete + the classic-piece narrowing (`p.kind === 'wall'`). Still
 *  render-only — no wire, no PROTOCOL_VERSION bump. */
export type LandmarkKind = 'lighthouse' | 'wall' | 'mast' | 'islet' | 'sandbar' | 'pine' | 'pine_tall' | 'rock' | 'cliff' | (string & {});
/** One exterior piece, LOCAL to the outpost centre. `scale` is an optional uniform multiplier on the kind's
 *  default footprint (default 1). `rot` is yaw (radians). `h` is an optional ALTITUDE in metres above the
 *  waterline (default 0 = the piece sits on the water plane, byte-identical to before this field existed);
 *  it maps to world-Y (up) at render — NOT to be confused with `y`, which is the sim plane's north/south
 *  (→ world-Z). A small negative `h` sinks a piece; the editor + server clamp it to a sane -20..+200 m.
 *  `tint` (Onda Sistema de Luz) is an optional 0xRRGGBB colour override for the `light_*` kinds — the owner's
 *  "custom colour" knob; omitted = the preset's colour (byte-identical to a piece saved before the field). */
export interface LandmarkPiece { kind: LandmarkKind; x: number; y: number; rot: number; scale?: number; h?: number; tint?: number }

/** Per-kind default WORLD dimensions the renderer normalizes each GLB/proxy to, kept HERE so the default
 *  builder below and the renderer read the same numbers (no drift). */
export const LANDMARK = {
  /** The shipped Capital breakwater the default was baked from. */
  ARC_R: 80, MOUTH_GAP: 0.5, WALL_COUNT: 20,
  /** A seawall panel's world height + thickness (m); its width is `LANDMARK_WALL_W`. */
  WALL_H: 8, WALL_D: 4,
  /** Lighthouse tower height (height-normalized) (m). */
  LIGHTHOUSE_H: 30,
  /** Anchored mast cylinder height (m) + a small cloth sail. */
  MAST_H: 20,
  /** Onda Fix 5 shore islets (`islet`/`sandbar`): the treasure-islet GLB's default footprint radius (m) at
   *  scale 1 — a compact sand isle to dress a port's approach; the piece `scale` multiplies it. */
  ISLET_R: 24,
  /** Onda Entorno landscape kit — the default WORLD size each kind's GLB/proxy normalizes to at scale 1 (the
   *  piece `scale` multiplies it). Trees are HEIGHT-normalized (a canonical crown height); rock/cliff are
   *  FOOTPRINT-normalized (a canonical ground width). Shared by the renderer + the editor proxy so both agree. */
  PINE_H: 15,        // a stout dense spruce ~15 m tall (height-normalized)
  PINE_TALL_H: 24,   // a slim steepled fir ~24 m tall (height-normalized) — the tall silhouette variant
  ROCK_W: 9,         // a coastal boulder cluster ~9 m across (footprint-normalized)
  CLIFF_W: 26,       // a stackable cliff-edge chunk ~26 m across (footprint-normalized)
} as const;
/** One seawall panel's world width (m) — the arc pitch the shipped breakwater instanced its segments at. */
export const LANDMARK_WALL_W = ((Math.PI * 2 - 2 * LANDMARK.MOUTH_GAP) / (LANDMARK.WALL_COUNT - 1)) * LANDMARK.ARC_R * 1.06;

/** Port exteriors seen from the sea, keyed by 1-based outpost id. The Capital (id 1) ships the breakwater
 *  (lighthouse + 20 seawall panels + 4 anchored masts); villages ship nothing. A Save-Online `landmark`
 *  override replaces this per port at the client's next boot. The Capital block is BAKED from the exact math
 *  the old `addCapitalLandmark` ran at the default capital's seaward heading (phi = -PI/2). The World Editor's
 *  Landmark mode exports this as an explicit literal a future wave commits. */
export const LANDMARK_LAYOUTS: Record<number, readonly LandmarkPiece[]> = (() => {
  const phi = -Math.PI / 2;                              // the shipped Capital's seaward heading (outpost → sea)
  const R = LANDMARK.ARC_R, GAP = LANDMARK.MOUTH_GAP, N = LANDMARK.WALL_COUNT;
  const sx = Math.cos(phi), sy = Math.sin(phi);          // unit vector toward open sea
  const cap: LandmarkPiece[] = [];
  // the guardian lighthouse at the eastern lip of the harbour mouth (door turned landward)
  const lhAng = phi + GAP;
  cap.push({ kind: 'lighthouse', x: Math.cos(lhAng) * R, y: Math.sin(lhAng) * R, rot: phi + Math.PI });
  // the seawall arcs around everything but the seaward mouth — one panel per step
  const a0 = phi + GAP, a1 = phi + Math.PI * 2 - GAP, step = (a1 - a0) / (N - 1);
  for (let i = 0; i < N; i++) { const a = a0 + step * i; cap.push({ kind: 'wall', x: Math.cos(a) * R, y: Math.sin(a) * R, rot: -(a + Math.PI / 2) }); }
  // anchored ship masts poking over the wall (landward) — [lateral, landward-depth] in the seaward frame
  const spots: [number, number][] = [[-30, 22], [-8, 32], [16, 28], [34, 18]];
  for (const [lx, ly] of spots) { cap.push({ kind: 'mast', x: -sx * ly + (-sy) * lx, y: -sy * ly + sx * lx, rot: Math.atan2(sx, sy) }); }
  return { 1: cap };
})();

/** The exterior pieces for a 1-based outpost id — empty when the port has none (most villages). */
export function landmarkForOutpost(outpostId: number): readonly LandmarkPiece[] {
  return LANDMARK_LAYOUTS[outpostId] ?? [];
}

/** ── Landmark placement catalog (SINGLE SOURCE OF TRUTH) ──────────────────────────────────────────────────
 *  THE authoritative registry of every kind the ⚓ Landmark editor may place on a port's SEA-EXTERIOR, grouped
 *  for the palette UI. Both the CLIENT palette (`landmarkeditor` builds its Add buttons from this) AND the
 *  SERVER Save-Online validator (`editorApi.validateLandmark` → `LANDMARK_KINDS`) derive from this — so a kit
 *  building the editor can drop is always a kind the save will accept. This closes the "placed a GLB outside
 *  the city, Save Online silently rejected it (400)" bug: Onda Portos Livres added the kit kinds to the palette
 *  but NOT the validator, so every kit-landmark save was 400-rejected while the client flashed a save toast.
 *  Group KEYS are UI section ids (the client maps them to headers + per-kind emoji); the flat
 *  `LANDMARK_PLACE_KINDS` is what the validator + the matrix test read. Render-only DATA — no wire. */
export const LANDMARK_PLACE_KIND_GROUPS = {
  /** the classic hand-built breakwater pieces */
  structures: ['lighthouse', 'wall', 'mast'],
  /** treasure-islet GLBs reused to dress the approach */
  shore: ['islet', 'sandbar'],
  /** Onda Entorno — greenery/rock around the ramparts */
  landscape: ['pine', 'pine_tall', 'rock', 'cliff'],
  /** Onda Portos Livres — generated harbour-kit buildings/docks placed outside the walls */
  buildings: ['wharf_skullflag', 'citadel_skull', 'tavern_skull', 'inn_salted', 'treasure_tree'],
  /** Onda Portos Livres — town dressing placed outside the walls */
  dressing: ['cottage_ember', 'street_lamp', 'lantern_grove', 'cobble_bench'],
  /** Onda Sistema de Luz — pure FakeLight sources ("adicionar luz onde quiser"): no mesh, just a warm
   *  instanced glow + an ocean glint at night, rendered by the client light system (lights.ts). Each kind is
   *  a PRESET (colour/radius/flicker); the piece `scale` multiplies the glow radius and the optional `tint`
   *  overrides the colour. Render-only DATA — no wire, exactly like every other landmark kind. */
  lights: ['light_lantern', 'light_candle', 'light_window', 'light_torch', 'light_bonfire', 'light_beacon'],
} as const;
/** The flat catalog the Landmark validator + palette + matrix test share (drift-proof by construction). */
export const LANDMARK_PLACE_KINDS: readonly string[] = Object.values(LANDMARK_PLACE_KIND_GROUPS).flat();

/** ── Push-notification opt-in timing (Pacote Funil, item 5) ───────────────────────────────────────────────
 *  Client-only UX knobs (no wire). The one-time notification ask used to fire on the 1st HOISTED CHEST — too
 *  deep in the session (it converted ~1 opt-in/day). v-funil moves it to the FIRST MOMENT OF VICTORY: the
 *  earliest of a hoisted chest, a first kill, OR a first outpost sale, with a time fallback so an engaged
 *  captain who hits none of those is still asked after a few minutes at sea. Still ONE ask per device, still a
 *  permanent no-nag on "Later" (the `sor:pushAsked` flag) — only the TRIGGER moved earlier. */
export const PUSH_OPTIN = {
  /** ms to let a victory beat (kill / chest / sale) breathe before the diegetic ask slides in. */
  FIRST_WIN_DELAY_MS: 2500,
  /** Fallback: ms of ACTIVE time at sea (alive, not ashore) after which an as-yet-unasked captain is offered
   *  the opt-in even without a discrete victory — so "engaged but hasn't killed/looted/sold" still converts.
   *  (With the quest-FTUE gone this fallback is ALSO the newcomer path: a brand-new captain who hits no victory
   *  beat is asked here, never inside the Helm Coach.) */
  SEA_FALLBACK_MS: 240_000, // 4 min
} as const;

/** ── Helm Coach (onboarding v4 — the quest-FTUE's replacement) ────────────────────────────────────────────
 *  Client-only UX knobs (no wire) for the contextual tutorial that points at the REAL HUD controls while the
 *  ship stays fully controllable (owner 2026-07-16: "só diz pra ele os comandos, como é que joga, aonde vê
 *  isso" — no quests, no gates). Each step advances the moment the captain USES the pointed control (or taps
 *  Next); the whole thing is skippable and never re-shows once done/skipped. */
export const HELM_COACH = {
  /** ms after reaching open water before the first coach card slides in (let the spawn camera settle). */
  START_DELAY_MS: 1400,
  /** |rudder| at/above this counts as "using the wheel" for the steering step… */
  STEER_MIN_RUDDER: 0.35,
  /** …held for this long (cumulative ms) to advance — a single accidental brush doesn't count. */
  STEER_HOLD_MS: 450,
  /** sailTarget at/above this (or making way faster than SAIL_MIN_SPEED) advances the sails step. */
  SAIL_MIN_TARGET: 0.5,
  /** sim m/s at/above which she's clearly under way (advances the sails step even on keyboard). */
  SAIL_MIN_SPEED: 2.5,
  /** cumulative ms the 🪣 bail control must be HELD to advance the bailing step (a real, felt hold). */
  BAIL_HOLD_MS: 600,
} as const;

/** ── Cinematic Kill-Cam / Highlight replay (CLIENT-RENDER; the ONLY wire touch is SINK.ballId, v56) ──────────
 *  A shareable, director-varied slow-mo replay ARMED on a kill/death. The client keeps a short rolling buffer of
 *  transforms + combat events + environment, then a "scene director" (shotdirector.ts) composes a FIXED narrative
 *  arc — the killer's cannons firing → the KILLING BALL's trajectory in flight → the impact → the victim sinking
 *  to the END → a fade-out — and plays it back through the SAME renderer (invariant #1: everything INTERPOLATED,
 *  local prediction OFF). Variety lives in framing/distance/slow-mo TIMING, never in the beat order. It exports a
 *  VERTICAL 9:16 clip with music + a watermark baked in. Every feel knob lives here so the owner can tune it
 *  without touching the code. The one server dependency: the SINK event carries the FATAL BALL ID (v56), so the
 *  hero ball is the TRUE killing shot pulled from the buffer — not a fragile "closest ball to the impact" guess
 *  (which collapsed at close range / with several balls in flight). Lives beside SINK_FX / STORM. */
export const REPLAY = {
  WINDOW_S: 12,            // seconds of pre-kill history the rolling buffer retains + replays (the "tape length")
  TAIL_S: 3.5,             // extra seconds captured AFTER the kill so the WHOLE naufrágio unfolds on the tape (the
                           //   hero + surrounding hulls keep sailing while the victim goes under) — the director
                           //   films the sink to the END, not cutting it off mid-plunge (the owner's beat #4)
  FADE_S: 0.9,             // the clip FADES TO BLACK over its final seconds (baked into the export + on-screen) —
                           //   the owner's beat #5. Applied by replayplayer over the last FADE_S of the cut.
  CAPTURE_HZ: 30,          // how often the rolling buffer snapshots a frame (memory vs replay smoothness)
  MAX_SHIPS: 24,           // per-frame ship cap held in the buffer (farthest beyond this are dropped — memory guard)
  MAX_BALLS: 48,           // per-frame cannonball cap
  MAX_EVENTS: 80,          // rolling combat-event ring size (fire / hit / sink / ram inside the window)
  SPEED: 0.8,              // base playback speed (a cinematic slow drift; 1 = real-time)
  SLOWMO_SCALE: 0.4,       // extra time pinch near the fatal hit on the NON-bullet-time beats (multiplies SPEED)
  SLOWMO_LEAD_S: 0.5,      // sim-seconds around the fatal hit that get that ordinary slow-mo pinch
  SHOT_MIN_S: 2.6,         // director shot duration bounds (real seconds) — longer cuts that breathe + build
  SHOT_MAX_S: 4.0,
  CROSSFADE_S: 0.55,       // camera crossfade at every shot boundary (never a hard cut-jump of position)
  TOTAL_MIN_S: 15,         // clamp the whole composed cut to this real-time span…
  TOTAL_MAX_S: 20,         // …so the export lands in the fuller cinematic 15–20 s window (builds to the climax)
  CAM_MIN_HEIGHT: 2.5,     // metres the camera is ALWAYS held above the sea surface (anti-clip / anti-flip floor)
  ARM_MS: 45000,           // how long the "★ SAVE HIGHLIGHT" prompt PERSISTS before it self-dismisses. Deliberately
                           //   long (was 6 s — a mobile flash the owner's testers missed: "apareceu mas foi bem
                           //   rápido"). The prompt is meant to be CATCHABLE — it stays until the captain taps it,
                           //   a fresh kill re-arms it, or a respawn/port transition ends the moment (clearTape); this
                           //   is only the backstop so an ignored prompt eventually bows out. NEVER a flash.
  EXPORT_FPS: 30,          // recorded clip frame rate
  OUT_W: 720,  OUT_H: 1280,     // default VERTICAL 9:16 output (iPhone / TikTok / X portrait — the shareable target)
  OUT_W_LAND: 1280, OUT_H_LAND: 720, // the 16:9 landscape alternative
  BITRATE: 6_000_000,      // ~6 Mbps — crisp at this resolution/length yet still 4G-uploadable
  LETTERBOX_FRAC: 0.11,    // each cinematic bar's height as a fraction of the frame (top + bottom)
  WATERMARK: 'seaofrobin.quest', // the corner wordmark text (the PNG wordmark, when present, sits above it)
} as const;

/** ── BULLET_TIME (the kill-cam CLIMAX — owner: "Matrix-style") ─────────────────────────────────────────────
 *  The signature shot. The camera LOCKS onto the hero cannonball in mid-flight, sim-time collapses to a near
 *  freeze (the ball almost stops in the air), the camera WHIP-ORBITS the frozen ball 180–360° with ease-in/out
 *  (the "spin around the bullet"), holds the beat, then SNAPS time back to 1× and the ball BURSTS on the hull.
 *  A DEDICATED time ramp, deliberately separate from REPLAY.SLOWMO_SCALE. Used as the FINAL beat whenever the
 *  kill had a cannonball heading to the target; ram/flood kills fall back to RAM / IMPACT. */
export const BULLET_TIME = {
  APPROACH_S: 0.8,         // real seconds riding the ball inbound before the freeze bites
  RAMP_IN_S: 0.35,         // real seconds ramping 1× → FREEZE_SCALE as the freeze sets in
  FREEZE_SCALE: 0.05,      // sim-time scale at full freeze (the ball ~stopped mid-air)
  ORBIT_S: 3.2,            // real seconds of the whip-orbit around the frozen ball (longer, more dramatic)
  ORBIT_DEG: 340,          // total sweep of the orbit (clamp 180–360) — how far it spins around the bullet
  ORBIT_RADIUS: 14,        // metres the camera holds off the frozen ball (tight — auto-widened to clear hulls)
  ORBIT_HEIGHT: 4.5,       // metres above the ball the orbit rides (auto-lifted over masts when a ship crowds it)
  HOLD_S: 0.5,             // dead-freeze dramatic beat after the orbit, before the release
  RELEASE_S: 0.6,          // real seconds snapping FREEZE_SCALE → 1× as the ball completes its flight into the hull
  FOV_PUNCH_DEG: 7,        // degrees of extra FOV pulled in across the orbit (a subtle dolly-zoom)
  VIGNETTE: 0.55,          // extra vignette strength during the orbit (0..1) — the cheap "motion-blur" mood
  // ── close-range ROBUSTNESS (the orbit ring must never enter a hull, whatever the combat distance) ──
  ORBIT_RADIUS_MIN: 12,    // never orbit tighter than this (clears a hull centred on the frozen ball)
  ORBIT_RADIUS_MAX: 46,    // never fly out past this (a runaway-radius guard)
  ORBIT_HEIGHT_MAX: 26,    // ceiling for the auto-lift when ships crowd the ring
  HULL_CLEAR: 15,          // metres the orbit RADIUS is floored to, so the ring always clears the target hull centre
  HULL_MAST_H: 15.5,       // metres the camera lifts to when a ship's hull footprint would otherwise be pierced
  HULL_GRAZE: 12.5,        // horizontal clearance under which the camera lifts (full lift at ≤ this, over the masts)
  HULL_GRAZE_BAND: 6,      // metres above HULL_GRAZE over which the lift eases in (a gentle rise, no vertical snap)
} as const;
